import { z } from 'zod';

// src/patterns/patterns-registry.json
var patterns_registry_default = {
  version: "1.0.0",
  exportedAt: "2026-10-10T01:07:16.949Z",
  patterns: {
    "detail-panel": {
      type: "detail-panel",
      category: "display",
      sourceFile: "components/core/organisms/DetailPanel.tsx",
      tier: "organisms",
      family: "core",
      description: "Detail view panel for drawers/sidebars",
      fieldsContract: "display",
      suggestedFor: [
        "drawer content",
        "side panels",
        "quick view"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "detail",
          tier: "presentation"
        },
        entity: {
          types: [
            "object"
          ],
          description: "RECORD-cardinality: renders ONE record (see body collapse below).",
          kind: "entity",
          cardinality: "record"
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        onTitleCommit: {
          types: [
            "function"
          ],
          description: "When bound, the title becomes editable in place: clicking it swaps the heading for an input, Enter or blur commits, Escape reverts. Receives the committed title and the record's id so the host can persist a partial update. Unbound, the title stays a plain heading.",
          kind: "callback",
          callbackArgs: [
            {
              name: "title",
              type: "string"
            },
            {
              name: "id",
              type: "string"
            }
          ],
          default: ""
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "subtitle prop"
        },
        status: {
          types: [
            "object"
          ],
          description: "status prop",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "default",
                "success",
                "warning",
                "danger",
                "info"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        avatar: {
          types: [
            "node"
          ],
          description: "avatar prop"
        },
        sections: {
          types: [
            "array"
          ],
          description: "sections prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              title: {
                types: [
                  "string"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object",
                    "string"
                  ],
                  properties: {
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    value: {
                      types: [
                        "node"
                      ]
                    },
                    icon: {
                      types: [
                        "icon",
                        "string"
                      ]
                    },
                    copyable: {
                      types: [
                        "boolean"
                      ]
                    }
                  },
                  required: [
                    "label",
                    "value"
                  ]
                }
              }
            },
            required: [
              "title",
              "fields"
            ]
          }
        },
        actions: {
          types: [
            "array"
          ],
          description: "Unified actions array - first action with variant='primary' is the main action",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: `Max inline action buttons before the rest collapse into a "\u22EF" overflow menu (mirrors DataGrid's maxInlineActions). Defaults to 2 \u2014 primary + secondary visible, the rest under the menu \u2014 so every detail panel carries the same action pattern.`,
          default: 2
        },
        backAction: {
          types: [
            "object"
          ],
          description: "Navigation-back affordance. Renders top-LEFT before the title (OS/back convention \u2014 Almadar_UX \xA78.4), spatially separated from the right-aligned action buttons + close X. Never inferred from actions[] labels.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            icon: {
              types: [
                "icon",
                "string"
              ]
            },
            onClick: {
              types: [
                "function"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            },
            navigatesTo: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "primary",
                "secondary",
                "ghost",
                "danger"
              ]
            },
            when: {
              types: [
                "sexpr",
                "function"
              ]
            },
            payload: {
              types: [
                "sexpr",
                "function"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "The event of the declared action that dismisses the panel. The matching action renders as the header close \xD7; omitted, no \xD7 is drawn.",
          kind: "event"
        },
        footer: {
          types: [
            "node"
          ],
          description: "footer prop"
        },
        slideOver: {
          types: [
            "boolean"
          ],
          description: "slideOver prop",
          default: false
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out. Inline mode only; the slide-over is itself a surface.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        fields: {
          types: [
            "array"
          ],
          description: "Fields to display: field names, or declared fields ({ name, label, variant, format, colorMap, labels })",
          required: true,
          items: {
            types: [
              "string",
              "object"
            ],
            nonAuthorable: true,
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              type: {
                types: [
                  "string"
                ]
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        fieldNames: {
          types: [
            "array"
          ],
          description: "Alias for fields - backwards compatibility",
          items: {
            types: [
              "string"
            ]
          }
        },
        initialData: {
          types: [
            "object"
          ],
          description: "Initial data for edit mode (passed by compiler)",
          kind: "entity",
          cardinality: "record"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Display mode (passed by compiler)"
        },
        position: {
          types: [
            "string"
          ],
          description: "Panel position (for drawer/sidebar placement)",
          enumValues: [
            "left",
            "right"
          ]
        },
        width: {
          types: [
            "string"
          ],
          description: "Panel width (CSS value, e.g., '400px', '50%')"
        },
        displayFields: {
          types: [
            "array"
          ],
          description: "Display fields (alias for fields)",
          items: {
            types: [
              "string"
            ]
          }
        },
        showActions: {
          types: [
            "boolean"
          ],
          description: "When false, hides the action buttons + overflow menu (and the close \xD7). Defaults to true.",
          default: true
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        look: {
          types: [
            "string"
          ],
          description: "Page variant: panel (labelled fields, default), profile (person/member hero), showcase (image-first product/listing), workflow (lifecycle stepper + activity), map (location hero), ledger (document with line items + totals), conversation (thread beside the record), workspace (tabbed record).",
          enumValues: [
            "panel",
            "profile",
            "showcase",
            "workflow",
            "map",
            "ledger",
            "conversation",
            "workspace"
          ],
          default: "panel"
        },
        avatarField: {
          types: [
            "string"
          ],
          description: "Record field holding the avatar image. The profile look always shows an avatar, with the title's initials when this is unset or empty."
        },
        coverField: {
          types: [
            "string"
          ],
          description: "Record field holding the cover image (profile)."
        },
        mediaField: {
          types: [
            "string"
          ],
          description: "Record field holding the image, or list of images, the showcase look leads with."
        },
        stageField: {
          types: [
            "string"
          ],
          description: "Record field holding the current lifecycle stage (workflow)."
        },
        stages: {
          types: [
            "array"
          ],
          description: "The lifecycle in order, `{ value, label }` per stage (workflow).",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        activity: {
          types: [
            "array"
          ],
          description: "Activity entries shown under the record (workflow).",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              date: {
                types: [
                  "string"
                ]
              },
              status: {
                types: [
                  "string"
                ],
                enumValues: [
                  "complete",
                  "active",
                  "pending",
                  "error"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              tags: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "id",
              "title"
            ]
          }
        },
        activityContent: {
          types: [
            "node"
          ],
          description: "Activity composed in instead of `activity` \u2014 e.g. the record's own history trait (workflow)."
        },
        latitudeField: {
          types: [
            "string"
          ],
          description: "Record fields holding the location (map)."
        },
        longitudeField: {
          types: [
            "string"
          ],
          description: "longitudeField prop"
        },
        lineItems: {
          types: [
            "object",
            "array"
          ],
          description: "Child rows shown as the document's line items (ledger).",
          kind: "entity",
          cardinality: "collection"
        },
        lineItemColumns: {
          types: [
            "array"
          ],
          description: "Columns of the line-items table (ledger).",
          items: {
            types: [
              "object"
            ],
            properties: {
              key: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "string"
                ],
                enumValues: [
                  "normal",
                  "medium",
                  "semibold"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "badge",
                  "date",
                  "currency",
                  "number",
                  "percent",
                  "boolean"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              sortable: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "key"
            ]
          }
        },
        lineItemsContent: {
          types: [
            "node"
          ],
          description: "Line items composed in instead of `lineItems` \u2014 e.g. the child rows' own trait (ledger)."
        },
        totals: {
          types: [
            "array"
          ],
          description: "Record fields shown as the document's totals, in order (ledger).",
          items: {
            types: [
              "string",
              "object"
            ],
            nonAuthorable: true,
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              type: {
                types: [
                  "string"
                ]
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        thread: {
          types: [
            "object",
            "array"
          ],
          description: "The thread shown as the main surface (conversation).",
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "authorName",
            "content"
          ],
          properties: {
            authorName: {
              types: [
                "string"
              ]
            },
            content: {
              types: [
                "string"
              ]
            },
            authorAvatarUrl: {
              types: [
                "string"
              ]
            },
            postedAt: {
              types: [
                "string"
              ]
            },
            voteCount: {
              types: [
                "number"
              ]
            },
            userVote: {
              types: [
                "string"
              ],
              enumValues: [
                "up",
                "down",
                "none"
              ]
            },
            collapsed: {
              types: [
                "boolean"
              ]
            },
            replies: {
              types: [
                "object",
                "array"
              ],
              cyclic: true,
              cyclicTypeName: "ReplyNodeRow"
            }
          }
        },
        threadContent: {
          types: [
            "node"
          ],
          description: "The thread composed in instead of `thread` \u2014 e.g. the replies' own trait with its composer (conversation)."
        },
        tabs: {
          types: [
            "array"
          ],
          description: "Tabs after the overview tab, each listing declared field names (workspace).",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              content: {
                types: [
                  "node"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "page-header": {
      type: "page-header",
      category: "header",
      sourceFile: "components/core/molecules/PageHeader.tsx",
      tier: "molecules",
      family: "core",
      description: "Page title with optional breadcrumb and action buttons",
      suggestedFor: [
        "list pages",
        "detail pages",
        "dashboards"
      ],
      typicalSize: "tiny",
      propsSchema: {
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "The shape an empty slot shows while this header's server render is in flight (`none` opts out). @default 'header'",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "header",
          tier: "presentation"
        },
        title: {
          types: [
            "string",
            "number"
          ],
          description: "Page title - accepts string or number from generated code accessing dynamic entity data"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon shown before the title."
        },
        subtitle: {
          types: [
            "string",
            "number"
          ],
          description: "Optional subtitle/description"
        },
        showBack: {
          types: [
            "boolean"
          ],
          description: "Show back button",
          default: false
        },
        backEvent: {
          types: [
            "string"
          ],
          description: "Event to emit when back is clicked (default: BACK)",
          kind: "event-ref",
          emitPayloadSchema: [],
          default: "BACK"
        },
        breadcrumbs: {
          types: [
            "array"
          ],
          description: "Breadcrumbs",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        status: {
          types: [
            "object",
            "string"
          ],
          description: "Status badge \u2014 a bare string (an entity's status field bound directly, e.g. `status: @entity.status`) renders as a default-variant badge",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "default",
                "success",
                "warning",
                "danger",
                "info"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions array - first action with variant='primary' (or first action) is the main action",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              loading: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        tabs: {
          types: [
            "array"
          ],
          description: "Tabs for sub-navigation",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              count: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        activeTab: {
          types: [
            "string"
          ],
          description: "activeTab prop"
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "onTabChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Custom content in the header"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    form: {
      type: "form",
      category: "form",
      sourceFile: "components/core/organisms/Form.tsx",
      tier: "organisms",
      family: "core",
      description: "Complete form with fields and actions",
      fieldsContract: "form",
      suggestedFor: [
        "create forms",
        "edit forms",
        "data entry"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface (with card padding) behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "The shape an empty slot shows while this form's server render is in flight (`none` opts out). @default 'form'",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "form",
          tier: "presentation"
        },
        children: {
          types: [
            "node"
          ],
          description: "Form fields (traditional React children)"
        },
        onSubmit: {
          types: [
            "string"
          ],
          description: "Submit event name for trait dispatch (emitted via eventBus as UI:{onSubmit})"
        },
        onCancel: {
          types: [
            "string"
          ],
          description: "Cancel event name for trait dispatch (emitted via eventBus as UI:{onCancel})"
        },
        layout: {
          types: [
            "string"
          ],
          description: "Form layout",
          enumValues: [
            "vertical",
            "horizontal",
            "inline"
          ],
          default: "vertical"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between fields",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema-based props",
          kind: "entity",
          cardinality: "collection"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Form mode \u2014 'create' for new records, 'edit' for updating existing. Accepts `string` so schema-driven callers (whose `config.mode` is typed as `string` per the trait's declared config block) compile cleanly. The runtime treats anything other than 'edit' as 'create'."
        },
        fields: {
          types: [
            "array"
          ],
          description: "Fields definition (schema format) - accepts readonly for generated const arrays",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              inputType: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              required: {
                types: [
                  "boolean"
                ]
              },
              defaultValue: {
                types: [
                  "object"
                ],
                controlValue: true
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    value: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "value",
                    "label"
                  ]
                }
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  displayField: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              pattern: {
                types: [
                  "string"
                ]
              },
              validation: {
                types: [
                  "object"
                ],
                properties: {
                  enum: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              readonly: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        initialData: {
          types: [
            "object"
          ],
          description: "Initial form data",
          kind: "entity",
          cardinality: "record"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        submitLabel: {
          types: [
            "string"
          ],
          description: "Submit button label"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Cancel button label (if provided, shows cancel button)"
        },
        showCancel: {
          types: [
            "boolean"
          ],
          description: "Show cancel button (defaults to true for schema forms)"
        },
        showSubmit: {
          types: [
            "boolean"
          ],
          description: "Show submit button (defaults to true for schema forms). Set false when a parent atom owns the submit action externally (e.g. wizard footers).",
          default: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Form title (used by ModalSlot to extract title)"
        },
        submitEvent: {
          types: [
            "string"
          ],
          description: "Event dispatch props (for trait state machine integration)",
          kind: "event-ref",
          entityRowField: "data",
          emitPayloadSchema: [
            {
              name: "data",
              type: "@entity",
              required: true
            }
          ],
          default: "SAVE"
        },
        cancelEvent: {
          types: [
            "string"
          ],
          description: "Event to dispatch on cancel (defaults to 'CANCEL')",
          kind: "event",
          default: "CANCEL"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation data props",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          },
          default: {}
        },
        relationsLoading: {
          types: [
            "object"
          ],
          description: "Loading state for relation data: { fieldName: boolean }",
          mapValue: {
            types: [
              "boolean"
            ]
          },
          default: {}
        },
        fieldOverrides: {
          types: [
            "array"
          ],
          description: "Per-field display-copy overrides (label/placeholder/hint), merged by field name over the schema-enriched fields.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        conditionalFields: {
          types: [
            "object",
            "boolean"
          ],
          description: "Inspection form extensions",
          mapValue: {
            types: [
              "sexpr"
            ]
          }
        },
        hiddenCalculations: {
          types: [
            "array",
            "boolean"
          ],
          description: "Hidden calculations that emit GLOBAL_VARIABLE_SET on field change (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              variableName: {
                types: [
                  "string"
                ]
              },
              expression: {
                types: [
                  "sexpr"
                ]
              },
              triggerFields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "variableName",
              "expression",
              "triggerFields"
            ]
          }
        },
        violationTriggers: {
          types: [
            "array",
            "boolean"
          ],
          description: "Violation conditions that emit VIOLATION_DETECTED when met (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              condition: {
                types: [
                  "sexpr"
                ]
              },
              violation: {
                types: [
                  "object"
                ],
                properties: {
                  law: {
                    types: [
                      "string"
                    ]
                  },
                  article: {
                    types: [
                      "string"
                    ]
                  },
                  actionType: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "measure",
                      "admin",
                      "penalty"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "law",
                  "article",
                  "actionType",
                  "message"
                ]
              },
              fieldId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "condition",
              "violation"
            ]
          }
        },
        evaluationContext: {
          types: [
            "object"
          ],
          description: "Context for S-expression evaluation",
          properties: {
            formValues: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            globalVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            localVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            entity: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            }
          },
          propertyRequired: [
            "formValues",
            "globalVariables"
          ]
        },
        sections: {
          types: [
            "array"
          ],
          description: "Nested form sections with optional conditions",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              condition: {
                types: [
                  "sexpr"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    field: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    inputType: {
                      types: [
                        "string"
                      ]
                    },
                    placeholder: {
                      types: [
                        "string"
                      ]
                    },
                    required: {
                      types: [
                        "boolean"
                      ]
                    },
                    defaultValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    },
                    options: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          value: {
                            types: [
                              "string"
                            ]
                          },
                          label: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "value",
                          "label"
                        ]
                      }
                    },
                    values: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    labels: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    relation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        displayField: {
                          types: [
                            "string"
                          ]
                        },
                        cardinality: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "one",
                            "many",
                            "one-to-many",
                            "many-to-one",
                            "many-to-many"
                          ]
                        }
                      },
                      required: [
                        "entity"
                      ]
                    },
                    min: {
                      types: [
                        "number"
                      ]
                    },
                    max: {
                      types: [
                        "number"
                      ]
                    },
                    pattern: {
                      types: [
                        "string"
                      ]
                    },
                    validation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        enum: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "string"
                            ]
                          }
                        }
                      }
                    },
                    readonly: {
                      types: [
                        "boolean"
                      ]
                    },
                    disabled: {
                      types: [
                        "boolean"
                      ]
                    },
                    hint: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              collapsible: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "title",
              "fields"
            ]
          },
          default: []
        },
        onFieldChange: {
          types: [
            "function"
          ],
          description: "Callback when any field value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "change",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  fieldId: {
                    types: [
                      "string"
                    ]
                  },
                  value: {
                    types: [
                      "string",
                      "number",
                      "boolean",
                      "array",
                      "object"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    },
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      url: {
                        types: [
                          "string"
                        ]
                      },
                      mimeType: {
                        types: [
                          "string"
                        ]
                      },
                      sizeBytes: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "url",
                      "mimeType",
                      "sizeBytes"
                    ]
                  },
                  formValues: {
                    types: [
                      "object"
                    ],
                    mapValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    }
                  }
                },
                required: [
                  "fieldId",
                  "value",
                  "formValues"
                ]
              }
            }
          ]
        },
        configPath: {
          types: [
            "string"
          ],
          description: "Config path for form configuration (schema-driven)"
        },
        repeatable: {
          types: [
            "boolean"
          ],
          description: "Whether the form supports repeatable entries"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-section": {
      type: "form-section",
      category: "form",
      sourceFile: "components/core/organisms/Form.tsx",
      tier: "organisms",
      family: "core",
      description: "Alias for form \u2014 Complete form with fields and actions",
      fieldsContract: "form",
      suggestedFor: [
        "create forms",
        "edit forms",
        "data entry"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface (with card padding) behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "The shape an empty slot shows while this form's server render is in flight (`none` opts out). @default 'form'",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "form",
          tier: "presentation"
        },
        children: {
          types: [
            "node"
          ],
          description: "Form fields (traditional React children)"
        },
        onSubmit: {
          types: [
            "string"
          ],
          description: "Submit event name for trait dispatch (emitted via eventBus as UI:{onSubmit})"
        },
        onCancel: {
          types: [
            "string"
          ],
          description: "Cancel event name for trait dispatch (emitted via eventBus as UI:{onCancel})"
        },
        layout: {
          types: [
            "string"
          ],
          description: "Form layout",
          enumValues: [
            "vertical",
            "horizontal",
            "inline"
          ],
          default: "vertical"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between fields",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema-based props",
          kind: "entity",
          cardinality: "collection"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Form mode \u2014 'create' for new records, 'edit' for updating existing. Accepts `string` so schema-driven callers (whose `config.mode` is typed as `string` per the trait's declared config block) compile cleanly. The runtime treats anything other than 'edit' as 'create'."
        },
        fields: {
          types: [
            "array"
          ],
          description: "Fields definition (schema format) - accepts readonly for generated const arrays",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              inputType: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              required: {
                types: [
                  "boolean"
                ]
              },
              defaultValue: {
                types: [
                  "object"
                ],
                controlValue: true
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    value: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "value",
                    "label"
                  ]
                }
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  displayField: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              pattern: {
                types: [
                  "string"
                ]
              },
              validation: {
                types: [
                  "object"
                ],
                properties: {
                  enum: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              readonly: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        initialData: {
          types: [
            "object"
          ],
          description: "Initial form data",
          kind: "entity",
          cardinality: "record"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        submitLabel: {
          types: [
            "string"
          ],
          description: "Submit button label"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Cancel button label (if provided, shows cancel button)"
        },
        showCancel: {
          types: [
            "boolean"
          ],
          description: "Show cancel button (defaults to true for schema forms)"
        },
        showSubmit: {
          types: [
            "boolean"
          ],
          description: "Show submit button (defaults to true for schema forms). Set false when a parent atom owns the submit action externally (e.g. wizard footers).",
          default: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Form title (used by ModalSlot to extract title)"
        },
        submitEvent: {
          types: [
            "string"
          ],
          description: "Event dispatch props (for trait state machine integration)",
          kind: "event-ref",
          entityRowField: "data",
          emitPayloadSchema: [
            {
              name: "data",
              type: "@entity",
              required: true
            }
          ],
          default: "SAVE"
        },
        cancelEvent: {
          types: [
            "string"
          ],
          description: "Event to dispatch on cancel (defaults to 'CANCEL')",
          kind: "event",
          default: "CANCEL"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation data props",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          },
          default: {}
        },
        relationsLoading: {
          types: [
            "object"
          ],
          description: "Loading state for relation data: { fieldName: boolean }",
          mapValue: {
            types: [
              "boolean"
            ]
          },
          default: {}
        },
        fieldOverrides: {
          types: [
            "array"
          ],
          description: "Per-field display-copy overrides (label/placeholder/hint), merged by field name over the schema-enriched fields.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        conditionalFields: {
          types: [
            "object",
            "boolean"
          ],
          description: "Inspection form extensions",
          mapValue: {
            types: [
              "sexpr"
            ]
          }
        },
        hiddenCalculations: {
          types: [
            "array",
            "boolean"
          ],
          description: "Hidden calculations that emit GLOBAL_VARIABLE_SET on field change (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              variableName: {
                types: [
                  "string"
                ]
              },
              expression: {
                types: [
                  "sexpr"
                ]
              },
              triggerFields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "variableName",
              "expression",
              "triggerFields"
            ]
          }
        },
        violationTriggers: {
          types: [
            "array",
            "boolean"
          ],
          description: "Violation conditions that emit VIOLATION_DETECTED when met (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              condition: {
                types: [
                  "sexpr"
                ]
              },
              violation: {
                types: [
                  "object"
                ],
                properties: {
                  law: {
                    types: [
                      "string"
                    ]
                  },
                  article: {
                    types: [
                      "string"
                    ]
                  },
                  actionType: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "measure",
                      "admin",
                      "penalty"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "law",
                  "article",
                  "actionType",
                  "message"
                ]
              },
              fieldId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "condition",
              "violation"
            ]
          }
        },
        evaluationContext: {
          types: [
            "object"
          ],
          description: "Context for S-expression evaluation",
          properties: {
            formValues: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            globalVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            localVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            entity: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            }
          },
          propertyRequired: [
            "formValues",
            "globalVariables"
          ]
        },
        sections: {
          types: [
            "array"
          ],
          description: "Nested form sections with optional conditions",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              condition: {
                types: [
                  "sexpr"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    field: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    inputType: {
                      types: [
                        "string"
                      ]
                    },
                    placeholder: {
                      types: [
                        "string"
                      ]
                    },
                    required: {
                      types: [
                        "boolean"
                      ]
                    },
                    defaultValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    },
                    options: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          value: {
                            types: [
                              "string"
                            ]
                          },
                          label: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "value",
                          "label"
                        ]
                      }
                    },
                    values: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    labels: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    relation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        displayField: {
                          types: [
                            "string"
                          ]
                        },
                        cardinality: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "one",
                            "many",
                            "one-to-many",
                            "many-to-one",
                            "many-to-many"
                          ]
                        }
                      },
                      required: [
                        "entity"
                      ]
                    },
                    min: {
                      types: [
                        "number"
                      ]
                    },
                    max: {
                      types: [
                        "number"
                      ]
                    },
                    pattern: {
                      types: [
                        "string"
                      ]
                    },
                    validation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        enum: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "string"
                            ]
                          }
                        }
                      }
                    },
                    readonly: {
                      types: [
                        "boolean"
                      ]
                    },
                    disabled: {
                      types: [
                        "boolean"
                      ]
                    },
                    hint: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              collapsible: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "title",
              "fields"
            ]
          },
          default: []
        },
        onFieldChange: {
          types: [
            "function"
          ],
          description: "Callback when any field value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "change",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  fieldId: {
                    types: [
                      "string"
                    ]
                  },
                  value: {
                    types: [
                      "string",
                      "number",
                      "boolean",
                      "array",
                      "object"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    },
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      url: {
                        types: [
                          "string"
                        ]
                      },
                      mimeType: {
                        types: [
                          "string"
                        ]
                      },
                      sizeBytes: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "url",
                      "mimeType",
                      "sizeBytes"
                    ]
                  },
                  formValues: {
                    types: [
                      "object"
                    ],
                    mapValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    }
                  }
                },
                required: [
                  "fieldId",
                  "value",
                  "formValues"
                ]
              }
            }
          ]
        },
        configPath: {
          types: [
            "string"
          ],
          description: "Config path for form configuration (schema-driven)"
        },
        repeatable: {
          types: [
            "boolean"
          ],
          description: "Whether the form supports repeatable entries"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "search-input": {
      type: "search-input",
      category: "filter",
      sourceFile: "components/core/molecules/SearchInput.tsx",
      tier: "molecules",
      family: "core",
      description: "Search input with events",
      minUsableWidth: 160,
      suggestedFor: [
        "search functionality",
        "quick filters"
      ],
      typicalSize: "tiny",
      propsSchema: {
        value: {
          types: [
            "string"
          ],
          description: "Search value (controlled mode)"
        },
        onSearch: {
          types: [
            "function"
          ],
          description: "Callback when search value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        debounceMs: {
          types: [
            "number"
          ],
          description: "Debounce delay in milliseconds",
          default: 300
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Show loading state",
          default: false
        },
        placeholder: {
          types: [
            "string"
          ],
          description: 'Placeholder text (the translated "Search" when omitted)'
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show clear button",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        event: {
          types: [
            "string"
          ],
          description: "Declarative search event \u2014 emits UI:{event} via eventBus on query submit (alongside canonical UI:SEARCH)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "searchTerm",
              type: "string",
              required: true
            },
            {
              name: "entity",
              type: "string",
              required: false
            }
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity type for context-aware search. When provided, search events include entity context."
        },
        query: {
          types: [
            "string"
          ],
          description: 'Query singleton binding for state management. When provided, syncs search state with the query singleton. Example: "@TaskQuery"'
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "filter-group": {
      type: "filter-group",
      category: "filter",
      sourceFile: "components/core/molecules/FilterGroup.tsx",
      tier: "molecules",
      family: "core",
      description: "FilterGroup \u2014 a panel of filter controls that narrows a collection by field values.",
      capabilities: "search refinement panel, facet filters, admin list filters, records filter sidebar, filter chips panel",
      suggestedFor: [
        "list pages with filterable data"
      ],
      typicalSize: "small",
      propsSchema: {
        entity: {
          types: [
            "string"
          ],
          description: "Entity name to filter",
          required: true
        },
        filters: {
          types: [
            "array"
          ],
          description: "Filter definitions from schema",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              filterType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "field",
              "label"
            ]
          }
        },
        onFilterChange: {
          types: [
            "function"
          ],
          description: "Callback when a filter changes - for EntityStore integration",
          kind: "callback",
          callbackArgs: [
            {
              name: "field",
              type: "string"
            },
            {
              name: "value",
              type: "string"
            }
          ]
        },
        onClearAll: {
          types: [
            "function"
          ],
          description: "Callback to clear all filters",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant style",
          enumValues: [
            "default",
            "compact",
            "pills",
            "vertical"
          ],
          default: "default"
        },
        showIcon: {
          types: [
            "boolean"
          ],
          description: "Show filter icon",
          default: true
        },
        query: {
          types: [
            "string"
          ],
          description: 'Query singleton binding for state management. When provided, syncs filter state with the query singleton. Example: "@TaskQuery"'
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant.",
          enumValues: [
            "toolbar",
            "chips",
            "pills",
            "popover-trigger",
            "inline-column-header"
          ],
          default: "toolbar"
        },
        event: {
          types: [
            "string"
          ],
          description: "Declarative filter event \u2014 emits UI:{event} via eventBus on filter change (alongside canonical UI:FILTER)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "entity",
              type: "string",
              required: true
            },
            {
              name: "field",
              type: "string",
              required: true
            },
            {
              name: "value",
              type: "string",
              required: true
            },
            {
              name: "query",
              type: "string",
              required: false
            }
          ]
        },
        clearEvent: {
          types: [
            "string"
          ],
          description: "Declarative clear event \u2014 emits UI:{clearEvent} via eventBus on clear-all (alongside canonical UI:CLEAR_FILTERS)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "entity",
              type: "string",
              required: true
            },
            {
              name: "query",
              type: "string",
              required: false
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    tabs: {
      type: "tabs",
      category: "navigation",
      sourceFile: "components/core/molecules/Tabs.tsx",
      tier: "molecules",
      family: "core",
      description: "Tab navigation within page",
      suggestedFor: [
        "pages with multiple views",
        "filtered lists"
      ],
      typicalSize: "tiny",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Tab items",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              iconAsset: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              active: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        tabs: {
          types: [
            "array"
          ],
          description: "Tab items (alias for items - used by generated code)",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              iconAsset: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              active: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        defaultActiveTab: {
          types: [
            "string"
          ],
          description: "Default active tab ID"
        },
        activeTab: {
          types: [
            "string"
          ],
          description: "Controlled active tab ID"
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "Callback when tab changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "tabId",
              type: "string"
            }
          ]
        },
        tabChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative tab change event \u2014 emits UI:{tabChangeEvent} with { tabId }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "tabId",
              type: "string",
              required: true
            }
          ]
        },
        variant: {
          types: [
            "string"
          ],
          description: "Tab variant",
          enumValues: [
            "default",
            "pills",
            "underline"
          ],
          default: "default"
        },
        orientation: {
          types: [
            "string"
          ],
          description: "Tab orientation",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    breadcrumb: {
      type: "breadcrumb",
      category: "navigation",
      sourceFile: "components/core/molecules/Breadcrumb.tsx",
      tier: "molecules",
      family: "core",
      description: "Breadcrumb navigation",
      suggestedFor: [
        "hierarchical navigation",
        "detail pages"
      ],
      typicalSize: "tiny",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Breadcrumb items. Omit together with `fromNavStack` to render the orbital-scoped navigation stack instead of an authored trail.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              path: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              isCurrent: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              }
            },
            required: [
              "label"
            ]
          },
          example: [
            {
              label: "Home",
              href: "/"
            },
            {
              label: "Page"
            }
          ]
        },
        fromNavStack: {
          types: [
            "boolean"
          ],
          description: "Render the current orbital's navigation stack (NavStackProvider) as the trail: one crumb per visited/declared level, last entry current. The stack is client-session state both execution paths maintain; outside a provider this renders nothing.",
          default: false
        },
        itemEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a non-current crumb is clicked, with payload `{ label, href, index }` \u2014 the trait handles it with `(navigate ?href)`. Without it, stack crumbs navigate directly.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "label",
              type: "string",
              required: true
            },
            {
              name: "href",
              type: "string",
              required: false
            },
            {
              name: "index",
              type: "number",
              required: true
            }
          ]
        },
        separator: {
          types: [
            "icon",
            "string"
          ],
          description: "Separator icon (canonical kebab-case name or LucideIcon component)",
          default: "chevron-right"
        },
        maxItems: {
          types: [
            "number"
          ],
          description: "Maximum items to show (truncates with ellipsis)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "wizard-progress": {
      type: "wizard-progress",
      category: "navigation",
      sourceFile: "components/core/molecules/WizardProgress.tsx",
      tier: "molecules",
      family: "core",
      description: "Wizard step progress indicator",
      suggestedFor: [
        "multi-step forms",
        "onboarding"
      ],
      typicalSize: "tiny",
      propsSchema: {
        steps: {
          types: [
            "array"
          ],
          description: "Step definitions (compatible with WizardContainer's WizardStep). A string is shorthand for `{ id: str, title: str }`.",
          items: {
            types: [
              "object",
              "string"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "title"
            ]
          }
        },
        currentStep: {
          types: [
            "number"
          ],
          description: "Current step index (0-based)",
          required: true
        },
        onStepClick: {
          types: [
            "function"
          ],
          description: "Callback when a completed step is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "stepIndex",
              type: "number"
            }
          ]
        },
        allowNavigation: {
          types: [
            "boolean"
          ],
          description: "Allow clicking on completed steps to navigate back",
          default: true
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact mode (smaller, no titles)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        stepClickEvent: {
          types: [
            "string"
          ],
          description: "Declarative step click event \u2014 emits UI:{stepClickEvent} with { stepIndex }. Setting it requires declaring that event (with a `{ stepIndex }` payload) and a transition handling it in the same trait; omit this prop for non-clickable progress indicators.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stepIndex",
              type: "number",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "wizard-navigation": {
      type: "wizard-navigation",
      category: "navigation",
      sourceFile: "components/core/molecules/WizardNavigation.tsx",
      tier: "molecules",
      family: "core",
      description: "Wizard navigation buttons",
      suggestedFor: [
        "multi-step forms",
        "wizards"
      ],
      typicalSize: "tiny",
      propsSchema: {
        currentStep: {
          types: [
            "number"
          ],
          description: "Current step index (0-based)",
          required: true
        },
        totalSteps: {
          types: [
            "number"
          ],
          description: "Total number of steps",
          required: true
        },
        isValid: {
          types: [
            "boolean"
          ],
          description: "Whether the current step is valid (enables Next/Complete)",
          default: true
        },
        showBack: {
          types: [
            "boolean"
          ],
          description: "Show the Back button",
          default: true
        },
        showNext: {
          types: [
            "boolean"
          ],
          description: "Show the Next button",
          default: true
        },
        showComplete: {
          types: [
            "boolean"
          ],
          description: "Show the Complete button (on last step)",
          default: true
        },
        backLabel: {
          types: [
            "string"
          ],
          description: "Custom label for Back button"
        },
        nextLabel: {
          types: [
            "string"
          ],
          description: "Custom label for Next button"
        },
        completeLabel: {
          types: [
            "string"
          ],
          description: "Custom label for Complete button"
        },
        onBack: {
          types: [
            "string"
          ],
          description: "Event to emit on Back click \u2014 emits UI:{onBack} with { currentStep, totalSteps }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "currentStep",
              type: "number",
              required: true
            },
            {
              name: "totalSteps",
              type: "number",
              required: true
            }
          ],
          default: "WIZARD_BACK"
        },
        onNext: {
          types: [
            "string"
          ],
          description: "Event to emit on Next click \u2014 emits UI:{onNext} with { currentStep, totalSteps }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "currentStep",
              type: "number",
              required: true
            },
            {
              name: "totalSteps",
              type: "number",
              required: true
            }
          ],
          default: "WIZARD_NEXT"
        },
        onComplete: {
          types: [
            "string"
          ],
          description: "Event to emit on Complete click \u2014 emits UI:{onComplete} with { currentStep, totalSteps }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "currentStep",
              type: "number",
              required: true
            },
            {
              name: "totalSteps",
              type: "number",
              required: true
            }
          ],
          default: "WIZARD_COMPLETE"
        },
        onBackClick: {
          types: [
            "function"
          ],
          description: "Direct callback for Back (alternative to event)",
          kind: "callback",
          callbackArgs: []
        },
        onNextClick: {
          types: [
            "function"
          ],
          description: "Direct callback for Next (alternative to event)",
          kind: "callback",
          callbackArgs: []
        },
        onCompleteClick: {
          types: [
            "function"
          ],
          description: "Direct callback for Complete (alternative to event)",
          kind: "callback",
          callbackArgs: []
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact mode (smaller padding)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "wizard-container": {
      type: "wizard-container",
      category: "navigation",
      sourceFile: "components/core/molecules/WizardContainer.tsx",
      tier: "molecules",
      family: "core",
      description: "Multi-step wizard container",
      suggestedFor: [
        "multi-step forms",
        "wizards",
        "onboarding flows"
      ],
      typicalSize: "large",
      propsSchema: {
        steps: {
          types: [
            "array"
          ],
          description: "Wizard steps",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              tabId: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              optional: {
                types: [
                  "boolean"
                ]
              },
              isValid: {
                types: [
                  "function"
                ]
              },
              sections: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    description: {
                      types: [
                        "string"
                      ]
                    },
                    fields: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          id: {
                            types: [
                              "string"
                            ]
                          },
                          type: {
                            types: [
                              "string"
                            ]
                          },
                          label: {
                            types: [
                              "string"
                            ]
                          },
                          required: {
                            types: [
                              "boolean"
                            ]
                          },
                          repeatable: {
                            types: [
                              "boolean"
                            ]
                          },
                          options: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                value: {
                                  types: [
                                    "string"
                                  ]
                                },
                                label: {
                                  types: [
                                    "string"
                                  ]
                                },
                                isDefault: {
                                  types: [
                                    "boolean"
                                  ]
                                }
                              },
                              required: [
                                "value",
                                "label"
                              ]
                            }
                          },
                          defaultValue: {
                            types: [
                              "string",
                              "number",
                              "boolean",
                              "array",
                              "object"
                            ],
                            items: {
                              types: [
                                "string"
                              ]
                            },
                            properties: {
                              name: {
                                types: [
                                  "string"
                                ]
                              },
                              url: {
                                types: [
                                  "string"
                                ]
                              },
                              mimeType: {
                                types: [
                                  "string"
                                ]
                              },
                              sizeBytes: {
                                types: [
                                  "number"
                                ]
                              }
                            },
                            required: [
                              "name",
                              "url",
                              "mimeType",
                              "sizeBytes"
                            ]
                          },
                          condition: {
                            types: [
                              "sexpr"
                            ]
                          },
                          placeholder: {
                            types: [
                              "string"
                            ]
                          },
                          entityField: {
                            types: [
                              "string"
                            ]
                          },
                          minLength: {
                            types: [
                              "number"
                            ]
                          },
                          maxLength: {
                            types: [
                              "number"
                            ]
                          },
                          dataSource: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          displayFields: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          searchConfig: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          hiddenCalculations: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                variable: {
                                  types: [
                                    "string"
                                  ]
                                },
                                expression: {
                                  types: [
                                    "string"
                                  ]
                                },
                                scope: {
                                  types: [
                                    "string"
                                  ]
                                }
                              },
                              required: [
                                "variable",
                                "expression"
                              ]
                            }
                          },
                          signatureConfig: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          displayTemplate: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          lawReference: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          contextMenu: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          calculated: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          readOnly: {
                            types: [
                              "boolean"
                            ]
                          },
                          minDate: {
                            types: [
                              "string"
                            ]
                          },
                          stats: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                label: {
                                  types: [
                                    "string"
                                  ]
                                },
                                value: {
                                  types: [
                                    "string",
                                    "number",
                                    "boolean",
                                    "array",
                                    "object"
                                  ],
                                  items: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  properties: {
                                    name: {
                                      types: [
                                        "string"
                                      ]
                                    },
                                    url: {
                                      types: [
                                        "string"
                                      ]
                                    },
                                    mimeType: {
                                      types: [
                                        "string"
                                      ]
                                    },
                                    sizeBytes: {
                                      types: [
                                        "number"
                                      ]
                                    }
                                  },
                                  required: [
                                    "name",
                                    "url",
                                    "mimeType",
                                    "sizeBytes"
                                  ]
                                },
                                icon: {
                                  types: [
                                    "string"
                                  ]
                                }
                              },
                              required: [
                                "label",
                                "value"
                              ]
                            }
                          },
                          items: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                id: {
                                  types: [
                                    "string"
                                  ]
                                },
                                label: {
                                  types: [
                                    "string"
                                  ]
                                },
                                autoCheck: {
                                  types: [
                                    "boolean"
                                  ]
                                }
                              },
                              required: [
                                "id",
                                "label"
                              ]
                            }
                          }
                        },
                        required: [
                          "id",
                          "type"
                        ]
                      }
                    },
                    subsections: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        cyclic: true,
                        cyclicTypeName: "WizardSection"
                      }
                    },
                    condition: {
                      types: [
                        "sexpr"
                      ]
                    },
                    repeatable: {
                      types: [
                        "boolean"
                      ]
                    },
                    minItems: {
                      types: [
                        "number"
                      ]
                    },
                    addButtonLabel: {
                      types: [
                        "string"
                      ]
                    },
                    hiddenCalculations: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          variable: {
                            types: [
                              "string"
                            ]
                          },
                          expression: {
                            types: [
                              "string"
                            ]
                          },
                          scope: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "variable",
                          "expression"
                        ]
                      }
                    },
                    dataSource: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "object"
                        ],
                        controlValue: true
                      }
                    },
                    readOnly: {
                      types: [
                        "boolean"
                      ]
                    }
                  },
                  required: [
                    "id"
                  ]
                }
              },
              globalVariablesRequired: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              globalVariablesSet: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              localVariables: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              entityMapping: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  mode: {
                    types: [
                      "string"
                    ]
                  },
                  parentField: {
                    types: [
                      "string"
                    ]
                  },
                  idField: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "entity",
                  "mode"
                ]
              },
              validationRules: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    condition: {
                      types: [
                        "sexpr"
                      ]
                    },
                    message: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "condition",
                    "message"
                  ]
                }
              },
              lawReferences: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    law: {
                      types: [
                        "string"
                      ]
                    },
                    article: {
                      types: [
                        "string"
                      ]
                    },
                    description: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "law",
                    "article"
                  ]
                }
              },
              phase: {
                types: [
                  "string"
                ]
              },
              contextMenu: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          }
        },
        currentStep: {
          types: [
            "number",
            "string"
          ],
          description: "Current step index (controlled) - accepts number or string for generated code compatibility"
        },
        onStepChange: {
          types: [
            "function"
          ],
          description: "Callback when step changes",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "stepIndex",
              type: "number"
            }
          ]
        },
        onComplete: {
          types: [
            "function"
          ],
          description: "Callback when wizard is completed",
          kind: "callback",
          callbackArgs: []
        },
        showProgress: {
          types: [
            "boolean"
          ],
          description: "Show progress indicator",
          default: true
        },
        allowBack: {
          types: [
            "boolean"
          ],
          description: "Allow navigation to previous steps",
          default: true
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Modal mode (compact header, no padding)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        nextEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{nextEvent} when advancing to next step",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "step",
              type: "number",
              required: true
            }
          ]
        },
        backEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{backEvent} when going to previous step",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "step",
              type: "number",
              required: true
            }
          ]
        },
        completeEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{completeEvent} when wizard completes",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    pagination: {
      type: "pagination",
      category: "navigation",
      sourceFile: "components/core/molecules/Pagination.tsx",
      tier: "molecules",
      family: "core",
      description: "Page navigation controls",
      suggestedFor: [
        "paginated lists",
        "tables"
      ],
      typicalSize: "tiny",
      propsSchema: {
        currentPage: {
          types: [
            "number"
          ],
          description: "Current page (1-indexed)",
          required: true
        },
        totalPages: {
          types: [
            "number"
          ],
          description: "Total number of pages",
          required: true
        },
        onPageChange: {
          types: [
            "function"
          ],
          description: "Callback when page changes (optional - can be a no-op if not interactive)",
          kind: "callback",
          callbackArgs: [
            {
              name: "page",
              type: "number"
            }
          ]
        },
        showPageSize: {
          types: [
            "boolean"
          ],
          description: "Show page size selector",
          default: false
        },
        pageSizeOptions: {
          types: [
            "array"
          ],
          description: "Page size options",
          items: {
            types: [
              "number"
            ]
          },
          default: [
            10,
            20,
            50,
            100
          ]
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Current page size"
        },
        onPageSizeChange: {
          types: [
            "function"
          ],
          description: "Callback when page size changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "size",
              type: "number"
            }
          ]
        },
        showJumpToPage: {
          types: [
            "boolean"
          ],
          description: "Show jump to page input",
          default: false
        },
        showTotal: {
          types: [
            "boolean"
          ],
          description: "Show total count",
          default: false
        },
        totalItems: {
          types: [
            "number"
          ],
          description: "Total items count"
        },
        maxVisiblePages: {
          types: [
            "number"
          ],
          description: "Maximum number of page buttons to show",
          default: 7
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        pageChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative page change event \u2014 emits UI:{pageChangeEvent} with { page }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "page",
              type: "number",
              required: true
            }
          ]
        },
        pageSizeChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative page size change event \u2014 emits UI:{pageSizeChangeEvent} with { pageSize }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "pageSize",
              type: "number",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "empty-state": {
      type: "empty-state",
      category: "state",
      sourceFile: "components/core/molecules/EmptyState.tsx",
      tier: "molecules",
      family: "core",
      description: "Message when no data exists",
      suggestedFor: [
        "list pages",
        "search results"
      ],
      typicalSize: "tiny",
      propsSchema: {
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: 'Icon to display. Accepts either: - A Lucide icon component (LucideIcon) - A string icon name (e.g., "check-circle", "x-circle")'
        },
        title: {
          types: [
            "string"
          ],
          description: "Primary text to display - use title or message (message is alias for backwards compat)"
        },
        message: {
          types: [
            "string"
          ],
          description: "Alias for title - used by schema patterns"
        },
        description: {
          types: [
            "string"
          ],
          description: "description prop"
        },
        actionLabel: {
          types: [
            "string"
          ],
          description: "actionLabel prop"
        },
        onAction: {
          types: [
            "function"
          ],
          description: "onAction prop",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        destructive: {
          types: [
            "boolean"
          ],
          description: "Destructive styling for confirmation dialogs"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant for color styling",
          enumValues: [
            "default",
            "success",
            "error",
            "warning",
            "info"
          ]
        },
        actionEvent: {
          types: [
            "string"
          ],
          description: "Declarative action event \u2014 emits UI:{actionEvent} via eventBus when action button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant (which carries status color).",
          enumValues: [
            "illustrated",
            "icon-only",
            "text-only",
            "mascot"
          ],
          default: "icon-only"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "loading-state": {
      type: "loading-state",
      category: "state",
      sourceFile: "components/core/molecules/LoadingState.tsx",
      tier: "molecules",
      family: "core",
      description: "Loading indicator",
      suggestedFor: [
        "any page with async data"
      ],
      typicalSize: "tiny",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        message: {
          types: [
            "string"
          ],
          description: "message prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        fullPage: {
          types: [
            "boolean"
          ],
          description: "Center over the whole viewport (fix: inset-0 overlay) instead of inline.",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "error-state": {
      type: "error-state",
      category: "state",
      sourceFile: "components/core/molecules/ErrorState.tsx",
      tier: "molecules",
      family: "core",
      description: "Error message display",
      suggestedFor: [
        "error handling",
        "failed requests"
      ],
      typicalSize: "small",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        message: {
          types: [
            "string"
          ],
          description: "Error message to display"
        },
        description: {
          types: [
            "string"
          ],
          description: "Alias for message (schema compatibility)"
        },
        onRetry: {
          types: [
            "function"
          ],
          description: "onRetry prop",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        retryEvent: {
          types: [
            "string"
          ],
          description: "Declarative retry event \u2014 emits UI:{retryEvent} via eventBus when retry button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    notification: {
      type: "notification",
      category: "state",
      sourceFile: "components/core/molecules/Toast.tsx",
      tier: "molecules",
      family: "core",
      description: "Toast notification message",
      suggestedFor: [
        "success messages",
        "error alerts",
        "info messages"
      ],
      typicalSize: "tiny",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Toast variant",
          enumValues: [
            "success",
            "error",
            "info",
            "warning"
          ],
          default: "info"
        },
        message: {
          types: [
            "string"
          ],
          description: "Toast message",
          required: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Toast title (optional)"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Auto-dismiss duration in milliseconds (0 = no auto-dismiss). The timer pauses while the toast is hovered or focused, and a toast carrying an action (`actionLabel` with `onAction`/`actionEvent`) never auto-dismisses.",
          default: 5e3
        },
        dismissible: {
          types: [
            "boolean"
          ],
          description: "Show dismiss button",
          default: true
        },
        onDismiss: {
          types: [
            "function"
          ],
          description: "Callback when toast is dismissed",
          kind: "callback",
          callbackArgs: []
        },
        actionLabel: {
          types: [
            "string"
          ],
          description: "Action button label"
        },
        onAction: {
          types: [
            "function"
          ],
          description: "Action button click handler",
          kind: "callback",
          callbackArgs: []
        },
        badge: {
          types: [
            "string",
            "number"
          ],
          description: "Badge count (optional)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        dismissEvent: {
          types: [
            "string"
          ],
          description: "Declarative dismiss event \u2014 emits UI:{dismissEvent} via eventBus when toast is dismissed",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        actionEvent: {
          types: [
            "string"
          ],
          description: "Declarative action event \u2014 emits UI:{actionEvent} via eventBus when action button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    modal: {
      type: "modal",
      category: "container",
      sourceFile: "components/core/molecules/Modal.tsx",
      tier: "molecules",
      family: "core",
      description: "Modal dialog overlay",
      suggestedFor: [
        "dialogs",
        "confirmations",
        "forms"
      ],
      typicalSize: "medium",
      propsSchema: {
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the modal is open (defaults to true when rendered by slot wrapper)",
          default: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Callback when modal should close (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fires after the exit animation completes (the modal is about to unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        children: {
          types: [
            "node"
          ],
          description: "Modal content (can be empty if using slot content)",
          default: null
        },
        footer: {
          types: [
            "node"
          ],
          description: "footer prop"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "md"
        },
        showCloseButton: {
          types: [
            "boolean"
          ],
          description: "showCloseButton prop",
          default: true
        },
        closeOnOverlayClick: {
          types: [
            "boolean"
          ],
          description: "closeOnOverlayClick prop",
          default: true
        },
        closeOnEscape: {
          types: [
            "boolean"
          ],
          description: "closeOnEscape prop",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Declarative close event \u2014 emits UI:{closeEvent} via eventBus when modal should close",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        swipeDownToClose: {
          types: [
            "boolean"
          ],
          description: "Enable swipe-down-to-close on mobile bottom sheet (default: true)",
          default: true
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant.",
          enumValues: [
            "centered-card",
            "top-sheet",
            "side-drawer",
            "full-screen"
          ],
          default: "centered-card"
        },
        contained: {
          types: [
            "boolean"
          ],
          description: "Render inside the nearest positioned ancestor (a preview frame) instead of portaling over the whole window; page scroll is left alone.",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    drawer: {
      type: "drawer",
      category: "container",
      sourceFile: "components/core/molecules/Drawer.tsx",
      tier: "molecules",
      family: "core",
      description: "Side drawer panel",
      suggestedFor: [
        "detail panels",
        "settings",
        "side navigation"
      ],
      typicalSize: "medium",
      propsSchema: {
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the drawer is open (defaults to true when rendered by slot wrapper)",
          default: true
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fires after the exit animation completes (the drawer is about to unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        contained: {
          types: [
            "boolean"
          ],
          description: "Render inside the nearest positioned ancestor (a preview frame) instead of over the window.",
          default: false
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Callback when drawer should close (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        title: {
          types: [
            "node"
          ],
          description: "Drawer title (text or any node, e.g. a brand logo + name)."
        },
        children: {
          types: [
            "node"
          ],
          description: "Drawer content (can be empty if using slot content)",
          default: null
        },
        footer: {
          types: [
            "node"
          ],
          description: "Footer content"
        },
        position: {
          types: [
            "string"
          ],
          description: "Position (left or right)",
          enumValues: [
            "left",
            "right"
          ],
          default: "right"
        },
        width: {
          types: [
            "string"
          ],
          description: "Width (CSS value or preset size)",
          default: "md"
        },
        showCloseButton: {
          types: [
            "boolean"
          ],
          description: "Show close button",
          default: true
        },
        closeOnOverlayClick: {
          types: [
            "boolean"
          ],
          description: "Close on overlay click",
          default: true
        },
        closeOnEscape: {
          types: [
            "boolean"
          ],
          description: "Close on escape key",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class name"
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Declarative close event \u2014 emits UI:{closeEvent} via eventBus when drawer should close",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "confirm-dialog": {
      type: "confirm-dialog",
      category: "container",
      sourceFile: "components/core/molecules/ConfirmDialog.tsx",
      tier: "molecules",
      family: "core",
      description: "Confirmation dialog",
      suggestedFor: [
        "destructive actions",
        "confirmations"
      ],
      typicalSize: "small",
      propsSchema: {
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the dialog is open (defaults to true when rendered by slot wrapper)",
          default: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Callback when dialog is closed (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fires after the exit animation completes (the dialog is about to unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        onConfirm: {
          types: [
            "function"
          ],
          description: "Callback when action is confirmed (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        title: {
          types: [
            "string"
          ],
          description: "Dialog title",
          required: true
        },
        message: {
          types: [
            "string",
            "node"
          ],
          description: "Dialog message/description"
        },
        description: {
          types: [
            "string",
            "node"
          ],
          description: "Alias for message (schema compatibility)"
        },
        confirmText: {
          types: [
            "string"
          ],
          description: "Confirm button text"
        },
        confirmLabel: {
          types: [
            "string"
          ],
          description: "Alias for confirmText (schema compatibility)"
        },
        cancelText: {
          types: [
            "string"
          ],
          description: "Cancel button text"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Alias for cancelText (schema compatibility)"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Dialog variant",
          enumValues: [
            "danger",
            "warning",
            "info",
            "default"
          ],
          default: "danger"
        },
        size: {
          types: [
            "string"
          ],
          description: "Dialog size",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "sm"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state for confirm button",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    box: {
      type: "box",
      category: "layout",
      sourceFile: "components/core/atoms/Box.tsx",
      tier: "atoms",
      family: "core",
      description: "Generic styled container with padding, borders, and backgrounds",
      suggestedFor: [
        "card containers",
        "sections",
        "styled wrappers",
        "content boxes"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        "data-theme": {
          types: [
            "string"
          ],
          description: "Data-theme attribute applied to the root element for CSS theme scoping (e.g. almadar-website-dark)."
        },
        padding: {
          types: [
            "string"
          ],
          description: "Padding on all sides",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          example: "md"
        },
        paddingX: {
          types: [
            "string"
          ],
          description: "Horizontal padding (overrides padding for x-axis)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        paddingY: {
          types: [
            "string"
          ],
          description: "Vertical padding (overrides padding for y-axis)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        margin: {
          types: [
            "string"
          ],
          description: "Margin on all sides",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ]
        },
        marginX: {
          types: [
            "string"
          ],
          description: "Horizontal margin",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ]
        },
        marginY: {
          types: [
            "string"
          ],
          description: "Vertical margin",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ]
        },
        bg: {
          types: [
            "string"
          ],
          description: "Background color",
          enumValues: [
            "transparent",
            "primary",
            "secondary",
            "muted",
            "accent",
            "surface",
            "overlay",
            "gradient",
            "inverse"
          ],
          default: "transparent"
        },
        border: {
          types: [
            "boolean"
          ],
          description: "Show border",
          default: false,
          example: true
        },
        rounded: {
          types: [
            "string"
          ],
          description: "Border radius",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ],
          default: "none"
        },
        shadow: {
          types: [
            "string"
          ],
          description: "Box shadow",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "none"
        },
        display: {
          types: [
            "string"
          ],
          description: "Display type",
          enumValues: [
            "block",
            "inline",
            "inline-block",
            "flex",
            "inline-flex",
            "grid"
          ]
        },
        fullWidth: {
          types: [
            "boolean"
          ],
          description: "Fill available width",
          default: false
        },
        fullHeight: {
          types: [
            "boolean"
          ],
          description: "Fill available height",
          default: false
        },
        overflow: {
          types: [
            "string"
          ],
          description: "Overflow behavior",
          enumValues: [
            "auto",
            "hidden",
            "visible",
            "scroll"
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Position",
          enumValues: [
            "relative",
            "absolute",
            "fixed",
            "sticky"
          ]
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        hoverEvent: {
          types: [
            "string"
          ],
          description: "Declarative hover event \u2014 emits UI:{hoverEvent} with { hovered: true/false } on mouseEnter/mouseLeave",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "hovered",
              type: "boolean",
              required: true
            }
          ]
        },
        tapReveal: {
          types: [
            "boolean"
          ],
          description: "When true (default), a touch/pen tap also fires `hoverEvent` (toggling hovered) so hover-only reveals work on touch.",
          default: true
        },
        maxWidth: {
          types: [
            "string"
          ],
          description: 'Maximum width (CSS value, e.g., "550px", "80rem")'
        },
        edgeTop: {
          types: [
            "string"
          ],
          description: `Shape cut into the top edge of a full-width band; "curve" uses the theme's shape`,
          enumValues: [
            "none",
            "curve",
            "wave",
            "arc",
            "tilt",
            "step",
            "scallop"
          ],
          default: "none"
        },
        edgeBottom: {
          types: [
            "string"
          ],
          description: `Shape cut into the bottom edge of a full-width band; "curve" uses the theme's shape`,
          enumValues: [
            "none",
            "curve",
            "wave",
            "arc",
            "tilt",
            "step",
            "scallop"
          ],
          default: "none"
        },
        edgeColor: {
          types: [
            "string"
          ],
          description: "Surface of the neighbouring band, which fills the cut",
          enumValues: [
            "background",
            "muted",
            "surface",
            "gradient",
            "inverse"
          ],
          default: "background"
        },
        edgeFlip: {
          types: [
            "boolean"
          ],
          description: "Mirror the edge shapes horizontally",
          default: false
        },
        texture: {
          types: [
            "boolean"
          ],
          description: "Lay the theme's texture over the band",
          default: false
        },
        animateChanges: {
          types: [
            "boolean"
          ],
          description: "Content that appears or changes inside the box eases in instead of switching in one frame (new rows, swapped views, changed text), played in presentation motion (the theme's dramatic duration, standard easing) so it can be watched. Off under reduced motion.",
          default: false
        },
        backdrop: {
          types: [
            "node"
          ],
          description: "Scene behind the band's content (photo, illustration, pattern); never takes clicks"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    grid: {
      type: "grid",
      category: "layout",
      sourceFile: "components/core/molecules/Grid.tsx",
      tier: "molecules",
      family: "core",
      description: "CSS Grid layout for multi-column content",
      suggestedFor: [
        "dashboards",
        "card grids",
        "multi-column layouts",
        "responsive grids"
      ],
      typicalSize: "large",
      propsSchema: {
        cols: {
          types: [
            "number",
            "string",
            "object"
          ],
          description: "Number of columns (can be responsive object)",
          enumValues: [
            "none"
          ],
          properties: {
            base: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            sm: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            md: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            lg: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            xl: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            }
          },
          default: 1
        },
        rows: {
          types: [
            "number"
          ],
          description: "Number of rows"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between items",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        rowGap: {
          types: [
            "string"
          ],
          description: "Row gap (overrides gap for rows)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        colGap: {
          types: [
            "string"
          ],
          description: "Column gap (overrides gap for columns)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        alignItems: {
          types: [
            "string"
          ],
          description: "Align items on block axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ]
        },
        justifyItems: {
          types: [
            "string"
          ],
          description: "Justify items on inline axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch"
          ]
        },
        flow: {
          types: [
            "string"
          ],
          description: "Auto-flow direction",
          enumValues: [
            "row",
            "col",
            "row-dense",
            "col-dense"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    center: {
      type: "center",
      category: "layout",
      sourceFile: "components/core/atoms/Center.tsx",
      tier: "atoms",
      family: "core",
      description: "Centers content horizontally and vertically",
      suggestedFor: [
        "centered modals",
        "loading states",
        "empty states",
        "hero sections"
      ],
      typicalSize: "medium",
      propsSchema: {
        inline: {
          types: [
            "boolean"
          ],
          description: "Center inline (width fits content) vs block (full width)",
          default: false
        },
        horizontal: {
          types: [
            "boolean"
          ],
          description: "Center only horizontally",
          default: true
        },
        vertical: {
          types: [
            "boolean"
          ],
          description: "Center only vertically",
          default: true
        },
        minHeight: {
          types: [
            "string",
            "number"
          ],
          description: "Minimum height (useful for vertical centering)"
        },
        fullHeight: {
          types: [
            "boolean"
          ],
          description: "Fill available height",
          default: false
        },
        fullWidth: {
          types: [
            "boolean"
          ],
          description: "Fill available width",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    spacer: {
      type: "spacer",
      category: "layout",
      sourceFile: "components/core/atoms/Spacer.tsx",
      tier: "atoms",
      family: "core",
      description: "Flexible space that expands to fill available room",
      suggestedFor: [
        "pushing elements apart",
        "flexible spacing",
        "layout adjustment"
      ],
      typicalSize: "tiny",
      propsSchema: {
        size: {
          types: [
            "string"
          ],
          description: "Fixed size (auto = flex grow)",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ],
          default: "auto"
        },
        axis: {
          types: [
            "string"
          ],
          description: "Orientation (for fixed sizes)",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    divider: {
      type: "divider",
      category: "layout",
      sourceFile: "components/core/atoms/Divider.tsx",
      tier: "atoms",
      family: "core",
      description: "Visual separator between content sections",
      suggestedFor: [
        "section breaks",
        "content separation",
        "visual grouping"
      ],
      typicalSize: "tiny",
      propsSchema: {
        orientation: {
          types: [
            "string"
          ],
          description: "Orientation of the divider",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        label: {
          types: [
            "string"
          ],
          description: "Text label to display in the divider"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Line style variant",
          enumValues: [
            "solid",
            "dashed",
            "dotted"
          ],
          default: "solid"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "runtime-debugger": {
      type: "runtime-debugger",
      category: "debug",
      sourceFile: "components/core/organisms/debug/RuntimeDebugger.tsx",
      tier: "organisms",
      family: "core",
      description: "Debug overlay showing FPS, entity states, event logs",
      suggestedFor: [
        "development",
        "debugging",
        "testing"
      ],
      typicalSize: "small",
      propsSchema: {
        position: {
          types: [
            "string"
          ],
          description: "Initial position",
          enumValues: [
            "bottom-right",
            "bottom-left",
            "top-right",
            "top-left"
          ],
          default: "bottom-right"
        },
        defaultCollapsed: {
          types: [
            "boolean"
          ],
          description: "Initial collapsed state",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Display mode: floating (fixed overlay), inline (block element), or verify (always-visible compact overlay for verification runs)",
          enumValues: [
            "floating",
            "inline",
            "verify"
          ],
          default: "floating"
        },
        defaultTab: {
          types: [
            "string"
          ],
          description: "Default active tab id"
        },
        schema: {
          types: [
            "sexpr"
          ],
          description: "Raw schema for EventDispatcherTab payload extraction",
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    button: {
      type: "button",
      category: "component",
      sourceFile: "components/core/atoms/Button.tsx",
      tier: "atoms",
      family: "core",
      description: "Clickable button that emits events",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "actions",
        "form submission",
        "navigation triggers"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "primary",
            "secondary",
            "ghost",
            "danger",
            "success",
            "warning",
            "default",
            "link"
          ],
          default: "primary"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop",
          default: false
        },
        leftIcon: {
          types: [
            "icon",
            "string"
          ],
          description: 'Left icon: a Lucide component or a canonical icon name string (e.g. "plus", "trash").'
        },
        rightIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Right icon: a Lucide component or a canonical icon name string."
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Alias for leftIcon"
        },
        iconRight: {
          types: [
            "icon",
            "string"
          ],
          description: "Alias for rightIcon"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Asset image rendered as the leading icon; takes precedence over icon/leftIcon when provided.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        label: {
          types: [
            "string"
          ],
          description: "Button label text (alternative to children for schema-driven rendering)",
          example: "Button"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable the button (greys out, blocks click events)"
        },
        href: {
          types: [
            "string"
          ],
          description: "Makes the button a link: `#anchor` scrolls, an absolute URL loads, a path navigates in-app"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Runs on activation, on the button or, with `href`, on the link; calling `preventDefault()` there keeps a link from following its `href`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "event",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        "data-testid": {
          types: [
            "string"
          ],
          description: "Test identifier for automated tests"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    badge: {
      type: "badge",
      category: "component",
      sourceFile: "components/core/atoms/Badge.tsx",
      tier: "atoms",
      family: "core",
      description: "Small label for status or count",
      suggestedFor: [
        "status indicators",
        "counts",
        "tags"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "default",
            "primary",
            "secondary",
            "success",
            "warning",
            "danger",
            "error",
            "info",
            "neutral"
          ],
          default: "default"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        amount: {
          types: [
            "number"
          ],
          description: "Numeric count or amount to display in badge"
        },
        label: {
          types: [
            "string",
            "number"
          ],
          description: "Badge label text (alternative to children for schema-driven rendering). Numeric values are auto-coerced to string for rendering \u2014 common case is unread counts, error counts, status codes, etc."
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component or canonical kebab-case icon name string"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Asset image rendered as the badge icon; takes precedence over icon when provided.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        onRemove: {
          types: [
            "function"
          ],
          description: 'When set, renders a small X button on the right of the badge that invokes this handler \u2014 turns the badge into a removable chip. Used by the TagInput molecule and other "list of removable values" surfaces.',
          kind: "callback",
          callbackArgs: [],
          default: ""
        },
        removeLabel: {
          types: [
            "string"
          ],
          description: 'Accessible label for the remove button. Defaults to "Remove".'
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    avatar: {
      type: "avatar",
      category: "component",
      sourceFile: "components/core/atoms/Avatar.tsx",
      tier: "atoms",
      family: "core",
      description: "User avatar image or initials",
      accessibleName: [
        "alt",
        "name"
      ],
      suggestedFor: [
        "user profiles",
        "comments",
        "team lists"
      ],
      typicalSize: "tiny",
      propsSchema: {
        src: {
          types: [
            "asset"
          ],
          description: "Image source URL"
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alt text for the image"
        },
        name: {
          types: [
            "string"
          ],
          description: "Full name - initials will be generated automatically"
        },
        initials: {
          types: [
            "string"
          ],
          description: 'Initials to display (e.g., "JD" for John Doe) If not provided but name is, initials will be auto-generated'
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon to display when no image or initials. Accepts a Lucide component directly, or a canonical kebab-case icon name string (resolved via `resolveIcon`) so trait/factory authors can pass an icon by name."
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the avatar",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        status: {
          types: [
            "string"
          ],
          description: "Status indicator",
          enumValues: [
            "online",
            "offline",
            "away",
            "busy"
          ]
        },
        badge: {
          types: [
            "string",
            "number"
          ],
          description: "Badge content (e.g., notification count)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: []
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    icon: {
      type: "icon",
      category: "component",
      sourceFile: "components/core/atoms/Icon.tsx",
      tier: "atoms",
      family: "core",
      description: "Icon from Lucide icon set",
      suggestedFor: [
        "decorative icons",
        "status indicators",
        "buttons"
      ],
      typicalSize: "tiny",
      propsSchema: {
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component (preferred for type-safe usage), OR a canonical kebab-case icon name string \u2014 a string is treated exactly like `name` so trait/factory authors can pass an icon by name through the `icon` prop."
        },
        name: {
          types: [
            "string"
          ],
          description: "Icon name as string (resolved from iconMap)"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the icon",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        color: {
          types: [
            "string"
          ],
          description: "Semantic palette token or an arbitrary Tailwind color class."
        },
        animation: {
          types: [
            "string"
          ],
          description: "Animation type",
          enumValues: [
            "spin",
            "pulse",
            "none"
          ],
          default: "none"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Icon stroke width - uses theme default if not specified"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline style",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    card: {
      type: "card",
      category: "component",
      sourceFile: "components/core/atoms/Card.tsx",
      tier: "atoms",
      family: "core",
      description: "Card container with optional header and footer",
      suggestedFor: [
        "content cards",
        "product cards",
        "info cards"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "default",
            "bordered",
            "elevated",
            "interactive"
          ],
          default: "bordered"
        },
        padding: {
          types: [
            "string"
          ],
          description: "padding prop",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        title: {
          types: [
            "string"
          ],
          description: "Card title - renders in header if provided"
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Card subtitle - renders below title"
        },
        shadow: {
          types: [
            "string"
          ],
          description: "Shadow size override",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg"
          ]
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant.",
          enumValues: [
            "elevated",
            "flat-bordered",
            "borderless-divider",
            "ticket",
            "invoice",
            "chip",
            "tile-image-first"
          ],
          default: "elevated"
        },
        children: {
          types: [
            "node"
          ],
          description: "Card content"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event key emitted on click for trait dispatch",
          kind: "event"
        },
        loading: {
          types: [
            "boolean"
          ],
          description: "Shows a skeleton/spinner overlay while true."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "progress-bar": {
      type: "progress-bar",
      category: "component",
      sourceFile: "components/core/atoms/ProgressBar.tsx",
      tier: "atoms",
      family: "core",
      description: "Progress indicator bar",
      suggestedFor: [
        "progress tracking",
        "loading",
        "completion status"
      ],
      typicalSize: "tiny",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Progress value (0-100)",
          required: true
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value (for calculating percentage)",
          default: 100
        },
        progressType: {
          types: [
            "string"
          ],
          description: "Type of the progress bar (linear, circular, stepped)",
          enumValues: [
            "linear",
            "circular",
            "stepped"
          ],
          default: "linear"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant/color of the progress bar",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "error",
            "danger"
          ],
          default: "primary"
        },
        color: {
          types: [
            "string"
          ],
          description: "Color variant (alias for variant; `variant` applies when omitted)",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "error",
            "danger"
          ]
        },
        showPercentage: {
          types: [
            "boolean"
          ],
          description: "Show percentage text",
          default: false
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: "Alias for showPercentage (pattern compatibility)",
          default: false
        },
        label: {
          types: [
            "string"
          ],
          description: "Label text"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size (for circular variant)",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        steps: {
          types: [
            "number"
          ],
          description: "Number of steps (for stepped variant)",
          default: 5
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    spinner: {
      type: "spinner",
      category: "component",
      sourceFile: "components/core/atoms/Spinner.tsx",
      tier: "atoms",
      family: "core",
      description: "Loading spinner animation",
      suggestedFor: [
        "loading states",
        "async operations"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        overlay: {
          types: [
            "boolean"
          ],
          description: "Renders a centered overlay backdrop instead of inline."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    input: {
      type: "input",
      category: "component",
      sourceFile: "components/core/atoms/Input.tsx",
      tier: "atoms",
      family: "core",
      description: "Text input field",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "forms",
        "search",
        "data entry"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        value: {
          types: [
            "string",
            "number"
          ],
          description: "Current value"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether input is disabled"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              typeWhen: [
                {
                  knob: "inputType",
                  equals: "number",
                  type: "number"
                }
              ]
            }
          ]
        },
        inputType: {
          types: [
            "string"
          ],
          description: "Input type \u2014 selects the field's data mode. Use 'password' for masked credentials / secret / passphrase entry (there is no separate password pattern); 'email', 'tel', 'url', 'number', 'search', 'date', and 'time' for their respective values; and 'select' / 'textarea' for choice and multi-line entry in addition to the standard single-line types.",
          enumValues: [
            "text",
            "email",
            "password",
            "number",
            "tel",
            "url",
            "search",
            "date",
            "datetime-local",
            "time",
            "checkbox",
            "select",
            "textarea"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "helperText prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "error prop"
        },
        leftIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "leftIcon prop"
        },
        rightIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "rightIcon prop"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component or canonical kebab-case icon name string for left side"
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show clear button when input has value"
        },
        onClear: {
          types: [
            "function",
            "string"
          ],
          description: "Callback or declarative event key when clear button is clicked"
        },
        options: {
          types: [
            "array"
          ],
          description: "Options for select type",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        rows: {
          types: [
            "number"
          ],
          description: "Rows for textarea type",
          default: 3
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "_payload",
              type: "object",
              required: false
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    textarea: {
      type: "textarea",
      category: "component",
      sourceFile: "components/core/atoms/Textarea.tsx",
      tier: "atoms",
      family: "core",
      description: "Multi-line text input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "long text",
        "descriptions",
        "comments"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        label: {
          types: [
            "string"
          ],
          description: "Visible label naming the field."
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        rows: {
          types: [
            "number"
          ],
          description: "Number of visible rows"
        },
        value: {
          types: [
            "string"
          ],
          description: "Controlled value, for a field the program fills or clears."
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event: fires on \u2318/Ctrl+Enter with `{ value }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text describing the field."
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    select: {
      type: "select",
      category: "component",
      sourceFile: "components/core/atoms/Select.tsx",
      tier: "atoms",
      family: "core",
      description: "Dropdown select input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "option selection",
        "filters",
        "forms"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        label: {
          types: [
            "string"
          ],
          description: "Visible label naming the control."
        },
        options: {
          types: [
            "array"
          ],
          description: "Select options (flat list)",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        groups: {
          types: [
            "array"
          ],
          description: "Grouped options \u2014 rendered as <optgroup> in native mode, sections in rich mode.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    value: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "value",
                    "label"
                  ]
                }
              }
            },
            required: [
              "label",
              "options"
            ]
          }
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        value: {
          types: [
            "string",
            "array"
          ],
          description: "Current value (string for single, string[] for multiple)",
          items: {
            types: [
              "string"
            ]
          }
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch \u2014 emits `{ value }` on selection commit (same gestures as `onChange`).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              schema: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        multiple: {
          types: [
            "boolean"
          ],
          description: "Allow selecting multiple values \u2014 activates the rich dropdown."
        },
        searchable: {
          types: [
            "boolean"
          ],
          description: "Show a search input inside the dropdown \u2014 activates the rich dropdown."
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show a clear button when a value is selected."
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler (native ChangeEvent) or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              schema: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        onValueChange: {
          types: [
            "function",
            "string"
          ],
          description: "Value-based change: a React callback (internal use) OR a declarative event key that emits `{ value }` on the bus (render-ui / lolo authoring). Mirrors the `onChange` handler|event convention so it's an event-emitting prop, not a bare callback.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              schema: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    checkbox: {
      type: "checkbox",
      category: "component",
      sourceFile: "components/core/atoms/Checkbox.tsx",
      tier: "atoms",
      family: "core",
      description: "Checkbox input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "boolean options",
        "multi-select",
        "toggles"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        checked: {
          types: [
            "boolean"
          ],
          description: "Whether the checkbox is checked"
        },
        defaultChecked: {
          types: [
            "boolean"
          ],
          description: "Default checked state (uncontrolled)"
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "checked",
              type: "boolean",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    radio: {
      type: "radio",
      category: "component",
      sourceFile: "components/core/atoms/Radio.tsx",
      tier: "atoms",
      family: "core",
      description: "Radio button input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "single selection",
        "options"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        options: {
          types: [
            "array"
          ],
          description: "Radio options (string array or SelectOption array)",
          items: {
            types: [
              "string"
            ]
          }
        },
        value: {
          types: [
            "string"
          ],
          description: "Current selected value"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Label text displayed next to the radio button"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text displayed below the radio button"
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message displayed below the radio button"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the radio button",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    switch: {
      type: "switch",
      category: "component",
      sourceFile: "components/core/atoms/Switch.tsx",
      tier: "atoms",
      family: "core",
      description: "Toggle switch input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "on/off settings",
        "toggles",
        "preferences"
      ],
      typicalSize: "tiny",
      propsSchema: {
        checked: {
          types: [
            "boolean"
          ],
          description: "checked prop"
        },
        defaultChecked: {
          types: [
            "boolean"
          ],
          description: "defaultChecked prop",
          default: false
        },
        onChange: {
          types: [
            "function"
          ],
          description: "onChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "checked",
              type: "boolean"
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "disabled prop",
          default: false
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        id: {
          types: [
            "string"
          ],
          description: "id prop"
        },
        name: {
          types: [
            "string"
          ],
          description: "name prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    label: {
      type: "label",
      category: "component",
      sourceFile: "components/core/atoms/Label.tsx",
      tier: "atoms",
      family: "core",
      description: "Form field label",
      suggestedFor: [
        "form fields",
        "input labels"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        text: {
          types: [
            "string"
          ],
          description: "Label text content"
        },
        htmlFor: {
          types: [
            "string"
          ],
          description: "Associated input element ID"
        },
        required: {
          types: [
            "boolean"
          ],
          description: "Show required indicator (decorative; the field itself carries aria-required)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    alert: {
      type: "alert",
      category: "component",
      sourceFile: "components/core/molecules/Alert.tsx",
      tier: "molecules",
      family: "core",
      description: "Alert message box",
      suggestedFor: [
        "warnings",
        "errors",
        "info messages",
        "success messages"
      ],
      typicalSize: "small",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Alert content (children or message)"
        },
        message: {
          types: [
            "string"
          ],
          description: "Alert message (alias for children)"
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "info",
            "success",
            "warning",
            "error"
          ],
          default: "info"
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        dismissible: {
          types: [
            "boolean"
          ],
          description: "dismissible prop",
          default: false
        },
        onDismiss: {
          types: [
            "function"
          ],
          description: "onDismiss prop",
          kind: "callback",
          callbackArgs: []
        },
        onClose: {
          types: [
            "function"
          ],
          description: "onClose prop",
          kind: "callback",
          callbackArgs: []
        },
        actions: {
          types: [
            "node"
          ],
          description: "actions prop"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon shown instead of the variant's default (Lucide name or component)"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        dismissEvent: {
          types: [
            "string"
          ],
          description: "Declarative dismiss event \u2014 emits UI:{dismissEvent} via eventBus when alert is dismissed",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    tooltip: {
      type: "tooltip",
      category: "component",
      sourceFile: "components/core/molecules/Tooltip.tsx",
      tier: "molecules",
      family: "core",
      description: "Hover tooltip",
      suggestedFor: [
        "hints",
        "extra info",
        "help text"
      ],
      typicalSize: "tiny",
      propsSchema: {
        content: {
          types: [
            "node"
          ],
          description: "Tooltip content",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Tooltip trigger element (ReactElement or ReactNode that will be wrapped in span)",
          required: true
        },
        position: {
          types: [
            "string"
          ],
          description: "Tooltip position",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "top"
        },
        delay: {
          types: [
            "number"
          ],
          description: "Show delay in milliseconds",
          default: 200
        },
        hideDelay: {
          types: [
            "number"
          ],
          description: "Hide delay in milliseconds",
          default: 0
        },
        showArrow: {
          types: [
            "boolean"
          ],
          description: "Show arrow",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    popover: {
      type: "popover",
      category: "component",
      sourceFile: "components/core/molecules/Popover.tsx",
      tier: "molecules",
      family: "core",
      description: "Click-triggered popover",
      suggestedFor: [
        "menus",
        "additional info",
        "actions"
      ],
      typicalSize: "small",
      propsSchema: {
        content: {
          types: [
            "node"
          ],
          description: "Popover content",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Popover trigger element (ReactElement or ReactNode that will be wrapped in span)",
          required: true
        },
        position: {
          types: [
            "string"
          ],
          description: "Popover position",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "bottom"
        },
        trigger: {
          types: [
            "string"
          ],
          description: "Trigger type",
          enumValues: [
            "click",
            "hover"
          ],
          default: "click"
        },
        showArrow: {
          types: [
            "boolean"
          ],
          description: "Show arrow",
          default: true
        },
        open: {
          types: [
            "boolean"
          ],
          description: "Controlled open state. When set, the host owns visibility and the popover reports intent through onOpenChange instead of toggling itself."
        },
        onOpenChange: {
          types: [
            "function"
          ],
          description: "Fired when the popover wants to change visibility (trigger click, outside click)",
          kind: "callback",
          callbackArgs: [
            {
              name: "open",
              type: "boolean"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    menu: {
      type: "menu",
      category: "component",
      sourceFile: "components/core/molecules/Menu.tsx",
      tier: "molecules",
      family: "core",
      description: "Dropdown menu",
      suggestedFor: [
        "actions menu",
        "context menu",
        "dropdown"
      ],
      typicalSize: "small",
      propsSchema: {
        trigger: {
          types: [
            "node"
          ],
          description: "Menu trigger element",
          required: true
        },
        items: {
          types: [
            "array"
          ],
          description: "Menu items",
          required: true,
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "item",
                  "divider"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              href: {
                types: [
                  "string"
                ]
              },
              url: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "danger"
                ]
              },
              subMenu: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "MenuItem"
                }
              }
            },
            required: [
              "label"
            ]
          }
        },
        position: {
          types: [
            "string"
          ],
          description: "Menu position",
          enumValues: [
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
            "top-start",
            "top-end",
            "bottom-start",
            "bottom-end"
          ],
          default: "bottom-left"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        header: {
          types: [
            "node"
          ],
          description: "Optional slot rendered above the items."
        },
        footer: {
          types: [
            "node"
          ],
          description: "Optional slot rendered below the items."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    accordion: {
      type: "accordion",
      category: "component",
      sourceFile: "components/core/molecules/Accordion.tsx",
      tier: "molecules",
      family: "core",
      description: "Collapsible accordion sections",
      suggestedFor: [
        "FAQs",
        "collapsible content",
        "settings"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Accordion items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "node"
                ]
              },
              title: {
                types: [
                  "node"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              defaultOpen: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "content"
            ]
          }
        },
        multiple: {
          types: [
            "boolean"
          ],
          description: "Allow multiple items open at once",
          default: false
        },
        defaultOpenItems: {
          types: [
            "array"
          ],
          description: "Default open items (IDs)",
          items: {
            types: [
              "string"
            ]
          }
        },
        defaultOpen: {
          types: [
            "array"
          ],
          description: "Default open items by index (pattern compatibility)",
          items: {
            types: [
              "number"
            ]
          }
        },
        openItems: {
          types: [
            "array"
          ],
          description: "Controlled open items (IDs)",
          items: {
            types: [
              "string"
            ]
          }
        },
        onItemToggle: {
          types: [
            "function"
          ],
          description: "Callback when item opens/closes",
          kind: "callback",
          callbackArgs: [
            {
              name: "itemId",
              type: "string"
            },
            {
              name: "isOpen",
              type: "boolean"
            }
          ]
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        toggleEvent: {
          types: [
            "string"
          ],
          description: "Declarative toggle event \u2014 emits UI:{toggleEvent} with { itemId, isOpen }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "itemId",
              type: "string",
              required: true
            },
            {
              name: "isOpen",
              type: "boolean",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    container: {
      type: "container",
      category: "component",
      sourceFile: "components/core/molecules/Container.tsx",
      tier: "molecules",
      family: "core",
      description: "Centered max-width container",
      suggestedFor: [
        "page content",
        "centered layouts"
      ],
      typicalSize: "medium",
      propsSchema: {
        size: {
          types: [
            "string"
          ],
          description: "Maximum width",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ]
        },
        maxWidth: {
          types: [
            "string"
          ],
          description: "Alias for size (pattern compatibility)",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ]
        },
        padding: {
          types: [
            "string"
          ],
          description: "Horizontal padding",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        center: {
          types: [
            "boolean"
          ],
          description: "Center horizontally",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "simple-grid": {
      type: "simple-grid",
      category: "component",
      sourceFile: "components/core/molecules/SimpleGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "Simple responsive grid with auto columns",
      suggestedFor: [
        "card grids",
        "gallery",
        "responsive layouts"
      ],
      typicalSize: "medium",
      propsSchema: {
        minChildWidth: {
          types: [
            "number",
            "string"
          ],
          description: 'Minimum width of each child (e.g., 200, "200px", "15rem")',
          default: 250
        },
        maxCols: {
          types: [
            "number"
          ],
          description: "Maximum number of columns",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        cols: {
          types: [
            "number"
          ],
          description: "Exact number of columns (disables auto-fit)",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between items",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id forwarded to the grid element."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "repeatable-form-section": {
      type: "repeatable-form-section",
      category: "form",
      sourceFile: "components/core/molecules/RepeatableFormSection.tsx",
      tier: "molecules",
      family: "core",
      description: "RepeatableFormSection \u2014 a form section that repeats a fixed group of fields, letting the user add or remove entries.",
      capabilities: "dynamic line items, repeatable field group, add/remove entry rows, multi-entry form block",
      suggestedFor: [
        "repeating fields",
        "dynamic forms",
        "multi-entry sections"
      ],
      typicalSize: "medium",
      propsSchema: {
        sectionType: {
          types: [
            "string"
          ],
          description: "Section type identifier",
          required: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Section title",
          required: true
        },
        items: {
          types: [
            "array"
          ],
          description: "Items in the section",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              addedInState: {
                types: [
                  "string"
                ]
              },
              addedAt: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        renderItem: {
          types: [
            "function"
          ],
          description: "Render function for each item. In .lolo: renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.",
          required: true,
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  addedInState: {
                    types: [
                      "string"
                    ]
                  },
                  addedAt: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        minItems: {
          types: [
            "number"
          ],
          description: "Minimum items required",
          default: 0
        },
        maxItems: {
          types: [
            "number"
          ],
          description: "Maximum items allowed"
        },
        allowReorder: {
          types: [
            "boolean"
          ],
          description: "Allow reordering",
          default: false
        },
        addLabel: {
          types: [
            "string"
          ],
          description: "Add button label"
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: "Empty state message"
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "Read-only mode",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        onAdd: {
          types: [
            "function"
          ],
          description: "Add handler",
          kind: "callback",
          callbackArgs: []
        },
        onRemove: {
          types: [
            "function"
          ],
          description: "Remove handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "itemId",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ]
        },
        onReorder: {
          types: [
            "function"
          ],
          description: "Reorder handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "fromIndex",
              type: "number"
            },
            {
              name: "toIndex",
              type: "number"
            }
          ]
        },
        trackAddedInState: {
          types: [
            "boolean"
          ],
          description: "Inspection-specific enhancements",
          default: false
        },
        currentState: {
          types: [
            "string"
          ],
          description: "Current inspection state (used when trackAddedInState is true)"
        },
        showAuditInfo: {
          types: [
            "boolean"
          ],
          description: "Show audit metadata (addedInState, addedAt)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "conditional-wrapper": {
      type: "conditional-wrapper",
      category: "component",
      sourceFile: "components/core/atoms/ConditionalWrapper.tsx",
      tier: "atoms",
      family: "core",
      description: "ConditionalWrapper Atom Component  A wrapper component that conditionally renders its children based on S-expression evaluation. Used for dynamic field visibility in inspection forms.",
      suggestedFor: [
        "conditional",
        "wrapper",
        "conditional wrapper"
      ],
      typicalSize: "small",
      propsSchema: {
        condition: {
          types: [
            "sexpr"
          ],
          description: "The S-expression condition to evaluate"
        },
        context: {
          types: [
            "object"
          ],
          description: "Context for evaluating the condition",
          required: true,
          properties: {
            formValues: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            globalVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            localVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            entity: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            }
          },
          propertyRequired: [
            "formValues",
            "globalVariables"
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Children to render when condition is true (or when no condition is provided)",
          required: true
        },
        fallback: {
          types: [
            "node"
          ],
          description: "Optional fallback to render when condition is false",
          default: null
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Whether to animate the transition (uses CSS transitions)",
          default: false
        }
      }
    },
    "law-reference-tooltip": {
      type: "law-reference-tooltip",
      category: "component",
      sourceFile: "components/core/atoms/LawReferenceTooltip.tsx",
      tier: "atoms",
      family: "core",
      description: "LawReferenceTooltip Atom Component  A specialized tooltip for displaying law references in inspection forms. Shows law name, article number, and relevant clause text.",
      suggestedFor: [
        "law",
        "reference",
        "tooltip",
        "law reference tooltip"
      ],
      typicalSize: "small",
      propsSchema: {
        reference: {
          types: [
            "object"
          ],
          description: "The law reference to display",
          required: true,
          properties: {
            law: {
              types: [
                "string"
              ]
            },
            lawName: {
              types: [
                "string"
              ]
            },
            article: {
              types: [
                "string"
              ]
            },
            clause: {
              types: [
                "string"
              ]
            },
            link: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "law",
            "article"
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Children element that triggers the tooltip",
          required: true
        },
        position: {
          types: [
            "string"
          ],
          description: "Tooltip position",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "top"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    overlay: {
      type: "overlay",
      category: "container",
      sourceFile: "components/core/atoms/Overlay.tsx",
      tier: "atoms",
      family: "core",
      description: "Overlay Atom Component  A fixed backdrop for modals and drawers.",
      suggestedFor: [
        "overlay"
      ],
      typicalSize: "small",
      propsSchema: {
        isVisible: {
          types: [
            "boolean"
          ],
          description: "isVisible prop",
          default: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "onClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        blur: {
          types: [
            "boolean"
          ],
          description: "blur prop",
          default: false
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    stack: {
      type: "stack",
      category: "component",
      sourceFile: "components/core/atoms/Stack.tsx",
      tier: "atoms",
      family: "core",
      description: "Stack Component  A layout primitive for arranging children in a vertical or horizontal stack with consistent spacing. Includes convenience exports VStack and HStack for common use cases.",
      suggestedFor: [
        "stack"
      ],
      typicalSize: "small",
      propsSchema: {
        direction: {
          types: [
            "string"
          ],
          description: "Stack direction",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "vertical"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between children",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items on the cross axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify items on the main axis",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        wrap: {
          types: [
            "boolean"
          ],
          description: "Allow items to wrap",
          default: false
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order of children",
          default: false
        },
        flex: {
          types: [
            "boolean"
          ],
          description: "Fill available space (flex: 1)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        onKeyDown: {
          types: [
            "function"
          ],
          description: "Keyboard handler",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        responsive: {
          types: [
            "boolean"
          ],
          description: "When true, horizontal stacks flip to vertical below the md breakpoint (768px)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "text-highlight": {
      type: "text-highlight",
      category: "component",
      sourceFile: "components/core/atoms/TextHighlight.tsx",
      tier: "atoms",
      family: "core",
      description: "TextHighlight Atom Component  A styled span component for highlighting text with annotations (questions or notes). Uses different colors for different annotation types: - Questions: Blue highlight - Notes: Yellow highlight",
      suggestedFor: [
        "text",
        "highlight",
        "text highlight"
      ],
      typicalSize: "small",
      propsSchema: {
        highlightType: {
          types: [
            "string"
          ],
          description: "Type of highlight (determines color)",
          required: true,
          enumValues: [
            "question",
            "note"
          ]
        },
        isActive: {
          types: [
            "boolean"
          ],
          description: "Whether the highlight is currently active/focused",
          default: false
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Callback when highlight is clicked",
          kind: "callback",
          callbackArgs: []
        },
        onMouseEnter: {
          types: [
            "function"
          ],
          description: "Callback when highlight is hovered",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        onMouseLeave: {
          types: [
            "function"
          ],
          description: "Callback when hover ends",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        annotationId: {
          types: [
            "string"
          ],
          description: "Unique ID for the annotation"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Highlighted text content",
          required: true
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        hoverEvent: {
          types: [
            "string"
          ],
          description: "Declarative hover event \u2014 emits UI:{hoverEvent} with { hovered, annotationId }",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "hovered",
              type: "boolean",
              required: true
            },
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "theme-toggle": {
      type: "theme-toggle",
      category: "component",
      sourceFile: "components/core/atoms/ThemeToggle.tsx",
      tier: "atoms",
      family: "core",
      description: "ThemeToggle Atom Component  A button that toggles between light and dark themes. Uses Sun and Moon icons to indicate current/target theme.",
      suggestedFor: [
        "theme",
        "toggle",
        "theme toggle"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: "Show label text",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    typography: {
      type: "typography",
      category: "component",
      sourceFile: "components/core/atoms/Typography.tsx",
      tier: "atoms",
      family: "core",
      description: "Typography Atom Component  Text elements following the KFlow design system with theme-aware styling.",
      suggestedFor: [
        "typography"
      ],
      typicalSize: "small",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Typography variant",
          enumValues: [
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "heading",
            "subheading",
            "body1",
            "body2",
            "body",
            "caption",
            "overline",
            "small",
            "large",
            "label"
          ]
        },
        level: {
          types: [
            "number"
          ],
          description: "Heading level (1-6) - alternative to variant for headings",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        color: {
          types: [
            "string"
          ],
          description: "Text color",
          enumValues: [
            "primary",
            "secondary",
            "muted",
            "error",
            "success",
            "warning",
            "inherit"
          ],
          default: "primary"
        },
        align: {
          types: [
            "string"
          ],
          description: "Text alignment",
          enumValues: [
            "left",
            "center",
            "right"
          ]
        },
        weight: {
          types: [
            "string"
          ],
          description: "Font weight override \u2014 `none` = no override, the variant's baked weight applies",
          enumValues: [
            "none",
            "light",
            "normal",
            "medium",
            "semibold",
            "bold"
          ]
        },
        size: {
          types: [
            "string"
          ],
          description: "Font size override",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "3xl"
          ]
        },
        truncate: {
          types: [
            "boolean"
          ],
          description: "Truncate with ellipsis (single line)",
          default: false
        },
        overflow: {
          types: [
            "string"
          ],
          description: "Overflow handling mode",
          enumValues: [
            "visible",
            "hidden",
            "wrap",
            "clamp-2",
            "clamp-3"
          ]
        },
        as: {
          types: [
            "string"
          ],
          description: "Custom HTML element",
          enumValues: [
            "a",
            "abbr",
            "address",
            "area",
            "article",
            "aside",
            "audio",
            "b",
            "base",
            "bdi",
            "bdo",
            "big",
            "blockquote",
            "body",
            "br",
            "button",
            "canvas",
            "caption",
            "center",
            "cite",
            "code",
            "col",
            "colgroup",
            "data",
            "datalist",
            "dd",
            "del",
            "details",
            "dfn",
            "dialog",
            "div",
            "dl",
            "dt",
            "em",
            "embed",
            "fieldset",
            "figcaption",
            "figure",
            "footer",
            "form",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "head",
            "header",
            "hgroup",
            "hr",
            "html",
            "i",
            "iframe",
            "img",
            "input",
            "ins",
            "kbd",
            "keygen",
            "label",
            "legend",
            "li",
            "link",
            "main",
            "map",
            "mark",
            "menu",
            "menuitem",
            "meta",
            "meter",
            "nav",
            "noindex",
            "noscript",
            "object",
            "ol",
            "optgroup",
            "option",
            "output",
            "p",
            "param",
            "picture",
            "pre",
            "progress",
            "q",
            "rp",
            "rt",
            "ruby",
            "s",
            "samp",
            "search",
            "slot",
            "script",
            "section",
            "select",
            "small",
            "source",
            "span",
            "strong",
            "style",
            "sub",
            "summary",
            "sup",
            "table",
            "template",
            "tbody",
            "td",
            "textarea",
            "tfoot",
            "th",
            "thead",
            "time",
            "title",
            "tr",
            "track",
            "u",
            "ul",
            "var",
            "video",
            "wbr",
            "webview",
            "svg",
            "animate",
            "animateMotion",
            "animateTransform",
            "circle",
            "clipPath",
            "defs",
            "desc",
            "ellipse",
            "feBlend",
            "feColorMatrix",
            "feComponentTransfer",
            "feComposite",
            "feConvolveMatrix",
            "feDiffuseLighting",
            "feDisplacementMap",
            "feDistantLight",
            "feDropShadow",
            "feFlood",
            "feFuncA",
            "feFuncB",
            "feFuncG",
            "feFuncR",
            "feGaussianBlur",
            "feImage",
            "feMerge",
            "feMergeNode",
            "feMorphology",
            "feOffset",
            "fePointLight",
            "feSpecularLighting",
            "feSpotLight",
            "feTile",
            "feTurbulence",
            "filter",
            "foreignObject",
            "g",
            "image",
            "line",
            "linearGradient",
            "marker",
            "mask",
            "metadata",
            "mpath",
            "path",
            "pattern",
            "polygon",
            "polyline",
            "radialGradient",
            "rect",
            "set",
            "stop",
            "switch",
            "symbol",
            "text",
            "textPath",
            "tspan",
            "use",
            "view"
          ]
        },
        id: {
          types: [
            "string"
          ],
          description: "HTML id attribute"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline style",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        format: {
          types: [
            "string"
          ],
          description: 'Value formatting applied to string/number/Date content \u2014 `none` (the default) renders content as-is. Covers the raw-ISO-date class: a bound datetime renders human-readable via `format="date"|"time"|"datetime"`.',
          enumValues: [
            "none",
            "date",
            "time",
            "datetime",
            "number",
            "currency",
            "percent"
          ]
        },
        content: {
          types: [
            "node"
          ],
          description: "Text content (alternative to children)"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-actions": {
      type: "form-actions",
      category: "component",
      sourceFile: "components/core/molecules/ButtonGroup.tsx",
      tier: "molecules",
      family: "core",
      description: "ButtonGroup Molecule Component  A component for grouping buttons together with connected styling. Supports both children-based and form-actions pattern (primary/secondary) usage. Uses Button atoms.",
      suggestedFor: [
        "button",
        "group",
        "button group"
      ],
      typicalSize: "medium",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Button group content (Button components) - use this OR primary/secondary"
        },
        primary: {
          types: [
            "object"
          ],
          description: "Primary action button config (for form-actions pattern) Accepts Readonly for compatibility with generated const objects",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            actionType: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            },
            navigatesTo: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        secondary: {
          types: [
            "array"
          ],
          description: "Secondary action buttons config (for form-actions pattern) Accepts readonly array for compatibility with generated const arrays",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              actionType: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          enumValues: [
            "default",
            "segmented",
            "toggle"
          ],
          default: "default"
        },
        orientation: {
          types: [
            "string"
          ],
          description: "Orientation",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity type for filter-group pattern (schema metadata)"
        },
        filters: {
          types: [
            "array"
          ],
          description: "Filter definitions for filter-group pattern",
          items: {
            types: [
              "object"
            ],
            properties: {
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              filterType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "field",
              "label"
            ]
          }
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment for the action-cluster (form-actions) pattern.",
          enumValues: [
            "right-aligned-buttons",
            "floating-bar",
            "inline-row",
            "dropdown-menu",
            "command-palette-trigger"
          ],
          default: "right-aligned-buttons"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    flex: {
      type: "flex",
      category: "component",
      sourceFile: "components/core/molecules/Flex.tsx",
      tier: "molecules",
      family: "core",
      description: "Flex Component  A flexbox wrapper with all common flex properties exposed as props. More explicit than Stack for when you need full flex control.",
      suggestedFor: [
        "flex"
      ],
      typicalSize: "medium",
      propsSchema: {
        direction: {
          types: [
            "string"
          ],
          description: "Flex direction",
          enumValues: [
            "row",
            "row-reverse",
            "col",
            "col-reverse"
          ],
          default: "row"
        },
        wrap: {
          types: [
            "string"
          ],
          description: "Flex wrap",
          enumValues: [
            "nowrap",
            "wrap",
            "wrap-reverse"
          ],
          default: "nowrap"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify content",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between items",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "none"
        },
        inline: {
          types: [
            "boolean"
          ],
          description: "Inline flex",
          default: false
        },
        grow: {
          types: [
            "boolean",
            "number"
          ],
          description: "Flex grow"
        },
        shrink: {
          types: [
            "boolean",
            "number"
          ],
          description: "Flex shrink"
        },
        basis: {
          types: [
            "string",
            "number"
          ],
          description: "Flex basis"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "floating-action-button": {
      type: "floating-action-button",
      category: "component",
      sourceFile: "components/core/molecules/FloatingActionButton.tsx",
      tier: "molecules",
      family: "core",
      description: "FloatingActionButton Molecule Component  A floating action button that can expand into multiple actions vertically. Uses Button atom.",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "floating",
        "action",
        "button",
        "floating action button"
      ],
      typicalSize: "medium",
      propsSchema: {
        action: {
          types: [
            "string"
          ],
          description: 'Declarative event name. When set, clicking the FAB emits `UI:{action}` via the event bus and (if also provided) calls `onClick`. Mirrors the Button atom\'s `action` prop so schemas can write `{ type: "floating-action-button", action: "INIT" }` uniformly.',
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the dispatched action event.",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        actions: {
          types: [
            "array"
          ],
          description: "Multiple actions. When provided, the button expands to show all of them.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "success",
                  "danger",
                  "warning"
                ]
              }
            },
            required: [
              "id",
              "label",
              "icon"
            ]
          }
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon: a canonical icon name string (resolved by Button) or a Lucide component."
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Optional direct click handler. Runs after the action emit when both are present.",
          kind: "callback",
          callbackArgs: []
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant.",
          enumValues: [
            "primary",
            "secondary",
            "success",
            "danger",
            "warning"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Optional label shown via `aria-label` (visually hidden in single-action mode; rendered as a tooltip beside expanded actions in multi-action mode)."
        },
        position: {
          types: [
            "string"
          ],
          description: "Button position",
          default: "bottom-right"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-field": {
      type: "form-field",
      category: "form",
      sourceFile: "components/core/molecules/FormField.tsx",
      tier: "molecules",
      family: "core",
      description: "FormField Molecule Component  A form field wrapper with label, hint, and error message support. **Atomic Design**: Composed using Label and Typography atoms.",
      suggestedFor: [
        "form",
        "field",
        "form field"
      ],
      typicalSize: "medium",
      propsSchema: {
        label: {
          types: [
            "string"
          ],
          description: "label prop",
          required: true
        },
        required: {
          types: [
            "boolean"
          ],
          description: "required prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "error prop"
        },
        hint: {
          types: [
            "string"
          ],
          description: "hint prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-section-header": {
      type: "form-section-header",
      category: "form",
      sourceFile: "components/core/molecules/FormSectionHeader.tsx",
      tier: "molecules",
      family: "core",
      description: "FormSectionHeader  Header component for collapsible form sections. Provides consistent styling and interaction for section headers.",
      suggestedFor: [
        "form",
        "section",
        "header",
        "form section header"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Section title",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Section subtitle"
        },
        isCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether section is collapsed",
          default: false
        },
        onToggle: {
          types: [
            "function"
          ],
          description: "Toggle collapse handler (makes header clickable)",
          kind: "callback",
          callbackArgs: []
        },
        badge: {
          types: [
            "string"
          ],
          description: 'Badge text (e.g., "3 fields", "Required", "Complete")'
        },
        badgeVariant: {
          types: [
            "string"
          ],
          description: "Badge variant",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "danger"
          ],
          default: "default"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon name or component to show before title"
        },
        hasErrors: {
          types: [
            "boolean"
          ],
          description: "Whether section has validation errors",
          default: false
        },
        isComplete: {
          types: [
            "boolean"
          ],
          description: "Whether section is complete",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "input-group": {
      type: "input-group",
      category: "form",
      sourceFile: "components/core/molecules/InputGroup.tsx",
      tier: "molecules",
      family: "core",
      description: "InputGroup Molecule Component  A component for grouping input with addons (icons, buttons, text). Uses Input, Button, Icon, and Typography atoms.",
      suggestedFor: [
        "input",
        "group",
        "input group"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        value: {
          types: [
            "string",
            "number"
          ],
          description: "Current value"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether input is disabled"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              typeWhen: [
                {
                  knob: "inputType",
                  equals: "number",
                  type: "number"
                }
              ]
            }
          ]
        },
        inputType: {
          types: [
            "string"
          ],
          description: "Input type \u2014 selects the field's data mode. Use 'password' for masked credentials / secret / passphrase entry (there is no separate password pattern); 'email', 'tel', 'url', 'number', 'search', 'date', and 'time' for their respective values; and 'select' / 'textarea' for choice and multi-line entry in addition to the standard single-line types.",
          enumValues: [
            "text",
            "email",
            "password",
            "number",
            "tel",
            "url",
            "search",
            "date",
            "datetime-local",
            "time",
            "checkbox",
            "select",
            "textarea"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "helperText prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "error prop"
        },
        leftIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "leftIcon prop"
        },
        rightIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "rightIcon prop"
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show clear button when input has value"
        },
        onClear: {
          types: [
            "function",
            "string"
          ],
          description: "Callback or declarative event key when clear button is clicked"
        },
        options: {
          types: [
            "array"
          ],
          description: "Options for select type",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        rows: {
          types: [
            "number"
          ],
          description: "Rows for textarea type"
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "_payload",
              type: "object",
              required: false
            }
          ]
        },
        leftAddon: {
          types: [
            "node",
            "icon"
          ],
          description: "Left addon (icon, button, or text)"
        },
        rightAddon: {
          types: [
            "node",
            "icon"
          ],
          description: "Right addon (icon, button, or text)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "relation-select": {
      type: "relation-select",
      category: "form",
      sourceFile: "components/core/molecules/RelationSelect.tsx",
      tier: "molecules",
      family: "core",
      description: "RelationSelect Molecule Component  A searchable select component for relation fields. Allows users to search and select from related entities.  Composed from: Box, HStack, VStack, Input, Button, Spinner, Typography atoms",
      suggestedFor: [
        "relation",
        "select",
        "relation select"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "string"
          ],
          description: "Current value (ID)"
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Callback when value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        options: {
          types: [
            "array"
          ],
          description: "Available options - accepts readonly for compatibility with generated const arrays",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          },
          default: []
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        required: {
          types: [
            "boolean"
          ],
          description: "Whether the field is required",
          default: false
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the field is disabled",
          default: false
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Whether data is loading",
          default: false
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Allow clearing the selection",
          default: true
        },
        name: {
          types: [
            "string"
          ],
          description: "Name attribute for forms"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        searchPlaceholder: {
          types: [
            "string"
          ],
          description: "Search placeholder"
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: "Empty state message"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "side-panel": {
      type: "side-panel",
      category: "component",
      sourceFile: "components/core/molecules/SidePanel.tsx",
      tier: "molecules",
      family: "core",
      description: "SidePanel Molecule Component  A side panel that slides in from the right (or left) with header and content. Uses Button, Typography atoms.",
      suggestedFor: [
        "side",
        "panel",
        "side panel"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Panel title",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Panel content",
          required: true
        },
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Is panel open",
          required: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "On close handler",
          required: true,
          kind: "callback",
          callbackArgs: []
        },
        width: {
          types: [
            "string"
          ],
          description: "Panel width as Tailwind class string. Default fills the viewport on mobile and snaps to `w-96` (384 px) at `sm:` and above so phones don't lose content to a fixed 384 px column. Consumers passing a custom value should include a `w-full` mobile fallback in the same string (e.g. `\"w-full sm:w-[480px]\"`) \u2014 Tailwind's JIT can't see dynamically-concatenated class names.",
          default: "w-full sm:w-96"
        },
        position: {
          types: [
            "string"
          ],
          description: "Panel position",
          enumValues: [
            "left",
            "right"
          ],
          default: "right"
        },
        showOverlay: {
          types: [
            "boolean"
          ],
          description: "Show overlay on mobile",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Declarative close event \u2014 emits UI:{closeEvent} via eventBus when panel should close",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "violation-alert": {
      type: "violation-alert",
      category: "state",
      sourceFile: "components/core/molecules/ViolationAlert.tsx",
      tier: "molecules",
      family: "core",
      description: "ViolationAlert  Displays inspection violations with law references and action types. Used in inspection forms to show detected compliance violations.  Action Types: - measure: Corrective measure required (warning) - admin: Administrative action (error) - penalty: Penalty proceedings (error, severe)",
      suggestedFor: [
        "violation",
        "alert",
        "violation alert"
      ],
      typicalSize: "medium",
      propsSchema: {
        violation: {
          types: [
            "object"
          ],
          description: "Violation data",
          required: true,
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            law: {
              types: [
                "string"
              ]
            },
            article: {
              types: [
                "string"
              ]
            },
            message: {
              types: [
                "string"
              ]
            },
            actionType: {
              types: [
                "string"
              ],
              enumValues: [
                "measure",
                "admin",
                "penalty"
              ]
            },
            adminAction: {
              types: [
                "string"
              ]
            },
            penaltyAction: {
              types: [
                "string"
              ]
            },
            fieldId: {
              types: [
                "string"
              ]
            },
            tabId: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "id",
            "law",
            "article",
            "message",
            "actionType"
          ]
        },
        severity: {
          types: [
            "string"
          ],
          description: "Visual severity (derived from actionType if not specified)",
          enumValues: [
            "warning",
            "error"
          ]
        },
        dismissible: {
          types: [
            "boolean"
          ],
          description: "Dismissible alert",
          default: false
        },
        onDismiss: {
          types: [
            "function"
          ],
          description: "Dismiss handler",
          kind: "callback",
          callbackArgs: []
        },
        onNavigateToField: {
          types: [
            "function"
          ],
          description: "Navigate to the field that caused violation",
          kind: "callback",
          callbackArgs: [
            {
              name: "fieldId",
              type: "string"
            }
          ]
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact display mode",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        message: {
          types: [
            "string"
          ],
          description: "Fallback message from playground render-ui"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "drawer-slot": {
      type: "drawer-slot",
      category: "layout",
      sourceFile: "components/core/organisms/DrawerSlot.tsx",
      tier: "organisms",
      family: "core",
      description: "DrawerSlot Component  Wraps drawer slot content in a proper Drawer component. Used by trait-driven pages to display drawer UI from render_ui effects.  Handles: - Auto-open when content is present - Dispatches CLOSE/CANCEL events when closed - Extracts title from Form or DetailPanel components - Configurable position and size",
      suggestedFor: [
        "drawer",
        "slot",
        "drawer slot"
      ],
      typicalSize: "large",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to display in the drawer"
        },
        title: {
          types: [
            "string"
          ],
          description: "Override drawer title (extracted from children if not provided)"
        },
        position: {
          types: [
            "string"
          ],
          description: "Drawer position",
          enumValues: [
            "left",
            "right"
          ],
          default: "right"
        },
        size: {
          types: [
            "string"
          ],
          description: "Drawer size",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for qualified event emission"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    header: {
      type: "header",
      category: "header",
      sourceFile: "components/core/molecules/Header.tsx",
      tier: "molecules",
      family: "core",
      description: "Header Organism Component  A header component for mobile/responsive layouts with menu toggle, brand, and user avatar. Styled to match the main Layout component's mobile header.",
      suggestedFor: [
        "header"
      ],
      typicalSize: "large",
      propsSchema: {
        logo: {
          types: [
            "node"
          ],
          description: "Logo/Brand content"
        },
        logoSrc: {
          types: [
            "asset"
          ],
          description: "Logo image source"
        },
        brandName: {
          types: [
            "string"
          ],
          description: "Brand/App name",
          default: "KFlow"
        },
        navigationItems: {
          types: [
            "array"
          ],
          description: "Navigation items (for desktop header variant)",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              active: {
                types: [
                  "boolean"
                ]
              },
              children: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "HeaderNavigationItem"
                }
              }
            },
            required: [
              "label"
            ]
          }
        },
        showMenuToggle: {
          types: [
            "boolean"
          ],
          description: "Show menu toggle button",
          default: true
        },
        isMenuOpen: {
          types: [
            "boolean"
          ],
          description: "Is menu open (for toggle icon)",
          default: false
        },
        onMenuToggle: {
          types: [
            "function"
          ],
          description: "Menu toggle callback",
          kind: "callback",
          callbackArgs: []
        },
        showSearch: {
          types: [
            "boolean"
          ],
          description: "Show search input",
          default: false
        },
        searchPlaceholder: {
          types: [
            "string"
          ],
          description: "Search placeholder"
        },
        onSearch: {
          types: [
            "function"
          ],
          description: "Search callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        userAvatar: {
          types: [
            "object"
          ],
          description: "User avatar configuration",
          properties: {
            src: {
              types: [
                "asset"
              ]
            },
            alt: {
              types: [
                "string"
              ]
            },
            initials: {
              types: [
                "string"
              ]
            }
          }
        },
        userName: {
          types: [
            "string"
          ],
          description: "User name (display name or email)"
        },
        onUserClick: {
          types: [
            "function"
          ],
          description: "Callback when user avatar is clicked",
          kind: "callback",
          callbackArgs: []
        },
        actions: {
          types: [
            "node"
          ],
          description: "Action buttons (right side)"
        },
        sticky: {
          types: [
            "boolean"
          ],
          description: "Sticky header",
          default: true
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant - mobile shows menu toggle, desktop shows full nav",
          enumValues: [
            "mobile",
            "desktop"
          ],
          default: "mobile"
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the structural variant (which currently flips mobile vs desktop).",
          enumValues: [
            "hero",
            "compact-bar",
            "breadcrumb",
            "contextual",
            "editorial-banner"
          ],
          default: "compact-bar"
        },
        onLogoClick: {
          types: [
            "function"
          ],
          description: "Callback when logo/brand is clicked",
          kind: "callback",
          callbackArgs: []
        },
        brandHref: {
          types: [
            "string"
          ],
          description: "Where the brand (logo + name) links to, usually the home page; empty leaves it unlinked"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator (closed circuit)"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (closed circuit)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "modal-slot": {
      type: "modal-slot",
      category: "layout",
      sourceFile: "components/core/organisms/ModalSlot.tsx",
      tier: "organisms",
      family: "core",
      description: "ModalSlot Component  Wraps modal slot content in a proper Modal component. Used by trait-driven pages to display modal UI from render_ui effects.  Handles: - Auto-open when content is present - Dispatches CLOSE/CANCEL events when closed - Extracts title from Form components",
      suggestedFor: [
        "modal",
        "slot",
        "modal slot"
      ],
      typicalSize: "large",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to display in the modal"
        },
        title: {
          types: [
            "string"
          ],
          description: "Override modal title (extracted from children if not provided)"
        },
        size: {
          types: [
            "string"
          ],
          description: "Modal size",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for qualified event emission"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    navigation: {
      type: "navigation",
      category: "navigation",
      sourceFile: "components/core/molecules/Navigation.tsx",
      tier: "molecules",
      family: "core",
      description: "Navigation Organism Component  A navigation component with items, active indicators, icons, and badges. Uses Menu, ButtonGroup molecules and Button, Icon, Badge, Typography, Divider atoms.",
      suggestedFor: [
        "navigation"
      ],
      typicalSize: "large",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Navigation items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              isActive: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              subMenu: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "NavigationItem"
                }
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        orientation: {
          types: [
            "string"
          ],
          description: "Navigation orientation",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "orbital-visualization": {
      type: "orbital-visualization",
      category: "display",
      sourceFile: "components/core/molecules/OrbitalVisualization.tsx",
      tier: "molecules",
      family: "core",
      description: "OrbitalVisualization Component  Visualizes KFlow schemas as atomic orbitals based on complexity. Uses CSS 3D transforms for lightweight rendering without Three.js.  Orbital Types (based on complexity score): - 1s (1-3): Simple sphere - Red - 2s (4-8): Larger sphere - Orange - 2p (9-15): Dumbbell shape - Yellow - 3s (16-25): Sphere with node - Green - 3p (26-40): Complex dumbbell - Blue - 3d (41-60): Cloverleaf - Indigo - 4f (61+): Multi-lobe - Violet",
      suggestedFor: [
        "orbital",
        "visualization",
        "orbital visualization"
      ],
      typicalSize: "large",
      propsSchema: {
        schema: {
          types: [
            "object"
          ],
          description: "Complexity-scoring summary of a KFlow schema (counts only \u2014 see {@link OrbitalVisualizationSchemaSummary}).",
          properties: {
            dataEntities: {
              types: [
                "number"
              ]
            },
            ui: {
              types: [
                "object"
              ],
              properties: {
                pages: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      sections: {
                        types: [
                          "number"
                        ]
                      }
                    }
                  }
                }
              }
            },
            traits: {
              types: [
                "number"
              ]
            }
          }
        },
        complexity: {
          types: [
            "number"
          ],
          description: "Direct complexity override (1-100+)"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the visualization",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: "Show complexity label",
          default: true
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "Animation enabled",
          default: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes",
          default: ""
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    section: {
      type: "section",
      category: "display",
      sourceFile: "components/core/molecules/Section.tsx",
      tier: "molecules",
      family: "core",
      description: "Section Component  A semantic section wrapper with optional title, description, and action. Perfect for grouping related content with consistent spacing.",
      suggestedFor: [
        "section"
      ],
      typicalSize: "large",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Section title"
        },
        description: {
          types: [
            "string"
          ],
          description: "Section subtitle/description"
        },
        action: {
          types: [
            "node"
          ],
          description: "Action element (e.g., button, link)"
        },
        padding: {
          types: [
            "string"
          ],
          description: "Padding amount",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          enumValues: [
            "default",
            "card",
            "bordered",
            "filled"
          ],
          default: "default"
        },
        divider: {
          types: [
            "boolean"
          ],
          description: "Show divider below header",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        headerClassName: {
          types: [
            "string"
          ],
          description: "Header custom class name"
        },
        contentClassName: {
          types: [
            "string"
          ],
          description: "Content custom class name"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    sidebar: {
      type: "sidebar",
      category: "navigation",
      sourceFile: "components/core/molecules/Sidebar.tsx",
      tier: "molecules",
      family: "core",
      description: "Sidebar Organism Component  A sidebar component with logo, navigation items, user section, and collapse/expand. Styled to match the main Layout component with theme-aware CSS variables.",
      suggestedFor: [
        "sidebar"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        logo: {
          types: [
            "node"
          ],
          description: "Logo/Brand content - can be a ReactNode or logo config"
        },
        logoSrc: {
          types: [
            "asset"
          ],
          description: "Logo image source"
        },
        brandName: {
          types: [
            "string"
          ],
          description: "Brand/App name",
          default: "KFlow"
        },
        items: {
          types: [
            "array"
          ],
          description: "Navigation items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              active: {
                types: [
                  "boolean"
                ]
              },
              isActive: {
                types: [
                  "boolean"
                ]
              },
              subItems: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "SidebarItem"
                }
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        userSection: {
          types: [
            "node"
          ],
          description: "User section content"
        },
        footerContent: {
          types: [
            "node"
          ],
          description: "Footer content (e.g., theme toggle)"
        },
        collapsed: {
          types: [
            "boolean"
          ],
          description: "Collapsed state (controlled)"
        },
        defaultCollapsed: {
          types: [
            "boolean"
          ],
          description: "Default collapsed state",
          default: false
        },
        collapseChangeEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when collapse state changes, payload: { collapsed: boolean }",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "collapsed",
              type: "boolean",
              required: true
            }
          ]
        },
        hideCollapseButton: {
          types: [
            "boolean"
          ],
          description: "Hide the collapse/expand button",
          default: false
        },
        showCloseButton: {
          types: [
            "boolean"
          ],
          description: "Show a close button (for mobile)",
          default: false
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when close button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        logoClickEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when logo/brand is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    split: {
      type: "split",
      category: "layout",
      sourceFile: "components/core/molecules/Split.tsx",
      tier: "molecules",
      family: "core",
      description: "Split Component  A two-column layout with configurable ratios. Perfect for sidebar/content layouts or side-by-side comparisons.",
      suggestedFor: [
        "split"
      ],
      typicalSize: "large",
      propsSchema: {
        ratio: {
          types: [
            "string"
          ],
          description: "Size ratio between left and right panels",
          enumValues: [
            "1:1",
            "1:2",
            "2:1",
            "1:3",
            "3:1",
            "1:4",
            "4:1",
            "2:3",
            "3:2"
          ],
          default: "1:1"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between panels",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order (right first)",
          default: false
        },
        stackOnMobile: {
          types: [
            "boolean"
          ],
          description: "Stack vertically on mobile",
          default: true
        },
        stackBreakpoint: {
          types: [
            "string"
          ],
          description: "Breakpoint to switch from stacked to side-by-side",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items vertically",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch"
          ],
          default: "stretch"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        leftClassName: {
          types: [
            "string"
          ],
          description: "Left/first panel class name"
        },
        rightClassName: {
          types: [
            "string"
          ],
          description: "Right/second panel class name"
        },
        children: {
          types: [
            "array"
          ],
          description: "Exactly two children: [left, right]",
          required: true,
          tupleItems: [
            {
              types: [
                "node"
              ]
            },
            {
              types: [
                "node"
              ]
            }
          ],
          items: {
            types: [
              "node"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "toast-slot": {
      type: "toast-slot",
      category: "layout",
      sourceFile: "components/core/organisms/ToastSlot.tsx",
      tier: "organisms",
      family: "core",
      description: "ToastSlot Component  Wraps toast slot content in a proper Toast component with positioning. Used by trait-driven pages to display toast UI from render_ui effects.  Handles: - Auto-show when content is present - Dispatches DISMISS event when dismissed - Fixed positioning in corner of screen",
      suggestedFor: [
        "toast",
        "slot",
        "toast slot"
      ],
      typicalSize: "large",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to display in the toast (message or ReactNode)"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Toast variant",
          enumValues: [
            "success",
            "error",
            "info",
            "warning"
          ]
        },
        title: {
          types: [
            "string"
          ],
          description: "Toast title"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Auto-dismiss duration in ms (0 = no auto-dismiss)",
          default: 5e3
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for qualified event emission"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "auth-layout": {
      type: "auth-layout",
      category: "template",
      sourceFile: "components/core/templates/AuthLayout.tsx",
      tier: "templates",
      family: "core",
      description: "AuthLayout component",
      suggestedFor: [
        "auth",
        "layout",
        "auth layout"
      ],
      typicalSize: "full",
      propsSchema: {
        appName: {
          types: [
            "string"
          ],
          description: "App name"
        },
        logo: {
          types: [
            "node"
          ],
          description: "Logo component or URL"
        },
        backgroundImage: {
          types: [
            "asset"
          ],
          description: "Background image URL"
        },
        showBranding: {
          types: [
            "boolean"
          ],
          description: "Show branding panel on the side",
          default: true
        },
        brandingContent: {
          types: [
            "node"
          ],
          description: "Branding panel content"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "counter-template": {
      type: "counter-template",
      category: "template",
      sourceFile: "components/core/templates/CounterTemplate.tsx",
      tier: "templates",
      family: "core",
      description: "CounterTemplate  A presentational template for counter/incrementer features. Supports increment, decrement, and reset operations.",
      suggestedFor: [
        "counter",
        "template",
        "counter template"
      ],
      typicalSize: "full",
      propsSchema: {
        entity: {
          types: [
            "object"
          ],
          description: "Entity data, the sole source of runtime state",
          required: true,
          kind: "entity",
          requiredFields: [
            "id",
            "count"
          ],
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            count: {
              types: [
                "number"
              ]
            },
            decrementDisabled: {
              types: [
                "boolean"
              ]
            },
            incrementDisabled: {
              types: [
                "boolean"
              ]
            },
            decrementLabel: {
              types: [
                "string"
              ]
            },
            incrementLabel: {
              types: [
                "string"
              ]
            },
            rangeText: {
              types: [
                "string"
              ]
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "External styling override"
        },
        onIncrement: {
          types: [
            "function"
          ],
          description: "Called when increment is clicked",
          kind: "callback",
          callbackArgs: []
        },
        onDecrement: {
          types: [
            "function"
          ],
          description: "Called when decrement is clicked",
          kind: "callback",
          callbackArgs: []
        },
        onReset: {
          types: [
            "function"
          ],
          description: "Called when reset is clicked",
          kind: "callback",
          callbackArgs: []
        },
        incrementEvent: {
          types: [
            "string"
          ],
          description: "Declarative event key \u2014 emits UI:{incrementEvent} via bus on increment",
          kind: "event"
        },
        decrementEvent: {
          types: [
            "string"
          ],
          description: "Declarative event key \u2014 emits UI:{decrementEvent} via bus on decrement",
          kind: "event"
        },
        resetEvent: {
          types: [
            "string"
          ],
          description: "Declarative event key \u2014 emits UI:{resetEvent} via bus on reset",
          kind: "event"
        },
        title: {
          types: [
            "string"
          ],
          description: "Title displayed above the counter"
        },
        showReset: {
          types: [
            "boolean"
          ],
          description: "Show reset button",
          default: true
        },
        size: {
          types: [
            "string"
          ],
          description: "Counter display size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Template variant",
          enumValues: [
            "minimal",
            "standard",
            "full"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dashboard-layout": {
      type: "dashboard-layout",
      category: "template",
      sourceFile: "components/core/templates/DashboardLayout.tsx",
      tier: "templates",
      family: "core",
      description: "DashboardLayout component",
      suggestedFor: [
        "dashboard",
        "layout",
        "dashboard layout"
      ],
      typicalSize: "full",
      propsSchema: {
        appName: {
          types: [
            "string"
          ],
          description: "App name shown in sidebar"
        },
        logo: {
          types: [
            "node"
          ],
          description: "Logo component or URL"
        },
        navItems: {
          types: [
            "array"
          ],
          description: "Navigation items. Apps that need a Settings page should add it as a navItems entry, not depend on baked-in chrome.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              children: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "NavItem"
                }
              }
            },
            required: [
              "label",
              "href"
            ]
          },
          default: []
        },
        user: {
          types: [
            "object"
          ],
          description: "Current user info (optional - auto-populated from auth context if not provided). Accepts `null` as well as `undefined`: the `.lolo` substrate's signed-out spelling is `(if @config.viewerName {\u2026} null)`, and the compiled path passes that `null` through verbatim.",
          properties: {
            name: {
              types: [
                "string"
              ]
            },
            email: {
              types: [
                "string"
              ]
            },
            avatar: {
              types: [
                "asset"
              ]
            }
          },
          propertyRequired: [
            "name"
          ]
        },
        headerActions: {
          types: [
            "node"
          ],
          description: "Header actions (extra slots beyond bell/search/theme)."
        },
        showSearch: {
          types: [
            "boolean"
          ],
          description: "Show the top-bar search box. Default `false` \u2014 opt in by setting `searchEvent` (any truthy value implies showSearch) or this flag.",
          default: false
        },
        searchEvent: {
          types: [
            "string"
          ],
          description: "Declarative search event \u2014 fires `UI:{searchEvent}` on the bus when the user submits the search box (Enter key). Setting this implies `showSearch=true`. Use `onSearchSubmit` for direct React usage instead.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        onSearchSubmit: {
          types: [
            "function"
          ],
          description: "React-side search submit callback. Used when the host wires the layout directly (not via render-ui pattern resolution).",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        topBarActions: {
          types: [
            "array"
          ],
          description: "Generic top-bar action buttons rendered between the search bar and the notifications bell. Each entry: `{icon, label?, event, badge?, variant?}`. Domain-agnostic \u2014 consumers add a cart icon, profile button, help-bubble, or any other top-bar action by appending entries. Empty array (default) renders nothing.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              badge: {
                types: [
                  "number",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "primary",
                  "danger"
                ]
              }
            },
            required: [
              "icon"
            ]
          },
          default: []
        },
        userMenuItems: {
          types: [
            "array"
          ],
          description: "Account-menu entries listed above Sign out (Settings, a profile page), each `{icon, label, event?, navigatesTo?, variant?}` like `topBarActions`.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              badge: {
                types: [
                  "number",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "primary",
                  "danger"
                ]
              }
            },
            required: [
              "icon"
            ]
          },
          default: []
        },
        fullBleed: {
          types: [
            "boolean"
          ],
          description: "Content fills the area edge to edge \u2014 no padding, no max width, full height (workspaces, canvases, maps).",
          default: false
        },
        notifications: {
          types: [
            "array"
          ],
          description: "Notification list. Pass an empty array to show the bell with no badge; omit / pass null to hide the bell entirely.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              createdAt: {
                types: [
                  "string"
                ]
              },
              read: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "message"
            ]
          }
        },
        notificationClickEvent: {
          types: [
            "string"
          ],
          description: "Declarative bell click event \u2014 fires `UI:{notificationClickEvent}` on the bus with an empty payload when the user clicks the bell.",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        onNotificationClick: {
          types: [
            "function"
          ],
          description: "React-side bell click callback.",
          kind: "callback",
          callbackArgs: []
        },
        showThemeToggle: {
          types: [
            "boolean"
          ],
          description: "Show the theme toggle button in the header. Default `true` \u2014 universally useful for a11y / dark mode.",
          default: true
        },
        sidebarFooter: {
          types: [
            "node"
          ],
          description: "Custom sidebar footer (optional). When omitted, the sidebar has no footer \u2014 apps that need Settings/etc. add them via navItems."
        },
        sidebarContent: {
          types: [
            "node"
          ],
          description: "Optional content region rendered between the nav links and the footer \u2014 a page tree or any consumer navigation surface; scrolls independently and takes the sidebar's remaining height. Hidden in the collapsed rail."
        },
        currentPath: {
          types: [
            "string"
          ],
          description: "Active path used to highlight the matching nav item. Falls back to `useCurrentPagePath()` (set by `CurrentPagePathProvider`), then to `useLocation().pathname`. Production deploys can omit it; the studio preview passes the in-frame `?page=` value so highlighting works inside the embedded playground where `pathname` would be the studio route."
        },
        onSignOut: {
          types: [
            "function"
          ],
          description: "Callback when user clicks sign out (optional - uses auth context signOut if not provided)",
          kind: "callback",
          callbackArgs: []
        },
        layoutMode: {
          types: [
            "string"
          ],
          description: 'Layout mode. Default `"sidebar"`. - `sidebar`: Left sidebar nav + top header (classic dashboard) - `topnav`: Horizontal nav in header, no sidebar - `bottomnav`: Bottom tab bar, no sidebar - `minimal`: No nav chrome, content only - `drawer`: Sidebar nav behind a menu button at every width; content gets the full width',
          enumValues: [
            "sidebar",
            "topnav",
            "bottomnav",
            "minimal",
            "drawer"
          ],
          default: "sidebar"
        },
        children: {
          types: [
            "node"
          ],
          description: "Page content rendered inside the main area"
        }
      }
    },
    "generic-app-template": {
      type: "generic-app-template",
      category: "template",
      sourceFile: "components/core/templates/GenericAppTemplate.tsx",
      tier: "templates",
      family: "core",
      description: "GenericAppTemplate  A simple, generic template for any application. Includes a header with title and actions, and a main content area. **Atomic Design**: Composed using Box, Typography, and Button atoms.",
      suggestedFor: [
        "generic",
        "app",
        "template",
        "generic app template"
      ],
      typicalSize: "full",
      propsSchema: {
        entity: {
          types: [
            "object"
          ],
          description: "Entity data, the sole source of runtime state",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class name"
        },
        title: {
          types: [
            "string"
          ],
          description: "Page title",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Subtitle or description"
        },
        children: {
          types: [
            "node"
          ],
          description: "Main content",
          required: true
        },
        headerActions: {
          types: [
            "node"
          ],
          description: "Header actions (buttons, links)"
        },
        footer: {
          types: [
            "node"
          ],
          description: "Footer content"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-shell": {
      type: "game-shell",
      category: "game",
      sourceFile: "components/game/templates/GameShell.tsx",
      tier: "templates",
      family: "game",
      description: 'GameShell  A full-screen layout wrapper for game applications. Replaces DashboardLayout for game clients \u2014 no sidebar nav, just full-viewport rendering with an optional HUD overlay bar.  Used as a React Router layout route element: <Route element={<GameShell appName="TraitWars" />}> <Route index element={<WorldMapPage />} /> ... </Route>',
      suggestedFor: [
        "game",
        "shell",
        "game shell"
      ],
      typicalSize: "full",
      propsSchema: {
        appName: {
          types: [
            "string"
          ],
          description: "Application / game title shown as a floating chip"
        },
        hud: {
          types: [
            "node"
          ],
          description: 'Stat chips row \u2014 floats along the top edge. Legacy chrome surface: new behaviors emit HUD chrome via `render-ui "hud-top"` instead.'
        },
        addons: {
          types: [
            "node"
          ],
          description: 'Action cluster \u2014 floats bottom-right (End Turn, Fire, Reset, \u2026). Legacy chrome surface: new behaviors emit action chrome via `render-ui "hud-bottom"` or `"floating"` instead.'
        },
        controls: {
          types: [
            "node"
          ],
          description: 'Movement controls \u2014 floats bottom-left (d-pad / control-grid). Legacy chrome surface: new behaviors emit movement controls via `render-ui "hud-bottom"` instead.'
        },
        overlay: {
          types: [
            "node"
          ],
          description: 'Centered overlay layer (victory/defeat banners, dialogs). Legacy chrome surface: new behaviors emit overlays via `render-ui "overlay"` (clear with `render-ui "overlay" null` on every exit) instead.'
        },
        className: {
          types: [
            "string"
          ],
          description: "Extra class name on the root container"
        },
        showTopBar: {
          types: [
            "boolean"
          ],
          description: "Whether to show the title chip (default: true)",
          default: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Game content \u2014 fills the whole surface underneath the overlays"
        },
        backgroundAsset: {
          types: [
            "object"
          ],
          description: "Pattern slice tiled at low opacity across the surface (never cover-stretched).",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        hudBackgroundAsset: {
          types: [
            "object"
          ],
          description: "Per-call-site 9-sliced panel override. Chrome normally comes from the active theme; most callers leave this unset.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        fontFamily: {
          types: [
            "string"
          ],
          description: "Game display-font key (pixel | fredoka | future | future-narrow | blocks | mini) or a CSS font-family. Scoped override of the theme contract's --font-family-display slot: titles and numerics take this face, body text follows the active theme. Unset = the theme token decides."
        },
        "data-theme": {
          types: [
            "string"
          ],
          description: 'Scopes an `@almadar/ui` theme (e.g. "game-sci-fi-dark") to this shell\'s subtree.'
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    chart: {
      type: "chart",
      category: "visualization",
      sourceFile: "components/core/molecules/Chart.tsx",
      tier: "molecules",
      family: "core",
      description: "Data visualization chart supporting bar, line, pie, area, and donut types",
      suggestedFor: [
        "dashboards",
        "analytics",
        "data visualization",
        "reporting",
        "bar chart",
        "line chart",
        "pie chart",
        "area chart",
        "donut chart",
        "bar",
        "line",
        "pie",
        "area",
        "donut"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        title: {
          types: [
            "string"
          ],
          description: "Chart title"
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Chart subtitle / description"
        },
        chartType: {
          types: [
            "string"
          ],
          description: "Chart type (legacy alias for `look`).",
          enumValues: [
            "bar",
            "line",
            "pie",
            "area",
            "donut",
            "scatter",
            "histogram"
          ],
          synonyms: "chart style, graph type, bar chart, line chart, pie chart, area chart, donut chart, scatter plot, histogram",
          tier: "domain"
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 drives the chart sub-renderer (bar / line / pie / etc.).",
          enumValues: [
            "bar-vertical",
            "bar-horizontal",
            "line",
            "area",
            "pie",
            "donut",
            "scatter",
            "histogram"
          ],
          synonyms: "chart style, render style, visual treatment",
          tier: "presentation"
        },
        series: {
          types: [
            "array"
          ],
          description: "Multi-series data",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              data: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    value: {
                      types: [
                        "number"
                      ]
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "label",
                    "value"
                  ]
                }
              },
              color: {
                types: [
                  "string"
                ]
              },
              dashed: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "name",
              "data"
            ]
          }
        },
        data: {
          types: [
            "array"
          ],
          description: "Simple single-series shorthand (bar/line/pie/area/donut/histogram)",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        scatterData: {
          types: [
            "array"
          ],
          description: "Raw {x,y} points for scatter",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          }
        },
        height: {
          types: [
            "number"
          ],
          description: "Chart height in px",
          default: 200
        },
        showLegend: {
          types: [
            "boolean"
          ],
          description: "Show legend",
          default: true
        },
        showValues: {
          types: [
            "boolean"
          ],
          description: "Show values on chart",
          default: false
        },
        stack: {
          types: [
            "string"
          ],
          description: "Stack mode for bar / area",
          enumValues: [
            "none",
            "stack",
            "normalize"
          ],
          default: "none"
        },
        timeAxis: {
          types: [
            "boolean"
          ],
          description: 'Format X-axis labels as time (ISO date in \u2192 "Mar 2026"-style label out)',
          default: false
        },
        period: {
          types: [
            "string"
          ],
          description: 'Bucket granularity of a time axis \u2014 labels read "Oct 2", "Week of Sep 28", "Oct 2026", "Q4 2026" or "2026"',
          enumValues: [
            "day",
            "week",
            "month",
            "quarter",
            "year"
          ]
        },
        drillEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted as `UI:{drillEvent}` with `{ label, value, seriesLabel? }` on data-point click"
        },
        actions: {
          types: [
            "array"
          ],
          description: "Top-level chart actions (export, refresh, etc.)",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "card",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    meter: {
      type: "meter",
      category: "visualization",
      sourceFile: "components/core/molecules/Meter.tsx",
      tier: "molecules",
      family: "core",
      description: "Gauge/meter component for displaying a value within a range with thresholds",
      suggestedFor: [
        "dashboards",
        "progress tracking",
        "credit/quota displays",
        "KPI indicators"
      ],
      typicalSize: "small",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Current value",
          required: true
        },
        min: {
          types: [
            "number"
          ],
          description: "Minimum value",
          default: 0
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value",
          default: 100
        },
        label: {
          types: [
            "string"
          ],
          description: "Display label"
        },
        unit: {
          types: [
            "string"
          ],
          description: "Unit suffix (e.g., '%', 'MB', 'credits')"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Display variant",
          enumValues: [
            "linear",
            "radial",
            "segmented"
          ],
          default: "linear"
        },
        thresholds: {
          types: [
            "array"
          ],
          description: "Color thresholds",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "color"
            ]
          }
        },
        segments: {
          types: [
            "number"
          ],
          description: "Number of segments (for segmented variant)",
          default: 5
        },
        showValue: {
          types: [
            "boolean"
          ],
          description: "Show value text",
          default: true
        },
        size: {
          types: [
            "string"
          ],
          description: "Size (for radial variant)",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    timeline: {
      type: "timeline",
      category: "display",
      sourceFile: "components/core/organisms/Timeline.tsx",
      tier: "organisms",
      family: "core",
      description: "Vertical timeline for displaying chronological events with status indicators",
      suggestedFor: [
        "activity logs",
        "process tracking",
        "history views",
        "workflow steps"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "list",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema entity data typed against @almadar/core's EntityRow. Items from `entity` are normalised into `items` when `items` is omitted. UI-specific TimelineItem fields (`icon`, callbacks) cannot round-trip through the event bus, so decorative stories that need them pass `items` directly.",
          kind: "entity",
          cardinality: "collection"
        },
        title: {
          types: [
            "string"
          ],
          description: "Timeline title"
        },
        items: {
          types: [
            "array"
          ],
          description: "Timeline items",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              date: {
                types: [
                  "string"
                ]
              },
              status: {
                types: [
                  "string"
                ],
                enumValues: [
                  "complete",
                  "active",
                  "pending",
                  "error"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              tags: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "id",
              "title"
            ]
          }
        },
        fields: {
          types: [
            "array"
          ],
          description: "Entity fields the timeline draws; each slot is named by its own `*Field` prop.",
          items: {
            types: [
              "string"
            ]
          }
        },
        titleField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's title."
        },
        descriptionField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's description."
        },
        dateField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's date."
        },
        statusField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's status (complete | active | pending | error)."
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Actions per item",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment.",
          enumValues: [
            "vertical-compact",
            "vertical-spacious",
            "horizontal",
            "swimlane"
          ],
          default: "vertical-spacious"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "media-gallery": {
      type: "media-gallery",
      category: "media",
      sourceFile: "components/core/organisms/MediaGallery.tsx",
      tier: "organisms",
      family: "core",
      description: "Grid gallery for images and media with lightbox, selection, and upload",
      suggestedFor: [
        "photo galleries",
        "image management",
        "media libraries",
        "portfolio displays"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Entity rows to display (collection cardinality).",
          kind: "entity",
          cardinality: "collection"
        },
        title: {
          types: [
            "string"
          ],
          description: "Gallery title"
        },
        items: {
          types: [
            "array"
          ],
          description: "Media items",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              src: {
                types: [
                  "asset"
                ]
              },
              alt: {
                types: [
                  "string"
                ]
              },
              thumbnail: {
                types: [
                  "asset"
                ]
              },
              mediaType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "image",
                  "video"
                ]
              },
              caption: {
                types: [
                  "string"
                ]
              },
              fileSize: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        columns: {
          types: [
            "number"
          ],
          description: "Column count",
          numericEnumValues: [
            2,
            3,
            4,
            5,
            6
          ],
          default: 3
        },
        selectable: {
          types: [
            "boolean"
          ],
          description: "Enable item selection",
          default: false
        },
        selectedItems: {
          types: [
            "array"
          ],
          description: "Selected item IDs",
          items: {
            types: [
              "string"
            ]
          },
          default: []
        },
        selectionEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted when selection changes (emitted as UI:{selectionEvent})",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selection",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ],
          default: "SELECTION_CHANGE"
        },
        showUpload: {
          types: [
            "boolean"
          ],
          description: "Show upload button",
          default: false
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        aspectRatio: {
          types: [
            "string"
          ],
          description: "Aspect ratio for thumbnails",
          enumValues: [
            "square",
            "landscape",
            "portrait"
          ],
          default: "square"
        },
        srcField: {
          types: [
            "string"
          ],
          description: "Record field holding each image's URL (entity mode). Default `src`.",
          default: "src"
        },
        captionField: {
          types: [
            "string"
          ],
          description: "Record field holding each image's caption (entity mode). Default `caption`.",
          default: "caption"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "signature-pad": {
      type: "signature-pad",
      category: "form",
      sourceFile: "components/core/molecules/SignaturePad.tsx",
      tier: "molecules",
      family: "core",
      description: "SignaturePad \u2014 a draw-to-sign canvas that captures a handwritten signature or initials.",
      capabilities: "e-signature capture, sign-here field, consent signature, initial-here field, wet-signature substitute",
      suggestedFor: [
        "form signing",
        "approval workflows",
        "document signing",
        "consent forms"
      ],
      typicalSize: "small",
      propsSchema: {
        label: {
          types: [
            "string"
          ],
          description: "Label above the pad"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text"
        },
        strokeColor: {
          types: [
            "string"
          ],
          description: "Stroke color"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width",
          default: 2
        },
        height: {
          types: [
            "number"
          ],
          description: "Pad height",
          default: 200
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "Whether the pad is read-only",
          default: false
        },
        value: {
          types: [
            "string"
          ],
          description: "Existing signature image URL"
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Callback when signature changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "dataUrl",
              type: "string"
            }
          ]
        },
        signEvent: {
          types: [
            "string"
          ],
          description: "Event to emit on sign",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "signature",
              type: "string",
              required: true
            }
          ]
        },
        clearEvent: {
          types: [
            "string"
          ],
          description: "Event to emit on clear",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "document-viewer": {
      type: "document-viewer",
      category: "display",
      sourceFile: "components/core/molecules/DocumentViewer.tsx",
      tier: "molecules",
      family: "core",
      description: "Document viewer for PDFs, text, HTML, and markdown with zoom and pagination",
      suggestedFor: [
        "document management",
        "PDF viewing",
        "content preview",
        "report viewing"
      ],
      typicalSize: "large",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        title: {
          types: [
            "string"
          ],
          description: "Document title"
        },
        src: {
          types: [
            "asset"
          ],
          description: "Document URL (for PDF/external documents)"
        },
        content: {
          types: [
            "string"
          ],
          description: "Document content (for text/html/markdown)"
        },
        documentType: {
          types: [
            "string"
          ],
          description: "Document type",
          enumValues: [
            "pdf",
            "text",
            "html",
            "markdown"
          ],
          default: "pdf"
        },
        currentPage: {
          types: [
            "number"
          ],
          description: "Current page (for multi-page documents)"
        },
        totalPages: {
          types: [
            "number"
          ],
          description: "Total pages"
        },
        height: {
          types: [
            "number",
            "string"
          ],
          description: "Viewer height",
          default: 600
        },
        showToolbar: {
          types: [
            "boolean"
          ],
          description: "Show toolbar",
          default: true
        },
        showDownload: {
          types: [
            "boolean"
          ],
          description: "Show download button",
          default: false
        },
        showPrint: {
          types: [
            "boolean"
          ],
          description: "Show print button",
          default: false
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        documents: {
          types: [
            "array"
          ],
          description: "Multiple documents (tabbed view)",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              src: {
                types: [
                  "asset"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              },
              documentType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pdf",
                  "text",
                  "html",
                  "markdown"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "graph-canvas": {
      type: "graph-canvas",
      category: "game",
      sourceFile: "components/core/molecules/GraphCanvas.tsx",
      tier: "molecules",
      family: "core",
      description: "Force-directed graph visualization for node-link data with interactive zoom, pan, and layout",
      suggestedFor: [
        "knowledge graphs",
        "network visualization",
        "relationship mapping",
        "dependency graphs"
      ],
      typicalSize: "large",
      propsSchema: {
        description: {
          types: [
            "string"
          ],
          description: "Text alternative for the graph, rendered as visually hidden text and linked by aria-describedby."
        },
        title: {
          types: [
            "string"
          ],
          description: "Graph title"
        },
        nodes: {
          types: [
            "array"
          ],
          description: "Graph nodes",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              group: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              badge: {
                types: [
                  "number"
                ]
              },
              mark: {
                types: [
                  "object"
                ],
                variants: [
                  {
                    types: [
                      "object"
                    ],
                    properties: {
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "suggested"
                        ]
                      },
                      suggestionId: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "kind",
                      "suggestionId"
                    ]
                  },
                  {
                    types: [
                      "object"
                    ],
                    properties: {
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "proposed"
                        ]
                      },
                      suggestionId: {
                        types: [
                          "string"
                        ]
                      },
                      tint: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "kind",
                      "suggestionId"
                    ]
                  }
                ],
                discriminator: "kind"
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        edges: {
          types: [
            "array"
          ],
          description: "Graph edges (the only rendered links)",
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "source",
              "target"
            ]
          }
        },
        proposedEdges: {
          types: [
            "array"
          ],
          description: "Uncommitted proposal links \u2014 rendered dashed/faint in a separate pass, never folded into the layout springs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "source",
              "target"
            ]
          }
        },
        similarity: {
          types: [
            "array"
          ],
          description: "All-pairs similarity (cosine 0\u20131) used ONLY for layout, never drawn. It is the secondary macro-layout: non-connected pairs get a weak spring (higher cosine \u21D2 closer) so clusters arrange relative to each other; drawn `edges` stay the primary tight structure. When omitted, edges alone drive the layout.",
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "source",
              "target",
              "weight"
            ]
          }
        },
        height: {
          types: [
            "number"
          ],
          description: "Canvas height",
          default: 400
        },
        showLabels: {
          types: [
            "boolean"
          ],
          description: "Show node labels",
          default: true
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "Enable zoom/pan",
          default: true
        },
        draggable: {
          types: [
            "boolean"
          ],
          description: "Enable node dragging",
          default: true
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "On node click",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onMarkClick: {
          types: [
            "function"
          ],
          description: "On marked-node click (suggestion/proposal target). Takes priority over onNodeClick.",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onNodeDoubleClick: {
          types: [
            "function"
          ],
          description: "On node double-click (e.g. to drill in)",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onBadgeClick: {
          types: [
            "function"
          ],
          description: "On node badge click (e.g. to expand a merged cluster).",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        nodeClickEvent: {
          types: [
            "string"
          ],
          description: "Node click event",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        selectedNodeId: {
          types: [
            "string"
          ],
          description: "Currently selected node id (rendered emphasized)"
        },
        repulsion: {
          types: [
            "number"
          ],
          description: "Force-sim repulsion strength between nodes (larger \u21D2 more spread out)",
          default: 800
        },
        linkDistance: {
          types: [
            "number"
          ],
          description: "Force-sim target edge length (larger \u21D2 more spread out)",
          default: 100
        },
        nodeSpacing: {
          types: [
            "number"
          ],
          description: "Minimum empty gap (px) enforced between node edges so nodes never overlap.",
          default: 28
        },
        linkOpacity: {
          types: [
            "number"
          ],
          description: "Base opacity for links when nothing is hovered (kept faint so dense graphs stay readable; incident links brighten on hover).",
          default: 0.18
        },
        layout: {
          types: [
            "string"
          ],
          description: "Layout algorithm",
          enumValues: [
            "force",
            "circular",
            "grid"
          ],
          default: "force"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "error-boundary": {
      type: "error-boundary",
      category: "state",
      sourceFile: "components/core/molecules/ErrorBoundary.tsx",
      tier: "molecules",
      family: "core",
      description: "ErrorBoundary component",
      suggestedFor: [
        "error",
        "boundary",
        "error boundary"
      ],
      typicalSize: "medium",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to render when no error",
          required: true
        },
        fallback: {
          types: [
            "node",
            "function"
          ],
          description: "Fallback UI when an error is caught \u2014 ReactNode or render function"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes for the wrapper"
        },
        onError: {
          types: [
            "function"
          ],
          description: "Called when an error is caught (for logging/telemetry)",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "error",
              type: "object"
            },
            {
              name: "errorInfo",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    skeleton: {
      type: "skeleton",
      category: "component",
      sourceFile: "components/core/molecules/Skeleton.tsx",
      tier: "molecules",
      family: "core",
      description: "Skeleton component",
      suggestedFor: [
        "skeleton"
      ],
      typicalSize: "medium",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "The skeleton variant to render",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text"
          ],
          default: "text"
        },
        spec: {
          types: [
            "string"
          ],
          description: "A declared `skeleton` prop value (shape name, sized shape or `none`); overrides variant/rows/columns/fields",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ]
        },
        rows: {
          types: [
            "number"
          ],
          description: "Rows for table/list/text, cards for grid, field pairs for detail",
          default: 5
        },
        columns: {
          types: [
            "number"
          ],
          description: "Columns for table, tiles for stats",
          default: 4
        },
        fields: {
          types: [
            "number"
          ],
          description: "Number of fields for form variant",
          default: 4
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "quiz-block": {
      type: "quiz-block",
      category: "component",
      sourceFile: "components/core/molecules/QuizBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "QuizBlock \u2014 a single quiz question with answer choices and submit/reveal feedback.",
      capabilities: "multiple-choice quiz, test question, exam item, assessment question, knowledge check, trivia question",
      suggestedFor: [
        "quiz",
        "block",
        "quiz block"
      ],
      typicalSize: "medium",
      propsSchema: {
        question: {
          types: [
            "string"
          ],
          description: "The quiz question",
          required: true
        },
        answer: {
          types: [
            "string"
          ],
          description: "The quiz answer (revealed on toggle)",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "code-block": {
      type: "code-block",
      category: "component",
      sourceFile: "components/core/molecules/markdown/CodeBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "CodeBlock Molecule Component  A syntax-highlighted code block with copy-to-clipboard functionality. Preserves scroll position during re-renders.  Event Contract: - Emits: UI:COPY_CODE { language, success }",
      suggestedFor: [
        "code",
        "block",
        "code block"
      ],
      typicalSize: "medium",
      propsSchema: {
        code: {
          types: [
            "string"
          ],
          description: "The code content to display"
        },
        language: {
          types: [
            "string"
          ],
          description: "Programming language for syntax highlighting (any Prism id; loaded on demand if not pre-registered)",
          default: "text"
        },
        showCopyButton: {
          types: [
            "boolean"
          ],
          description: "Show the copy button",
          default: true
        },
        showLanguageBadge: {
          types: [
            "boolean"
          ],
          description: "Show the language badge",
          default: true
        },
        maxHeight: {
          types: [
            "string",
            "number"
          ],
          description: "Maximum height before scrolling",
          default: "60vh"
        },
        foldable: {
          types: [
            "boolean"
          ],
          description: "Enable brace-based code folding of multi-line `{}`/`[]` blocks (default: true)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "When true, render an editable surface that composes a transparent `Textarea` over a Prism-highlighted overlay. The overlay re-tokenizes on each keystroke (driven from a local mirror of the textarea value), so users see syntax-highlighted code while still being able to type. Folding is skipped in editable mode. History: GAP-51 first-cut shipped plain (no-highlighting) editable text; GAP-77 (2026-04-12) added the Prism overlay layer. Default: false (existing read-only behavior unchanged).",
          default: false
        },
        onChange: {
          types: [
            "function"
          ],
          description: "GAP-51: called with the new code on every keystroke when `editable === true`. Consumers should debounce + parse downstream \u2014 `CodeBlock` does not.",
          kind: "callback",
          callbackArgs: [
            {
              name: "code",
              type: "string"
            }
          ]
        },
        errorLines: {
          types: [
            "object"
          ],
          description: "GAP-80: line-level error/warning highlights. Map of 1-based line number \u2192 severity. Paints a colored background on each line: error = red, warning = yellow. Honored by the editable overlay and (GAP-84) by viewer mode's non-diff highlight; pass undefined (default) to disable. The consumer is responsible for computing the path \u2192 line map from the schema + validation results.",
          nonAuthorable: true
        },
        title: {
          types: [
            "string"
          ],
          description: "\u2500\u2500 Viewer props (absorbed from CodeViewer / DocCodeBlock) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Diff or plain-code display mode",
          enumValues: [
            "code",
            "diff"
          ],
          default: "code"
        },
        diff: {
          types: [
            "array"
          ],
          description: "Pre-computed diff lines",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "added",
                  "removed",
                  "unchanged",
                  "context"
                ]
              },
              beforeLineNumber: {
                types: [
                  "number"
                ]
              },
              afterLineNumber: {
                types: [
                  "number"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "type",
              "content"
            ]
          }
        },
        oldValue: {
          types: [
            "string"
          ],
          description: "Old source text \u2014 generates diff when combined with newValue"
        },
        newValue: {
          types: [
            "string"
          ],
          description: "New source text \u2014 generates diff when combined with oldValue"
        },
        showLineNumbers: {
          types: [
            "boolean"
          ],
          description: "Show line numbers in code / diff mode",
          default: false
        },
        wordWrap: {
          types: [
            "boolean"
          ],
          description: "Enable word-wrap in code / diff mode",
          default: false
        },
        files: {
          types: [
            "array"
          ],
          description: "Multiple files shown as tabs",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              code: {
                types: [
                  "string"
                ]
              },
              language: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "json",
                  "javascript",
                  "js",
                  "typescript",
                  "ts",
                  "jsx",
                  "tsx",
                  "css",
                  "markdown",
                  "md",
                  "bash",
                  "shell",
                  "sh",
                  "yaml",
                  "yml",
                  "rust",
                  "python",
                  "py",
                  "sql",
                  "diff",
                  "toml",
                  "go",
                  "graphql",
                  "html",
                  "xml",
                  "clojure",
                  "clj",
                  "edn",
                  "haskell",
                  "hs",
                  "lisp",
                  "scheme",
                  "scala",
                  "elixir",
                  "ex",
                  "exs",
                  "erlang",
                  "erl",
                  "elm",
                  "fsharp",
                  "fs",
                  "fsx",
                  "ocaml",
                  "ml",
                  "java",
                  "c",
                  "cpp",
                  "c++",
                  "cc",
                  "cxx",
                  "hpp",
                  "h",
                  "csharp",
                  "cs",
                  "objectivec",
                  "objc",
                  "php",
                  "ruby",
                  "rb",
                  "swift",
                  "kotlin",
                  "kt",
                  "lua",
                  "r",
                  "dart",
                  "julia",
                  "fortran",
                  "f90",
                  "f95",
                  "perl",
                  "pl",
                  "powershell",
                  "ps1",
                  "makefile",
                  "make",
                  "nginx",
                  "ini",
                  "orb",
                  "lolo"
                ]
              }
            },
            required: [
              "label",
              "code"
            ]
          }
        },
        naturalLanguages: {
          types: [
            "array"
          ],
          description: "Show the program in these natural languages as tabs, translating on the fly. Only meaningful for `language` 'lolo' / 'orb'; ignored otherwise. Fewer than two entries renders exactly as without the prop. `editable` wins: editing a translation is not a thing, so the English source is shown and this is ignored.",
          items: {
            types: [
              "object"
            ]
          }
        },
        actions: {
          types: [
            "array"
          ],
          description: "Action badges in the toolbar",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        showCopy: {
          types: [
            "boolean"
          ],
          description: "Show copy button (viewer alias for showCopyButton)"
        },
        editorId: {
          types: [
            "string"
          ],
          description: "Stable identity for the keyboard router / plugin host to target this editor. Unset = this instance never receives capability events.",
          tier: "presentation"
        },
        onEditorFocus: {
          types: [
            "string"
          ],
          description: "Declarative bus emit fired when this editor gains focus.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            }
          ],
          default: "EDITOR_FOCUS",
          tier: "presentation"
        },
        onEditorBlur: {
          types: [
            "string"
          ],
          description: "Declarative bus emit fired when this editor loses focus.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            }
          ],
          default: "EDITOR_BLUR",
          tier: "presentation"
        },
        onMotion: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: move the cursor per the vim-style motion vocabulary.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "motion",
              type: "string",
              required: true,
              enumValues: [
                "left",
                "right",
                "up",
                "down",
                "word-forward",
                "word-back",
                "word-end",
                "line-start",
                "line-end",
                "first-nonblank",
                "doc-start",
                "doc-end",
                "paragraph-forward",
                "paragraph-back",
                "line",
                "selection",
                "match-bracket"
              ]
            },
            {
              name: "count",
              type: "number",
              required: true
            }
          ],
          default: "MOTION",
          tier: "presentation"
        },
        onOperate: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: apply a text operator over a motion's range.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "operator",
              type: "string",
              required: true,
              enumValues: [
                "delete",
                "yank",
                "change",
                "put",
                "put-before",
                "undo",
                "redo",
                "join",
                "toggle-case",
                "indent",
                "dedent",
                "replace"
              ]
            },
            {
              name: "motion",
              type: "string",
              required: true,
              enumValues: [
                "left",
                "right",
                "up",
                "down",
                "word-forward",
                "word-back",
                "word-end",
                "line-start",
                "line-end",
                "first-nonblank",
                "doc-start",
                "doc-end",
                "paragraph-forward",
                "paragraph-back",
                "line",
                "selection",
                "match-bracket"
              ]
            },
            {
              name: "count",
              type: "number",
              required: true
            }
          ],
          default: "OPERATE",
          tier: "presentation"
        },
        onInsertText: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: insert literal text at the cursor.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "text",
              type: "string",
              required: true
            }
          ],
          default: "INSERT_TEXT",
          tier: "presentation"
        },
        onSetMode: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: switch editor mode and caret style.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "mode",
              type: "string",
              required: true
            },
            {
              name: "caret",
              type: "string",
              required: true,
              enumValues: [
                "bar",
                "block",
                "underline"
              ]
            }
          ],
          default: "SET_MODE",
          tier: "presentation"
        },
        motions: {
          types: [
            "array"
          ],
          description: "Motion vocabulary this editor accepts. Default: the full `EditorMotion` set.",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "left",
              "right",
              "up",
              "down",
              "word-forward",
              "word-back",
              "word-end",
              "line-start",
              "line-end",
              "first-nonblank",
              "doc-start",
              "doc-end",
              "paragraph-forward",
              "paragraph-back",
              "line",
              "selection",
              "match-bracket"
            ]
          },
          default: [
            "left",
            "right",
            "up",
            "down",
            "word-forward",
            "word-back",
            "word-end",
            "line-start",
            "line-end",
            "first-nonblank",
            "doc-start",
            "doc-end",
            "paragraph-forward",
            "paragraph-back",
            "line",
            "selection",
            "match-bracket"
          ],
          tier: "presentation"
        },
        operators: {
          types: [
            "array"
          ],
          description: "Operator vocabulary this editor accepts. Default: the full `EditorOperator` set.",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "delete",
              "yank",
              "change",
              "put",
              "put-before",
              "undo",
              "redo",
              "join",
              "toggle-case",
              "indent",
              "dedent",
              "replace"
            ]
          },
          default: [
            "delete",
            "yank",
            "change",
            "put",
            "put-before",
            "undo",
            "redo",
            "join",
            "toggle-case",
            "indent",
            "dedent",
            "replace"
          ],
          tier: "presentation"
        },
        completions: {
          types: [
            "function"
          ],
          description: "Editable only: answers the caret's valid continuations after a keystroke (and on Ctrl-Space); they show under the editor and Tab or a click inserts one.",
          callbackArgs: [
            {
              name: "code",
              type: "string"
            },
            {
              name: "offset",
              type: "number"
            }
          ],
          renderCallback: true,
          tier: "presentation"
        },
        assist: {
          types: [
            "function"
          ],
          description: "Editable only: model-assisted editing. Asked after a pause (and on Ctrl-Space) for a completion at the caret, shown as ghost text; on \u2318/Ctrl-. for fixes, listed under the editor. Tab accepts the next, \u2318/Ctrl-Enter accepts all, Escape dismisses.",
          callbackArgs: [
            {
              name: "request",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  offset: {
                    types: [
                      "number"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "complete",
                      "fix"
                    ]
                  },
                  trigger: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "idle",
                      "explicit"
                    ]
                  }
                },
                required: [
                  "code",
                  "offset",
                  "kind",
                  "trigger"
                ]
              }
            }
          ],
          renderCallback: true,
          tier: "presentation"
        },
        problems: {
          types: [
            "number"
          ],
          description: "Editable only: how many problems the host knows the code has (some, like a parse error, have no line to mark). With `assist`, the editor offers to fix them.",
          tier: "presentation"
        },
        diagnostics: {
          types: [
            "array"
          ],
          description: "Editable only: problems located in the code (1-based line/column in characters, `endColumn` just past the last one), each underlined exactly \u2014 red wavy for an error, amber for a warning, dotted where the position is approximate. The message of the one under the caret shows below the editor.",
          items: {
            types: [
              "object"
            ],
            properties: {
              line: {
                types: [
                  "number"
                ]
              },
              column: {
                types: [
                  "number"
                ]
              },
              endLine: {
                types: [
                  "number"
                ]
              },
              endColumn: {
                types: [
                  "number"
                ]
              },
              severity: {
                types: [
                  "string"
                ],
                enumValues: [
                  "error",
                  "warning"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              approximate: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "line",
              "severity",
              "message"
            ]
          },
          tier: "presentation"
        },
        definitionAt: {
          types: [
            "function"
          ],
          description: "Editable only: go to definition. Whether the identifier (a dotted path is one) leads somewhere; with Cmd/Ctrl held the one under the pointer is underlined when it does. Comments and strings never qualify.",
          callbackArgs: [
            {
              name: "identifier",
              type: "string"
            }
          ],
          renderCallback: true
        },
        onGoToDefinition: {
          types: [
            "function"
          ],
          description: "Editable only: Cmd/Ctrl+click, or Cmd/Ctrl+Enter at the caret, on an identifier `definitionAt` accepts.",
          kind: "callback",
          callbackArgs: [
            {
              name: "identifier",
              type: "string"
            }
          ]
        },
        diagnosticsCode: {
          types: [
            "string"
          ],
          description: "The code `diagnostics` were computed on. When the editor's text has moved on, each range is carried over the edit; a range the edit touches is dropped until the code is checked again. Absent: they describe the current text.",
          tier: "presentation"
        },
        lineNumbers: {
          types: [
            "boolean"
          ],
          description: "Editable only: number the lines in a gutter (lines with errors in red). Default on.",
          tier: "presentation"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "markdown-content": {
      type: "markdown-content",
      category: "component",
      sourceFile: "components/core/molecules/markdown/MarkdownContent.tsx",
      tier: "molecules",
      family: "core",
      description: "MarkdownContent Molecule Component  Renders markdown content with support for GFM (GitHub Flavored Markdown), math equations (KaTeX), and raw HTML. Handles inline code only \u2014 fenced code blocks should be parsed out and rendered with CodeBlock component.  Event Contract: - No events emitted (display-only component) - entityAware: false  NOTE: react-markdown's `components` override API requires native HTML elements \u2014 this is the same library-boundary exception as `<iframe>` in DocumentViewer and `<svg>` in JazariStateMachine/StateMachineView.",
      suggestedFor: [
        "markdown",
        "content",
        "markdown content"
      ],
      typicalSize: "medium",
      propsSchema: {
        content: {
          types: [
            "string"
          ],
          description: "The markdown content to render",
          required: true
        },
        direction: {
          types: [
            "string"
          ],
          description: "Text direction (defaults to ltr; `ltr` is first so the generated config seeds it)",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        askEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Ask on selected text (as `UI:<askEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        noteEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Note on selected text (as `UI:<noteEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        askLabel: {
          types: [
            "string"
          ],
          description: "Ask action label (default: translated)"
        },
        noteLabel: {
          types: [
            "string"
          ],
          description: "Note action label (default: translated)"
        },
        annotations: {
          types: [
            "array"
          ],
          description: "Passages to highlight. Each `text` is matched exactly, first occurrence in document order inside a single rendered text node (outside code); a passage that spans formatting boundaries is not highlighted.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "question",
                  "note"
                ]
              }
            },
            required: [
              "id",
              "text",
              "kind"
            ]
          }
        },
        annotationEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a highlighted passage is clicked (as `UI:<annotationEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "content-renderer": {
      type: "content-renderer",
      category: "display",
      sourceFile: "components/core/molecules/ContentRenderer.tsx",
      tier: "molecules",
      family: "core",
      description: "ContentRenderer Organism  Renders rich content as a sequence of typed segments: markdown, code, orbital diagrams (via JazariStateMachine), and quiz blocks. Accepts either raw content string (auto-parsed) or pre-parsed segments.  Event Contract: - Delegates to child components (CodeBlock -> UI:COPY_CODE) - entityAware: false",
      suggestedFor: [
        "content",
        "renderer",
        "content renderer"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "\u2500\u2500 Display hints (set by trait via render-ui, never written by organism) \u2500\u2500"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number (1-indexed)"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items (for pagination display)"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters, keyed by field name. Scalar-only (`string | number | boolean`) rather than the full `FieldValue` domain: `FieldValue` is a self-referential union (adds `Date`, nested arrays/objects) the pattern-sync scanner can't shape into a `Map string V` knob, so it always degraded to `json`. This narrows out date/array/nested-object filter values \u2014 no current call site in this package constructs this prop, so nothing here breaks, but a caller wiring a date-range or multi-select filter through this prop would need those. Flagged for the coordinator; revert to `Record<string, FieldValue>` if that capability is needed.",
          mapValue: {
            types: [
              "string",
              "number",
              "boolean"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        content: {
          types: [
            "string"
          ],
          description: "Raw content string \u2014 auto-parsed into segments"
        },
        segments: {
          types: [
            "array"
          ],
          description: "Pre-parsed segments (overrides content)",
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "markdown"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "code"
                    ]
                  },
                  language: {
                    types: [
                      "string"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "language",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital"
                    ]
                  },
                  language: {
                    types: [
                      "string"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  schema: {
                    types: [
                      "sexpr"
                    ]
                  }
                },
                required: [
                  "type",
                  "language",
                  "content",
                  "schema"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "quiz"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  answer: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "question",
                  "answer"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Text direction for markdown",
          enumValues: [
            "rtl",
            "ltr"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "jazari-state-machine": {
      type: "jazari-state-machine",
      category: "display",
      sourceFile: "components/core/molecules/JazariStateMachine.tsx",
      tier: "molecules",
      family: "core",
      description: "JazariStateMachine component",
      suggestedFor: [
        "jazari",
        "state",
        "machine",
        "jazari state machine"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        schema: {
          types: [
            "sexpr"
          ],
          description: "Full schema \u2014 extracts first trait's state machine",
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        trait: {
          types: [
            "object"
          ],
          description: "Or pass a single trait directly",
          nonAuthorable: true
        },
        traitIndex: {
          types: [
            "number"
          ],
          description: "Which trait to visualize (default: 0)",
          default: 0
        },
        entityFields: {
          types: [
            "array"
          ],
          description: "Override entity field labels",
          items: {
            types: [
              "string"
            ]
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Text direction (default: 'ltr')",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "state-machine-view": {
      type: "state-machine-view",
      category: "display",
      sourceFile: "components/core/organisms/StateMachineView.tsx",
      tier: "organisms",
      family: "core",
      description: "StateMachineView component",
      suggestedFor: [
        "state",
        "machine",
        "view",
        "state machine view"
      ],
      typicalSize: "large",
      propsSchema: {
        activeState: {
          types: [
            "string"
          ],
          description: "State execution is currently in."
        },
        activeTransition: {
          types: [
            "number"
          ],
          description: "`index` of the transition that just fired."
        },
        visitedStates: {
          types: [
            "array"
          ],
          description: "States already passed through, in order.",
          items: {
            types: [
              "string"
            ]
          }
        },
        stateClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{stateClickEvent} with { stateId } when a state is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stateId",
              type: "string",
              required: true
            }
          ]
        },
        transitionClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{transitionClickEvent} with { index, event, from, to } when a transition label is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "event",
              type: "string",
              required: true
            },
            {
              name: "from",
              type: "string",
              required: true
            },
            {
              name: "to",
              type: "string",
              required: true
            }
          ]
        },
        onTransitionClick: {
          types: [
            "function"
          ],
          description: "Direct transition-click callback, for React hosts (e.g. a canvas node).",
          kind: "callback",
          callbackArgs: [
            {
              name: "transition",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  index: {
                    types: [
                      "number"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "index",
                  "event",
                  "from",
                  "to"
                ]
              }
            }
          ]
        },
        selectedState: {
          types: [
            "string"
          ],
          description: "State picked in an editor (first click)."
        },
        pendingSourceState: {
          types: [
            "string"
          ],
          description: "Source of a transition being drawn \u2014 the next state click picks its target."
        },
        nodeShape: {
          types: [
            "string"
          ],
          description: "Node silhouette. @default 'pill'",
          enumValues: [
            "pill",
            "gear"
          ],
          default: "pill"
        },
        entityFields: {
          types: [
            "array"
          ],
          description: "Entity field names listed in the header.",
          items: {
            types: [
              "string"
            ]
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Flow direction. @default 'ltr'",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        showHeader: {
          types: [
            "boolean"
          ],
          description: "Show the trait header (name, entity, listens/emits). @default true",
          default: true
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations shown in a popover on hover: a note per state name, per transition event and per effect type.",
          properties: {
            states: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            transitions: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            effects: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            }
          },
          example: {
            states: {
              pending: {
                title: "Unpaid",
                body: "Every order starts here."
              }
            },
            transitions: {
              PAY: {
                body: "Only fires when the amount is positive."
              }
            },
            effects: {
              persist: {
                body: "Saves the order."
              }
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        trait: {
          types: [
            "object"
          ],
          description: "The trait to draw (see `parseTraitLevel` / `traitLevelFromTrait`).",
          properties: {
            name: {
              types: [
                "string"
              ]
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            states: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  isInitial: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  },
                  isTerminal: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  }
                },
                required: [
                  "name"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  guard: {
                    types: [
                      "sexpr"
                    ],
                    nullable: true
                  },
                  effects: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        args: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "sexpr"
                            ]
                          }
                        }
                      },
                      required: [
                        "type",
                        "args"
                      ]
                    }
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "from",
                  "to",
                  "event",
                  "effects",
                  "index"
                ]
              }
            },
            emittedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listenedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            }
          },
          propertyRequired: [
            "name",
            "linkedEntity",
            "states",
            "transitions",
            "emittedEvents",
            "listenedEvents"
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "scaled-diagram": {
      type: "scaled-diagram",
      category: "component",
      sourceFile: "components/core/molecules/ScaledDiagram.tsx",
      tier: "molecules",
      family: "core",
      description: "ScaledDiagram Molecule  Wraps a fixed-size diagram (like JazariStateMachine / StateMachineView) and CSS-scales it to fit the parent container width.  The diagram renders at its natural (large) size. We observe the content element and once the diagram is measured we apply transform:scale() with a corrected container height so surrounding layout flows correctly.  Event Contract: - No events emitted (layout-only wrapper) - entityAware: false",
      suggestedFor: [
        "scaled",
        "diagram",
        "scaled diagram"
      ],
      typicalSize: "medium",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "day-cell": {
      type: "day-cell",
      category: "component",
      sourceFile: "components/core/atoms/DayCell.tsx",
      tier: "atoms",
      family: "core",
      description: "DayCell component",
      suggestedFor: [
        "day",
        "cell",
        "day cell"
      ],
      typicalSize: "small",
      propsSchema: {
        date: {
          types: [
            "date"
          ],
          description: "The date this cell represents. Optional at the dynamic render edge: an unbound `@config.date` arrives as `undefined`, so the cell falls back to today."
        },
        isToday: {
          types: [
            "boolean"
          ],
          description: "Whether this date is today",
          default: false
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Called when the day is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "date",
              type: "date"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "flip-container": {
      type: "flip-container",
      category: "layout",
      sourceFile: "components/core/atoms/FlipContainer.tsx",
      tier: "atoms",
      family: "core",
      description: "FlipContainer component",
      suggestedFor: [
        "flip",
        "container",
        "flip container"
      ],
      typicalSize: "small",
      propsSchema: {
        flipped: {
          types: [
            "boolean"
          ],
          description: "Whether the container is flipped (rotateY 180deg)",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Optional className for the outer wrapper"
        },
        children: {
          types: [
            "node"
          ],
          description: "The two face elements (front and back)",
          required: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler on the outer wrapper",
          kind: "callback",
          callbackArgs: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "time-slot-cell": {
      type: "time-slot-cell",
      category: "layout",
      sourceFile: "components/core/atoms/TimeSlotCell.tsx",
      tier: "atoms",
      family: "core",
      description: "TimeSlotCell component",
      suggestedFor: [
        "time",
        "slot",
        "cell",
        "time slot cell"
      ],
      typicalSize: "small",
      propsSchema: {
        time: {
          types: [
            "string"
          ],
          description: 'Time label for this slot (e.g. "09:00")',
          required: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Called when the slot is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "time",
              type: "string"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Event content placed inside the slot"
        },
        isOccupied: {
          types: [
            "boolean"
          ],
          description: "Whether this slot contains an event",
          default: false
        },
        onPointerDown: {
          types: [
            "function"
          ],
          description: "onPointerDown prop",
          kind: "callback",
          callbackArgs: []
        },
        onPointerUp: {
          types: [
            "function"
          ],
          description: "onPointerUp prop",
          kind: "callback",
          callbackArgs: []
        },
        onPointerCancel: {
          types: [
            "function"
          ],
          description: "onPointerCancel prop",
          kind: "callback",
          callbackArgs: []
        },
        onPointerLeave: {
          types: [
            "function"
          ],
          description: "onPointerLeave prop",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        "data-testid": {
          types: [
            "string"
          ],
          description: "data-testid prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "calendar-grid": {
      type: "calendar-grid",
      category: "display",
      sourceFile: "components/core/molecules/CalendarGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "CalendarGrid component",
      suggestedFor: [
        "calendar",
        "grid",
        "calendar grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        weekStart: {
          types: [
            "date"
          ],
          description: "Start of the week (defaults to current week's Monday)"
        },
        timeSlots: {
          types: [
            "array"
          ],
          description: "Time slot labels (defaults to 09:00-17:00)",
          items: {
            types: [
              "string"
            ]
          }
        },
        events: {
          types: [
            "object",
            "array"
          ],
          description: "Events to display on the grid",
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "title",
            "startTime"
          ],
          properties: {
            title: {
              types: [
                "string"
              ]
            },
            startTime: {
              types: [
                "string"
              ]
            },
            endTime: {
              types: [
                "string"
              ]
            },
            color: {
              types: [
                "string"
              ]
            }
          },
          default: []
        },
        onSlotClick: {
          types: [
            "function"
          ],
          description: "Called when a time slot is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "day",
              type: "date"
            },
            {
              name: "time",
              type: "string"
            }
          ]
        },
        onDayClick: {
          types: [
            "function"
          ],
          description: "Called when a day header is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "day",
              type: "date"
            }
          ]
        },
        onEventClick: {
          types: [
            "function"
          ],
          description: "Called when an event is clicked",
          kind: "callback",
          entityRowField: "event",
          callbackArgs: [
            {
              name: "event",
              type: "object"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        longPressEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on long-press of a time slot: UI:{longPressEvent} with { date, time, ...longPressPayload }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "date",
              type: "string",
              required: true
            },
            {
              name: "time",
              type: "string",
              required: false
            }
          ]
        },
        longPressPayload: {
          types: [
            "object"
          ],
          description: "Additional payload for long-press events",
          payloadFor: "longPressEvent",
          eventPayloadBrand: true
        },
        swipeLeftEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on swipe left (next week): UI:{swipeLeftEvent}",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        swipeRightEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on swipe right (prev week): UI:{swipeRightEvent}",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        dayWindow: {
          types: [
            "number",
            "string"
          ],
          description: "Override the viewport-driven day window. `'auto'` (default) tracks `window.innerWidth` \u2014 1 day on mobile, 3 on tablet, 7 on laptop+. Pass an explicit number to force a fixed window (useful for print layouts or screenshot tests).",
          enumValues: [
            "auto"
          ],
          default: "auto"
        },
        titleField: {
          types: [
            "string"
          ],
          description: "Row field holding the chip label. Defaults to `title`. Lets a host bound to its own entity name the column instead of being forced to rename its fields to this grid's contract (same accessor idiom as `DataList`'s `senderField` / `DataGrid`'s `imageField`).",
          default: "title"
        },
        startField: {
          types: [
            "string"
          ],
          description: "Row field holding the start timestamp (ISO or epoch). Defaults to `startTime`.",
          default: "startTime"
        },
        endField: {
          types: [
            "string"
          ],
          description: "Row field holding the end timestamp (ISO or epoch). Defaults to `endTime`. When a row carries an end, the event's chip renders in its start slot and every further slot it covers shows a subdued continuation bar \u2014 without this, multi-hour events silently collapsed to their first hour.",
          default: "endTime"
        },
        colorField: {
          types: [
            "string"
          ],
          description: "Row field holding the chip's Tailwind colour class. Defaults to `color`.",
          default: "color"
        },
        children: {
          types: [
            "function"
          ],
          description: "Render function for one event chip's content. Receives the row and its index in the materialised events array. The grid keeps ownership of placement, colour and the click target; this replaces only what is drawn INSIDE the chip, so a host can show a time + instructor + badge stack instead of the default single label.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-event render function (schema-level alias for the children render prop). In `.orb`: `["fn", "item", { pattern tree with @item.field bindings }]`. In `.lolo`: `renderItem: (fn item <Component \u2026={@item.field}/>)` \u2014 the same contract `data-list` / `data-grid` / `carousel` already use.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "chart-legend": {
      type: "chart-legend",
      category: "display",
      sourceFile: "components/core/molecules/ChartLegend.tsx",
      tier: "molecules",
      family: "core",
      description: "ChartLegend component",
      suggestedFor: [
        "chart",
        "legend",
        "chart legend"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Legend items with label and color",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "color"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        direction: {
          types: [
            "string"
          ],
          description: "Layout direction",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "date-range-selector": {
      type: "date-range-selector",
      category: "form",
      sourceFile: "components/core/molecules/DateRangeSelector.tsx",
      tier: "molecules",
      family: "core",
      description: "DateRangeSelector component",
      suggestedFor: [
        "date",
        "range",
        "selector",
        "date range selector"
      ],
      typicalSize: "medium",
      propsSchema: {
        options: {
          types: [
            "array"
          ],
          description: "Available range options",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        selected: {
          types: [
            "string"
          ],
          description: "Currently selected value",
          default: "month"
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Callback when a range is selected",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "flip-card": {
      type: "flip-card",
      category: "display",
      sourceFile: "components/core/molecules/FlipCard.tsx",
      tier: "molecules",
      family: "core",
      description: "FlipCard \u2014 flip card that reveals a hidden back face on tap or click, toggling between a front and back content slot.",
      capabilities: "flashcard, study deck, spaced-repetition review card, memorization drill, quiz reveal card, question/answer card, before/after reveal, term-and-definition card",
      suggestedFor: [
        "flip",
        "card",
        "flip card"
      ],
      typicalSize: "medium",
      propsSchema: {
        front: {
          types: [
            "node"
          ],
          description: "Content rendered on the front face",
          required: true
        },
        back: {
          types: [
            "node"
          ],
          description: "Content rendered on the back face",
          required: true
        },
        flipped: {
          types: [
            "boolean"
          ],
          description: "Controlled flip state",
          default: false
        },
        onFlip: {
          types: [
            "function"
          ],
          description: "Callback when the card is clicked to flip",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "Optional className for the outer container"
        },
        height: {
          types: [
            "string"
          ],
          description: "Card height as a Tailwind class (default: 'h-64')",
          default: "h-64"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "graph-view": {
      type: "graph-view",
      category: "component",
      sourceFile: "components/core/molecules/GraphView.tsx",
      tier: "molecules",
      family: "core",
      description: "GraphView component",
      suggestedFor: [
        "graph",
        "view",
        "graph view"
      ],
      typicalSize: "medium",
      propsSchema: {
        nodes: {
          types: [
            "array"
          ],
          description: "Graph nodes",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              group: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        edges: {
          types: [
            "array"
          ],
          description: "Graph edges",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "source",
              "target"
            ]
          }
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "Callback when a node is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onNodeHover: {
          types: [
            "function"
          ],
          description: "Callback when a node is hovered (null on leave)",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        width: {
          types: [
            "number"
          ],
          description: "Width in pixels"
        },
        height: {
          types: [
            "number"
          ],
          description: "Height in pixels"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        showLabels: {
          types: [
            "boolean"
          ],
          description: "Show node labels (default true)",
          default: true
        },
        zoomToFit: {
          types: [
            "boolean"
          ],
          description: "Auto zoom-to-fit after layout settles (default true)",
          default: true
        },
        layout: {
          types: [
            "string"
          ],
          description: "Layout mode. force = physics simulation (default); flow = layered left-to-right process flow; tree = top-down hierarchy tiers from the roots; radial = concentric rings by depth (a pure cycle renders as a single ring). Non-force layouts are deterministic.",
          enumValues: [
            "force",
            "flow",
            "tree",
            "radial"
          ],
          default: "force",
          synonyms: "layout mode, flow layout, tree layout, radial layout, hierarchy layout",
          tier: "domain"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "line-chart": {
      type: "line-chart",
      category: "display",
      sourceFile: "components/core/molecules/LineChart.tsx",
      tier: "molecules",
      family: "core",
      description: "LineChart component",
      suggestedFor: [
        "line",
        "chart",
        "line chart"
      ],
      typicalSize: "medium",
      propsSchema: {
        data: {
          types: [
            "array"
          ],
          description: "Data points to plot",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              date: {
                types: [
                  "string",
                  "date"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value"
            ]
          }
        },
        width: {
          types: [
            "number"
          ],
          description: "SVG viewBox width",
          default: 400
        },
        height: {
          types: [
            "number"
          ],
          description: "SVG viewBox height",
          default: 200
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "Show horizontal grid lines at 25/50/75%",
          default: true
        },
        showValues: {
          types: [
            "boolean"
          ],
          description: "Show value labels near data points",
          default: false
        },
        showArea: {
          types: [
            "boolean"
          ],
          description: "Fill area under line with gradient",
          default: true
        },
        lineColor: {
          types: [
            "string"
          ],
          description: "Line stroke color",
          default: "var(--color-primary)"
        },
        areaColor: {
          types: [
            "string"
          ],
          description: "Area fill color (used for gradient)",
          default: "var(--color-primary)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "progress-dots": {
      type: "progress-dots",
      category: "component",
      sourceFile: "components/core/molecules/ProgressDots.tsx",
      tier: "molecules",
      family: "core",
      description: "ProgressDots component",
      suggestedFor: [
        "progress",
        "dots",
        "progress dots"
      ],
      typicalSize: "medium",
      propsSchema: {
        count: {
          types: [
            "number"
          ],
          description: "Total number of dots",
          required: true
        },
        currentIndex: {
          types: [
            "number"
          ],
          description: "Current active index (0-based)",
          required: true
        },
        getState: {
          types: [
            "function"
          ],
          description: "Custom state resolver per dot index",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        onDotClick: {
          types: [
            "function"
          ],
          description: "Callback when a dot is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        size: {
          types: [
            "string"
          ],
          description: "Dot size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "data-grid": {
      type: "data-grid",
      category: "display",
      sourceFile: "components/core/molecules/DataGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "DataGrid \u2014 structured records grid rendering rows over configurable columns, with sort, select, and drag-reorder.",
      capabilities: "admin table, records grid, user list, CRUD list, manage-records view, spreadsheet-style data grid, sortable columns",
      fieldsContract: "display",
      minUsableWidth: 200,
      suggestedFor: [
        "data",
        "grid",
        "data grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        dragGroup: {
          types: [
            "string"
          ],
          description: "When set, items in this container are draggable and announce this group as their source"
        },
        accepts: {
          types: [
            "string"
          ],
          description: "When set, the container accepts drops whose dragGroup matches this value (or '*' for any group)"
        },
        sortable: {
          types: [
            "boolean"
          ],
          description: "Enable in-container reorder; items can be dragged to new positions among siblings"
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on cross-container drop. Payload: { id, sourceGroup, targetGroup, newIndex }"
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on in-container reorder. Payload: { id, oldIndex, newIndex }"
        },
        positionEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired N times per drop (one per item in affected zones) so the trait can persist new positions for the WHOLE zone, not just the moved item. Payload: { id, position }"
        },
        dndItemIdField: {
          types: [
            "string"
          ],
          description: "Row field used as the @dnd-kit draggable id (default 'id')"
        },
        dndRoot: {
          types: [
            "boolean"
          ],
          description: "Mark this container as the DnD root for its subtree \u2014 descendant zones share one DndContext (cross-container drag). Use on an outer container whose own items are not draggable."
        },
        emptyIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Domain icon for the empty state."
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: 'Specific title ("No orders yet").'
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "What will appear here once rows exist."
        },
        emptyAction: {
          types: [
            "object"
          ],
          description: "Create-the-first-item action.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: 'Schema entity data \u2014 the collection of rows to render. pattern-sync tags it `kind:"entity", cardinality:"collection"` so consumers bind the domain entity without name-matching the prop.',
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        fields: {
          types: [
            "array"
          ],
          description: "Field definitions for rendering each card. The pattern contract in `@almadar/core/patterns` documents `columns` as the wire-format alias the compiler emits \u2014 both names resolve to the same shape here. Pass either.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        columns: {
          types: [
            "array"
          ],
          description: "Alias for `fields` \u2014 the compiler emits `columns` for field defs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Per-item action buttons",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        itemClickEvent: {
          types: [
            "string"
          ],
          description: "When set, clicking a card emits UI:{itemClickEvent} with { id, row }. Omit = cards are not clickable.",
          kind: "event-ref",
          entityRowField: "row",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "row",
              type: "@entity",
              required: true
            }
          ]
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: 'Max inline primary action buttons before the rest collapse into a "\u22EF" overflow menu. Omit = all inline.'
        },
        scrollX: {
          types: [
            "boolean"
          ],
          description: "Lay items in a single horizontally-scrolling row (kanban columns) sized to `minCardWidth` instead of a wrapping grid.",
          default: false
        },
        cols: {
          types: [
            "number"
          ],
          description: "Number of columns (uses auto-fit if omitted)",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between cards",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        minCardWidth: {
          types: [
            "number"
          ],
          description: "Minimum card width in pixels (used when cols is not set, default 280)",
          default: 280
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "grid",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        imageField: {
          types: [
            "string"
          ],
          description: "Entity field name containing an image URL for card thumbnails"
        },
        selectable: {
          types: [
            "boolean"
          ],
          description: "Enable multi-select with checkboxes",
          default: false
        },
        selectionEvent: {
          types: [
            "string"
          ],
          description: "Selection change event name (emits UI:{selectionEvent} with { selectedIds: string[] })",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedIds",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        infiniteScroll: {
          types: [
            "boolean"
          ],
          description: "Enable infinite scroll loading"
        },
        loadMoreEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when more items needed: UI:{loadMoreEvent}",
          kind: "event"
        },
        hasMore: {
          types: [
            "boolean"
          ],
          description: "Whether more items are available for infinite scroll"
        },
        children: {
          types: [
            "function"
          ],
          description: "Render prop for custom per-card content. When provided, `fields` are ignored; `itemActions` render as a footer beneath the custom content.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-item render function (schema-level alias for children render prop). In .orb schemas: ["fn", "item", { pattern tree with @item.field bindings }] The compiler converts this to the children render prop. In .lolo, author the per-item renderer as renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        itemEnter: {
          types: [
            "string"
          ],
          description: "Entrance animation each card plays when it mounts; a card that changes place glides there. Lists rendered inside the grid share its movement, so a card moved between them glides across",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ]
        },
        pageSize: {
          types: [
            "number"
          ],
          description: 'Max items to show before "Show More" button. Defaults to 0 (disabled).',
          default: 0
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 mirrors the `entity-table` look enum so data-grid / data-list / entity-table share one knob name from authors.",
          enumValues: [
            "dense",
            "spacious",
            "striped",
            "borderless",
            "card-rows"
          ],
          default: "dense"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names for a field whose type is relation. Same contract DetailPanel takes.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "data-list": {
      type: "data-list",
      category: "display",
      sourceFile: "components/core/molecules/DataList.tsx",
      tier: "molecules",
      family: "core",
      description: "DataList component",
      fieldsContract: "display",
      minUsableWidth: 200,
      suggestedFor: [
        "data",
        "list",
        "data list"
      ],
      typicalSize: "medium",
      propsSchema: {
        dragGroup: {
          types: [
            "string"
          ],
          description: "When set, items in this container are draggable and announce this group as their source"
        },
        accepts: {
          types: [
            "string"
          ],
          description: "When set, the container accepts drops whose dragGroup matches this value (or '*' for any group)"
        },
        sortable: {
          types: [
            "boolean"
          ],
          description: "Enable in-container reorder; items can be dragged to new positions among siblings"
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on cross-container drop. Payload: { id, sourceGroup, targetGroup, newIndex }"
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on reorder: UI:{reorderEvent} with { fromIndex, toIndex }",
          kind: "event"
        },
        positionEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired N times per drop (one per item in affected zones) so the trait can persist new positions for the WHOLE zone, not just the moved item. Payload: { id, position }"
        },
        dndItemIdField: {
          types: [
            "string"
          ],
          description: "Row field used as the @dnd-kit draggable id (default 'id')"
        },
        dndRoot: {
          types: [
            "boolean"
          ],
          description: "Mark this container as the DnD root for its subtree \u2014 descendant zones share one DndContext (cross-container drag). Use on an outer container whose own items are not draggable."
        },
        emptyIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Domain icon for the empty state."
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: 'Specific title ("No orders yet").'
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "What will appear here once rows exist."
        },
        emptyAction: {
          types: [
            "object"
          ],
          description: "Create-the-first-item action.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema entity data \u2014 the collection of rows to render.",
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        fields: {
          types: [
            "array"
          ],
          description: "Field definitions for rendering each row. The pattern contract in `@almadar/core/patterns` documents `columns` as the wire-format alias the compiler emits \u2014 both names resolve to the same shape here. Pass either.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        columns: {
          types: [
            "array"
          ],
          description: "Alias for `fields` \u2014 the compiler emits `columns` for field defs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Per-item action buttons",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        itemEnter: {
          types: [
            "string"
          ],
          description: "Entrance animation each row plays when it mounts, so an item arriving in this list (moved from another) animates in",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ]
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: 'Max inline action buttons before the rest collapse into a "\u22EF" overflow menu. Omit = all inline.'
        },
        itemClickEvent: {
          types: [
            "string"
          ],
          description: "When set, the whole row is clickable and emits UI:{itemClickEvent} with { id, row } (action-button clicks stopPropagation so they still win).",
          kind: "event-ref",
          entityRowField: "row",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "row",
              type: "@entity",
              required: true
            }
          ]
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between rows",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg"
          ],
          default: "none"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          enumValues: [
            "default",
            "card",
            "compact",
            "message"
          ],
          default: "default"
        },
        groupBy: {
          types: [
            "string"
          ],
          description: "Group items by a field value (renders section headers between groups)"
        },
        groupFormat: {
          types: [
            "string"
          ],
          description: "How a `groupBy` value is bucketed and labelled (`date` = one section per day)",
          enumValues: [
            "date",
            "time",
            "datetime",
            "currency",
            "number",
            "boolean",
            "percent"
          ]
        },
        senderField: {
          types: [
            "string"
          ],
          description: 'Field name identifying the sender (used with variant: "message")'
        },
        senderLabelField: {
          types: [
            "string"
          ],
          description: 'Field holding the sender\'s human-readable name (used with variant: "message"). Defaults to `senderField`. Set this when `senderField` holds an id \u2014 an id-keyed thread otherwise labels bubbles `staff-1` instead of `Sam Staff`.'
        },
        currentUser: {
          types: [
            "string"
          ],
          description: 'Current user identifier; messages matching this value align right (used with variant: "message")'
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: 'Message shown when there are no rows. Falls back to the generic "no items" translation. `TableView` already exposes this; an agenda or an inbox wants to say what "empty" means in its own words.'
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "list",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        reorderable: {
          types: [
            "boolean"
          ],
          description: "Enable drag-to-reorder with grip handles"
        },
        swipeLeftEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on left swipe: UI:{swipeLeftEvent} with { row }",
          kind: "event"
        },
        swipeLeftActions: {
          types: [
            "array"
          ],
          description: "Actions revealed on left swipe",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        swipeRightEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on right swipe: UI:{swipeRightEvent} with { row }",
          kind: "event"
        },
        swipeRightActions: {
          types: [
            "array"
          ],
          description: "Actions revealed on right swipe",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        longPressEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on long press: UI:{longPressEvent} with { row }",
          kind: "event"
        },
        infiniteScroll: {
          types: [
            "boolean"
          ],
          description: "Enable infinite scroll loading"
        },
        loadMoreEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when more items needed: UI:{loadMoreEvent}",
          kind: "event"
        },
        hasMore: {
          types: [
            "boolean"
          ],
          description: "Whether more items are available for infinite scroll"
        },
        children: {
          types: [
            "function"
          ],
          description: "Render prop for custom per-item content. When provided, `fields` and `itemActions` are ignored.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-item render function (schema-level alias for children render prop). In .orb schemas: ["fn", "item", { pattern tree with @item.field bindings }] The compiler converts this to the children render prop. In .lolo, author the per-item renderer as renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        pageSize: {
          types: [
            "number"
          ],
          description: 'Max items to show before "Show More" button. Defaults to 5. Set to 0 to disable.',
          default: 5
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Field name to order rows by. Values are compared by their own type \u2014 numbers numerically, date-like strings chronologically, everything else alphabetically \u2014 never by what the field is called. Empty cells sort last. Unset leaves the incoming order untouched. Orders only the rows already fetched: a collection larger than its `limit` still needs ordering at the data layer."
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Direction for `sortBy`. Defaults to ascending.",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 mirrors the `entity-table` look enum so data-grid / data-list / entity-table share one knob name from authors.",
          enumValues: [
            "dense",
            "spacious",
            "striped",
            "borderless",
            "card-rows"
          ],
          default: "dense"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names for a field whose type is relation. Same contract DetailPanel takes.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "map-view": {
      type: "map-view",
      category: "component",
      sourceFile: "components/core/molecules/MapView.tsx",
      tier: "molecules",
      family: "core",
      description: "MapView component",
      suggestedFor: [
        "map",
        "view",
        "map view"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        markers: {
          types: [
            "array"
          ],
          description: "Array of markers to display",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string",
                  "number"
                ]
              },
              lat: {
                types: [
                  "number"
                ]
              },
              lng: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "lat",
              "lng"
            ]
          },
          default: []
        },
        routes: {
          types: [
            "array"
          ],
          description: "Routes (polylines with optional popups) drawn over the tile layer",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string",
                  "number"
                ]
              },
              waypoints: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    lat: {
                      types: [
                        "number"
                      ]
                    },
                    lng: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "lat",
                    "lng"
                  ]
                }
              },
              color: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              dashArray: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "waypoints"
            ]
          },
          default: []
        },
        centerLat: {
          types: [
            "number"
          ],
          description: "Map center latitude",
          default: 51.505
        },
        centerLng: {
          types: [
            "number"
          ],
          description: "Map center longitude",
          default: -0.09
        },
        zoom: {
          types: [
            "number"
          ],
          description: "Zoom level (1-18, default 13)",
          default: 13
        },
        height: {
          types: [
            "string"
          ],
          description: 'Height of the map container (default "400px")',
          default: "400px"
        },
        onMarkerClick: {
          types: [
            "function"
          ],
          description: "Callback when a marker is clicked (programmatic use)",
          kind: "callback",
          callbackArgs: [
            {
              name: "marker",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string",
                      "number"
                    ]
                  },
                  lat: {
                    types: [
                      "number"
                    ]
                  },
                  lng: {
                    types: [
                      "number"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id",
                  "lat",
                  "lng"
                ]
              }
            }
          ]
        },
        onMapClick: {
          types: [
            "function"
          ],
          description: "Callback when the map is clicked (programmatic use)",
          kind: "callback",
          callbackArgs: [
            {
              name: "lat",
              type: "number"
            },
            {
              name: "lng",
              type: "number"
            }
          ]
        },
        mapClickEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the map is clicked. Payload: { latitude, longitude }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "latitude",
              type: "number",
              required: true
            },
            {
              name: "longitude",
              type: "number",
              required: true
            }
          ]
        },
        markerClickEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when a marker is clicked. Payload: full MapMarkerData spread.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "lat",
              type: "number",
              required: true
            },
            {
              name: "lng",
              type: "number",
              required: true
            },
            {
              name: "label",
              type: "string",
              required: false
            },
            {
              name: "category",
              type: "string",
              required: false
            }
          ]
        },
        showClickedPin: {
          types: [
            "boolean"
          ],
          description: "Whether to show a pin at the clicked location",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        showAttribution: {
          types: [
            "boolean"
          ],
          description: "Show attribution (default true)",
          default: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "range-slider": {
      type: "range-slider",
      category: "component",
      sourceFile: "components/core/atoms/RangeSlider.tsx",
      tier: "atoms",
      family: "core",
      description: "RangeSlider component",
      suggestedFor: [
        "range",
        "slider",
        "range slider"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        min: {
          types: [
            "number"
          ],
          description: "Minimum value",
          default: 0
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value",
          default: 100
        },
        value: {
          types: [
            "number"
          ],
          description: "Current value",
          default: 0
        },
        step: {
          types: [
            "number"
          ],
          description: "Step increment",
          default: 1
        },
        showTooltip: {
          types: [
            "boolean"
          ],
          description: "Show tooltip with current value on drag",
          default: false
        },
        showTicks: {
          types: [
            "boolean"
          ],
          description: "Show tick marks at step intervals",
          default: false
        },
        buffered: {
          types: [
            "number"
          ],
          description: "Buffered range (0-100), for media seek bars"
        },
        size: {
          types: [
            "string"
          ],
          description: "Slider size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for value changes",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct onChange callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        formatValue: {
          types: [
            "function"
          ],
          description: "Format function for tooltip display",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ],
          renderCallback: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "status-dot": {
      type: "status-dot",
      category: "display",
      sourceFile: "components/core/atoms/StatusDot.tsx",
      tier: "atoms",
      family: "core",
      description: "StatusDot component",
      suggestedFor: [
        "status",
        "dot",
        "status dot"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        status: {
          types: [
            "string"
          ],
          description: "Status determines the dot color",
          enumValues: [
            "online",
            "offline",
            "away",
            "busy",
            "warning",
            "critical"
          ],
          default: "offline"
        },
        pulse: {
          types: [
            "boolean"
          ],
          description: "Enable pulse animation for active/critical states",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Dot size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label (rendered as sr-only text)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "trend-indicator": {
      type: "trend-indicator",
      category: "component",
      sourceFile: "components/core/atoms/TrendIndicator.tsx",
      tier: "atoms",
      family: "core",
      description: "TrendIndicator component",
      suggestedFor: [
        "trend",
        "indicator",
        "trend indicator"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        value: {
          types: [
            "number"
          ],
          description: "Numeric value to display (e.g., 12.5 for +12.5%)"
        },
        direction: {
          types: [
            "string"
          ],
          description: "Override automatic direction detection (positive=up, negative=down, zero=flat)",
          enumValues: [
            "up",
            "down",
            "flat"
          ]
        },
        showValue: {
          types: [
            "boolean"
          ],
          description: "Show the formatted value text next to the arrow",
          default: true
        },
        invert: {
          types: [
            "boolean"
          ],
          description: "Invert color logic (for metrics where down is good, e.g., error rate, bounce rate)",
          default: false
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label override"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the indicator",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    lightbox: {
      type: "lightbox",
      category: "component",
      sourceFile: "components/core/molecules/Lightbox.tsx",
      tier: "molecules",
      family: "core",
      description: "Lightbox component",
      suggestedFor: [
        "lightbox"
      ],
      typicalSize: "medium",
      propsSchema: {
        images: {
          types: [
            "array"
          ],
          description: "Array of images to display",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              src: {
                types: [
                  "asset"
                ]
              },
              alt: {
                types: [
                  "string"
                ]
              },
              caption: {
                types: [
                  "string"
                ]
              }
            }
          },
          default: []
        },
        currentIndex: {
          types: [
            "number"
          ],
          description: "Current image index",
          default: 0
        },
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the lightbox is open",
          default: false
        },
        showCounter: {
          types: [
            "boolean"
          ],
          description: 'Show image counter (e.g., "3 of 12")',
          default: true
        },
        closeAction: {
          types: [
            "string"
          ],
          description: "Declarative close event name"
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Direct onClose callback",
          kind: "callback",
          callbackArgs: []
        },
        onIndexChange: {
          types: [
            "function"
          ],
          description: "Direct onIndexChange callback",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "number-stepper": {
      type: "number-stepper",
      category: "component",
      sourceFile: "components/core/molecules/NumberStepper.tsx",
      tier: "molecules",
      family: "core",
      description: "NumberStepper component",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "number",
        "stepper",
        "number stepper"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Current value",
          default: 0
        },
        min: {
          types: [
            "number"
          ],
          description: "Minimum value"
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value"
        },
        step: {
          types: [
            "number"
          ],
          description: "Step increment",
          default: 1
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct onChange callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for value changes",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "star-rating": {
      type: "star-rating",
      category: "component",
      sourceFile: "components/core/molecules/StarRating.tsx",
      tier: "molecules",
      family: "core",
      description: "StarRating component",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "star",
        "rating",
        "star rating"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Current rating value",
          default: 0
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum number of stars",
          default: 5
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "Read-only display mode",
          default: false
        },
        precision: {
          types: [
            "string"
          ],
          description: "Full or half-star precision",
          enumValues: [
            "full",
            "half"
          ],
          default: "full"
        },
        size: {
          types: [
            "string"
          ],
          description: "Star size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for rating changes",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct onChange callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "upload-drop-zone": {
      type: "upload-drop-zone",
      category: "component",
      sourceFile: "components/core/molecules/UploadDropZone.tsx",
      tier: "molecules",
      family: "core",
      description: "UploadDropZone component",
      suggestedFor: [
        "upload",
        "drop",
        "zone",
        "upload drop zone"
      ],
      typicalSize: "medium",
      propsSchema: {
        accept: {
          types: [
            "string"
          ],
          description: 'Accepted MIME types (e.g., "image/*", "application/pdf")'
        },
        maxSize: {
          types: [
            "number"
          ],
          description: "Max file size in bytes"
        },
        maxFiles: {
          types: [
            "number"
          ],
          description: "Max number of files",
          default: 1
        },
        label: {
          types: [
            "string"
          ],
          description: "Label text"
        },
        description: {
          types: [
            "string"
          ],
          description: "Description text below label"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon name or component"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        readAs: {
          types: [
            "string"
          ],
          description: "How the file is read into `content`: a base64 data URL (default) or its UTF-8 text",
          enumValues: [
            "dataUrl",
            "text"
          ],
          default: "dataUrl"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for file selection",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "files",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    size: {
                      types: [
                        "number"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    content: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "name",
                    "size",
                    "type",
                    "content"
                  ]
                }
              }
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        onFiles: {
          types: [
            "function"
          ],
          description: "Direct onFiles callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "files",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ]
                }
              }
            }
          ],
          nonEmittable: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "animated-counter": {
      type: "animated-counter",
      category: "component",
      sourceFile: "components/core/molecules/AnimatedCounter.tsx",
      tier: "molecules",
      family: "core",
      description: "AnimatedCounter component",
      suggestedFor: [
        "animated",
        "counter",
        "animated counter"
      ],
      typicalSize: "small",
      propsSchema: {
        value: {
          types: [
            "string",
            "number"
          ],
          description: 'Target value. Strings may carry display affixes (e.g. "500+", "99.9%", "$1.2M").',
          required: true
        },
        label: {
          types: [
            "string"
          ],
          description: "Label displayed below the number"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Animation duration in ms",
          default: 1500
        },
        prefix: {
          types: [
            "string"
          ],
          description: "Text displayed before the number"
        },
        suffix: {
          types: [
            "string"
          ],
          description: "Text displayed after the number"
        },
        format: {
          types: [
            "string"
          ],
          description: `"number" (locale grouping), "currency" (locale currency), "percent" (rounded %). Unset keeps the value's own decimals.`
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "confetti-effect": {
      type: "confetti-effect",
      category: "component",
      sourceFile: "components/core/atoms/ConfettiEffect.tsx",
      tier: "atoms",
      family: "core",
      description: "ConfettiEffect component",
      suggestedFor: [
        "confetti",
        "effect",
        "confetti effect"
      ],
      typicalSize: "small",
      propsSchema: {
        trigger: {
          types: [
            "boolean"
          ],
          description: "When this changes from false to true, a burst of particles is spawned",
          required: true
        },
        duration: {
          types: [
            "number"
          ],
          description: "How long the animation lasts in milliseconds",
          default: 2e3
        },
        particleCount: {
          types: [
            "number"
          ],
          description: "Number of particles to spawn",
          default: 30
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "infinite-scroll-sentinel": {
      type: "infinite-scroll-sentinel",
      category: "component",
      sourceFile: "components/core/atoms/InfiniteScrollSentinel.tsx",
      tier: "atoms",
      family: "core",
      description: "InfiniteScrollSentinel component",
      suggestedFor: [
        "infinite",
        "scroll",
        "sentinel",
        "infinite scroll sentinel"
      ],
      typicalSize: "small",
      propsSchema: {
        loadMoreEvent: {
          types: [
            "string"
          ],
          description: "Event name to emit when load-more is triggered (emitted as UI:{loadMoreEvent})",
          required: true,
          kind: "event"
        },
        loadMorePayload: {
          types: [
            "object"
          ],
          description: "Optional payload to include with the load-more event",
          payloadFor: "loadMoreEvent",
          eventPayloadBrand: true
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Whether a load operation is currently in progress",
          default: false
        },
        hasMore: {
          types: [
            "boolean"
          ],
          description: "Whether there are more items to load",
          default: true
        },
        threshold: {
          types: [
            "string"
          ],
          description: "IntersectionObserver rootMargin for early trigger",
          default: "200px"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "typewriter-text": {
      type: "typewriter-text",
      category: "component",
      sourceFile: "components/core/atoms/TypewriterText.tsx",
      tier: "atoms",
      family: "core",
      description: "TypewriterText component",
      suggestedFor: [
        "typewriter",
        "text",
        "typewriter text"
      ],
      typicalSize: "small",
      propsSchema: {
        text: {
          types: [
            "string"
          ],
          description: "The full text to reveal",
          required: true
        },
        speed: {
          types: [
            "number"
          ],
          description: "Milliseconds per character",
          default: 40
        },
        startDelay: {
          types: [
            "number"
          ],
          description: "Delay before typing starts (ms)",
          default: 0
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        onComplete: {
          types: [
            "function"
          ],
          description: "Called when the entire text has been revealed",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    carousel: {
      type: "carousel",
      category: "component",
      sourceFile: "components/core/molecules/Carousel.tsx",
      tier: "molecules",
      family: "core",
      description: "Carousel component",
      suggestedFor: [
        "carousel"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Array of items to display as slides",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              image: {
                types: [
                  "asset"
                ]
              }
            }
          }
        },
        renderItem: {
          types: [
            "function"
          ],
          description: "Render function for each slide. In .lolo: renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  image: {
                    types: [
                      "asset"
                    ]
                  }
                }
              }
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        children: {
          types: [
            "function"
          ],
          description: "Children-as-function fallback for renderItem (compiler compatibility)",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  image: {
                    types: [
                      "asset"
                    ]
                  }
                }
              }
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        autoPlay: {
          types: [
            "boolean"
          ],
          description: "Enable auto-play rotation",
          default: false
        },
        autoPlayInterval: {
          types: [
            "number"
          ],
          description: "Auto-play interval in milliseconds (default: 5000)",
          default: 5e3
        },
        showDots: {
          types: [
            "boolean"
          ],
          description: "Show dot indicators",
          default: true
        },
        showArrows: {
          types: [
            "boolean"
          ],
          description: "Show prev/next arrow buttons",
          default: true
        },
        loop: {
          types: [
            "boolean"
          ],
          description: "Enable infinite loop",
          default: false
        },
        slideChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name for slide change",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            }
          ]
        },
        slideChangePayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the slide change event",
          payloadFor: "slideChangeEvent",
          mapValue: {
            types: [
              "object"
            ],
            jsonValueBrand: true
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "pull-to-refresh": {
      type: "pull-to-refresh",
      category: "component",
      sourceFile: "components/core/molecules/PullToRefresh.tsx",
      tier: "molecules",
      family: "core",
      description: "PullToRefresh component",
      suggestedFor: [
        "pull",
        "refresh",
        "pull to refresh"
      ],
      typicalSize: "medium",
      propsSchema: {
        refreshEvent: {
          types: [
            "string"
          ],
          description: "Event name to emit on refresh (emitted as UI:{refreshEvent})",
          required: true,
          kind: "event"
        },
        refreshPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the refresh event",
          payloadFor: "refreshEvent",
          eventPayloadBrand: true
        },
        threshold: {
          types: [
            "number"
          ],
          description: "Pull distance threshold to trigger refresh in px (default: 60)",
          default: 60
        },
        children: {
          types: [
            "node"
          ],
          description: "Content to wrap",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "sortable-list": {
      type: "sortable-list",
      category: "display",
      sourceFile: "components/core/molecules/SortableList.tsx",
      tier: "molecules",
      family: "core",
      description: "SortableList \u2014 a drag-and-drop reorderable list of items.",
      capabilities: "checklist, task list, to-do list, priority list, ranked list, drag-to-reorder queue",
      suggestedFor: [
        "sortable",
        "list",
        "sortable list"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "object",
            "array"
          ],
          description: "items prop",
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        renderItem: {
          types: [
            "function"
          ],
          description: "Render function for each item. In .lolo: renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.",
          required: true,
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "reorderEvent prop",
          required: true,
          kind: "event-ref",
          entityRowField: "item",
          emitPayloadSchema: [
            {
              name: "fromIndex",
              type: "number",
              required: true
            },
            {
              name: "toIndex",
              type: "number",
              required: true
            },
            {
              name: "item",
              type: "@entity",
              required: true
            }
          ]
        },
        reorderPayload: {
          types: [
            "object"
          ],
          description: "reorderPayload prop",
          payloadFor: "reorderEvent",
          eventPayloadBrand: true
        },
        dragHandlePosition: {
          types: [
            "string"
          ],
          description: "dragHandlePosition prop",
          enumValues: [
            "left",
            "right"
          ],
          default: "left"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "swipeable-row": {
      type: "swipeable-row",
      category: "component",
      sourceFile: "components/core/molecules/SwipeableRow.tsx",
      tier: "molecules",
      family: "core",
      description: "SwipeableRow component",
      suggestedFor: [
        "swipeable",
        "row",
        "swipeable row"
      ],
      typicalSize: "medium",
      propsSchema: {
        leftActions: {
          types: [
            "array"
          ],
          description: "leftActions prop",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              eventPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "event"
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        rightActions: {
          types: [
            "array"
          ],
          description: "rightActions prop",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              eventPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "event"
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        threshold: {
          types: [
            "number"
          ],
          description: "threshold prop",
          default: 80
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        itemData: {
          types: [
            "object"
          ],
          description: "itemData prop",
          kind: "entity",
          cardinality: "record"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "stat-display": {
      type: "stat-display",
      category: "display",
      sourceFile: "components/core/molecules/StatDisplay.tsx",
      tier: "molecules",
      family: "core",
      description: "StatDisplay component",
      suggestedFor: [
        "stat",
        "display",
        "stat display"
      ],
      typicalSize: "medium",
      propsSchema: {
        label: {
          types: [
            "string"
          ],
          description: 'Display label (e.g., "Total", "Remaining")',
          required: true
        },
        value: {
          types: [
            "number",
            "string"
          ],
          description: "Primary value (number or formatted string)",
          required: true
        },
        max: {
          types: [
            "number"
          ],
          description: 'Optional denominator. >0 renders "value / max"; 0 or omitted hides the divider.'
        },
        target: {
          types: [
            "number"
          ],
          description: "Optional progress target. >0 renders a progress bar at value/target."
        },
        trend: {
          types: [
            "number"
          ],
          description: "Signed delta vs previous period. >0 renders \u2191 green, <0 renders \u2193 red, 0 hides. Polarity reversal via `trendPolarity`."
        },
        trendPolarity: {
          types: [
            "string"
          ],
          description: 'Controls whether `trend > 0` is "good" (default) or "bad". `lower-is-better` inverts the color logic.',
          enumValues: [
            "higher-is-better",
            "lower-is-better"
          ],
          default: "higher-is-better"
        },
        trendFormat: {
          types: [
            "string"
          ],
          description: "When `'percent'`, renders trend with a `%` suffix (e.g. `\u2193 3.3%`); default is `'absolute'`.",
          enumValues: [
            "absolute",
            "percent"
          ],
          default: "absolute"
        },
        sparklineData: {
          types: [
            "array"
          ],
          description: "Optional inline sparkline data appended to the trend row. Length < 2 renders nothing.",
          items: {
            types: [
              "number"
            ]
          }
        },
        clickEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted as `UI:{clickEvent}` with `{ metricLabel }` when the card is clicked."
        },
        prefix: {
          types: [
            "string"
          ],
          description: 'Prefix prepended to the formatted value (e.g. "\u2248 ").'
        },
        suffix: {
          types: [
            "string"
          ],
          description: 'Suffix appended to the formatted value (e.g. " /mo", " ms").'
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon name or component"
        },
        iconBg: {
          types: [
            "string"
          ],
          description: "Icon background color class",
          default: "bg-muted"
        },
        iconColor: {
          types: [
            "string"
          ],
          description: "Icon color class",
          default: "text-foreground"
        },
        format: {
          types: [
            "string"
          ],
          description: 'Display format: "number", "currency", "percent"'
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Color variant",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "error",
            "info"
          ],
          default: "default"
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact mode (inline, no card wrapper)",
          default: false
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment.",
          enumValues: [
            "elevated",
            "flat",
            "progress-backed",
            "gauge",
            "sparkline"
          ],
          default: "elevated"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: {
            variant: "stats",
            columns: 1
          },
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "section-header": {
      type: "section-header",
      category: "header",
      sourceFile: "components/core/atoms/SectionHeader.tsx",
      tier: "atoms",
      family: "core",
      description: "SectionHeader component",
      suggestedFor: [
        "section",
        "header",
        "section header"
      ],
      typicalSize: "small",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Section title text",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Optional subtitle text"
        },
        align: {
          types: [
            "string"
          ],
          description: "Text alignment",
          enumValues: [
            "center",
            "left",
            "right"
          ],
          default: "center"
        },
        level: {
          types: [
            "number"
          ],
          description: "Heading level (1, 2, or 3)",
          numericEnumValues: [
            1,
            2,
            3
          ],
          default: 2
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "feature-card": {
      type: "feature-card",
      category: "display",
      sourceFile: "components/core/molecules/FeatureCard.tsx",
      tier: "molecules",
      family: "core",
      description: "FeatureCard \u2014 an icon-led card pairing a title and description to call out a single product feature.",
      capabilities: "feature highlight, benefit callout, product capability card, feature-grid tile",
      suggestedFor: [
        "feature",
        "card",
        "feature card"
      ],
      typicalSize: "medium",
      propsSchema: {
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "icon prop"
        },
        title: {
          types: [
            "string"
          ],
          description: "Feature title",
          required: true
        },
        description: {
          types: [
            "string"
          ],
          description: "Feature description",
          required: true
        },
        href: {
          types: [
            "string"
          ],
          description: "Optional link URL"
        },
        linkLabel: {
          types: [
            "string"
          ],
          description: "Label for the link button"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Card visual variant",
          enumValues: [
            "default",
            "bordered",
            "elevated",
            "interactive"
          ],
          default: "bordered"
        },
        size: {
          types: [
            "string"
          ],
          description: "Card size affecting icon and spacing",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "feature-grid": {
      type: "feature-grid",
      category: "display",
      sourceFile: "components/core/molecules/FeatureGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "FeatureGrid component",
      suggestedFor: [
        "feature",
        "grid",
        "feature grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Array of feature card configurations",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              linkLabel: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "bordered",
                  "elevated",
                  "interactive"
                ]
              },
              size: {
                types: [
                  "string"
                ],
                enumValues: [
                  "sm",
                  "md",
                  "lg"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "title",
              "description"
            ]
          }
        },
        columns: {
          types: [
            "number"
          ],
          description: "Number of grid columns",
          numericEnumValues: [
            2,
            3,
            4,
            6
          ],
          default: 3
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between grid items",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "social-proof": {
      type: "social-proof",
      category: "component",
      sourceFile: "components/core/molecules/SocialProof.tsx",
      tier: "molecules",
      family: "core",
      description: "SocialProof component",
      suggestedFor: [
        "social",
        "proof",
        "social proof"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "List of social proof items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              logo: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              quote: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        variant: {
          types: [
            "string"
          ],
          description: "Display variant",
          enumValues: [
            "logos",
            "quotes",
            "badges"
          ],
          default: "logos"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "animated-graphic": {
      type: "animated-graphic",
      category: "component",
      sourceFile: "components/core/atoms/AnimatedGraphic.tsx",
      tier: "atoms",
      family: "core",
      description: "AnimatedGraphic component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "animated",
        "graphic",
        "animated graphic"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        src: {
          types: [
            "asset"
          ],
          description: "URL to an SVG file. Fetched and inlined to enable stroke/fill animations."
        },
        svgContent: {
          types: [
            "string"
          ],
          description: "Inline SVG string. Takes precedence over src if both provided."
        },
        animation: {
          types: [
            "string"
          ],
          description: "Animation type applied to SVG paths/shapes",
          enumValues: [
            "draw",
            "fill",
            "pulse",
            "morph"
          ],
          default: "draw"
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Whether to run the animation (default: false). Controlled externally or via AnimatedReveal.",
          default: false
        },
        duration: {
          types: [
            "number"
          ],
          description: "Animation duration in ms (default: 1200)",
          default: 1200
        },
        delay: {
          types: [
            "number"
          ],
          description: "Delay before animation starts in ms (default: 0)",
          default: 0
        },
        easing: {
          types: [
            "string"
          ],
          description: "Easing function (default: cubic-bezier(0.16, 1, 0.3, 1))",
          default: "cubic-bezier(0.16, 1, 0.3, 1)"
        },
        width: {
          types: [
            "string",
            "number"
          ],
          description: "Width of the graphic container"
        },
        height: {
          types: [
            "string",
            "number"
          ],
          description: "Height of the graphic container"
        },
        strokeColor: {
          types: [
            "string"
          ],
          description: "Stroke color override for SVG paths"
        },
        fillColor: {
          types: [
            "string"
          ],
          description: "Fill color for the 'fill' animation end state"
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alt text for accessibility"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "animated-reveal": {
      type: "animated-reveal",
      category: "component",
      sourceFile: "components/core/atoms/AnimatedReveal.tsx",
      tier: "atoms",
      family: "core",
      description: "AnimatedReveal component",
      suggestedFor: [
        "animated",
        "reveal",
        "animated reveal"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        trigger: {
          types: [
            "string"
          ],
          description: "What triggers the animation",
          enumValues: [
            "scroll",
            "hover",
            "manual"
          ],
          default: "scroll"
        },
        animation: {
          types: [
            "string"
          ],
          description: "Built-in animation preset",
          enumValues: [
            "fade-up",
            "fade-down",
            "fade-in",
            "fade-left",
            "fade-right",
            "scale",
            "scale-up",
            "none"
          ],
          default: "fade-up"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Animation duration in ms (default: 600)",
          default: 600
        },
        delay: {
          types: [
            "number"
          ],
          description: "Delay before animation starts in ms (default: 0)",
          default: 0
        },
        threshold: {
          types: [
            "number"
          ],
          description: "How much of the element must be visible before triggering, 0-1 (default: 0.15)",
          default: 0.15
        },
        revealEvent: {
          types: [
            "string"
          ],
          description: "scroll trigger: event emitted once (as UI:{revealEvent}) the first time the element scrolls into view; never under reduced motion",
          kind: "event",
          notification: true
        },
        enterEvent: {
          types: [
            "string"
          ],
          description: "scroll trigger: event emitted (as UI:{enterEvent}) every time the element enters the view",
          kind: "event",
          notification: true
        },
        leaveEvent: {
          types: [
            "string"
          ],
          description: "scroll trigger: event emitted (as UI:{leaveEvent}) every time the element leaves the view after having entered it",
          kind: "event",
          notification: true
        },
        once: {
          types: [
            "boolean"
          ],
          description: "Animate only the first time the element enters the viewport (default: true)",
          default: true
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Manual control: when trigger='manual', set this to true to animate"
        },
        easing: {
          types: [
            "string"
          ],
          description: "Easing function (default: cubic-bezier(0.16, 1, 0.3, 1))",
          default: "cubic-bezier(0.16, 1, 0.3, 1)"
        },
        children: {
          types: [
            "node",
            "function"
          ],
          description: "Children: ReactNode or render function receiving animated state",
          required: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "pattern-tile": {
      type: "pattern-tile",
      category: "game",
      sourceFile: "components/core/atoms/PatternTile.tsx",
      tier: "atoms",
      family: "core",
      description: "PatternTile component",
      suggestedFor: [
        "pattern",
        "tile",
        "pattern tile"
      ],
      typicalSize: "small",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Which geometric pattern to render",
          enumValues: [
            "star8",
            "star6",
            "khatam",
            "star10",
            "star12",
            "rosette-double",
            "rosette-filled",
            "seigaiha",
            "greek-key",
            "celtic-knot",
            "kolam",
            "arch",
            "arabesque-vine",
            "arabesque-net"
          ],
          default: "star8"
        },
        size: {
          types: [
            "number"
          ],
          description: "Tile unit size in SVG units",
          default: 60
        },
        color: {
          types: [
            "string"
          ],
          description: "Stroke color (CSS variable or hex)",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Line thickness",
          default: 0.5
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "edge-decoration": {
      type: "edge-decoration",
      category: "component",
      sourceFile: "components/core/molecules/EdgeDecoration.tsx",
      tier: "molecules",
      family: "core",
      description: "EdgeDecoration component",
      suggestedFor: [
        "edge",
        "decoration",
        "edge decoration"
      ],
      typicalSize: "medium",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Which decorative element",
          enumValues: [
            "arch",
            "vine",
            "lattice"
          ],
          default: "arch"
        },
        side: {
          types: [
            "string"
          ],
          description: "Which side(s) to place the decoration",
          enumValues: [
            "left",
            "right",
            "both"
          ],
          default: "both"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "Overall opacity (default: 0.15)",
          default: 0.15
        },
        color: {
          types: [
            "string"
          ],
          description: "Semantic palette token or a raw CSS color value for the stroke.",
          default: "primary"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width",
          default: 0.5
        },
        width: {
          types: [
            "number"
          ],
          description: "Width of the decoration area as percentage of container (default: 15)",
          default: 15
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "ui-slot-renderer": {
      type: "ui-slot-renderer",
      category: "layout",
      sourceFile: "components/core/organisms/UISlotRenderer.tsx",
      tier: "organisms",
      family: "core",
      description: "UISlotRenderer component",
      suggestedFor: [
        "slot",
        "renderer",
        "u i slot renderer"
      ],
      typicalSize: "large",
      propsSchema: {
        includeHud: {
          types: [
            "boolean"
          ],
          description: "Include HUD slots",
          default: false
        },
        hudMode: {
          types: [
            "string"
          ],
          description: "HUD positioning mode: 'fixed' (default, viewport-relative) or 'inline' (container-relative, uses sticky)",
          enumValues: [
            "fixed",
            "inline"
          ],
          default: "fixed"
        },
        includeFloating: {
          types: [
            "boolean"
          ],
          description: "Include floating slot",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class name for the container"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        suspense: {
          types: [
            "boolean",
            "object"
          ],
          description: "Enable Suspense boundaries around each slot. When true, each inline slot is wrapped in `<ErrorBoundary><Suspense>` with Skeleton fallbacks. Opt-in \u2014 existing isLoading prop pattern still works.",
          properties: {
            enabled: {
              types: [
                "boolean"
              ]
            },
            slotFallbacks: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "node"
                ]
              }
            }
          },
          propertyRequired: [
            "enabled"
          ]
        },
        pageKey: {
          types: [
            "string"
          ],
          description: "The current page (path or name). When set, the main slot's content animates in on every page change (`PageTransition`); a layout's own content region claims the transition so its chrome stays still."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "trait-frame": {
      type: "trait-frame",
      category: "component",
      sourceFile: "components/core/atoms/TraitFrame.tsx",
      tier: "atoms",
      family: "core",
      description: "TraitFrame \u2014 renders the current frame of a referenced trait.  Resolves the `@trait.X[.slot]` binding at render time by looking up the referenced trait's last `render-ui` payload from the orbital's `UISlotManager`. Re-renders automatically when that trait transitions via the per-trait subscription channel (`subscribeTrait`).  This is the single rendering primitive both runtime paths converge on:  - **Interpreted path** \u2014 the trait-binding resolver in `renderer/trait-binding-resolver.ts` walks pattern trees at render time, substituting `\"@trait.X\"` string children with this component. - **Compiled path** \u2014 the TypeScript shell codegen in `orbital-shell-typescript/src/backend.rs::pattern_to_jsx_with_data` emits this component directly into generated JSX wherever a `@trait.*` string appears in a pattern tree.  Embedding is passive: the referenced trait's state machine runs unchanged; TraitFrame is only a read-only lens on its current frame. Event propagation happens over the shared event bus, not through this component.",
      suggestedFor: [
        "trait",
        "frame",
        "trait frame"
      ],
      typicalSize: "small",
      propsSchema: {
        traitName: {
          types: [
            "string"
          ],
          description: "Name of the trait whose current frame to embed. Must match a trait declared in the current orbital. Compiler validates that the name exists at build time (see `ORB_BINDING_TRAIT_UNKNOWN`).",
          required: true
        },
        fallback: {
          types: [
            "node"
          ],
          description: "Rendered when the referenced trait has not (yet) emitted any render-ui. Use a skeleton, spinner, or message. Defaults to `null` (renders nothing), so an unfulfilled reference drops cleanly out of a parent `children:` array.",
          default: null
        }
      }
    },
    "file-tree": {
      type: "file-tree",
      category: "component",
      sourceFile: "components/core/molecules/FileTree.tsx",
      tier: "molecules",
      family: "core",
      description: "FileTree component",
      suggestedFor: [
        "file",
        "tree",
        "file tree"
      ],
      typicalSize: "medium",
      propsSchema: {
        tree: {
          types: [
            "array"
          ],
          description: "The tree data (pre-nested). Ignored when `items` is provided.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              path: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "file",
                  "dir"
                ]
              },
              children: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "FileTreeNode"
                }
              },
              note: {
                types: [
                  "string"
                ]
              },
              expandable: {
                types: [
                  "boolean"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              language: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name",
              "path",
              "type"
            ]
          }
        },
        items: {
          types: [
            "array"
          ],
          description: "Flat node list, nested by `parentId` at render time \u2014 takes precedence over `tree` when provided. A `parentId` with no matching row renders that node as a root rather than dropping it.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              parentId: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        selectedPath: {
          types: [
            "string"
          ],
          description: "Currently selected file path"
        },
        selectedId: {
          types: [
            "string"
          ],
          description: 'Currently selected node id, in flat `items` mode: the row highlights and every ancestor auto-expands so the selection is always visible \u2014 the "you are here" a navigation tree needs.'
        },
        look: {
          types: [
            "string"
          ],
          description: "Visual treatment for flat `items` mode: `files` (default) keeps the monospace file-browser look; `nav` renders a sidebar navigation tree \u2014 regular font, document icons, roomier hit areas.",
          enumValues: [
            "files",
            "nav"
          ],
          default: "files"
        },
        onFileSelect: {
          types: [
            "function"
          ],
          description: "Called when a file is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "path",
              type: "string"
            }
          ]
        },
        onNodeExpand: {
          types: [
            "function"
          ],
          description: "Called the first time a node marked `expandable` is expanded, with its path, so its children can be loaded.",
          kind: "callback",
          callbackArgs: [
            {
              name: "path",
              type: "string"
            }
          ]
        },
        expandedPaths: {
          types: [
            "array"
          ],
          description: "Paths to show expanded (e.g. to reveal a node): each is expanded when it appears here, loading its children if needed.",
          items: {
            types: [
              "string"
            ]
          }
        },
        onNodeSelect: {
          types: [
            "function"
          ],
          description: "Called when a node is clicked, in flat `items` mode; carries the selected node's id. Presence-gated: binding this prop is what turns on the click-to-select affordance, mirroring Badge's `onRemove`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ],
          default: ""
        },
        onNodeAction: {
          types: [
            "function"
          ],
          description: "Called from the row's hover action button, in flat `items` mode; carries that node's id. Presence-gated: binding it renders the action.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ],
          default: ""
        },
        nodeActionIcon: {
          types: [
            "string"
          ],
          description: "Icon for the row hover action (default: plus)."
        },
        nodeActionLabel: {
          types: [
            "string"
          ],
          description: "Accessible label/tooltip for the row hover action."
        },
        onNodeReorder: {
          types: [
            "function"
          ],
          description: "Called when a row is dropped onto another, in flat `items` mode; carries the moved node's id, its new parent id (`null` for a root drop), and its index within the new parent's children. Presence-gated: binding this prop is what turns on row dragging.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "newParentId",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          default: ""
        },
        className: {
          types: [
            "string"
          ],
          description: "CSS class"
        },
        indent: {
          types: [
            "number"
          ],
          description: "Indent size per level in px (default: 16)",
          default: 16
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "filter-pill": {
      type: "filter-pill",
      category: "filter",
      sourceFile: "components/core/atoms/FilterPill.tsx",
      tier: "atoms",
      family: "core",
      description: "FilterPill component",
      suggestedFor: [
        "filter",
        "pill",
        "filter pill"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "default",
            "primary",
            "secondary",
            "success",
            "warning",
            "danger",
            "info",
            "neutral"
          ],
          default: "default"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        label: {
          types: [
            "string",
            "number"
          ],
          description: "Pill label text (alternative to children for schema-driven rendering)."
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component or canonical kebab-case icon name string"
        },
        onRemove: {
          types: [
            "function"
          ],
          description: "Called when the user clicks the remove (\xD7) button",
          kind: "callback",
          callbackArgs: [],
          default: ""
        },
        removable: {
          types: [
            "boolean"
          ],
          description: "Disable the remove button (renders without \xD7 control)",
          default: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler for the body of the pill (filter toggle)",
          kind: "callback",
          callbackArgs: []
        },
        clickEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the pill body is clicked. Payload: { label }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "label",
              type: "string",
              required: true
            }
          ]
        },
        removeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the remove (\xD7) button is clicked. Payload: { label }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "label",
              type: "string",
              required: true
            }
          ],
          default: ""
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "likert-scale": {
      type: "likert-scale",
      category: "component",
      sourceFile: "components/core/molecules/LikertScale.tsx",
      tier: "molecules",
      family: "core",
      description: "LikertScale component",
      suggestedFor: [
        "likert",
        "scale",
        "likert scale"
      ],
      typicalSize: "medium",
      propsSchema: {
        question: {
          types: [
            "string"
          ],
          description: "Optional row prompt above the scale"
        },
        options: {
          types: [
            "array"
          ],
          description: "Scale points (defaults to a translated 5-point agree/disagree set)",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        value: {
          types: [
            "number",
            "string"
          ],
          description: "Selected value (controlled)",
          default: null
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Change callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus on change. Payload: { value: number | string }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant: classic radios or pill segmented buttons",
          enumValues: [
            "radios",
            "buttons"
          ],
          default: "radios"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "matrix-question": {
      type: "matrix-question",
      category: "component",
      sourceFile: "components/core/molecules/MatrixQuestion.tsx",
      tier: "molecules",
      family: "core",
      description: "MatrixQuestion component",
      suggestedFor: [
        "matrix",
        "question",
        "matrix question"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Optional title rendered above the matrix"
        },
        rows: {
          types: [
            "array"
          ],
          description: "Question rows",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        columns: {
          types: [
            "array"
          ],
          description: "Column definitions; defaults to 5-point Likert",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        values: {
          types: [
            "object"
          ],
          description: "Selected value per row, keyed by rowId",
          mapValue: {
            types: [
              "number",
              "string"
            ]
          }
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Change handler invoked with rowId and selected column value",
          kind: "callback",
          callbackArgs: [
            {
              name: "rowId",
              type: "string"
            },
            {
              name: "value",
              type: "number"
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when a row's value changes. Payload: { rowId, value }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "rowId",
              type: "string",
              required: true
            },
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable all inputs",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Visual size",
          enumValues: [
            "sm",
            "md"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "qr-scanner": {
      type: "qr-scanner",
      category: "component",
      sourceFile: "components/core/molecules/QrScanner.tsx",
      tier: "molecules",
      family: "core",
      description: "QrScanner component",
      suggestedFor: [
        "scanner",
        "qr scanner"
      ],
      typicalSize: "medium",
      propsSchema: {
        onScan: {
          types: [
            "function"
          ],
          description: "onScan prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "result",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  text: {
                    types: [
                      "string"
                    ]
                  },
                  format: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "text",
                  "format",
                  "timestamp"
                ]
              }
            }
          ]
        },
        scanEvent: {
          types: [
            "string"
          ],
          description: "scanEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "text",
              type: "string",
              required: true
            },
            {
              name: "format",
              type: "string",
              required: true
            },
            {
              name: "timestamp",
              type: "number",
              required: true
            }
          ]
        },
        onError: {
          types: [
            "function"
          ],
          description: "onError prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "error",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        facingMode: {
          types: [
            "string"
          ],
          description: "facingMode prop",
          enumValues: [
            "environment",
            "user"
          ],
          default: "environment"
        },
        paused: {
          types: [
            "boolean"
          ],
          description: "paused prop",
          default: false
        },
        showOverlay: {
          types: [
            "boolean"
          ],
          description: "showOverlay prop",
          default: true
        },
        showCameraControls: {
          types: [
            "boolean"
          ],
          description: "showCameraControls prop",
          default: true
        },
        fallback: {
          types: [
            "node"
          ],
          description: "fallback prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "vote-stack": {
      type: "vote-stack",
      category: "component",
      sourceFile: "components/core/molecules/VoteStack.tsx",
      tier: "molecules",
      family: "core",
      description: "VoteStack component",
      suggestedFor: [
        "vote",
        "stack",
        "vote stack"
      ],
      typicalSize: "medium",
      propsSchema: {
        count: {
          types: [
            "number"
          ],
          description: "Current tally",
          required: true
        },
        userVote: {
          types: [
            "string"
          ],
          description: "Current user's vote (null = no vote cast)",
          enumValues: [
            "up",
            "down",
            "none"
          ],
          default: null
        },
        onVote: {
          types: [
            "function"
          ],
          description: "Toggle handler. Clicking the same arrow clears (emits null).",
          kind: "callback",
          callbackArgs: [
            {
              name: "next",
              type: "string"
            }
          ]
        },
        voteEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched on the bus when a vote is cast. Payload: { next: VoteValue }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "next",
              type: "string",
              required: true,
              enumValues: [
                "up",
                "down",
                "none"
              ]
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Layout orientation. `vertical` = forum/Q&A column; `horizontal` = compact row.",
          enumValues: [
            "vertical",
            "horizontal"
          ],
          default: "vertical"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label for the group"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "branching-logic-builder": {
      type: "branching-logic-builder",
      category: "display",
      sourceFile: "components/core/molecules/BranchingLogicBuilder.tsx",
      tier: "molecules",
      family: "core",
      description: "BranchingLogicBuilder component",
      suggestedFor: [
        "branching",
        "logic",
        "builder",
        "branching logic builder"
      ],
      typicalSize: "large",
      propsSchema: {
        questions: {
          types: [
            "array",
            "object"
          ],
          description: "questions prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              optionValues: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        rules: {
          types: [
            "array",
            "object"
          ],
          description: "Rules. Accepts either a typed array (direct consumers) or the runtime payload shape from a render-ui binding (`@payload.data`). Narrowed to `[]` internally when the value isn't an array.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              sourceQuestionId: {
                types: [
                  "string"
                ]
              },
              operator: {
                types: [
                  "string"
                ],
                enumValues: [
                  "equals",
                  "not-equals",
                  "contains",
                  "in"
                ]
              },
              value: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              targetQuestionId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "sourceQuestionId",
              "operator",
              "value",
              "targetQuestionId"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        onRulesChange: {
          types: [
            "function"
          ],
          description: "onRulesChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "rules",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    sourceQuestionId: {
                      types: [
                        "string"
                      ]
                    },
                    operator: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "equals",
                        "not-equals",
                        "contains",
                        "in"
                      ]
                    },
                    value: {
                      types: [
                        "string",
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    targetQuestionId: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "id",
                    "sourceQuestionId",
                    "operator",
                    "value",
                    "targetQuestionId"
                  ]
                }
              }
            }
          ]
        },
        rulesChangeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when rules change. Payload: `{ rules }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "rules",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    sourceQuestionId: {
                      types: [
                        "string"
                      ]
                    },
                    operator: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "equals",
                        "not-equals",
                        "contains",
                        "in"
                      ]
                    },
                    value: {
                      types: [
                        "string",
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    targetQuestionId: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "id",
                    "sourceQuestionId",
                    "operator",
                    "value",
                    "targetQuestionId"
                  ]
                }
              }
            }
          ]
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "readOnly prop",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "reply-tree": {
      type: "reply-tree",
      category: "display",
      sourceFile: "components/core/molecules/ReplyTree.tsx",
      tier: "molecules",
      family: "core",
      description: "ReplyTree component",
      suggestedFor: [
        "reply",
        "tree",
        "reply tree"
      ],
      typicalSize: "large",
      propsSchema: {
        nodes: {
          types: [
            "object",
            "array"
          ],
          description: "nodes prop",
          required: true,
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "authorName",
            "content"
          ],
          properties: {
            authorName: {
              types: [
                "string"
              ]
            },
            content: {
              types: [
                "string"
              ]
            },
            authorAvatarUrl: {
              types: [
                "string"
              ]
            },
            postedAt: {
              types: [
                "string"
              ]
            },
            voteCount: {
              types: [
                "number"
              ]
            },
            userVote: {
              types: [
                "string"
              ],
              enumValues: [
                "up",
                "down",
                "none"
              ]
            },
            collapsed: {
              types: [
                "boolean"
              ]
            },
            replies: {
              types: [
                "object",
                "array"
              ],
              cyclic: true,
              cyclicTypeName: "ReplyNodeRow"
            }
          }
        },
        maxDepth: {
          types: [
            "number"
          ],
          description: "maxDepth prop",
          default: 6
        },
        onVote: {
          types: [
            "function"
          ],
          description: "onVote prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "nodeId",
              type: "string"
            },
            {
              name: "vote",
              type: "string"
            }
          ]
        },
        onReply: {
          types: [
            "function"
          ],
          description: "onReply prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "parentNodeId",
              type: "string"
            }
          ]
        },
        onFlag: {
          types: [
            "function"
          ],
          description: "onFlag prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "nodeId",
              type: "string"
            }
          ]
        },
        onContinueThread: {
          types: [
            "function"
          ],
          description: "onContinueThread prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "nodeId",
              type: "string"
            }
          ]
        },
        voteEvent: {
          types: [
            "string"
          ],
          description: "voteEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "nodeId",
              type: "string",
              required: true
            },
            {
              name: "vote",
              type: "string",
              required: true,
              enumValues: [
                "up",
                "down",
                "none"
              ]
            }
          ]
        },
        replyEvent: {
          types: [
            "string"
          ],
          description: "replyEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "parentNodeId",
              type: "string",
              required: true
            },
            {
              name: "content",
              type: "string",
              required: true
            }
          ]
        },
        flagEvent: {
          types: [
            "string"
          ],
          description: "flagEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "nodeId",
              type: "string",
              required: true
            }
          ]
        },
        continueThreadEvent: {
          types: [
            "string"
          ],
          description: "continueThreadEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "nodeId",
              type: "string",
              required: true
            }
          ]
        },
        showActions: {
          types: [
            "boolean"
          ],
          description: "showActions prop",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "version-diff": {
      type: "version-diff",
      category: "display",
      sourceFile: "components/core/molecules/VersionDiff.tsx",
      tier: "molecules",
      family: "core",
      description: "A line diff between two versions \u2014 two whole texts, or a pre-computed diff such as one commit.",
      capabilities: "version diff, compare revisions, what changed, before and after, code diff, commit diff, revision comparison",
      suggestedFor: [
        "version",
        "diff",
        "version diff"
      ],
      typicalSize: "large",
      propsSchema: {
        revisions: {
          types: [
            "array",
            "object"
          ],
          description: "All available revisions (at least 2). Accepts either a typed array (direct consumers) or the runtime payload shape from a render-ui binding (`@payload.revisions`). Narrowed to `[]` internally when the value isn't an array.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              author: {
                types: [
                  "string"
                ]
              },
              timestamp: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "label",
              "content"
            ]
          }
        },
        hunks: {
          types: [
            "array"
          ],
          description: "A pre-computed diff to render instead of diffing `revisions` (no revision pickers).",
          items: {
            types: [
              "object"
            ],
            properties: {
              header: {
                types: [
                  "string"
                ]
              },
              lines: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    type: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "added",
                        "removed",
                        "unchanged",
                        "context"
                      ]
                    },
                    beforeLineNumber: {
                      types: [
                        "number"
                      ]
                    },
                    afterLineNumber: {
                      types: [
                        "number"
                      ]
                    },
                    content: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "type",
                    "content"
                  ]
                }
              }
            },
            required: [
              "header",
              "lines"
            ]
          }
        },
        beforeId: {
          types: [
            "string"
          ],
          description: 'Currently selected "before" revision id.'
        },
        afterId: {
          types: [
            "string"
          ],
          description: 'Currently selected "after" revision id.'
        },
        view: {
          types: [
            "string"
          ],
          description: "Display mode to start in; the toggle switches it.",
          enumValues: [
            "side-by-side",
            "inline"
          ],
          default: "side-by-side"
        },
        onSelectBefore: {
          types: [
            "function"
          ],
          description: 'Called when the user picks a different "before" revision.',
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onSelectAfter: {
          types: [
            "function"
          ],
          description: 'Called when the user picks a different "after" revision.',
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onRevert: {
          types: [
            "function"
          ],
          description: 'Called when the user clicks the revert button (passes the "before" id).',
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        selectBeforeEvent: {
          types: [
            "string"
          ],
          description: 'Event name dispatched via event bus when the "before" revision changes. Payload: { id }.',
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        selectAfterEvent: {
          types: [
            "string"
          ],
          description: 'Event name dispatched via event bus when the "after" revision changes. Payload: { id }.',
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        revertEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the user clicks revert. Payload: { id }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        language: {
          types: [
            "string"
          ],
          description: "Language label (informational)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "option-constraint-group": {
      type: "option-constraint-group",
      category: "component",
      sourceFile: "components/core/molecules/OptionConstraintGroup.tsx",
      tier: "molecules",
      family: "core",
      description: 'OptionConstraintGroup Molecule Component  Generic constrained option selector (radio for "single", checkbox for "multi") with required/optional/multi constraints. Renders radio inputs for `single` constraint and checkboxes for `multi`. Reports validation errors inline when the current selection violates the constraint.  **Atomic Design**: Composed using Typography atom and native form controls.',
      suggestedFor: [
        "option",
        "constraint",
        "group",
        "option constraint group"
      ],
      typicalSize: "medium",
      propsSchema: {
        groupId: {
          types: [
            "string"
          ],
          description: "groupId prop",
          required: true
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop",
          required: true
        },
        description: {
          types: [
            "string"
          ],
          description: "description prop"
        },
        options: {
          types: [
            "array"
          ],
          description: "options prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              priceDelta: {
                types: [
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              outOfStock: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        constraint: {
          types: [
            "object"
          ],
          description: "Selection constraint. Optional \u2014 defaults to single-select (the generic factory can't author a discriminated-union value; an author overrides it).",
          variants: [
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "single"
                  ]
                },
                required: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "type"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "multi"
                  ]
                },
                min: {
                  types: [
                    "number"
                  ]
                },
                max: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type"
              ]
            }
          ],
          discriminator: "type",
          default: {
            type: "single"
          }
        },
        selected: {
          types: [
            "array"
          ],
          description: "selected prop",
          items: {
            types: [
              "string"
            ]
          },
          default: []
        },
        onChange: {
          types: [
            "function"
          ],
          description: "onChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "selected",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "changeEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selected",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "positioned-canvas": {
      type: "positioned-canvas",
      category: "game",
      sourceFile: "components/core/molecules/PositionedCanvas.tsx",
      tier: "molecules",
      family: "core",
      description: "PositionedCanvas component",
      suggestedFor: [
        "positioned",
        "canvas",
        "positioned canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "object",
            "array"
          ],
          description: "Items to render. The molecule narrows non-array values to `[]` and reads each row's `id` / `x` / `y` / `label` fields at render time.",
          required: true,
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "label",
            "x",
            "y",
            "capacity"
          ],
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            capacity: {
              types: [
                "number"
              ]
            },
            partySize: {
              types: [
                "number"
              ]
            },
            serverName: {
              types: [
                "string"
              ]
            },
            status: {
              types: [
                "string"
              ],
              enumValues: [
                "empty",
                "seated",
                "ordered",
                "awaiting-bill",
                "cleaning"
              ]
            },
            shape: {
              types: [
                "string"
              ],
              enumValues: [
                "round",
                "rectangle",
                "square"
              ]
            }
          }
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 800
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 600
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "selectedId prop",
          default: null
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "editable prop",
          default: false
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "onSelect prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onMove: {
          types: [
            "function"
          ],
          description: "onMove prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "x",
              type: "number"
            },
            {
              name: "y",
              type: "number"
            }
          ]
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "selectEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        moveEvent: {
          types: [
            "string"
          ],
          description: "moveEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "choice-button": {
      type: "choice-button",
      category: "game",
      sourceFile: "components/game/atoms/ChoiceButton.tsx",
      tier: "atoms",
      family: "game",
      description: "ChoiceButton component",
      suggestedFor: [
        "choice",
        "button",
        "choice button"
      ],
      typicalSize: "small",
      propsSchema: {
        text: {
          types: [
            "string"
          ],
          description: "Choice text content",
          required: true
        },
        index: {
          types: [
            "number"
          ],
          description: "Choice index number (displayed as prefix)"
        },
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon displayed before the text"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the choice is disabled",
          default: false
        },
        selected: {
          types: [
            "boolean"
          ],
          description: "Whether the choice is currently selected",
          default: false
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: []
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        payload: {
          types: [
            "object"
          ],
          description: "Semantic payload emitted with the action (e.g. `{ nextId, choiceIndex }` for a branching choice).",
          mapValue: {
            types: [
              "string",
              "number",
              "boolean"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "control-button": {
      type: "control-button",
      category: "game",
      sourceFile: "components/game/atoms/ControlButton.tsx",
      tier: "atoms",
      family: "game",
      description: "ControlButton component",
      suggestedFor: [
        "control",
        "button",
        "control button"
      ],
      typicalSize: "small",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Button label text"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon component or emoji"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          default: "md"
        },
        shape: {
          types: [
            "string"
          ],
          description: "Shape variant",
          default: "circle"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          default: "secondary"
        },
        onPress: {
          types: [
            "function"
          ],
          description: "Called when button is pressed",
          kind: "callback",
          callbackArgs: []
        },
        onRelease: {
          types: [
            "function"
          ],
          description: "Called when button is released",
          kind: "callback",
          callbackArgs: []
        },
        pressEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name emitted on press via useEventBus",
          kind: "event"
        },
        releaseEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name emitted on release via useEventBus",
          kind: "event"
        },
        pressed: {
          types: [
            "boolean"
          ],
          description: "Whether the button is currently pressed"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the button is disabled"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dialogue-bubble": {
      type: "dialogue-bubble",
      category: "game",
      sourceFile: "components/game/atoms/DialogueBubble.tsx",
      tier: "atoms",
      family: "game",
      description: "DialogueBubble component",
      suggestedFor: [
        "dialogue",
        "bubble",
        "dialogue bubble"
      ],
      typicalSize: "small",
      propsSchema: {
        speaker: {
          types: [
            "string"
          ],
          description: "Speaker name displayed at the top"
        },
        text: {
          types: [
            "string"
          ],
          description: "Dialogue text content",
          required: true
        },
        portrait: {
          types: [
            "object"
          ],
          description: "Speaker portrait asset",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Position of the bubble on screen",
          enumValues: [
            "top",
            "bottom"
          ],
          default: "bottom"
        },
        mood: {
          types: [
            "string"
          ],
          description: "Speaker mood \u2014 drives a portrait-ring accent; LOLO sets this per dialogue node/transition.",
          enumValues: [
            "neutral",
            "happy",
            "concerned",
            "angry"
          ],
          default: "neutral"
        },
        revealedChars: {
          types: [
            "number"
          ],
          description: "Number of `text` characters to reveal (typewriter effect). Omit to show the full string \u2014 LOLO drives this from `@entity.revealedChars` via a tick, incrementing it until it reaches `text.length`."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "health-bar": {
      type: "health-bar",
      category: "game",
      sourceFile: "components/game/atoms/HealthBar.tsx",
      tier: "atoms",
      family: "game",
      description: "HealthBar component",
      suggestedFor: [
        "health",
        "bar",
        "health bar"
      ],
      typicalSize: "small",
      propsSchema: {
        current: {
          types: [
            "number"
          ],
          description: "Current value",
          required: true,
          default: 3
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value",
          required: true,
          default: 5
        },
        format: {
          types: [
            "string"
          ],
          description: "Display format. - `hearts` / `bar` / `numeric` \u2014 health display - `progress` \u2014 XP / generic progress bar with optional level badge and value label",
          enumValues: [
            "hearts",
            "bar",
            "numeric",
            "progress"
          ],
          default: "hearts"
        },
        level: {
          types: [
            "number"
          ],
          description: 'Current level \u2014 shown as "Lv.N" badge when format="progress"'
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: 'Show value label below bar when format="progress"',
          default: true
        },
        labelSuffix: {
          types: [
            "string"
          ],
          description: 'Label suffix shown next to the value (e.g. "XP") when showLabel=true'
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "Animation on change",
          default: true
        },
        frameAsset: {
          types: [
            "object"
          ],
          description: "Sprite image used as the bar track/frame border for bar/progress formats. Falls back to CSS rounded bg-muted.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        fillAsset: {
          types: [
            "object"
          ],
          description: "Sprite image tiled across the filled portion of bar/progress formats. Falls back to CSS color gradient.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "score-display": {
      type: "score-display",
      category: "game",
      sourceFile: "components/game/atoms/ScoreDisplay.tsx",
      tier: "atoms",
      family: "game",
      description: "ScoreDisplay component",
      suggestedFor: [
        "score",
        "display",
        "score display"
      ],
      typicalSize: "small",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        value: {
          types: [
            "number"
          ],
          description: "Current score value",
          required: true
        },
        score: {
          types: [
            "number"
          ],
          description: "Alias for value \u2014 common schema binding name"
        },
        label: {
          types: [
            "string"
          ],
          description: "Label to display before score"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon component or emoji"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        locale: {
          types: [
            "string"
          ],
          description: "Number formatting locale"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "timer-display": {
      type: "timer-display",
      category: "game",
      sourceFile: "components/game/atoms/TimerDisplay.tsx",
      tier: "atoms",
      family: "game",
      description: "TimerDisplay component",
      suggestedFor: [
        "timer",
        "display",
        "timer display"
      ],
      typicalSize: "small",
      propsSchema: {
        seconds: {
          types: [
            "number"
          ],
          description: "Time in seconds",
          required: true,
          default: 90
        },
        running: {
          types: [
            "boolean"
          ],
          description: "Whether the timer is running",
          default: true
        },
        format: {
          types: [
            "string"
          ],
          description: "Display format",
          enumValues: [
            "mm:ss",
            "ss",
            "countdown"
          ],
          default: "mm:ss"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        lowThreshold: {
          types: [
            "number"
          ],
          description: "Seconds below which to pulse red"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Icon asset rendered to the left of the time value. Falls back to no icon.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-branch": {
      type: "svg-branch",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgBranch.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgBranch component",
      suggestedFor: [
        "svg",
        "branch",
        "svg branch"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 5
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 50
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "fork",
            "merge",
            "diamond"
          ],
          default: "fork"
        },
        branches: {
          types: [
            "number"
          ],
          description: "branches prop",
          default: 2
        },
        size: {
          types: [
            "number"
          ],
          description: "size prop",
          default: 1
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-connection": {
      type: "svg-connection",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgConnection.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgConnection component",
      suggestedFor: [
        "svg",
        "connection",
        "svg connection"
      ],
      typicalSize: "small",
      propsSchema: {
        x1: {
          types: [
            "number"
          ],
          description: "x1 prop",
          default: 10
        },
        y1: {
          types: [
            "number"
          ],
          description: "y1 prop",
          default: 50
        },
        x2: {
          types: [
            "number"
          ],
          description: "x2 prop",
          default: 90
        },
        y2: {
          types: [
            "number"
          ],
          description: "y2 prop",
          default: 50
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "solid",
            "dashed",
            "animated"
          ],
          default: "solid"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop",
          default: 1.5
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-flow": {
      type: "svg-flow",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgFlow.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgFlow component",
      suggestedFor: [
        "svg",
        "flow",
        "svg flow"
      ],
      typicalSize: "small",
      propsSchema: {
        points: {
          types: [
            "array"
          ],
          description: "points prop",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            },
            tupleItems: [
              {
                types: [
                  "number"
                ]
              },
              {
                types: [
                  "number"
                ]
              }
            ]
          }
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop",
          default: 1.5
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "animated prop",
          default: false
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-grid": {
      type: "svg-grid",
      category: "display",
      sourceFile: "components/core/atoms/svg/SvgGrid.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgGrid component",
      suggestedFor: [
        "svg",
        "grid",
        "svg grid"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 10
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 10
        },
        cols: {
          types: [
            "number"
          ],
          description: "cols prop",
          default: 4
        },
        rows: {
          types: [
            "number"
          ],
          description: "rows prop",
          default: 3
        },
        spacing: {
          types: [
            "number"
          ],
          description: "spacing prop",
          default: 20
        },
        nodeRadius: {
          types: [
            "number"
          ],
          description: "nodeRadius prop",
          default: 3
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        highlights: {
          types: [
            "array"
          ],
          description: "highlights prop",
          items: {
            types: [
              "number"
            ]
          },
          default: []
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-lobe": {
      type: "svg-lobe",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgLobe.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgLobe component",
      suggestedFor: [
        "svg",
        "lobe",
        "svg lobe"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 50
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 50
        },
        rx: {
          types: [
            "number"
          ],
          description: "rx prop",
          default: 14
        },
        ry: {
          types: [
            "number"
          ],
          description: "ry prop",
          default: 20
        },
        rotation: {
          types: [
            "number"
          ],
          description: "rotation prop",
          default: 0
        },
        shells: {
          types: [
            "number"
          ],
          description: "shells prop",
          default: 2
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-mesh": {
      type: "svg-mesh",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgMesh.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgMesh component",
      suggestedFor: [
        "svg",
        "mesh",
        "svg mesh"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 60
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 60
        },
        nodes: {
          types: [
            "number"
          ],
          description: "nodes prop",
          default: 6
        },
        radius: {
          types: [
            "number"
          ],
          description: "radius prop",
          default: 50
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        connectionDensity: {
          types: [
            "number"
          ],
          description: "connectionDensity prop",
          default: 0.5
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 120
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 120
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-morph": {
      type: "svg-morph",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgMorph.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgMorph component",
      suggestedFor: [
        "svg",
        "morph",
        "svg morph"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 5
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 10
        },
        size: {
          types: [
            "number"
          ],
          description: "size prop",
          default: 1
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "text-to-code",
            "code-to-app",
            "generic"
          ],
          default: "generic"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 130
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 50
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-node": {
      type: "svg-node",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgNode.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgNode component",
      suggestedFor: [
        "svg",
        "node",
        "svg node"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 50
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 50
        },
        r: {
          types: [
            "number"
          ],
          description: "r prop",
          default: 6
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "filled",
            "stroked",
            "pulse"
          ],
          default: "filled"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-pulse": {
      type: "svg-pulse",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgPulse.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgPulse component",
      suggestedFor: [
        "svg",
        "pulse",
        "svg pulse"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 70
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 70
        },
        rings: {
          types: [
            "number"
          ],
          description: "rings prop",
          default: 3
        },
        maxRadius: {
          types: [
            "number"
          ],
          description: "maxRadius prop",
          default: 60
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "animated prop",
          default: true
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 140
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 140
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-ring": {
      type: "svg-ring",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgRing.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgRing component",
      suggestedFor: [
        "svg",
        "ring",
        "svg ring"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 50
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 50
        },
        r: {
          types: [
            "number"
          ],
          description: "r prop",
          default: 40
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "solid",
            "dashed",
            "glow"
          ],
          default: "solid"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop",
          default: 1.5
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-shield": {
      type: "svg-shield",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgShield.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgShield component",
      suggestedFor: [
        "svg",
        "shield",
        "svg shield"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 50
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 50
        },
        size: {
          types: [
            "number"
          ],
          description: "size prop",
          default: 1
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "outline",
            "filled",
            "check"
          ],
          default: "outline"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-stack": {
      type: "svg-stack",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgStack.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgStack component",
      suggestedFor: [
        "svg",
        "stack",
        "svg stack"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 10
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 40
        },
        layers: {
          types: [
            "number"
          ],
          description: "layers prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 60
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 40
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        labels: {
          types: [
            "array"
          ],
          description: "labels prop",
          items: {
            types: [
              "string"
            ]
          }
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        svgWidth: {
          types: [
            "number"
          ],
          description: "svgWidth prop",
          default: 90
        },
        svgHeight: {
          types: [
            "number"
          ],
          description: "svgHeight prop",
          default: 80
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-breadcrumb": {
      type: "doc-breadcrumb",
      category: "navigation",
      sourceFile: "components/core/molecules/DocBreadcrumb.tsx",
      tier: "molecules",
      family: "core",
      description: "DocBreadcrumb component",
      suggestedFor: [
        "doc",
        "breadcrumb",
        "doc breadcrumb"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Breadcrumb path items, last item is treated as current page",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-pagination": {
      type: "doc-pagination",
      category: "navigation",
      sourceFile: "components/core/molecules/DocPagination.tsx",
      tier: "molecules",
      family: "core",
      description: "DocPagination component",
      suggestedFor: [
        "doc",
        "pagination",
        "doc pagination"
      ],
      typicalSize: "medium",
      propsSchema: {
        prev: {
          types: [
            "object"
          ],
          description: "Previous page link",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            href: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "label",
            "href"
          ]
        },
        next: {
          types: [
            "object"
          ],
          description: "Next page link",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            href: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "label",
            "href"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-search": {
      type: "doc-search",
      category: "filter",
      sourceFile: "components/core/molecules/DocSearch.tsx",
      tier: "molecules",
      family: "core",
      description: "DocSearch component",
      suggestedFor: [
        "doc",
        "search",
        "doc search"
      ],
      typicalSize: "medium",
      propsSchema: {
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text for the input"
        },
        onSearch: {
          types: [
            "function"
          ],
          description: "Callback invoked with the search query, returns results",
          callbackArgs: [
            {
              name: "query",
              type: "string"
            }
          ],
          renderCallback: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-sidebar": {
      type: "doc-sidebar",
      category: "navigation",
      sourceFile: "components/core/molecules/DocSidebar.tsx",
      tier: "molecules",
      family: "core",
      description: "DocSidebar component",
      suggestedFor: [
        "doc",
        "sidebar",
        "doc sidebar"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Sidebar navigation items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "DocSidebarItem"
                }
              },
              active: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-toc": {
      type: "doc-toc",
      category: "component",
      sourceFile: "components/core/molecules/DocTOC.tsx",
      tier: "molecules",
      family: "core",
      description: "DocTOC component",
      suggestedFor: [
        "doc",
        "doc t o c"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Table of contents items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              level: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "id",
              "label",
              "level"
            ]
          }
        },
        activeId: {
          types: [
            "string"
          ],
          description: "Currently active section id"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "gradient-divider": {
      type: "gradient-divider",
      category: "component",
      sourceFile: "components/core/molecules/GradientDivider.tsx",
      tier: "molecules",
      family: "core",
      description: "GradientDivider component",
      suggestedFor: [
        "gradient",
        "divider",
        "gradient divider"
      ],
      typicalSize: "medium",
      propsSchema: {
        color: {
          types: [
            "string"
          ],
          description: "Semantic palette token or a raw CSS color value. Defaults to 'primary'."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "behavior-view": {
      type: "behavior-view",
      category: "component",
      sourceFile: "components/avl/molecules/BehaviorView.tsx",
      tier: "molecules",
      family: "avl",
      description: "BehaviorView component",
      suggestedFor: [
        "behavior",
        "view",
        "behavior view"
      ],
      typicalSize: "medium",
      propsSchema: {
        data: {
          types: [
            "object"
          ],
          description: "The orbital to summarize.",
          required: true,
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "object"
              ]
            },
            fields: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  required: {
                    types: [
                      "boolean"
                    ]
                  },
                  hasDefault: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "name",
                  "type",
                  "required",
                  "hasDefault"
                ]
              }
            },
            traits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  stateCount: {
                    types: [
                      "number"
                    ]
                  },
                  eventCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  emits: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listens: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "stateCount",
                  "eventCount",
                  "transitionCount",
                  "emits",
                  "listens"
                ]
              }
            },
            pages: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  route: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "route"
                ]
              }
            },
            traitDetails: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  linkedEntity: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        isInitial: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        },
                        isTerminal: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        }
                      },
                      required: [
                        "name"
                      ]
                    }
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guard: {
                          types: [
                            "sexpr"
                          ],
                          nullable: true
                        },
                        effects: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ]
                              },
                              args: {
                                types: [
                                  "array"
                                ],
                                items: {
                                  types: [
                                    "sexpr"
                                  ]
                                }
                              }
                            },
                            required: [
                              "type",
                              "args"
                            ]
                          }
                        },
                        index: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event",
                        "effects",
                        "index"
                      ]
                    }
                  },
                  emittedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listenedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "linkedEntity",
                  "states",
                  "transitions",
                  "emittedEvents",
                  "listenedEvents"
                ]
              }
            },
            externalLinks: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  targetOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  eventName: {
                    types: [
                      "string"
                    ]
                  },
                  direction: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "targetOrbital",
                  "eventName",
                  "direction",
                  "traitName"
                ]
              }
            }
          },
          propertyRequired: [
            "orbitalName",
            "entityName",
            "persistence",
            "fields",
            "traits",
            "pages",
            "traitDetails",
            "externalLinks"
          ],
          example: {
            orbitalName: "OrderOrbital",
            entityName: "Order",
            persistence: "persistent",
            fields: [
              {
                name: "customer",
                type: "string",
                required: true,
                hasDefault: false
              },
              {
                name: "qty",
                type: "number",
                required: true,
                hasDefault: true
              },
              {
                name: "status",
                type: "string",
                required: false,
                hasDefault: true
              }
            ],
            traits: [
              {
                name: "OrderFlow",
                stateCount: 5,
                eventCount: 7,
                transitionCount: 7,
                emits: [
                  "ORDER_SAVED"
                ],
                listens: [
                  "PAYMENT_OK"
                ]
              }
            ],
            pages: [
              {
                name: "Orders",
                route: "/orders"
              }
            ],
            traitDetails: {
              OrderFlow: {
                name: "OrderFlow",
                linkedEntity: "Order",
                states: [
                  {
                    name: "browsing",
                    isInitial: true,
                    isTerminal: false
                  },
                  {
                    name: "editing",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "saving",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "confirmed",
                    isInitial: false,
                    isTerminal: true
                  },
                  {
                    name: "failed",
                    isInitial: false,
                    isTerminal: false
                  }
                ],
                transitions: [
                  {
                    from: "browsing",
                    to: "editing",
                    event: "EDIT",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 0
                  },
                  {
                    from: "editing",
                    to: "saving",
                    event: "SAVE",
                    effects: [
                      {
                        type: "persist",
                        args: []
                      },
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 1
                  },
                  {
                    from: "saving",
                    to: "confirmed",
                    event: "SAVED",
                    effects: [
                      {
                        type: "emit",
                        args: []
                      },
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 2
                  },
                  {
                    from: "saving",
                    to: "failed",
                    event: "SAVE_FAILED",
                    effects: [
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 3
                  },
                  {
                    from: "failed",
                    to: "editing",
                    event: "RETRY",
                    effects: [],
                    index: 4
                  },
                  {
                    from: "editing",
                    to: "browsing",
                    event: "CANCEL",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 5
                  },
                  {
                    from: "confirmed",
                    to: "browsing",
                    event: "DONE",
                    effects: [
                      {
                        type: "navigate",
                        args: []
                      }
                    ],
                    index: 6
                  }
                ],
                emittedEvents: [
                  "ORDER_SAVED"
                ],
                listenedEvents: [
                  "PAYMENT_OK"
                ]
              }
            },
            externalLinks: [
              {
                targetOrbital: "PaymentOrbital",
                eventName: "PAYMENT_OK",
                direction: "in",
                traitName: "OrderFlow"
              }
            ]
          }
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "module-card": {
      type: "module-card",
      category: "display",
      sourceFile: "components/avl/molecules/ModuleCard.tsx",
      tier: "molecules",
      family: "avl",
      description: "ModuleCard \u2014 a compact summary card for one orbital module: its entity and fields, a mini state machine per trait, and its pages.",
      capabilities: "orbital module summary card, entity and trait overview card, app architecture node card",
      suggestedFor: [
        "module",
        "card",
        "module card"
      ],
      typicalSize: "medium",
      propsSchema: {
        data: {
          types: [
            "object"
          ],
          description: "The orbital to summarize.",
          required: true,
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "object"
              ]
            },
            fields: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  required: {
                    types: [
                      "boolean"
                    ]
                  },
                  hasDefault: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "name",
                  "type",
                  "required",
                  "hasDefault"
                ]
              }
            },
            traits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  stateCount: {
                    types: [
                      "number"
                    ]
                  },
                  eventCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  emits: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listens: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "stateCount",
                  "eventCount",
                  "transitionCount",
                  "emits",
                  "listens"
                ]
              }
            },
            pages: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  route: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "route"
                ]
              }
            },
            traitDetails: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  linkedEntity: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        isInitial: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        },
                        isTerminal: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        }
                      },
                      required: [
                        "name"
                      ]
                    }
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guard: {
                          types: [
                            "sexpr"
                          ],
                          nullable: true
                        },
                        effects: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ]
                              },
                              args: {
                                types: [
                                  "array"
                                ],
                                items: {
                                  types: [
                                    "sexpr"
                                  ]
                                }
                              }
                            },
                            required: [
                              "type",
                              "args"
                            ]
                          }
                        },
                        index: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event",
                        "effects",
                        "index"
                      ]
                    }
                  },
                  emittedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listenedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "linkedEntity",
                  "states",
                  "transitions",
                  "emittedEvents",
                  "listenedEvents"
                ]
              }
            },
            externalLinks: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  targetOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  eventName: {
                    types: [
                      "string"
                    ]
                  },
                  direction: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "targetOrbital",
                  "eventName",
                  "direction",
                  "traitName"
                ]
              }
            }
          },
          propertyRequired: [
            "orbitalName",
            "entityName",
            "persistence",
            "fields",
            "traits",
            "pages",
            "traitDetails",
            "externalLinks"
          ],
          example: {
            orbitalName: "OrderOrbital",
            entityName: "Order",
            persistence: "persistent",
            fields: [
              {
                name: "customer",
                type: "string",
                required: true,
                hasDefault: false
              },
              {
                name: "qty",
                type: "number",
                required: true,
                hasDefault: true
              },
              {
                name: "status",
                type: "string",
                required: false,
                hasDefault: true
              }
            ],
            traits: [
              {
                name: "OrderFlow",
                stateCount: 5,
                eventCount: 7,
                transitionCount: 7,
                emits: [
                  "ORDER_SAVED"
                ],
                listens: [
                  "PAYMENT_OK"
                ]
              }
            ],
            pages: [
              {
                name: "Orders",
                route: "/orders"
              }
            ],
            traitDetails: {
              OrderFlow: {
                name: "OrderFlow",
                linkedEntity: "Order",
                states: [
                  {
                    name: "browsing",
                    isInitial: true,
                    isTerminal: false
                  },
                  {
                    name: "editing",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "saving",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "confirmed",
                    isInitial: false,
                    isTerminal: true
                  },
                  {
                    name: "failed",
                    isInitial: false,
                    isTerminal: false
                  }
                ],
                transitions: [
                  {
                    from: "browsing",
                    to: "editing",
                    event: "EDIT",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 0
                  },
                  {
                    from: "editing",
                    to: "saving",
                    event: "SAVE",
                    effects: [
                      {
                        type: "persist",
                        args: []
                      },
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 1
                  },
                  {
                    from: "saving",
                    to: "confirmed",
                    event: "SAVED",
                    effects: [
                      {
                        type: "emit",
                        args: []
                      },
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 2
                  },
                  {
                    from: "saving",
                    to: "failed",
                    event: "SAVE_FAILED",
                    effects: [
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 3
                  },
                  {
                    from: "failed",
                    to: "editing",
                    event: "RETRY",
                    effects: [],
                    index: 4
                  },
                  {
                    from: "editing",
                    to: "browsing",
                    event: "CANCEL",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 5
                  },
                  {
                    from: "confirmed",
                    to: "browsing",
                    event: "DONE",
                    effects: [
                      {
                        type: "navigate",
                        args: []
                      }
                    ],
                    index: 6
                  }
                ],
                emittedEvents: [
                  "ORDER_SAVED"
                ],
                listenedEvents: [
                  "PAYMENT_OK"
                ]
              }
            },
            externalLinks: [
              {
                targetOrbital: "PaymentOrbital",
                eventName: "PAYMENT_OK",
                direction: "in",
                traitName: "OrderFlow"
              }
            ]
          }
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations per trait name (states, transition events, effect types), shown in a popover on hover.",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              states: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    body: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "body"
                  ]
                }
              },
              transitions: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    body: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "body"
                  ]
                }
              },
              effects: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    body: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "body"
                  ]
                }
              }
            }
          },
          example: {
            OrderFlow: {
              states: {
                saving: {
                  body: "Waiting for the server to confirm."
                }
              },
              effects: {
                persist: {
                  body: "Saves the order."
                }
              }
            }
          }
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-hud": {
      type: "game-hud",
      category: "game",
      sourceFile: "components/game/molecules/GameHud.tsx",
      tier: "molecules",
      family: "game",
      description: "GameHud component",
      suggestedFor: [
        "game",
        "hud",
        "game hud"
      ],
      typicalSize: "medium",
      propsSchema: {
        position: {
          types: [
            "string"
          ],
          description: "Position of the HUD"
        },
        stats: {
          types: [
            "array"
          ],
          description: "Stats to display - accepts readonly for compatibility with generated const arrays",
          items: {
            types: [
              "object"
            ],
            properties: {
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              source: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        items: {
          types: [
            "array"
          ],
          description: "Alias for stats (schema compatibility)",
          items: {
            types: [
              "object"
            ],
            properties: {
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              source: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        elements: {
          types: [
            "array"
          ],
          description: "Schema-style elements array (alternative to stats). Converted to stats internally for backwards compatibility.",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ]
              },
              bind: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              }
            }
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: 'Layout variant: "floating" (default) renders a compact pill cluster that hugs its content width; "bar" preserves the legacy full-width split bar.',
          enumValues: [
            "floating",
            "bar"
          ],
          default: "floating"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        transparent: {
          types: [
            "boolean"
          ],
          description: "Whether to use a semi-transparent background",
          default: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-menu": {
      type: "game-menu",
      category: "game",
      sourceFile: "components/game/molecules/GameMenu.tsx",
      tier: "molecules",
      family: "game",
      description: "GameMenu component",
      suggestedFor: [
        "game",
        "menu",
        "game menu"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Menu title",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Optional subtitle or version"
        },
        options: {
          types: [
            "array"
          ],
          description: "Menu options - accepts readonly for compatibility with generated const arrays",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              subLabel: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        menuItems: {
          types: [
            "array"
          ],
          description: "Alias for options (schema compatibility)",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              subLabel: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Called when an option is selected (legacy callback, prefer event bus)",
          kind: "callback",
          callbackArgs: [
            {
              name: "option",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ],
                    kind: "event"
                  },
                  navigatesTo: {
                    types: [
                      "string"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  disabled: {
                    types: [
                      "boolean"
                    ]
                  },
                  subLabel: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "label"
                ]
              }
            }
          ]
        },
        background: {
          types: [
            "string"
          ],
          description: "Background image or gradient"
        },
        logo: {
          types: [
            "object"
          ],
          description: "Logo asset",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "stat-badge": {
      type: "stat-badge",
      category: "game",
      sourceFile: "components/game/molecules/StatBadge.tsx",
      tier: "molecules",
      family: "game",
      description: "StatBadge component",
      suggestedFor: [
        "stat",
        "badge",
        "stat badge"
      ],
      typicalSize: "medium",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        iconUrl: {
          types: [
            "object"
          ],
          description: "Asset image (board ui/ PNG); takes precedence over the Lucide icon.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Stat label",
          required: true
        },
        value: {
          types: [
            "number",
            "string"
          ],
          description: "Current value (defaults to 0 if not provided)"
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value (for bar/hearts format)"
        },
        source: {
          types: [
            "string"
          ],
          description: "Data source entity name (for schema config)"
        },
        field: {
          types: [
            "string"
          ],
          description: "Field name in the source (for schema config)"
        },
        format: {
          types: [
            "string"
          ],
          description: "Display format",
          default: "number"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon component or emoji"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          default: "default"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-audio-toggle": {
      type: "game-audio-toggle",
      category: "game",
      sourceFile: "components/game/atoms/GameAudioToggle.tsx",
      tier: "atoms",
      family: "game",
      description: "GameAudioToggle component",
      suggestedFor: [
        "game",
        "audio",
        "toggle",
        "game audio toggle"
      ],
      typicalSize: "large",
      propsSchema: {
        size: {
          types: [
            "string"
          ],
          description: "Button size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state (passed through)"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (passed through)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        onAsset: {
          types: [
            "object"
          ],
          description: "Asset rendered when sound is ON. Falls back to \u{1F50A} emoji.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        offAsset: {
          types: [
            "object"
          ],
          description: "Asset rendered when sound is OFF (muted). Falls back to \u{1F507} emoji.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        toggleEvent: {
          types: [
            "string"
          ],
          description: "Event dispatched via event bus on toggle, carrying the new muted state.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "muted",
              type: "boolean",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "trait-slot": {
      type: "trait-slot",
      category: "game",
      sourceFile: "components/game/molecules/TraitSlot.tsx",
      tier: "molecules",
      family: "game",
      description: "Event Contract: Emits: UI:CLICK Emits: UI:REMOVE",
      suggestedFor: [
        "trait",
        "slot",
        "trait slot"
      ],
      typicalSize: "large",
      propsSchema: {
        slotNumber: {
          types: [
            "number"
          ],
          description: "Slot index (1-based)",
          required: true
        },
        equippedItem: {
          types: [
            "object"
          ],
          description: "Currently equipped item, if any",
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            description: {
              types: [
                "string"
              ]
            },
            iconEmoji: {
              types: [
                "string"
              ]
            },
            iconUrl: {
              types: [
                "object"
              ],
              properties: {
                url: {
                  types: [
                    "asset"
                  ]
                },
                role: {
                  types: [
                    "string"
                  ]
                },
                category: {
                  types: [
                    "string"
                  ]
                },
                animations: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                style: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "pixel",
                    "vector",
                    "hd",
                    "1-bit",
                    "isometric"
                  ]
                },
                variant: {
                  types: [
                    "string"
                  ]
                },
                dimension: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "2d",
                    "3d"
                  ]
                },
                aspect: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "1:1",
                    "16:9",
                    "5:7",
                    "8:1",
                    "4:1",
                    "5:3",
                    "1:2"
                  ]
                },
                atlas: {
                  types: [
                    "asset"
                  ]
                },
                sprite: {
                  types: [
                    "string"
                  ]
                },
                name: {
                  types: [
                    "string"
                  ]
                },
                thumbnailUrl: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "url",
                "role",
                "category"
              ]
            },
            stateMachine: {
              types: [
                "object"
              ],
              properties: {
                name: {
                  types: [
                    "string"
                  ]
                },
                states: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                currentState: {
                  types: [
                    "string"
                  ]
                },
                transitions: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      from: {
                        types: [
                          "string"
                        ]
                      },
                      to: {
                        types: [
                          "string"
                        ]
                      },
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      guardHint: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "from",
                      "to",
                      "event"
                    ]
                  }
                },
                description: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "name",
                "states",
                "currentState",
                "transitions"
              ]
            }
          },
          propertyRequired: [
            "id",
            "name",
            "category"
          ]
        },
        locked: {
          types: [
            "boolean"
          ],
          description: "Whether slot is locked",
          default: false
        },
        lockLabel: {
          types: [
            "string"
          ],
          description: "Label shown when locked"
        },
        selected: {
          types: [
            "boolean"
          ],
          description: "Whether slot is selected",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        showTooltip: {
          types: [
            "boolean"
          ],
          description: "Show tooltip on hover",
          default: true
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        tooltipFrameUrl: {
          types: [
            "object"
          ],
          description: "Optional tooltip frame image asset",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        onItemDrop: {
          types: [
            "function"
          ],
          description: "-- Drag and drop --",
          kind: "callback",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  },
                  iconEmoji: {
                    types: [
                      "string"
                    ]
                  },
                  iconUrl: {
                    types: [
                      "object"
                    ],
                    properties: {
                      url: {
                        types: [
                          "asset"
                        ]
                      },
                      role: {
                        types: [
                          "string"
                        ]
                      },
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      animations: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      style: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "pixel",
                          "vector",
                          "hd",
                          "1-bit",
                          "isometric"
                        ]
                      },
                      variant: {
                        types: [
                          "string"
                        ]
                      },
                      dimension: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "2d",
                          "3d"
                        ]
                      },
                      aspect: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "1:1",
                          "16:9",
                          "5:7",
                          "8:1",
                          "4:1",
                          "5:3",
                          "1:2"
                        ]
                      },
                      atlas: {
                        types: [
                          "asset"
                        ]
                      },
                      sprite: {
                        types: [
                          "string"
                        ]
                      },
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      thumbnailUrl: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "url",
                      "role",
                      "category"
                    ]
                  },
                  stateMachine: {
                    types: [
                      "object"
                    ],
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      states: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      currentState: {
                        types: [
                          "string"
                        ]
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            from: {
                              types: [
                                "string"
                              ]
                            },
                            to: {
                              types: [
                                "string"
                              ]
                            },
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            guardHint: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "from",
                            "to",
                            "event"
                          ]
                        }
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "states",
                      "currentState",
                      "transitions"
                    ]
                  }
                },
                required: [
                  "id",
                  "name",
                  "category"
                ]
              }
            }
          ]
        },
        draggable: {
          types: [
            "boolean"
          ],
          description: "Whether this slot's equipped item is draggable",
          default: false
        },
        onDragStart: {
          types: [
            "function"
          ],
          description: "Called when drag starts from this slot",
          kind: "callback",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  },
                  iconEmoji: {
                    types: [
                      "string"
                    ]
                  },
                  iconUrl: {
                    types: [
                      "object"
                    ],
                    properties: {
                      url: {
                        types: [
                          "asset"
                        ]
                      },
                      role: {
                        types: [
                          "string"
                        ]
                      },
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      animations: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      style: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "pixel",
                          "vector",
                          "hd",
                          "1-bit",
                          "isometric"
                        ]
                      },
                      variant: {
                        types: [
                          "string"
                        ]
                      },
                      dimension: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "2d",
                          "3d"
                        ]
                      },
                      aspect: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "1:1",
                          "16:9",
                          "5:7",
                          "8:1",
                          "4:1",
                          "5:3",
                          "1:2"
                        ]
                      },
                      atlas: {
                        types: [
                          "asset"
                        ]
                      },
                      sprite: {
                        types: [
                          "string"
                        ]
                      },
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      thumbnailUrl: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "url",
                      "role",
                      "category"
                    ]
                  },
                  stateMachine: {
                    types: [
                      "object"
                    ],
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      states: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      currentState: {
                        types: [
                          "string"
                        ]
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            from: {
                              types: [
                                "string"
                              ]
                            },
                            to: {
                              types: [
                                "string"
                              ]
                            },
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            guardHint: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "from",
                            "to",
                            "event"
                          ]
                        }
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "states",
                      "currentState",
                      "transitions"
                    ]
                  }
                },
                required: [
                  "id",
                  "name",
                  "category"
                ]
              }
            }
          ]
        },
        feedback: {
          types: [
            "string"
          ],
          description: "Per-slot correctness feedback after a failed attempt",
          enumValues: [
            "correct",
            "wrong"
          ]
        },
        onClick: {
          types: [
            "function"
          ],
          description: "-- Callbacks --",
          kind: "callback",
          callbackArgs: []
        },
        onRemove: {
          types: [
            "function"
          ],
          description: "Remove handler",
          kind: "callback",
          callbackArgs: [],
          default: ""
        },
        clickEvent: {
          types: [
            "string"
          ],
          description: "-- Declarative events --",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            }
          ]
        },
        removeEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{removeEvent} with { slotNumber }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            }
          ],
          default: ""
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{dropEvent} with { slotNumber, itemId } when an item is dropped into this slot",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            },
            {
              name: "itemId",
              type: "string",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "action-palette": {
      type: "action-palette",
      category: "game",
      sourceFile: "components/game/molecules/ActionPalette.tsx",
      tier: "molecules",
      family: "game",
      description: "ActionPalette Component  Grid of draggable ActionTile components for the Sequencer tier. Kids pick from these to build their sequence.",
      suggestedFor: [
        "action",
        "palette",
        "action palette"
      ],
      typicalSize: "large",
      propsSchema: {
        actions: {
          types: [
            "array"
          ],
          description: "Available actions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              iconEmoji: {
                types: [
                  "string"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              stateMachine: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  currentState: {
                    types: [
                      "string"
                    ]
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guardHint: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event"
                      ]
                    }
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "states",
                  "currentState",
                  "transitions"
                ]
              }
            },
            required: [
              "id",
              "name",
              "category"
            ]
          }
        },
        usedActionIds: {
          types: [
            "array"
          ],
          description: "IDs of actions that are already used (shown as disabled)",
          items: {
            types: [
              "string"
            ]
          },
          default: []
        },
        allowDuplicates: {
          types: [
            "boolean"
          ],
          description: "Whether each action can be used multiple times",
          default: true
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        label: {
          types: [
            "string"
          ],
          description: "Label above the palette"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "action-tile": {
      type: "action-tile",
      category: "game",
      sourceFile: "components/game/molecules/ActionTile.tsx",
      tier: "molecules",
      family: "game",
      description: "ActionTile Component  A draggable action tile for the Sequencer tier (ages 5-8). Kids drag these from the ActionPalette into SequenceBar slots. Sets SlotItemData on dataTransfer for TraitSlot compatibility.",
      suggestedFor: [
        "action",
        "tile",
        "action tile"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        action: {
          types: [
            "object"
          ],
          description: "The action data",
          required: true,
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            description: {
              types: [
                "string"
              ]
            },
            iconEmoji: {
              types: [
                "string"
              ]
            },
            iconUrl: {
              types: [
                "object"
              ],
              properties: {
                url: {
                  types: [
                    "asset"
                  ]
                },
                role: {
                  types: [
                    "string"
                  ]
                },
                category: {
                  types: [
                    "string"
                  ]
                },
                animations: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                style: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "pixel",
                    "vector",
                    "hd",
                    "1-bit",
                    "isometric"
                  ]
                },
                variant: {
                  types: [
                    "string"
                  ]
                },
                dimension: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "2d",
                    "3d"
                  ]
                },
                aspect: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "1:1",
                    "16:9",
                    "5:7",
                    "8:1",
                    "4:1",
                    "5:3",
                    "1:2"
                  ]
                },
                atlas: {
                  types: [
                    "asset"
                  ]
                },
                sprite: {
                  types: [
                    "string"
                  ]
                },
                name: {
                  types: [
                    "string"
                  ]
                },
                thumbnailUrl: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "url",
                "role",
                "category"
              ]
            },
            stateMachine: {
              types: [
                "object"
              ],
              properties: {
                name: {
                  types: [
                    "string"
                  ]
                },
                states: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                currentState: {
                  types: [
                    "string"
                  ]
                },
                transitions: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      from: {
                        types: [
                          "string"
                        ]
                      },
                      to: {
                        types: [
                          "string"
                        ]
                      },
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      guardHint: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "from",
                      "to",
                      "event"
                    ]
                  }
                },
                description: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "name",
                "states",
                "currentState",
                "transitions"
              ]
            }
          },
          propertyRequired: [
            "id",
            "name",
            "category"
          ]
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the tile is disabled / already used",
          default: false
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "sequence-bar": {
      type: "sequence-bar",
      category: "game",
      sourceFile: "components/game/molecules/SequenceBar.tsx",
      tier: "molecules",
      family: "game",
      description: "SequenceBar Component  A row of TraitSlot components forming the action sequence for the Sequencer tier (ages 5-8). Kids drag ActionTiles from the palette into these slots to build their sequence.",
      suggestedFor: [
        "sequence",
        "bar",
        "sequence bar"
      ],
      typicalSize: "large",
      propsSchema: {
        slots: {
          types: [
            "array"
          ],
          description: "The current sequence (sparse \u2014 undefined means empty slot)",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              iconEmoji: {
                types: [
                  "string"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              stateMachine: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  currentState: {
                    types: [
                      "string"
                    ]
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guardHint: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event"
                      ]
                    }
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "states",
                  "currentState",
                  "transitions"
                ]
              }
            },
            required: [
              "id",
              "name",
              "category"
            ]
          }
        },
        maxSlots: {
          types: [
            "number"
          ],
          description: "Max number of slots",
          required: true
        },
        onSlotDrop: {
          types: [
            "function"
          ],
          description: "Called when an item is dropped into slot at index (callback path)",
          kind: "callback",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            },
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  },
                  iconEmoji: {
                    types: [
                      "string"
                    ]
                  },
                  iconUrl: {
                    types: [
                      "object"
                    ],
                    properties: {
                      url: {
                        types: [
                          "asset"
                        ]
                      },
                      role: {
                        types: [
                          "string"
                        ]
                      },
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      animations: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      style: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "pixel",
                          "vector",
                          "hd",
                          "1-bit",
                          "isometric"
                        ]
                      },
                      variant: {
                        types: [
                          "string"
                        ]
                      },
                      dimension: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "2d",
                          "3d"
                        ]
                      },
                      aspect: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "1:1",
                          "16:9",
                          "5:7",
                          "8:1",
                          "4:1",
                          "5:3",
                          "1:2"
                        ]
                      },
                      atlas: {
                        types: [
                          "asset"
                        ]
                      },
                      sprite: {
                        types: [
                          "string"
                        ]
                      },
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      thumbnailUrl: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "url",
                      "role",
                      "category"
                    ]
                  },
                  stateMachine: {
                    types: [
                      "object"
                    ],
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      states: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      currentState: {
                        types: [
                          "string"
                        ]
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            from: {
                              types: [
                                "string"
                              ]
                            },
                            to: {
                              types: [
                                "string"
                              ]
                            },
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            guardHint: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "from",
                            "to",
                            "event"
                          ]
                        }
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "states",
                      "currentState",
                      "transitions"
                    ]
                  }
                },
                required: [
                  "id",
                  "name",
                  "category"
                ]
              }
            }
          ]
        },
        onSlotRemove: {
          types: [
            "function"
          ],
          description: "Called when a slot is cleared (callback path)",
          kind: "callback",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ]
        },
        slotDropEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{slotDropEvent} with { slotNumber, itemId } when an item is dropped (declarative path)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            },
            {
              name: "itemId",
              type: "string",
              required: true
            }
          ]
        },
        slotRemoveEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{slotRemoveEvent} with { slotNumber } when a slot is cleared (declarative path)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            }
          ]
        },
        playing: {
          types: [
            "boolean"
          ],
          description: "Whether the sequence is currently playing (disable interaction)",
          default: false
        },
        currentStep: {
          types: [
            "number"
          ],
          description: "Current step index during playback (-1 = not playing)",
          default: -1
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        slotFeedback: {
          types: [
            "array"
          ],
          description: "Per-slot correctness feedback shown after a failed attempt",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "correct",
              "wrong"
            ]
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "lg"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dashboard-grid": {
      type: "dashboard-grid",
      category: "game",
      sourceFile: "components/core/organisms/layout/DashboardGrid.tsx",
      tier: "organisms",
      family: "core",
      description: "DashboardGrid Component  Multi-column grid for widgets and stats cards. Supports cell spanning for flexible dashboard layouts.  Uses wireframe theme styling (high contrast, sharp edges).",
      suggestedFor: [
        "dashboard",
        "grid",
        "dashboard grid"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out. Each cell is a tile.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        columns: {
          types: [
            "number"
          ],
          description: "Number of columns",
          numericEnumValues: [
            2,
            3,
            4
          ],
          default: 3
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between cells",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        cells: {
          types: [
            "array"
          ],
          description: "Cell definitions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              colSpan: {
                types: [
                  "number"
                ],
                numericEnumValues: [
                  1,
                  2,
                  3,
                  4
                ]
              },
              rowSpan: {
                types: [
                  "number"
                ],
                numericEnumValues: [
                  1,
                  2
                ]
              }
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "split-pane": {
      type: "split-pane",
      category: "layout",
      sourceFile: "components/core/organisms/layout/SplitPane.tsx",
      tier: "organisms",
      family: "core",
      description: "SplitPane component",
      suggestedFor: [
        "split",
        "pane",
        "split pane"
      ],
      typicalSize: "large",
      propsSchema: {
        direction: {
          types: [
            "string"
          ],
          description: "Direction of the split",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        ratio: {
          types: [
            "number"
          ],
          description: "Initial ratio (0-100, percentage of first pane)"
        },
        minSize: {
          types: [
            "number"
          ],
          description: "Minimum size of either pane in pixels",
          default: 100
        },
        resizable: {
          types: [
            "boolean"
          ],
          description: "Allow user resizing",
          default: true
        },
        left: {
          types: [
            "node"
          ],
          description: "Content for the left/top pane",
          required: true
        },
        right: {
          types: [
            "node"
          ],
          description: "Content for the right/bottom pane",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        leftClassName: {
          types: [
            "string"
          ],
          description: "Class for left/top pane"
        },
        rightClassName: {
          types: [
            "string"
          ],
          description: "Class for right/bottom pane"
        },
        onRatioChange: {
          types: [
            "function"
          ],
          description: "Called with the new ratio while dragging. Providing this makes `ratio` controlled \u2014 the rendered split always tracks the prop instead of internal state, mirroring a controlled input. Omit for uncontrolled (self-tracked) behavior.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "ratio",
              type: "number"
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "tabbed-container": {
      type: "tabbed-container",
      category: "navigation",
      sourceFile: "components/core/organisms/layout/TabbedContainer.tsx",
      tier: "organisms",
      family: "core",
      description: "TabbedContainer component",
      suggestedFor: [
        "tabbed",
        "container",
        "tabbed container"
      ],
      typicalSize: "large",
      propsSchema: {
        tabs: {
          types: [
            "array"
          ],
          description: "Tab definitions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              sectionId: {
                types: [
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        defaultTab: {
          types: [
            "string"
          ],
          description: "Default active tab ID"
        },
        activeTab: {
          types: [
            "string"
          ],
          description: "Controlled active tab"
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "Callback when tab changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "tabId",
              type: "string"
            }
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Tab position",
          enumValues: [
            "top",
            "left"
          ],
          default: "top"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "master-detail-layout": {
      type: "master-detail-layout",
      category: "display",
      sourceFile: "components/core/organisms/layout/MasterDetailLayout.tsx",
      tier: "organisms",
      family: "core",
      description: "MasterDetail Component  Classic master-detail pattern with a list on the left and selected item detail on the right.  Uses wireframe theme styling (high contrast, sharp edges).",
      suggestedFor: [
        "master",
        "detail",
        "layout",
        "master detail layout"
      ],
      typicalSize: "large",
      propsSchema: {
        master: {
          types: [
            "node"
          ],
          description: "Master panel content (usually a list)",
          required: true
        },
        detail: {
          types: [
            "node"
          ],
          description: "Detail panel content",
          required: true
        },
        emptyDetail: {
          types: [
            "node"
          ],
          description: "Content shown when nothing is selected"
        },
        hasSelection: {
          types: [
            "boolean"
          ],
          description: "Whether an item is currently selected",
          default: false
        },
        masterWidth: {
          types: [
            "string"
          ],
          description: "Width of master panel (e.g., '350px', '30%')",
          default: "350px"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        masterClassName: {
          types: [
            "string"
          ],
          description: "Class for master pane"
        },
        detailClassName: {
          types: [
            "string"
          ],
          description: "Class for detail pane"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    sparkline: {
      type: "sparkline",
      category: "component",
      sourceFile: "components/core/atoms/Sparkline.tsx",
      tier: "atoms",
      family: "core",
      description: "Sparkline Atom Component  Minimal inline trend chart. Pure SVG polyline over a numeric series. Extracted from StatCard's sparkline path so any KPI or list row can include a trend visualization without compositing the whole card.",
      suggestedFor: [
        "sparkline"
      ],
      typicalSize: "small",
      propsSchema: {
        data: {
          types: [
            "array"
          ],
          description: "Numeric series to plot. Length < 2 renders nothing.",
          required: true,
          items: {
            types: [
              "number"
            ]
          }
        },
        color: {
          types: [
            "string"
          ],
          description: "Stroke color. `auto` (default): success when the series trends up (last \u2265 first), error when it trends down.",
          enumValues: [
            "auto",
            "primary",
            "success",
            "warning",
            "error",
            "info",
            "muted"
          ],
          default: "auto"
        },
        width: {
          types: [
            "number"
          ],
          description: "SVG width in px. Default 80.",
          default: 80
        },
        height: {
          types: [
            "number"
          ],
          description: "SVG height in px. Default 32.",
          default: 32
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width in px. Default 2.",
          default: 2
        },
        fill: {
          types: [
            "boolean"
          ],
          description: "Fill the area under the line with the stroke color at low opacity.",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "date-range-picker": {
      type: "date-range-picker",
      category: "component",
      sourceFile: "components/core/molecules/DateRangePicker.tsx",
      tier: "molecules",
      family: "core",
      description: "DateRangePicker component",
      suggestedFor: [
        "date",
        "range",
        "picker",
        "date range picker"
      ],
      typicalSize: "medium",
      propsSchema: {
        from: {
          types: [
            "string"
          ],
          description: "Controlled `from` (ISO date YYYY-MM-DD)."
        },
        to: {
          types: [
            "string"
          ],
          description: "Controlled `to` (ISO date YYYY-MM-DD)."
        },
        event: {
          types: [
            "string"
          ],
          description: "Event name emitted as `UI:{event}` with payload `{ from, to }`. Optional \u2014 when omitted, only the `onChange` callback fires.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "from",
              type: "string",
              required: true
            },
            {
              name: "to",
              type: "string",
              required: true
            }
          ]
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Callback fired alongside the bus emit.",
          kind: "callback",
          callbackArgs: [
            {
              name: "range",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "from",
                  "to"
                ]
              }
            }
          ]
        },
        presets: {
          types: [
            "array"
          ],
          description: "Preset shortcuts. Defaults to the translated standard finance set (Last 7d / Last 30d / This Month / This Quarter / YTD). Pass an empty array to hide presets entirely.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              range: {
                types: [
                  "function",
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "from",
                  "to"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        fromLabel: {
          types: [
            "string"
          ],
          description: "From-input label."
        },
        toLabel: {
          types: [
            "string"
          ],
          description: "To-input label."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    aside: {
      type: "aside",
      category: "component",
      sourceFile: "components/core/atoms/Aside.tsx",
      tier: "atoms",
      family: "core",
      description: "Aside \u2014 a secondary side panel that hosts navigation links or supplementary content alongside a main content area.",
      capabilities: "settings navigation sidebar, preferences menu panel, account settings nav, secondary panel, side navigation rail",
      suggestedFor: [
        "aside"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Aside contents"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    dialog: {
      type: "dialog",
      category: "container",
      sourceFile: "components/core/atoms/Dialog.tsx",
      tier: "atoms",
      family: "core",
      description: 'Dialog Atom Component  Semantic wrapper for the native `<dialog>` element. Owns the raw HTML rendering so molecules (Modal, ConfirmDialog, etc.) can compose the dialog primitive without dropping back to raw HTML.  Defaults `role="dialog"` and `aria-modal="true"`; both overridable for non-modal use cases.',
      suggestedFor: [
        "dialog"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Dialog contents"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "tag-input": {
      type: "tag-input",
      category: "form",
      sourceFile: "components/core/molecules/TagInput.tsx",
      tier: "molecules",
      family: "core",
      description: "TagInput component",
      suggestedFor: [
        "tag",
        "input",
        "tag input"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "array"
          ],
          description: "Current list of tags.",
          required: true,
          items: {
            types: [
              "string"
            ]
          }
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct callback emitted on every change. Stays as the Storybook / non-trait contract; trait-driven schemas should prefer the bus events below.",
          kind: "callback",
          callbackArgs: [
            {
              name: "next",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        placeholder: {
          types: [
            "string"
          ],
          description: 'Placeholder for the entry input. Default: `"Type and press Enter\u2026"`.'
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable add + remove interactions.",
          default: false
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant applied to each chip Badge. Default: `'default'`.",
          enumValues: [
            "default",
            "primary",
            "secondary",
            "success",
            "warning",
            "danger",
            "error",
            "info",
            "neutral"
          ],
          default: "default"
        },
        unique: {
          types: [
            "boolean"
          ],
          description: "Suppress duplicate entries. Default: `true`.",
          default: true
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text rendered under the input."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the outer container."
        },
        addEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a tag is added: `UI:{addEvent}` with payload `{ tag: string, value: string[] }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "tag",
              type: "string",
              required: true
            },
            {
              name: "value",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        removeEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a tag is removed: `UI:{removeEvent}` with payload `{ tag: string, index: number, value: string[] }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "tag",
              type: "string",
              required: true
            },
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "value",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "table-view": {
      type: "table-view",
      category: "display",
      sourceFile: "components/core/molecules/TableView.tsx",
      tier: "molecules",
      family: "core",
      description: "TableView \u2014 sortable, selectable data table rendering rows over configurable columns, with inline row actions and grouping.",
      capabilities: "admin console table, records list, CRUD data table, user list, manage-users grid, sortable columns, row selection with bulk actions, grouped list view",
      fieldsContract: "display",
      minUsableWidth: 240,
      suggestedFor: [
        "table",
        "view",
        "table view"
      ],
      typicalSize: "medium",
      propsSchema: {
        dragGroup: {
          types: [
            "string"
          ],
          description: "When set, items in this container are draggable and announce this group as their source"
        },
        accepts: {
          types: [
            "string"
          ],
          description: "When set, the container accepts drops whose dragGroup matches this value (or '*' for any group)"
        },
        sortable: {
          types: [
            "boolean"
          ],
          description: "Enable in-container reorder; items can be dragged to new positions among siblings"
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on cross-container drop. Payload: { id, sourceGroup, targetGroup, newIndex }"
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on in-container reorder. Payload: { id, oldIndex, newIndex }"
        },
        positionEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired N times per drop (one per item in affected zones) so the trait can persist new positions for the WHOLE zone, not just the moved item. Payload: { id, position }"
        },
        dndItemIdField: {
          types: [
            "string"
          ],
          description: "Row field used as the @dnd-kit draggable id (default 'id')"
        },
        dndRoot: {
          types: [
            "boolean"
          ],
          description: "Mark this container as the DnD root for its subtree \u2014 descendant zones share one DndContext (cross-container drag). Use on an outer container whose own items are not draggable."
        },
        emptyIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Domain icon for the empty state."
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: 'Specific title ("No orders yet").'
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "What will appear here once rows exist."
        },
        emptyAction: {
          types: [
            "object"
          ],
          description: "Create-the-first-item action.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema entity data \u2014 the collection of rows to render.",
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        columns: {
          types: [
            "array"
          ],
          description: "Column definitions. The compiler emits `columns`; `fields` is the alias.",
          items: {
            types: [
              "object"
            ],
            properties: {
              key: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "string"
                ],
                enumValues: [
                  "normal",
                  "medium",
                  "semibold"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "badge",
                  "date",
                  "currency",
                  "number",
                  "percent",
                  "boolean"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              sortable: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "key"
            ]
          }
        },
        fields: {
          types: [
            "array"
          ],
          description: "Alias for `columns`.",
          items: {
            types: [
              "object"
            ],
            properties: {
              key: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "string"
                ],
                enumValues: [
                  "normal",
                  "medium",
                  "semibold"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "badge",
                  "date",
                  "currency",
                  "number",
                  "percent",
                  "boolean"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              sortable: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "key"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Per-row actions, collected under a trailing kebab overflow menu.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: "maxInlineActions prop"
        },
        itemClickEvent: {
          types: [
            "string"
          ],
          description: "When set, the whole row is clickable and emits UI:{itemClickEvent} with { id, row } (action-button clicks stopPropagation so they still win). Mirrors DataList's contract. Omit = rows are not clickable.",
          kind: "event-ref",
          entityRowField: "row",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "row",
              type: "@entity",
              required: true
            }
          ]
        },
        selectable: {
          types: [
            "boolean"
          ],
          description: "Render a leading checkbox column. Selection changes emit `selectEvent`.",
          default: false
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on selection change: UI:{selectEvent} with { selectedIds }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedIds",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently-selected ids (controlled). Falls back to local state when omitted.",
          items: {
            types: [
              "string"
            ]
          }
        },
        sortEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on sortable-header click: UI:{sortEvent} with { column, direction }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "column",
              type: "string",
              required: true
            },
            {
              name: "direction",
              type: "string",
              required: true,
              enumValues: [
                "asc",
                "desc"
              ]
            }
          ]
        },
        sortColumn: {
          types: [
            "string"
          ],
          description: "Current sort column (display hint for the active header arrow)."
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction (display hint).",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the table root."
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: "Message shown when there are no rows."
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state.",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "table",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state.",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        groupBy: {
          types: [
            "string"
          ],
          description: "Group rows under section headers by a field value."
        },
        pageSize: {
          types: [
            "number"
          ],
          description: 'Max rows before a "Show More" button. Defaults to 0 (show all).',
          default: 0
        },
        children: {
          types: [
            "function"
          ],
          description: "Custom per-row render. When provided, the whole row content is delegated to this function (columns are ignored for the body; the header still renders). Mirrors DataList's children render prop.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-row render function (schema alias). In .orb: ["fn","item",{...}]. The compiler converts this to the children render prop. In .lolo, author the per-row renderer as renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 mirrors the data-list / entity-table look enum so authors share one knob name across row renderers.",
          enumValues: [
            "dense",
            "spacious",
            "striped",
            "borderless",
            "bordered"
          ],
          default: "dense"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names for a column whose field is relation-typed. Same contract DetailPanel takes.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "stat-card": {
      type: "stat-card",
      category: "display",
      sourceFile: "components/core/organisms/StatCard.tsx",
      tier: "organisms",
      family: "core",
      description: "StatCard component",
      suggestedFor: [
        "stat",
        "card",
        "stat card"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Entity data (single record or collection) used to compute metrics.",
          kind: "entity",
          cardinality: "collection"
        },
        label: {
          types: [
            "string"
          ],
          description: "Main label"
        },
        title: {
          types: [
            "string"
          ],
          description: "Title (alias for label)"
        },
        value: {
          types: [
            "string",
            "number",
            "array"
          ],
          description: "Primary value - accepts array from generated code (will use first element or length)",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        previousValue: {
          types: [
            "number"
          ],
          description: "Previous value for comparison"
        },
        currentValue: {
          types: [
            "number"
          ],
          description: "Current value as number for trend calculation"
        },
        trend: {
          types: [
            "number"
          ],
          description: "Manual trend percentage (overrides calculation)"
        },
        trendDirection: {
          types: [
            "string"
          ],
          description: "Trend direction (overrides calculation)",
          enumValues: [
            "up",
            "down",
            "neutral"
          ]
        },
        invertTrend: {
          types: [
            "boolean"
          ],
          description: "Whether up is good (green) or bad (red)",
          default: false
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon to display (Lucide component or icon name string)"
        },
        iconBg: {
          types: [
            "string"
          ],
          description: "Icon background color",
          default: "bg-muted"
        },
        iconColor: {
          types: [
            "string"
          ],
          description: "Icon color",
          default: "text-foreground"
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Subtitle or description"
        },
        action: {
          types: [
            "object"
          ],
          description: "Action button",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ]
            },
            navigatesTo: {
              types: [
                "string"
              ]
            },
            onClick: {
              types: [
                "function"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        metrics: {
          types: [
            "array"
          ],
          description: "Metrics to display (schema format) - accepts readonly for compatibility with generated const arrays",
          items: {
            types: [
              "object"
            ],
            properties: {
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              aggregate: {
                types: [
                  "string"
                ],
                enumValues: [
                  "count",
                  "sum"
                ]
              },
              filter: {
                types: [
                  "object"
                ],
                properties: {
                  field: {
                    types: [
                      "string"
                    ]
                  },
                  equals: {
                    types: [
                      "string",
                      "number",
                      "boolean"
                    ]
                  }
                },
                required: [
                  "field",
                  "equals"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact display mode"
        },
        sparklineData: {
          types: [
            "array"
          ],
          description: "Sparkline data points for an inline trend chart",
          items: {
            types: [
              "number"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "activation-block": {
      type: "activation-block",
      category: "component",
      sourceFile: "components/core/molecules/ActivationBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "ActivationBlock component",
      suggestedFor: [
        "activation",
        "block",
        "activation block"
      ],
      typicalSize: "medium",
      propsSchema: {
        question: {
          types: [
            "string"
          ],
          description: "The prior-knowledge question",
          required: true
        },
        savedResponse: {
          types: [
            "string"
          ],
          description: "Pre-filled response from saved state"
        },
        saveEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted on save/skip (as `UI:<saveEvent>`). Defaults to 'SAVE_ACTIVATION'.",
          default: "SAVE_ACTIVATION"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "bloom-quiz-block": {
      type: "bloom-quiz-block",
      category: "component",
      sourceFile: "components/core/molecules/BloomQuizBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "BloomQuizBlock component",
      suggestedFor: [
        "bloom",
        "quiz",
        "block",
        "bloom quiz block"
      ],
      typicalSize: "medium",
      propsSchema: {
        level: {
          types: [
            "string"
          ],
          description: "level prop",
          required: true,
          enumValues: [
            "remember",
            "understand",
            "apply",
            "analyze",
            "evaluate",
            "create"
          ]
        },
        question: {
          types: [
            "string"
          ],
          description: "question prop",
          required: true
        },
        answer: {
          types: [
            "string"
          ],
          description: "answer prop",
          required: true
        },
        index: {
          types: [
            "number"
          ],
          description: "Zero-based index (used in the emitted event payload)"
        },
        isAnswered: {
          types: [
            "boolean"
          ],
          description: "Whether the learner has already answered"
        },
        answerEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted on first reveal (as `UI:<answerEvent>`). Defaults to 'ANSWER_BLOOM'.",
          default: "ANSWER_BLOOM"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "connection-block": {
      type: "connection-block",
      category: "component",
      sourceFile: "components/core/molecules/ConnectionBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "ConnectionBlock component",
      suggestedFor: [
        "connection",
        "block",
        "connection block"
      ],
      typicalSize: "medium",
      propsSchema: {
        content: {
          types: [
            "string"
          ],
          description: "Markdown content summarising what the learner already knows",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "reflection-block": {
      type: "reflection-block",
      category: "component",
      sourceFile: "components/core/molecules/ReflectionBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "ReflectionBlock component",
      suggestedFor: [
        "reflection",
        "block",
        "reflection block"
      ],
      typicalSize: "medium",
      propsSchema: {
        prompt: {
          types: [
            "string"
          ],
          description: "The reflection prompt",
          required: true
        },
        index: {
          types: [
            "number"
          ],
          description: "Zero-based index of this block (used in the emitted event payload)",
          required: true
        },
        savedNote: {
          types: [
            "string"
          ],
          description: "Pre-filled note from saved state"
        },
        saveEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted on save (as `UI:<saveEvent>`). Defaults to 'SAVE_REFLECTION'.",
          default: "SAVE_REFLECTION"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "code-runner-panel": {
      type: "code-runner-panel",
      category: "display",
      sourceFile: "components/core/organisms/CodeRunnerPanel.tsx",
      tier: "organisms",
      family: "core",
      description: "CodeRunnerPanel component",
      suggestedFor: [
        "code",
        "runner",
        "panel",
        "code runner panel"
      ],
      typicalSize: "large",
      propsSchema: {
        code: {
          types: [
            "string"
          ],
          description: "Initial code content",
          required: true
        },
        language: {
          types: [
            "string"
          ],
          description: "Programming language for syntax highlighting",
          required: true
        },
        runnable: {
          types: [
            "boolean"
          ],
          description: "Whether the panel is editable and runnable (default false = read-only code block)",
          default: false
        },
        runEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on Run as `UI:<runEvent>` with `{ code, language, runId }`. Defaults to 'RUN_CODE'.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "code",
              type: "string",
              required: true
            },
            {
              name: "language",
              type: "string",
              required: true
            },
            {
              name: "runId",
              type: "string",
              required: true
            }
          ],
          default: "RUN_CODE"
        },
        resetEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on Reset as `UI:<resetEvent>` with `{ runId }`; Reset only restores the code when absent.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "runId",
              type: "string",
              required: true
            }
          ]
        },
        runId: {
          types: [
            "string"
          ],
          description: "Identifies this panel in its run/reset payloads (e.g. a lesson segment); empty when one panel stands alone.",
          default: ""
        },
        output: {
          types: [
            "object"
          ],
          description: "The program's result for the last run, bound back into the panel.",
          properties: {
            stdout: {
              types: [
                "string"
              ]
            },
            stderr: {
              types: [
                "string"
              ]
            },
            exitCode: {
              types: [
                "number"
              ]
            },
            testResults: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  input: {
                    types: [
                      "string"
                    ]
                  },
                  expectedOutput: {
                    types: [
                      "string"
                    ]
                  },
                  actualOutput: {
                    types: [
                      "string"
                    ]
                  },
                  passed: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "input",
                  "expectedOutput",
                  "actualOutput",
                  "passed"
                ]
              }
            }
          },
          propertyRequired: [
            "stdout",
            "stderr",
            "exitCode",
            "testResults"
          ],
          default: null
        },
        running: {
          types: [
            "boolean"
          ],
          description: "True while the program is running the code.",
          default: false
        },
        error: {
          types: [
            "string"
          ],
          description: "A run failure message from the program; replaces the output body.",
          default: null
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "segment-renderer": {
      type: "segment-renderer",
      category: "display",
      sourceFile: "components/core/organisms/SegmentRenderer.tsx",
      tier: "organisms",
      family: "core",
      description: "SegmentRenderer component",
      suggestedFor: [
        "segment",
        "renderer",
        "segment renderer"
      ],
      typicalSize: "large",
      propsSchema: {
        segments: {
          types: [
            "array"
          ],
          description: "Parsed lesson segments (see `parseLessonSegments`). Ignored when `lesson` is given.",
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "markdown"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "code"
                    ]
                  },
                  language: {
                    types: [
                      "string"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  runnable: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "language",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "quiz"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  answer: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "question",
                  "answer"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "activate"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "question"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "connect"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "reflect"
                    ]
                  },
                  prompt: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "prompt"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "bloom"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "remember",
                      "understand",
                      "apply",
                      "analyze",
                      "evaluate",
                      "create"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  answer: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "question",
                  "answer"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "visualization"
                    ]
                  },
                  visualizationType: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "algorithms",
                      "math",
                      "physics",
                      "biology",
                      "chemistry",
                      "probability"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "visualizationType",
                  "description"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        lesson: {
          types: [
            "string"
          ],
          description: "Raw lesson markdown with learning tags; parsed with `parseLessonSegments` and rendered"
        },
        activationSaveEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the activation prompt is saved or skipped (as `UI:<activationSaveEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "response",
              type: "string",
              required: true
            }
          ]
        },
        reflectionSaveEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a reflection note is saved (as `UI:<reflectionSaveEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "note",
              type: "string",
              required: true
            }
          ]
        },
        bloomAnswerEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on the first reveal of a Bloom question (as `UI:<bloomAnswerEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "level",
              type: "string",
              required: true,
              enumValues: [
                "remember",
                "understand",
                "apply",
                "analyze",
                "evaluate",
                "create"
              ]
            }
          ]
        },
        askEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Ask on selected text (as `UI:<askEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        noteEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Note on selected text (as `UI:<noteEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        askLabel: {
          types: [
            "string"
          ],
          description: "Ask action label (default: translated)"
        },
        noteLabel: {
          types: [
            "string"
          ],
          description: "Note action label (default: translated)"
        },
        annotations: {
          types: [
            "array"
          ],
          description: "Passages to highlight. Each `text` is matched exactly against the markdown segments, first segment whose source contains it, first occurrence inside one rendered text node (outside code); one that spans formatting is not highlighted.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "question",
                  "note"
                ]
              }
            },
            required: [
              "id",
              "text",
              "kind"
            ]
          }
        },
        annotationEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a highlighted passage is clicked (as `UI:<annotationEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes for the root container"
        },
        containerClassName: {
          types: [
            "string"
          ],
          description: "CSS classes for the outer wrapping div"
        },
        userProgress: {
          types: [
            "object"
          ],
          description: "User progress for restoring activation/reflection state",
          properties: {
            activationResponse: {
              types: [
                "string"
              ]
            },
            reflectionNotes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            bloomAnswered: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "boolean"
                ]
              }
            }
          }
        },
        runCodeEvent: {
          types: [
            "string"
          ],
          description: "Event a runnable code block emits on Run (`{ code, language, runId }`, runId = the segment index). Omit to render runnable blocks read-only.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "code",
              type: "string",
              required: true
            },
            {
              name: "language",
              type: "string",
              required: true
            },
            {
              name: "runId",
              type: "string",
              required: true
            }
          ]
        },
        resetCodeEvent: {
          types: [
            "string"
          ],
          description: "Event a runnable code block emits on Reset (`{ runId }`).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "runId",
              type: "string",
              required: true
            }
          ]
        },
        codeOutputs: {
          types: [
            "object"
          ],
          description: "The program's run results, keyed by segment runId.",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              stdout: {
                types: [
                  "string"
                ]
              },
              stderr: {
                types: [
                  "string"
                ]
              },
              exitCode: {
                types: [
                  "number"
                ]
              },
              testResults: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    input: {
                      types: [
                        "string"
                      ]
                    },
                    expectedOutput: {
                      types: [
                        "string"
                      ]
                    },
                    actualOutput: {
                      types: [
                        "string"
                      ]
                    },
                    passed: {
                      types: [
                        "boolean"
                      ]
                    }
                  },
                  required: [
                    "input",
                    "expectedOutput",
                    "actualOutput",
                    "passed"
                  ]
                }
              }
            },
            required: [
              "stdout",
              "stderr",
              "exitCode",
              "testResults"
            ]
          }
        },
        runningCode: {
          types: [
            "string"
          ],
          description: "The runId the program is currently running."
        },
        onRenderVisualization: {
          types: [
            "function"
          ],
          description: "Optional render slot for `visualization` segment types. When not provided, visualization segments are silently skipped. Callers can wire this to any custom component or orbital generator.",
          callbackArgs: [
            {
              name: "type",
              type: "string"
            },
            {
              name: "description",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "chat-bar": {
      type: "chat-bar",
      category: "display",
      sourceFile: "components/core/organisms/ChatBar.tsx",
      tier: "organisms",
      family: "core",
      description: "ChatBar component",
      suggestedFor: [
        "chat",
        "bar",
        "chat bar"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        status: {
          types: [
            "string"
          ],
          description: "Pipeline status",
          enumValues: [
            "idle",
            "running",
            "paused",
            "complete",
            "error"
          ],
          default: "idle"
        },
        activeGate: {
          types: [
            "string"
          ],
          description: 'Currently active gate label, e.g. "Gate 2: State machines"'
        },
        jepaValidity: {
          types: [
            "number"
          ],
          description: "JEPA validity probability 0-1"
        },
        runningIndicator: {
          types: [
            "node"
          ],
          description: "Shown beside the gate while `status` is running; defaults to a Spinner (a host passes its brand mark)."
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Input placeholder text"
        },
        context: {
          types: [
            "string"
          ],
          description: "Agent context description"
        },
        sendEvent: {
          types: [
            "string"
          ],
          description: 'The event a message is sent as \u2014 `UI:{sendEvent}` with `{ message }`, from Enter or the send button. @default "CHAT_SEND"',
          kind: "event",
          default: "CHAT_SEND"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "subagent-trace-panel": {
      type: "subagent-trace-panel",
      category: "display",
      sourceFile: "components/core/organisms/SubagentTracePanel.tsx",
      tier: "organisms",
      family: "core",
      description: "SubagentTracePanel component",
      suggestedFor: [
        "subagent",
        "trace",
        "panel",
        "subagent trace panel"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        subagents: {
          types: [
            "array"
          ],
          description: "All known subagents from the runner.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              role: {
                types: [
                  "string"
                ]
              },
              orbitalName: {
                types: [
                  "string"
                ]
              },
              parentId: {
                types: [
                  "string"
                ]
              },
              status: {
                types: [
                  "string"
                ],
                enumValues: [
                  "running",
                  "complete",
                  "error"
                ]
              },
              task: {
                types: [
                  "string"
                ]
              },
              messages: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    message: {
                      types: [
                        "string"
                      ]
                    },
                    tool: {
                      types: [
                        "string"
                      ]
                    },
                    timestamp: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "message",
                    "timestamp"
                  ]
                }
              },
              durationMs: {
                types: [
                  "number"
                ]
              },
              timeline: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  variants: [
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "message"
                          ]
                        },
                        role: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "user",
                            "assistant",
                            "system",
                            "tool"
                          ]
                        },
                        content: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        },
                        isStreaming: {
                          types: [
                            "boolean"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "role",
                        "content",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "tool_call"
                          ]
                        },
                        tool: {
                          types: [
                            "string"
                          ]
                        },
                        argsText: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        },
                        isExecuting: {
                          types: [
                            "boolean"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "tool",
                        "argsText",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "tool_result"
                          ]
                        },
                        tool: {
                          types: [
                            "string"
                          ]
                        },
                        resultText: {
                          types: [
                            "string"
                          ]
                        },
                        success: {
                          types: [
                            "boolean"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        },
                        durationMs: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "tool",
                        "resultText",
                        "success",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "file_operation"
                          ]
                        },
                        operation: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "ls",
                            "read_file",
                            "write_file",
                            "edit_file"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        success: {
                          types: [
                            "boolean"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "operation",
                        "path",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "schema_diff"
                          ]
                        },
                        filePath: {
                          types: [
                            "string"
                          ]
                        },
                        hunks: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              oldStart: {
                                types: [
                                  "number"
                                ]
                              },
                              oldLines: {
                                types: [
                                  "number"
                                ]
                              },
                              newStart: {
                                types: [
                                  "number"
                                ]
                              },
                              newLines: {
                                types: [
                                  "number"
                                ]
                              },
                              lines: {
                                types: [
                                  "array"
                                ],
                                items: {
                                  types: [
                                    "object"
                                  ],
                                  properties: {
                                    type: {
                                      types: [
                                        "string"
                                      ],
                                      enumValues: [
                                        "add",
                                        "remove",
                                        "context"
                                      ]
                                    },
                                    content: {
                                      types: [
                                        "string"
                                      ]
                                    }
                                  },
                                  required: [
                                    "type",
                                    "content"
                                  ]
                                }
                              }
                            },
                            required: [
                              "oldStart",
                              "oldLines",
                              "newStart",
                              "newLines",
                              "lines"
                            ]
                          }
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "filePath",
                        "hunks",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "error"
                          ]
                        },
                        message: {
                          types: [
                            "string"
                          ]
                        },
                        code: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "message",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "milestone"
                          ]
                        },
                        milestone: {
                          types: [
                            "string"
                          ]
                        },
                        summary: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "milestone",
                        "timestamp"
                      ]
                    }
                  ],
                  discriminator: "type"
                }
              }
            },
            required: [
              "id",
              "name",
              "role",
              "status",
              "task",
              "messages"
            ]
          }
        },
        focusedOrbital: {
          types: [
            "string"
          ],
          description: "Current canvas focus orbital \u2014 only used by overlay mode. Tab mode ignores this."
        },
        disclosureLevel: {
          types: [
            "number"
          ],
          description: "Drives density: 1-2 \u2192 compact, 3-4 \u2192 full activity embed (when mode='overlay').",
          required: true,
          numericEnumValues: [
            1,
            2,
            3,
            4
          ]
        },
        open: {
          types: [
            "boolean"
          ],
          description: "Whether the panel is visible. Typically auto-opens during generation in overlay mode.",
          required: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Optional dismiss handler.",
          kind: "callback",
          callbackArgs: []
        },
        coordinatorActivities: {
          types: [
            "array"
          ],
          description: "Full coordinator activities (tool calls, results, messages, errors).",
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "message"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "user",
                      "assistant",
                      "system"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isStreaming: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "role",
                  "content",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_call"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  argsText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isExecuting: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "argsText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_result"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  detail: {
                    types: [
                      "string"
                    ]
                  },
                  rows: {
                    types: [
                      "number"
                    ]
                  },
                  resultText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "success",
                  "resultText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "file_operation"
                    ]
                  },
                  operation: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ls",
                      "read_file",
                      "write_file",
                      "edit_file"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "operation",
                  "path",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_diff"
                    ]
                  },
                  filePath: {
                    types: [
                      "string"
                    ]
                  },
                  hunks: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        oldStart: {
                          types: [
                            "number"
                          ]
                        },
                        oldLines: {
                          types: [
                            "number"
                          ]
                        },
                        newStart: {
                          types: [
                            "number"
                          ]
                        },
                        newLines: {
                          types: [
                            "number"
                          ]
                        },
                        lines: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ],
                                enumValues: [
                                  "add",
                                  "remove",
                                  "context"
                                ]
                              },
                              content: {
                                types: [
                                  "string"
                                ]
                              }
                            },
                            required: [
                              "type",
                              "content"
                            ]
                          }
                        }
                      },
                      required: [
                        "oldStart",
                        "oldLines",
                        "newStart",
                        "newLines",
                        "lines"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "filePath",
                  "hunks",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "error"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "coordinator_decision"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  reason: {
                    types: [
                      "string"
                    ]
                  },
                  priorOrganism: {
                    types: [
                      "string"
                    ],
                    nullable: true
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "reason",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "plan_committed"
                    ]
                  },
                  orbitals: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitals",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pending_question"
                    ]
                  },
                  questionId: {
                    types: [
                      "string"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "questionId",
                  "question",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "clarification_question"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "organism",
                      "molecule",
                      "atom_trait"
                    ]
                  },
                  scope: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      }
                    }
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  candidates: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        id: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        description: {
                          types: [
                            "string"
                          ]
                        },
                        whyThisFits: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "id",
                        "label",
                        "description",
                        "whyThisFits"
                      ]
                    }
                  },
                  skipDefault: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "scope",
                  "question",
                  "candidates",
                  "skipDefault",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "analysis"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  organismReason: {
                    types: [
                      "string"
                    ]
                  },
                  complexity: {
                    types: [
                      "object"
                    ],
                    properties: {
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      reasoning: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "category",
                      "reasoning"
                    ]
                  },
                  renames: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to"
                      ]
                    }
                  },
                  deletes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "renames",
                  "deletes",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_started"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_done"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_change"
                    ]
                  },
                  changeKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital-added",
                      "orbital-removed",
                      "entity-fields-changed",
                      "trait-added",
                      "trait-removed",
                      "trait-config-changed",
                      "state-machine-changed",
                      "guard-changed",
                      "effect-changed",
                      "render-ui-changed",
                      "event-wiring-changed",
                      "page-changed",
                      "theme-changed",
                      "behavior-composed"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "changeKind",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "done"
                    ]
                  },
                  orbitalCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalCount",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "cancelled"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "llm_response"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  llm: {
                    types: [
                      "object"
                    ],
                    properties: {
                      service: {
                        types: [
                          "string"
                        ]
                      },
                      provider: {
                        types: [
                          "string"
                        ]
                      },
                      model: {
                        types: [
                          "string"
                        ]
                      },
                      systemPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      userPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      toolCallsJson: {
                        types: [
                          "string"
                        ]
                      },
                      attempt: {
                        types: [
                          "number"
                        ]
                      },
                      durationMs: {
                        types: [
                          "number"
                        ]
                      },
                      promptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      completionTokens: {
                        types: [
                          "number"
                        ]
                      },
                      cachedPromptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      costUSD: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "service",
                      "provider",
                      "model",
                      "systemPrompt",
                      "userPrompt",
                      "durationMs"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "content",
                  "timestamp"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        coordinatorMessages: {
          types: [
            "array"
          ],
          description: "Canonical Coordinator conversation \u2014 same shape live and on reload. When present, takes precedence over `coordinatorActivities` for the Coordinator section.",
          items: {
            types: [
              "object"
            ],
            properties: {
              role: {
                types: [
                  "string"
                ],
                enumValues: [
                  "system",
                  "user",
                  "assistant",
                  "tool"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              },
              toolCalls: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    args: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "object"
                        ],
                        jsonValueBrand: true
                      }
                    }
                  },
                  required: [
                    "id",
                    "name",
                    "args"
                  ]
                }
              },
              toolCallId: {
                types: [
                  "string"
                ]
              },
              toolName: {
                types: [
                  "string"
                ]
              },
              reasoningContent: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "role",
              "content"
            ]
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Rendering mode. - `'overlay'` (default): floating side panel positioned absolutely over the canvas. Density follows `disclosureLevel` (compact L1/L2, rich L3+). The `open` flag actually hides the panel when false. - `'tab'`: full-width content for a dedicated 'trace' supplemental tab. Always renders rich (regardless of disclosure level), always visible (the `open` flag is ignored), and includes an empty state when no subagents are present so users see the surface even before generation starts.",
          enumValues: [
            "overlay",
            "tab"
          ],
          default: "overlay"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "canvas-2d": {
      type: "canvas-2d",
      category: "game",
      sourceFile: "components/game/molecules/Canvas2D.tsx",
      tier: "molecules",
      family: "game",
      description: "Canvas2D component",
      suggestedFor: [
        "canvas2",
        "canvas2 d"
      ],
      typicalSize: "medium",
      propsSchema: {
        description: {
          types: [
            "string"
          ],
          description: "Text alternative for the scene, rendered as visually hidden text and linked by aria-describedby."
        },
        className: {
          types: [
            "string"
          ],
          description: "--- Closed-circuit ---"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        projection: {
          types: [
            "string"
          ],
          description: "--- Projection ---",
          enumValues: [
            "isometric",
            "hex",
            "flat",
            "free",
            "side"
          ],
          default: "isometric"
        },
        drawables: {
          types: [
            "array"
          ],
          description: "--- Scene ---",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        backgroundImage: {
          types: [
            "asset",
            "object"
          ],
          description: "Background image (tiled behind iso/hex/flat/free; cover-scaled for atlas slices). A bare URL string is accepted as shorthand for a standalone backdrop.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        tileClickEvent: {
          types: [
            "string"
          ],
          description: "--- Declarative events back to LOLO ---",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        unitClickEvent: {
          types: [
            "string"
          ],
          description: "Unit-click event. The neutral drawable host cannot resolve a click to a per-entity id (descriptors carry only a `ScenePos`, no id), so this prop is accepted but not yet emitted \u2014 the id-hit-test is a tracked fork (docs/Almadar_Std_Game_V2_PLAN.md).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "unitId",
              type: "string",
              required: true
            }
          ]
        },
        tileHoverEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{tileHoverEvent} with the unprojected scene { x, y } on hover.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        tileLeaveEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{tileLeaveEvent} with {} on pointer leave.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: []
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event (device-agnostic input), emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event, emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "--- Scene-edit mode (a game studio selecting/dragging drawables). Purely",
          default: false
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "The currently-selected drawable id (controlled); `null`/undefined = none.",
          default: null
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Fired on a click that changes selection \u2014 before `selectEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onMove: {
          types: [
            "function"
          ],
          description: "Fired once per drag gesture, on drop \u2014 before `moveEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "x",
              type: "number"
            },
            {
              name: "y",
              type: "number"
            }
          ]
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Declarative selection event, `UI:{selectEvent} { id }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        moveEvent: {
          types: [
            "string"
          ],
          description: "Declarative move event, `UI:{moveEvent} { id, x, y }` \u2014 fired once on drop.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        camera: {
          types: [
            "string"
          ],
          description: "--- View config (pure render) ---",
          enumValues: [
            "pan-zoom",
            "follow",
            "fixed"
          ],
          default: "pan-zoom"
        },
        scale: {
          types: [
            "number"
          ],
          description: "Render scale, legacy-squared semantics: on-screen cell \u2248 `256 \xD7 scale\xB2` px (the authored contract every board tuned its value for). Converted internally to the single camera zoom against the board's native tile width, so the cell pitch follows the asset while the on-screen size stays as authored. Ignored when `fit` is on; passed through raw for `free`/`side` (world-pixel-direct).",
          default: 0.4
        },
        tileWidth: {
          types: [
            "number"
          ],
          description: "Native tile/cell width in source px for this board's asset (e.g. 16 for Kenney tiny-dungeon, ~128 for iso blocks). The grid cell pitch follows the asset, so tile textures map 1:1 (crisp, no stretch). Defaults to the detected atlas tile width, else 256."
        },
        fit: {
          types: [
            "boolean"
          ],
          description: "Auto-fit the board's grid extent to the viewport (default false \u2014 boards render at their authored `scale` and overflow \u2192 pan). Opt in for whole-board-overview boards. User wheel/pinch zoom always wins after the initial fit.",
          default: false
        },
        showMinimap: {
          types: [
            "boolean"
          ],
          description: "Toggle minimap overlay.",
          default: true
        },
        followTarget: {
          types: [
            "object"
          ],
          description: "Follow-camera target in scene space (the neutral core `Camera.target`). When `camera:'follow'` the host lerps to keep this point centered.",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        cameraPos: {
          types: [
            "object"
          ],
          description: "Initial camera position in scene space (the neutral core `Camera.pos`). The host projects this to screen space and centers the viewport on it for the first render; ignored once the user pans or a follow target takes over.",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        bgColor: {
          types: [
            "string"
          ],
          description: "Solid backdrop colour \u2014 the base layer under any `backgroundImage`."
        },
        children: {
          types: [
            "node"
          ],
          description: "Declarative JSX drawable children (`<DrawShape .../>` composed in paint order). When `drawables` is empty, each child registers its descriptor via the drawable registry context and the host paints them."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawable: true,
      drawHost: true
    },
    "control-grid": {
      type: "control-grid",
      category: "game",
      sourceFile: "components/game/molecules/ControlGrid.tsx",
      tier: "molecules",
      family: "game",
      description: "ControlGrid component",
      suggestedFor: [
        "control",
        "grid",
        "control grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        kind: {
          types: [
            "string"
          ],
          description: "kind prop",
          required: true,
          enumValues: [
            "dpad",
            "actions"
          ]
        },
        buttons: {
          types: [
            "array"
          ],
          description: 'action buttons (kind="actions")',
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        layout: {
          types: [
            "string"
          ],
          description: 'Layout for kind="actions"',
          enumValues: [
            "horizontal",
            "vertical",
            "diamond"
          ],
          default: "horizontal"
        },
        includeDiagonals: {
          types: [
            "boolean"
          ],
          description: 'Whether to include diagonal buttons (kind="dpad")',
          default: false
        },
        onAction: {
          types: [
            "function"
          ],
          description: "Called when a button/direction is pressed or released",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "pressed",
              type: "boolean"
            }
          ]
        },
        actionEvent: {
          types: [
            "string"
          ],
          description: "Event-bus event emitted with `{ id, pressed }` on press/release",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "pressed",
              type: "boolean",
              required: true
            }
          ]
        },
        onDirection: {
          types: [
            "function"
          ],
          description: 'Called when a direction is pressed or released (kind="dpad" convenience overload)',
          kind: "callback",
          callbackArgs: [
            {
              name: "direction",
              type: "string"
            },
            {
              name: "pressed",
              type: "boolean"
            }
          ]
        },
        directionEvent: {
          types: [
            "string"
          ],
          description: 'Event-bus event emitted with `{ direction, pressed }` (kind="dpad")',
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "direction",
              type: "string",
              required: true,
              enumValues: [
                "up",
                "down",
                "left",
                "right"
              ]
            },
            {
              name: "pressed",
              type: "boolean",
              required: true
            }
          ]
        },
        directionEvents: {
          types: [
            "object"
          ],
          description: 'Per-direction PRESS \u2192 the board\'s SEMANTIC event (kind="dpad"), e.g. `{ left: "LEFT", right: "RIGHT", up: "JUMP" }`. Lets the d-pad emit the SAME intent events as the keyboard so the FSM stays device-agnostic.',
          mapKeyEnum: [
            "up",
            "down",
            "left",
            "right"
          ],
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        directionReleaseEvents: {
          types: [
            "object"
          ],
          description: 'Per-direction RELEASE \u2192 semantic event, e.g. `{ left: "STOP", right: "STOP" }`. Omit a direction to ignore its release.',
          mapKeyEnum: [
            "up",
            "down",
            "left",
            "right"
          ],
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        directionAssets: {
          types: [
            "object"
          ],
          description: 'Per-direction sprite assets for kind="dpad" buttons (e.g. Kenny arrow PNGs). Falls back to arrow emoji.',
          mapKeyEnum: [
            "up",
            "down",
            "left",
            "right"
          ],
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              url: {
                types: [
                  "asset"
                ]
              },
              role: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              animations: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              style: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pixel",
                  "vector",
                  "hd",
                  "1-bit",
                  "isometric"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              dimension: {
                types: [
                  "string"
                ],
                enumValues: [
                  "2d",
                  "3d"
                ]
              },
              aspect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "1:1",
                  "16:9",
                  "5:7",
                  "8:1",
                  "4:1",
                  "5:3",
                  "1:2"
                ]
              },
              atlas: {
                types: [
                  "asset"
                ]
              },
              sprite: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              thumbnailUrl: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "url",
              "role",
              "category"
            ]
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "disabled prop"
        },
        visibility: {
          types: [
            "string"
          ],
          description: '"auto" (default) renders only on coarse-pointer (touch) devices; "always" renders everywhere.',
          enumValues: [
            "auto",
            "always"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-icon": {
      type: "game-icon",
      category: "game",
      sourceFile: "components/core/atoms/GameIcon.tsx",
      tier: "atoms",
      family: "core",
      description: "GameIcon component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "game",
        "icon",
        "game icon"
      ],
      typicalSize: "small",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Asset \u2014 when url is present, renders an img (Asset-primary).",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide component or kebab-case icon name (fallback when no assetUrl).",
          required: true
        },
        size: {
          types: [
            "number",
            "string"
          ],
          description: "Pixel size or named variant.",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alt text for the img (defaults to assetUrl.category)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    vstack: {
      type: "vstack",
      category: "component",
      sourceFile: "components/core/atoms/Stack.tsx",
      tier: "atoms",
      family: "core",
      description: "VStack - Vertical stack shorthand",
      suggestedFor: [
        "stack",
        "v stack"
      ],
      typicalSize: "small",
      propsSchema: {
        gap: {
          types: [
            "string"
          ],
          description: "Gap between children",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items on the cross axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify items on the main axis",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        wrap: {
          types: [
            "boolean"
          ],
          description: "Allow items to wrap",
          default: false
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order of children",
          default: false
        },
        flex: {
          types: [
            "boolean"
          ],
          description: "Fill available space (flex: 1)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        onKeyDown: {
          types: [
            "function"
          ],
          description: "Keyboard handler",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        responsive: {
          types: [
            "boolean"
          ],
          description: "When true, horizontal stacks flip to vertical below the md breakpoint (768px)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    hstack: {
      type: "hstack",
      category: "component",
      sourceFile: "components/core/atoms/Stack.tsx",
      tier: "atoms",
      family: "core",
      description: "HStack - Horizontal stack shorthand",
      suggestedFor: [
        "stack",
        "h stack"
      ],
      typicalSize: "small",
      propsSchema: {
        gap: {
          types: [
            "string"
          ],
          description: "Gap between children",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items on the cross axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify items on the main axis",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        wrap: {
          types: [
            "boolean"
          ],
          description: "Allow items to wrap",
          default: false
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order of children",
          default: false
        },
        flex: {
          types: [
            "boolean"
          ],
          description: "Fill available space (flex: 1)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        onKeyDown: {
          types: [
            "function"
          ],
          description: "Keyboard handler",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        responsive: {
          types: [
            "boolean"
          ],
          description: "When true, horizontal stacks flip to vertical below the md breakpoint (768px)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-layout": {
      type: "form-layout",
      category: "form",
      sourceFile: "components/core/molecules/FormSection.tsx",
      tier: "molecules",
      family: "core",
      description: "Form layout with multiple sections",
      suggestedFor: [
        "form",
        "layout",
        "form layout"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "\u2500\u2500 Display hints (set by trait via render-ui, never written by organism) \u2500\u2500"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number (1-indexed)"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items (for pagination display)"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters, keyed by field name. Scalar-only (`string | number | boolean`) rather than the full `FieldValue` domain: `FieldValue` is a self-referential union (adds `Date`, nested arrays/objects) the pattern-sync scanner can't shape into a `Map string V` knob, so it always degraded to `json`. This narrows out date/array/nested-object filter values \u2014 no current call site in this package constructs this prop, so nothing here breaks, but a caller wiring a date-range or multi-select filter through this prop would need those. Flagged for the coordinator; revert to `Record<string, FieldValue>` if that capability is needed.",
          mapValue: {
            types: [
              "string",
              "number",
              "boolean"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        dividers: {
          types: [
            "boolean"
          ],
          description: "Show section dividers",
          default: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "learning-canvas": {
      type: "learning-canvas",
      category: "learning",
      sourceFile: "components/learning/atoms/LearningCanvas.tsx",
      tier: "atoms",
      family: "learning",
      description: "LearningCanvas component",
      suggestedFor: [
        "learning",
        "canvas",
        "learning canvas"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        width: {
          types: [
            "number"
          ],
          description: "Canvas width in CSS pixels.",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "Canvas height in CSS pixels.",
          default: 400
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "Canvas ground: a tone name, `var()` token or literal (default the theme's `--surface-diagram`)."
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette for this canvas: `series-1\u2026N` resolve to these colors instead. For real-world conventions (CPK atom colors, resistor bands); unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        fontFamily: {
          types: [
            "string"
          ],
          description: "Canvas text font family. Falls back to the theme's --font-family-body."
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Declarative shapes to draw.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        drawables: {
          types: [
            "array"
          ],
          description: "Neutral game-canvas drawables (e.g. `draw-sprite`, `draw-fx-layer`) painted in world coordinates via the supplied `projector`. This lets the learning canvas reuse the game drawable vocabulary without reimplementing sprites/FX.",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        projector: {
          types: [
            "object"
          ],
          description: "World-to-pixel projector for `drawables`. When absent, `drawables` are ignored.",
          properties: {
            tileWidth: {
              types: [
                "number"
              ]
            },
            floorHeight: {
              types: [
                "number"
              ]
            },
            diamondTopY: {
              types: [
                "number"
              ]
            },
            squareGrid: {
              types: [
                "boolean"
              ]
            },
            worldPixelDirect: {
              types: [
                "boolean"
              ]
            }
          },
          propertyRequired: [
            "tileWidth",
            "floorHeight",
            "diamondTopY",
            "squareGrid",
            "worldPixelDirect"
          ]
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row (live measurements, scores, counters).",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          },
          synonyms: "chips, stats, measurements"
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels stacked from the bottom-right corner.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          },
          synonyms: "sparkline, time series, history plot"
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` \u2014 to a SEMANTIC event emitted as `UI:{event}` (e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`); keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "Enable pointer interaction (click/hover).",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Enable continuous redraw loop.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "Clicked shape payload: { id?, type?, index }.",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        onShapeHover: {
          types: [
            "function"
          ],
          description: "Hovered shape payload: { id?, type?, index }.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        description: {
          types: [
            "string"
          ],
          description: "Text alternative for the canvas, rendered as visually hidden text and linked by aria-describedby."
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state."
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state.",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawHost: true
    },
    "biology-canvas": {
      type: "biology-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/BiologyCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "BiologyCanvas component",
      suggestedFor: [
        "biology",
        "canvas",
        "biology canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D raster (default) or 3D mesh scene via the lazy three.js host.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        camera: {
          types: [
            "object"
          ],
          description: "3D only: neutral camera pose ({ mode, zoom, fov, azimuth, target }).",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D only: scene light rig as data.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D only: post-processing stack (bloom/vignette).",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        nodes: {
          types: [
            "array"
          ],
          description: "nodes prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "cell",
                  "organelle",
                  "molecule",
                  "organism"
                ]
              },
              shape: {
                types: [
                  "string"
                ],
                enumValues: [
                  "box",
                  "sphere",
                  "capsule",
                  "cylinder",
                  "cone",
                  "torus",
                  "plane",
                  "circle",
                  "polyhedron"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "highlight",
                  "muted"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        edges: {
          types: [
            "array"
          ],
          description: "edges prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              directed: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        compartments: {
          types: [
            "array"
          ],
          description: "Organelle/membrane/cell-boundary ellipses (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "dashed",
                  "dotted"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y",
              "width",
              "height"
            ]
          },
          default: [],
          synonyms: "membrane, organelle region, cell boundary"
        },
        bands: {
          types: [
            "array"
          ],
          description: "Full-width horizontal background stripes, drawn first (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            }
          },
          default: [],
          synonyms: "trophic levels, zones, layers"
        },
        stages: {
          types: [
            "array"
          ],
          description: "Phase/step chips (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pending",
                  "active",
                  "done"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          },
          default: [],
          synonyms: "phases, timeline, cycle"
        },
        stageStyle: {
          types: [
            "string"
          ],
          description: "Layout for `stages`: a bottom timeline strip (default) or a radial ring.",
          enumValues: [
            "timeline",
            "ring"
          ],
          default: "timeline"
        },
        helix: {
          types: [
            "object"
          ],
          description: "A DNA double-helix ladder panel (2D only).",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            width: {
              types: [
                "number"
              ]
            },
            height: {
              types: [
                "number"
              ]
            },
            rungs: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  a: {
                    types: [
                      "string"
                    ]
                  },
                  b: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "paired",
                      "open",
                      "new"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                }
              }
            },
            fork: {
              types: [
                "number"
              ]
            },
            colorA: {
              types: [
                "string"
              ]
            },
            colorB: {
              types: [
                "string"
              ]
            },
            rungColor: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "rungs"
          ],
          synonyms: "DNA ladder, replication fork, base pairs"
        },
        helix3d: {
          types: [
            "object"
          ],
          description: "A 3D DNA double-helix ladder. 3D only.",
          properties: {
            count: {
              types: [
                "number"
              ]
            },
            rungs: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                }
              }
            },
            radius: {
              types: [
                "number"
              ]
            },
            rise: {
              types: [
                "number"
              ]
            },
            twistDeg: {
              types: [
                "number"
              ]
            },
            strandAColor: {
              types: [
                "string"
              ]
            },
            strandBColor: {
              types: [
                "string"
              ]
            },
            backboneRadius: {
              types: [
                "number"
              ]
            },
            rungRadius: {
              types: [
                "number"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            },
            unwoundCount: {
              types: [
                "number"
              ]
            },
            unwindSpread: {
              types: [
                "number"
              ]
            }
          }
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates (2D mode only \u2014 ignored in 3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D only: show the ground grid (default off)."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "3D only: enable shadows. Omitted \u2192 the host default."
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "2D: pointer interaction (click/hover). 3D: orbit camera controls."
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "2D only: continuous redraw loop. 3D motion is entity-state driven.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "chemistry-canvas": {
      type: "chemistry-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/ChemistryCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "ChemistryCanvas component",
      suggestedFor: [
        "chemistry",
        "canvas",
        "chemistry canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D raster (default) or 3D mesh scene via the lazy three.js host.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        camera: {
          types: [
            "object"
          ],
          description: "3D only: neutral camera pose ({ mode, zoom, fov, azimuth, target }).",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D only: scene light rig as data.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D only: post-processing stack (bloom/vignette).",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        atoms: {
          types: [
            "array"
          ],
          description: "atoms prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              element: {
                types: [
                  "string"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              charge: {
                types: [
                  "string"
                ]
              },
              lonePairs: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        bonds: {
          types: [
            "array"
          ],
          description: "bonds prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "single",
                  "double",
                  "triple"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "forming",
                  "breaking",
                  "highlight"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        arrows: {
          types: [
            "array"
          ],
          description: "arrows prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              angle: {
                types: [
                  "number"
                ]
              },
              length: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        bondStyle: {
          types: [
            "string"
          ],
          description: "Bond line rendering: 'thick' varies stroke width by order (default), 'parallel' draws offset parallel strokes. 2D only.",
          enumValues: [
            "thick",
            "parallel"
          ],
          default: "thick"
        },
        containers: {
          types: [
            "array"
          ],
          description: "Beaker/flask/container outlines (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              divider: {
                types: [
                  "string"
                ],
                enumValues: [
                  "none",
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              dividerColor: {
                types: [
                  "string"
                ]
              },
              leftLabel: {
                types: [
                  "string"
                ]
              },
              rightLabel: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              level: {
                types: [
                  "number"
                ]
              },
              levelColor: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "width",
              "height"
            ]
          },
          default: [],
          synonyms: "beaker, flask, box, membrane, burette"
        },
        equation: {
          types: [
            "string"
          ],
          description: "Reaction equation text centered at the top of the canvas. 2D only.",
          synonyms: "reaction equation, formula"
        },
        equationColor: {
          types: [
            "string"
          ],
          description: "Equation text color: a tone name, token or literal (default the `ink` tone)."
        },
        lattice3d: {
          types: [
            "object"
          ],
          description: "A 3D crystal lattice block (simple cubic, BCC, FCC, rock salt, diamond, ...). 3D only.",
          properties: {
            basis: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  key: {
                    types: [
                      "string"
                    ]
                  },
                  dx: {
                    types: [
                      "number"
                    ]
                  },
                  dy: {
                    types: [
                      "number"
                    ]
                  },
                  dz: {
                    types: [
                      "number"
                    ]
                  },
                  element: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  xEdge: {
                    types: [
                      "boolean"
                    ]
                  },
                  yEdge: {
                    types: [
                      "boolean"
                    ]
                  },
                  zEdge: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "key",
                  "dx",
                  "dy",
                  "dz"
                ]
              }
            },
            nx: {
              types: [
                "number"
              ]
            },
            ny: {
              types: [
                "number"
              ]
            },
            nz: {
              types: [
                "number"
              ]
            },
            latticeConstant: {
              types: [
                "number"
              ]
            },
            bonds: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  },
                  dx: {
                    types: [
                      "number"
                    ]
                  },
                  dy: {
                    types: [
                      "number"
                    ]
                  },
                  dz: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "from",
                  "to"
                ]
              }
            },
            bondRadius: {
              types: [
                "number"
              ]
            },
            highlightCell: {
              types: [
                "boolean"
              ]
            },
            dimColor: {
              types: [
                "string"
              ]
            },
            showLabels: {
              types: [
                "boolean"
              ]
            },
            selectedId: {
              types: [
                "string"
              ]
            },
            selectedColor: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "basis"
          ]
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates (2D mode only \u2014 ignored in 3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D only: show the ground grid (default off)."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "3D only: enable shadows. Omitted \u2192 the host default."
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "2D: pointer interaction (click/hover). 3D: orbit camera controls."
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "2D only: continuous redraw loop. 3D motion is entity-state driven.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "math-canvas": {
      type: "math-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/MathCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "MathCanvas component",
      suggestedFor: [
        "math",
        "canvas",
        "math canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        xMin: {
          types: [
            "number"
          ],
          description: "xMin prop",
          default: -10
        },
        xMax: {
          types: [
            "number"
          ],
          description: "xMax prop",
          default: 10
        },
        yMin: {
          types: [
            "number"
          ],
          description: "yMin prop",
          default: -10
        },
        yMax: {
          types: [
            "number"
          ],
          description: "yMax prop",
          default: 10
        },
        showAxes: {
          types: [
            "boolean"
          ],
          description: "showAxes prop",
          default: true
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "showGrid prop",
          default: true
        },
        gridStep: {
          types: [
            "number"
          ],
          description: "gridStep prop",
          default: 1
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "Canvas ground: a tone name, `var()` token or literal. Default the theme's `--surface-diagram`."
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        gridColor: {
          types: [
            "string"
          ],
          description: "Grid line color: a tone name, token or literal (default the `grid` tone).",
          default: "grid"
        },
        axisColor: {
          types: [
            "string"
          ],
          description: "Axis line color: a tone name, token or literal (default the `axis` tone).",
          default: "axis"
        },
        aspect: {
          types: [
            "string"
          ],
          description: "How the x and y ranges map to the canvas. `fit` stretches each range to fill the plot (function plots); `equal` uses one scale for both axes so circles stay round (geometry). Default `fit`.",
          enumValues: [
            "fit",
            "equal"
          ],
          default: "fit"
        },
        showTickLabels: {
          types: [
            "boolean"
          ],
          description: "Draw numeric labels on grid lines (default false).",
          default: false
        },
        tickLabelFontSize: {
          types: [
            "number"
          ],
          description: "Font size in px for axis tick labels (default the theme's `xs` step)."
        },
        labelFontSize: {
          types: [
            "number"
          ],
          description: "Font size in px for all other canvas annotations (default the theme's diagram label size)."
        },
        fontFamily: {
          types: [
            "string"
          ],
          description: 'Canvas text font family. Accepts a known game-font key (e.g. "future-narrow") or a CSS font-family string.'
        },
        showCurveLabels: {
          types: [
            "boolean"
          ],
          description: "Draw each curve's `label` at its last in-range sample (default false).",
          default: false
        },
        curves: {
          types: [
            "array"
          ],
          description: "curves prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              samples: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              }
            },
            required: [
              "samples"
            ]
          },
          default: []
        },
        points: {
          types: [
            "array"
          ],
          description: "points prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              style: {
                types: [
                  "string"
                ],
                enumValues: [
                  "closed",
                  "open"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        vectors: {
          types: [
            "array"
          ],
          description: "vectors prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "vx",
              "vy"
            ]
          },
          default: []
        },
        regions: {
          types: [
            "array"
          ],
          description: "Filled areas under curves or between curve pairs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              samples: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              samples2: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              baseline: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "samples"
            ]
          },
          default: []
        },
        bars: {
          types: [
            "array"
          ],
          description: "Riemann/histogram bar strips.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              y0: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "width",
              "y1"
            ]
          },
          default: []
        },
        guides: {
          types: [
            "array"
          ],
          description: "Fixed reference lines (vline/hline).",
          items: {
            types: [
              "object"
            ],
            properties: {
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "vline",
                  "hline"
                ]
              },
              at: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "kind",
              "at"
            ]
          },
          default: []
        },
        angles: {
          types: [
            "array"
          ],
          description: "Angle-sweep arcs at a vertex.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "from",
              "to"
            ]
          },
          default: []
        },
        hops: {
          types: [
            "array"
          ],
          description: "Number-line jump arcs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        drawables: {
          types: [
            "array"
          ],
          description: "Game-canvas drawables painted in math world coordinates (e.g. `draw-sprite` for a character sprite on the graph). The canvas maps their `ScenePos` through the same x/y world\u2192pixel transform used for curves/points.",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chips, forwarded verbatim to LearningCanvas.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded verbatim to LearningCanvas.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "interactive prop",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "animate prop",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event (device-agnostic input), emitted as `UI:{event}` \u2014 same contract as the game canvas keyMap; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event, emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawHost: true
    },
    "physics-canvas": {
      type: "physics-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/PhysicsCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "PhysicsCanvas component",
      suggestedFor: [
        "physics",
        "canvas",
        "physics canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D raster (default) or 3D mesh scene via the lazy three.js host.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        camera: {
          types: [
            "object"
          ],
          description: "3D only: neutral camera pose ({ mode, zoom, fov, azimuth, target }).",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D only: scene light rig as data.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D only: post-processing stack (bloom/vignette).",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        bodies: {
          types: [
            "array"
          ],
          description: "bodies prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              },
              fx: {
                types: [
                  "number"
                ]
              },
              fy: {
                types: [
                  "number"
                ]
              },
              fz: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        constraints: {
          types: [
            "array"
          ],
          description: "constraints prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "rod",
                  "spring",
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        showVelocity: {
          types: [
            "boolean"
          ],
          description: "showVelocity prop",
          default: true
        },
        showForces: {
          types: [
            "boolean"
          ],
          description: "showForces prop",
          default: false
        },
        velocityScale: {
          types: [
            "number"
          ],
          description: "velocityScale prop",
          default: 20
        },
        forceScale: {
          types: [
            "number"
          ],
          description: "forceScale prop",
          default: 20
        },
        sceneObjects: {
          types: [
            "array"
          ],
          description: "Ground/wall/ramp/box/pivot scene fixtures. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ground",
                  "wall",
                  "ramp",
                  "box",
                  "pivot"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "kind"
            ]
          },
          default: []
        },
        trails: {
          types: [
            "array"
          ],
          description: "Fading trajectory polylines (2D) / trajectory tubes (3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    },
                    z: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              color: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              fade: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "points"
            ]
          },
          default: []
        },
        vectors: {
          types: [
            "array"
          ],
          description: "Named force/velocity/free-body arrows, anchored by point or by body id. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              body: {
                types: [
                  "string"
                ]
              },
              dx: {
                types: [
                  "number"
                ]
              },
              dy: {
                types: [
                  "number"
                ]
              },
              scale: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "dashed",
                  "dotted"
                ]
              }
            },
            required: [
              "dx",
              "dy"
            ]
          },
          default: []
        },
        surface3d: {
          types: [
            "object"
          ],
          description: "A 3D height-field surface mesh (wave, terrain, potential well). 3D only.",
          properties: {
            nx: {
              types: [
                "number"
              ]
            },
            ny: {
              types: [
                "number"
              ]
            },
            heights: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "number"
                ]
              }
            },
            spacing: {
              types: [
                "number"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            bands: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  min: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "color"
                ]
              }
            },
            color: {
              types: [
                "string"
              ]
            },
            opacity: {
              types: [
                "number"
              ]
            },
            flatShading: {
              types: [
                "boolean"
              ]
            }
          },
          propertyRequired: [
            "nx",
            "ny",
            "heights"
          ]
        },
        vectors3d: {
          types: [
            "array"
          ],
          description: "Labeled 3D arrows (field arrows, axis triads). 3D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              dx: {
                types: [
                  "number"
                ]
              },
              dy: {
                types: [
                  "number"
                ]
              },
              dz: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y",
              "dx",
              "dy"
            ]
          },
          default: []
        },
        vectorScale: {
          types: [
            "number"
          ],
          description: "Multiplies every `vectors3d` entry's `dx`/`dy`/`dz` (default 1). 3D only.",
          default: 1
        },
        angles: {
          types: [
            "array"
          ],
          description: "Angle arcs between two bearings. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "from",
              "to"
            ]
          },
          default: []
        },
        field: {
          types: [
            "object"
          ],
          description: "A single uniform vector/into/out field lattice. 2D only.",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "arrows",
                "into",
                "out"
              ]
            },
            spacing: {
              types: [
                "number"
              ]
            },
            angle: {
              types: [
                "number"
              ]
            },
            size: {
              types: [
                "number"
              ]
            },
            color: {
              types: [
                "string"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            width: {
              types: [
                "number"
              ]
            },
            height: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "kind"
          ]
        },
        meters: {
          types: [
            "array"
          ],
          description: "Bottom-left labeled bar gauges. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          },
          default: []
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates (2D mode only \u2014 ignored in 3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D only: show the ground grid (default off)."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "3D only: enable shadows. Omitted \u2192 the host default."
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "2D: pointer interaction (click/hover). 3D: orbit camera controls."
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "2D only: continuous redraw loop. 3D motion is entity-state driven.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "state-graph": {
      type: "state-graph",
      category: "game",
      sourceFile: "components/game/molecules/StateGraph.tsx",
      tier: "molecules",
      family: "game",
      description: `StateGraph (molecule)  Dumb visual renderer for a state-machine graph: lays states out on a circle, draws the player's transitions as SVG arrows, and emits a node-click event. All state (which transitions exist, the pending "from" selection, test results) lives in the .lolo FSM \u2014 this molecule only renders and reports clicks.`,
      suggestedFor: [
        "state",
        "graph",
        "state graph"
      ],
      typicalSize: "medium",
      propsSchema: {
        states: {
          types: [
            "array"
          ],
          description: "All states in the machine (node labels).",
          required: true,
          items: {
            types: [
              "string"
            ]
          }
        },
        transitions: {
          types: [
            "array"
          ],
          description: "Player-built transitions rendered as arrows.",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ]
              },
              guardHint: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to",
              "event"
            ]
          },
          default: []
        },
        currentState: {
          types: [
            "string"
          ],
          description: "State highlighted as current (test playback / initial)."
        },
        selectedState: {
          types: [
            "string"
          ],
          description: "State the player has selected (first click)."
        },
        addingFrom: {
          types: [
            "string"
          ],
          description: 'When set, the graph is in "pick a target" mode from this state.'
        },
        initialState: {
          types: [
            "string"
          ],
          description: "The machine's initial state (ring-marked)."
        },
        width: {
          types: [
            "number"
          ],
          description: "Graph canvas width.",
          default: 500
        },
        height: {
          types: [
            "number"
          ],
          description: "Graph canvas height.",
          default: 400
        },
        nodeClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{nodeClickEvent} with { stateId } when a node is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stateId",
              type: "string",
              required: true
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "state-json-view": {
      type: "state-json-view",
      category: "game",
      sourceFile: "components/game/molecules/StateJsonView.tsx",
      tier: "molecules",
      family: "game",
      description: "StateJsonView Component  Shows a state machine's definition as an expandable code block, so the learner sees the declarative schema behind the visual graph. Dumb molecule: takes the machine as plain typed fields (name/states/initialState/transitions) \u2014 the .lolo organism owns the entity and the molecule renders + stringifies it.",
      suggestedFor: [
        "state",
        "json",
        "view",
        "state json view"
      ],
      typicalSize: "medium",
      propsSchema: {
        name: {
          types: [
            "string"
          ],
          description: "Machine name",
          required: true
        },
        initialState: {
          types: [
            "string"
          ],
          description: "Initial state",
          required: true
        },
        states: {
          types: [
            "array"
          ],
          description: "All state names",
          required: true,
          items: {
            types: [
              "string"
            ]
          }
        },
        transitions: {
          types: [
            "array"
          ],
          description: "The player's wired transitions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to",
              "event"
            ]
          }
        },
        label: {
          types: [
            "string"
          ],
          description: "Header label"
        },
        defaultExpanded: {
          types: [
            "boolean"
          ],
          description: "Whether the code block is expanded by default",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "algorithm-canvas": {
      type: "algorithm-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/AlgorithmCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "AlgorithmCanvas component",
      suggestedFor: [
        "algorithm",
        "canvas",
        "algorithm canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        bars: {
          types: [
            "array"
          ],
          description: "Sorting/histogram bars; laid out left-to-right, height proportional to value.",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value"
            ]
          },
          default: []
        },
        cells: {
          types: [
            "array"
          ],
          description: "Grid/array/DP cells; laid out on a row/col lattice sized to the extent.",
          items: {
            types: [
              "object"
            ],
            properties: {
              row: {
                types: [
                  "number"
                ]
              },
              col: {
                types: [
                  "number"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              corner: {
                types: [
                  "object"
                ],
                properties: {
                  tl: {
                    types: [
                      "string"
                    ]
                  },
                  tr: {
                    types: [
                      "string"
                    ]
                  },
                  bl: {
                    types: [
                      "string"
                    ]
                  },
                  br: {
                    types: [
                      "string"
                    ]
                  }
                }
              }
            },
            required: [
              "row",
              "col"
            ]
          },
          default: []
        },
        pointers: {
          types: [
            "array"
          ],
          description: "Index cursors (i/j/lo/hi/mid); drawn as markers beneath the referenced bar/slot column.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "index"
            ]
          },
          default: []
        },
        ranges: {
          types: [
            "array"
          ],
          description: "Highlighted index ranges over the bars panel (translucent fill bands or labeled brackets).",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "fill",
                  "bracket"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        slots: {
          types: [
            "array"
          ],
          description: "Discrete value slots (registers/variables); laid out in a row or a vertical stack.",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "empty",
                  "filled",
                  "highlight"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            }
          },
          default: []
        },
        slotOrientation: {
          types: [
            "string"
          ],
          description: "Slot stack direction; horizontal lays slots left-to-right, vertical stacks bottom-up.",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        frames: {
          types: [
            "array"
          ],
          description: "Recursion/call-stack frames; laid out bottom-up like a real call stack.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              detail: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "active",
                  "returning",
                  "done"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          },
          default: []
        },
        buckets: {
          types: [
            "array"
          ],
          description: "Hash-table buckets; each row is an index chip plus its chained entries.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              entries: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    state: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "default",
                        "highlight",
                        "probing"
                      ]
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "label"
                  ]
                }
              }
            },
            required: [
              "index",
              "entries"
            ]
          },
          default: []
        },
        auxBars: {
          types: [
            "array"
          ],
          description: "Secondary bar strip inside the bars panel (e.g. a running total beside the primary values).",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value"
            ]
          },
          default: []
        },
        rowLabels: {
          types: [
            "array"
          ],
          description: "Row-header labels drawn to the left of the cells grid.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "index",
              "text"
            ]
          },
          default: []
        },
        colLabels: {
          types: [
            "array"
          ],
          description: "Column-header labels drawn above the cells grid.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "index",
              "text"
            ]
          },
          default: []
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "interactive prop",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "animate prop",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "atlas-image": {
      type: "atlas-image",
      category: "component",
      sourceFile: "components/core/atoms/AtlasImage.tsx",
      tier: "atoms",
      family: "core",
      description: "AtlasImage component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "atlas",
        "image",
        "atlas image"
      ],
      typicalSize: "small",
      propsSchema: {
        asset: {
          types: [
            "object"
          ],
          description: "asset prop",
          properties: {
            url: {
              types: [
                "string"
              ]
            },
            atlas: {
              types: [
                "string"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          }
        },
        size: {
          types: [
            "number"
          ],
          description: "Square pixel size (icon usage). Ignored when `fill`."
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop"
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop"
        },
        fill: {
          types: [
            "boolean"
          ],
          description: "Absolutely fill the nearest positioned ancestor (background usage).",
          default: false
        },
        fit: {
          types: [
            "string"
          ],
          description: "fit prop",
          enumValues: [
            "contain",
            "cover",
            "fill"
          ],
          default: "contain"
        },
        alt: {
          types: [
            "string"
          ],
          description: "alt prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        style: {
          types: [
            "object"
          ],
          description: "style prop",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "aria-hidden prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "atlas-panel": {
      type: "atlas-panel",
      category: "component",
      sourceFile: "components/core/atoms/AtlasImage.tsx",
      tier: "atoms",
      family: "core",
      description: "AtlasPanel component",
      suggestedFor: [
        "atlas",
        "panel",
        "atlas panel"
      ],
      typicalSize: "small",
      propsSchema: {
        asset: {
          types: [
            "object"
          ],
          description: "asset prop",
          properties: {
            url: {
              types: [
                "string"
              ]
            },
            atlas: {
              types: [
                "string"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          }
        },
        borderSlice: {
          types: [
            "number"
          ],
          description: "Border thickness (source pixels) for the 9-slice cut.",
          default: 16
        },
        borderWidth: {
          types: [
            "number"
          ],
          description: "Rendered border width in CSS px.",
          default: 16
        },
        mode: {
          types: [
            "string"
          ],
          description: "'nineSlice' scales edges crisp (panels/buttons); 'repeat' tiles the slice (pattern fills).",
          enumValues: [
            "nineSlice",
            "repeat"
          ],
          default: "nineSlice"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        style: {
          types: [
            "object"
          ],
          description: "style prop",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop"
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "aria-hidden prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "draw-shape": {
      type: "draw-shape",
      category: "game",
      sourceFile: "components/game/atoms/DrawShape.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawShape component",
      suggestedFor: [
        "draw",
        "shape",
        "draw shape"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-shape"
          ]
        },
        shape: {
          types: [
            "string"
          ],
          description: "shape prop",
          required: true,
          enumValues: [
            "cell",
            "rect",
            "ellipse",
            "poly",
            "path"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        anchor: {
          types: [
            "string"
          ],
          description: "How the shape aligns to its projected position. Default varies by `shape`.",
          enumValues: [
            "top-left",
            "ground",
            "center"
          ]
        },
        width: {
          types: [
            "number"
          ],
          description: "Rect width in world units (fractions of `projector.tileWidth`)."
        },
        height: {
          types: [
            "number"
          ],
          description: "Rect height in world units (fractions of `projector.tileWidth`)."
        },
        radiusX: {
          types: [
            "number"
          ],
          description: "Ellipse horizontal radius in world units (fractions of `projector.tileWidth`)."
        },
        radiusY: {
          types: [
            "number"
          ],
          description: "Ellipse vertical radius in world units; omitted \u2192 `radiusX` (a circle)."
        },
        offsetX: {
          types: [
            "number"
          ],
          description: "Fine nudge in world units \u2014 applies to every shape kind except `cell` (rect/ellipse from their anchor point; poly/path from the projected top-left)."
        },
        offsetY: {
          types: [
            "number"
          ],
          description: "offsetY prop"
        },
        points: {
          types: [
            "array"
          ],
          description: "Poly vertices as world-unit offsets relative to the cell's projected top-left.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          }
        },
        d: {
          types: [
            "string"
          ],
          description: "SVG path data in world units relative to the cell's projected top-left \u2014 same coordinate convention as `points`."
        },
        fill: {
          types: [
            "string"
          ],
          description: "fill prop"
        },
        gradient: {
          types: [
            "object"
          ],
          description: "Gradient fill in world units; overrides `fill` when present.",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "linear",
                "radial",
                "conic"
              ]
            },
            from: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            to: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            center: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            radius: {
              types: [
                "number"
              ]
            },
            angle: {
              types: [
                "number"
              ]
            },
            stops: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  offset: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "offset",
                  "color"
                ]
              }
            }
          },
          propertyRequired: [
            "kind",
            "stops"
          ]
        },
        fillPattern: {
          types: [
            "object"
          ],
          description: "Repeating texture painted over the fill (grain / tiling image).",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "noise",
                "image"
              ]
            },
            url: {
              types: [
                "string"
              ]
            },
            scale: {
              types: [
                "number"
              ]
            },
            alpha: {
              types: [
                "number"
              ]
            },
            color: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "kind"
          ]
        },
        stroke: {
          types: [
            "string"
          ],
          description: "stroke prop"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop"
        },
        strokeGradient: {
          types: [
            "object"
          ],
          description: "Gradient stroke in world units; overrides `stroke` when present.",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "linear",
                "radial",
                "conic"
              ]
            },
            from: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            to: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            center: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            radius: {
              types: [
                "number"
              ]
            },
            angle: {
              types: [
                "number"
              ]
            },
            stops: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  offset: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "offset",
                  "color"
                ]
              }
            }
          },
          propertyRequired: [
            "kind",
            "stops"
          ]
        },
        strokeDash: {
          types: [
            "array"
          ],
          description: "Dash pattern in painter-px (constant under group scale, like `strokeWidth`).",
          items: {
            types: [
              "number"
            ]
          }
        },
        strokeDashOffset: {
          types: [
            "number"
          ],
          description: "Dash phase offset in painter-px; animatable (marching ants)."
        },
        blendMode: {
          types: [
            "string"
          ],
          description: "Composite/blend mode for this shape's draws (`lighter`, `multiply`, `screen`, \u2026).",
          enumValues: [
            "source-over",
            "lighter",
            "multiply",
            "screen",
            "overlay",
            "darken",
            "lighten",
            "color-dodge",
            "color-burn",
            "hard-light",
            "soft-light",
            "difference",
            "exclusion",
            "hue",
            "saturation",
            "color",
            "luminosity",
            "source-atop",
            "destination-over",
            "destination-out",
            "xor"
          ]
        },
        blur: {
          types: [
            "number"
          ],
          description: "Gaussian blur in painter-px. Expensive \u2014 forces an intermediate raster; use sparingly."
        },
        rotate: {
          types: [
            "number"
          ],
          description: "Rotation in radians (painter units, same as `draw-group`), about `pivot`."
        },
        pivot: {
          types: [
            "object"
          ],
          description: "Rotation pivot in world units relative to the cell's projected top-left; default `{x:0.5, y:0.5}` (cell center).",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ]
        },
        shadow: {
          types: [
            "object"
          ],
          description: "Soft glow / drop-shadow behind the shape; `blur` in world units.",
          properties: {
            color: {
              types: [
                "string"
              ]
            },
            blur: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "color",
            "blur"
          ]
        },
        animation: {
          types: [
            "object"
          ],
          description: "Keyframe animation over this shape's props; the host runs a paint clock while present.",
          properties: {
            durationMs: {
              types: [
                "number"
              ]
            },
            loop: {
              types: [
                "boolean"
              ]
            },
            keyframes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  at: {
                    types: [
                      "number"
                    ]
                  },
                  offsetX: {
                    types: [
                      "number"
                    ]
                  },
                  offsetY: {
                    types: [
                      "number"
                    ]
                  },
                  rotate: {
                    types: [
                      "number"
                    ]
                  },
                  opacity: {
                    types: [
                      "number"
                    ]
                  },
                  radiusX: {
                    types: [
                      "number"
                    ]
                  },
                  radiusY: {
                    types: [
                      "number"
                    ]
                  },
                  width: {
                    types: [
                      "number"
                    ]
                  },
                  height: {
                    types: [
                      "number"
                    ]
                  },
                  strokeWidth: {
                    types: [
                      "number"
                    ]
                  },
                  strokeDashOffset: {
                    types: [
                      "number"
                    ]
                  },
                  blur: {
                    types: [
                      "number"
                    ]
                  },
                  fill: {
                    types: [
                      "string"
                    ]
                  },
                  stroke: {
                    types: [
                      "string"
                    ]
                  },
                  shadow: {
                    types: [
                      "object"
                    ],
                    properties: {
                      color: {
                        types: [
                          "string"
                        ]
                      },
                      blur: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "color",
                      "blur"
                    ]
                  }
                },
                required: [
                  "at"
                ]
              }
            }
          },
          propertyRequired: [
            "durationMs",
            "keyframes"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        }
      },
      drawable: true
    },
    "draw-sprite": {
      type: "draw-sprite",
      category: "game",
      sourceFile: "components/game/atoms/DrawSprite.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawSprite component",
      suggestedFor: [
        "draw",
        "sprite",
        "draw sprite"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-sprite"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        asset: {
          types: [
            "object"
          ],
          description: "The image / atlas reference to blit.",
          required: true,
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        anchor: {
          types: [
            "string"
          ],
          description: "How the sprite aligns to its projected position. Default `'top-left'`.",
          enumValues: [
            "top-left",
            "ground",
            "center"
          ]
        },
        width: {
          types: [
            "number"
          ],
          description: "Draw width in world units (fractions of `projector.tileWidth`). Omitted \u2192 one cell on tile grids (`flat`/`iso`/`hex`); native source px on `free`/`side`."
        },
        height: {
          types: [
            "number"
          ],
          description: "Draw height in world units (fractions of `projector.tileWidth`). Omitted \u2192 one cell on tile grids (`flat`/`iso`/`hex`); native source px on `free`/`side`."
        },
        frame: {
          types: [
            "object"
          ],
          description: "Explicit atlas sub-rect override (px); omitted \u2192 resolved from `asset.atlas`/`asset.sprite`.",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            w: {
              types: [
                "number"
              ]
            },
            h: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y",
            "w",
            "h"
          ]
        },
        animation: {
          types: [
            "string"
          ],
          description: "Animation to play. 3D backend: a named GLB clip (matched case-insensitively). 2D painter: when `asset.atlas` resolves to a `SpriteSheetAtlas` manifest, the canonical row of that name (idle/walk/attack/hit/death) plays off the host paint clock; an explicit `frame` rect overrides."
        },
        loop: {
          types: [
            "boolean"
          ],
          description: "Override the sheet row's loop flag. The paint clock is wall-time, so one-shot rows hold their final frame \u2014 preview pages set `true` to cycle attack/hit/death continuously."
        },
        clockMs: {
          types: [
            "number"
          ],
          description: "Animation clock override in ms (e.g. an fx entry's age since spawn). Omitted \u2192 the host paint clock; one-shot rows anchored to wall-time would otherwise sit on their final frame."
        },
        flipX: {
          types: [
            "boolean"
          ],
          description: "Mirror horizontally (facing)."
        },
        rotation: {
          types: [
            "number"
          ],
          description: "Rotation in radians about the sprite's center."
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        },
        shadow: {
          types: [
            "object"
          ],
          description: "Drop shadow (e.g. a team-colored glow).",
          properties: {
            color: {
              types: [
                "string"
              ]
            },
            blur: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "color",
            "blur"
          ]
        }
      },
      drawable: true
    },
    "draw-text": {
      type: "draw-text",
      category: "game",
      sourceFile: "components/game/atoms/DrawText.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawText component",
      suggestedFor: [
        "draw",
        "text",
        "draw text"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-text"
          ]
        },
        text: {
          types: [
            "string"
          ],
          description: "text prop",
          required: true
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        anchor: {
          types: [
            "string"
          ],
          description: "anchor prop",
          enumValues: [
            "top-left",
            "ground",
            "center"
          ]
        },
        offsetX: {
          types: [
            "number"
          ],
          description: "offsetX prop"
        },
        offsetY: {
          types: [
            "number"
          ],
          description: "offsetY prop"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          required: true
        },
        font: {
          types: [
            "string"
          ],
          description: "font prop"
        },
        align: {
          types: [
            "string"
          ],
          description: "align prop",
          enumValues: [
            "center",
            "end",
            "left",
            "right",
            "start"
          ]
        },
        baseline: {
          types: [
            "string"
          ],
          description: "baseline prop",
          enumValues: [
            "alphabetic",
            "bottom",
            "hanging",
            "ideographic",
            "middle",
            "top"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop"
        }
      },
      drawable: true
    },
    "draw-shape-layer": {
      type: "draw-shape-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawShapeLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawShapeLayer component",
      suggestedFor: [
        "draw",
        "shape",
        "layer",
        "draw shape layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-shape-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: "items prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "draw-shape"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              shape: {
                types: [
                  "string"
                ],
                enumValues: [
                  "cell",
                  "rect",
                  "ellipse",
                  "poly",
                  "path"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              anchor: {
                types: [
                  "string"
                ],
                enumValues: [
                  "top-left",
                  "ground",
                  "center"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              radiusX: {
                types: [
                  "number"
                ]
              },
              radiusY: {
                types: [
                  "number"
                ]
              },
              offsetX: {
                types: [
                  "number"
                ]
              },
              offsetY: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              d: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              gradient: {
                types: [
                  "object"
                ],
                properties: {
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "linear",
                      "radial",
                      "conic"
                    ]
                  },
                  from: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  to: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  center: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  angle: {
                    types: [
                      "number"
                    ]
                  },
                  stops: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        offset: {
                          types: [
                            "number"
                          ]
                        },
                        color: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "offset",
                        "color"
                      ]
                    }
                  }
                },
                required: [
                  "kind",
                  "stops"
                ]
              },
              fillPattern: {
                types: [
                  "object"
                ],
                properties: {
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "noise",
                      "image"
                    ]
                  },
                  url: {
                    types: [
                      "string"
                    ]
                  },
                  scale: {
                    types: [
                      "number"
                    ]
                  },
                  alpha: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "kind"
                ]
              },
              stroke: {
                types: [
                  "string"
                ]
              },
              strokeWidth: {
                types: [
                  "number"
                ]
              },
              strokeGradient: {
                types: [
                  "object"
                ],
                properties: {
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "linear",
                      "radial",
                      "conic"
                    ]
                  },
                  from: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  to: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  center: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  angle: {
                    types: [
                      "number"
                    ]
                  },
                  stops: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        offset: {
                          types: [
                            "number"
                          ]
                        },
                        color: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "offset",
                        "color"
                      ]
                    }
                  }
                },
                required: [
                  "kind",
                  "stops"
                ]
              },
              strokeDash: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              },
              strokeDashOffset: {
                types: [
                  "number"
                ]
              },
              blendMode: {
                types: [
                  "string"
                ],
                enumValues: [
                  "source-over",
                  "lighter",
                  "multiply",
                  "screen",
                  "overlay",
                  "darken",
                  "lighten",
                  "color-dodge",
                  "color-burn",
                  "hard-light",
                  "soft-light",
                  "difference",
                  "exclusion",
                  "hue",
                  "saturation",
                  "color",
                  "luminosity",
                  "source-atop",
                  "destination-over",
                  "destination-out",
                  "xor"
                ]
              },
              blur: {
                types: [
                  "number"
                ]
              },
              rotate: {
                types: [
                  "number"
                ]
              },
              pivot: {
                types: [
                  "object"
                ],
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              shadow: {
                types: [
                  "object"
                ],
                properties: {
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  blur: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "color",
                  "blur"
                ]
              },
              animation: {
                types: [
                  "object"
                ],
                properties: {
                  durationMs: {
                    types: [
                      "number"
                    ]
                  },
                  loop: {
                    types: [
                      "boolean"
                    ]
                  },
                  keyframes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        at: {
                          types: [
                            "number"
                          ]
                        },
                        offsetX: {
                          types: [
                            "number"
                          ]
                        },
                        offsetY: {
                          types: [
                            "number"
                          ]
                        },
                        rotate: {
                          types: [
                            "number"
                          ]
                        },
                        opacity: {
                          types: [
                            "number"
                          ]
                        },
                        radiusX: {
                          types: [
                            "number"
                          ]
                        },
                        radiusY: {
                          types: [
                            "number"
                          ]
                        },
                        width: {
                          types: [
                            "number"
                          ]
                        },
                        height: {
                          types: [
                            "number"
                          ]
                        },
                        strokeWidth: {
                          types: [
                            "number"
                          ]
                        },
                        strokeDashOffset: {
                          types: [
                            "number"
                          ]
                        },
                        blur: {
                          types: [
                            "number"
                          ]
                        },
                        fill: {
                          types: [
                            "string"
                          ]
                        },
                        stroke: {
                          types: [
                            "string"
                          ]
                        },
                        shadow: {
                          types: [
                            "object"
                          ],
                          properties: {
                            color: {
                              types: [
                                "string"
                              ]
                            },
                            blur: {
                              types: [
                                "number"
                              ]
                            }
                          },
                          required: [
                            "color",
                            "blur"
                          ]
                        }
                      },
                      required: [
                        "at"
                      ]
                    }
                  }
                },
                required: [
                  "durationMs",
                  "keyframes"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "type",
              "shape",
              "position"
            ]
          }
        }
      },
      drawable: true
    },
    "draw-sprite-layer": {
      type: "draw-sprite-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawSpriteLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawSpriteLayer component",
      suggestedFor: [
        "draw",
        "sprite",
        "layer",
        "draw sprite layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-sprite-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: "Sprites painted in array order; z-ordering is the caller's responsibility.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "draw-sprite"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              asset: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              anchor: {
                types: [
                  "string"
                ],
                enumValues: [
                  "top-left",
                  "ground",
                  "center"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              frame: {
                types: [
                  "object"
                ],
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  w: {
                    types: [
                      "number"
                    ]
                  },
                  h: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y",
                  "w",
                  "h"
                ]
              },
              animation: {
                types: [
                  "string"
                ]
              },
              loop: {
                types: [
                  "boolean"
                ]
              },
              clockMs: {
                types: [
                  "number"
                ]
              },
              flipX: {
                types: [
                  "boolean"
                ]
              },
              rotation: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              shadow: {
                types: [
                  "object"
                ],
                properties: {
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  blur: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "color",
                  "blur"
                ]
              }
            },
            required: [
              "type",
              "position",
              "asset"
            ]
          }
        }
      },
      drawable: true
    },
    "draw-text-layer": {
      type: "draw-text-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawTextLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawTextLayer component",
      suggestedFor: [
        "draw",
        "text",
        "layer",
        "draw text layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-text-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: "items prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "draw-text"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              anchor: {
                types: [
                  "string"
                ],
                enumValues: [
                  "top-left",
                  "ground",
                  "center"
                ]
              },
              offsetX: {
                types: [
                  "number"
                ]
              },
              offsetY: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              font: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "center",
                  "end",
                  "left",
                  "right",
                  "start"
                ]
              },
              baseline: {
                types: [
                  "string"
                ],
                enumValues: [
                  "alphabetic",
                  "bottom",
                  "hanging",
                  "ideographic",
                  "middle",
                  "top"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "type",
              "text",
              "position",
              "color"
            ]
          }
        }
      },
      drawable: true
    },
    canvas: {
      type: "canvas",
      category: "game",
      sourceFile: "components/game/molecules/Canvas.tsx",
      tier: "molecules",
      family: "game",
      description: "Canvas component",
      suggestedFor: [
        "canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D sprite/shape/text raster, or 3D mesh. Default '2d'.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        drawables: {
          types: [
            "array"
          ],
          description: "Neutral drawable descriptors \u2014 the `draw-*` children. Routed to the host's painter.",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        camera: {
          types: [
            "object"
          ],
          description: "Neutral camera pose. Mapped to the underlying host's framing + zoom.",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        projection: {
          types: [
            "string"
          ],
          description: "2D grid layout (2D mode only): isometric / hex / flat / free / side.",
          enumValues: [
            "isometric",
            "hex",
            "flat",
            "free",
            "side"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "--- Shared view config ---"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        unitScale: {
          types: [
            "number"
          ],
          description: "Unit draw-size multiplier (both painters)."
        },
        showMinimap: {
          types: [
            "boolean"
          ],
          description: "Minimap overlay (2D)."
        },
        fit: {
          types: [
            "boolean"
          ],
          description: "Auto-fit the board's grid extent to the viewport (2D grid layouts; default false \u2014 boards render at their authored `camera.zoom` scale)."
        },
        tileWidth: {
          types: [
            "number"
          ],
          description: "Native tile/cell width in source px of the board's asset (2D grid layouts; defaults to the detected atlas tile width, else 256)."
        },
        backgroundImage: {
          types: [
            "asset",
            "object"
          ],
          description: "Backdrop image (2D iso/hex/flat/free/side).",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "Solid backdrop colour \u2014 2D `side` fill and 3D scene clear colour."
        },
        worldWidth: {
          types: [
            "number"
          ],
          description: "Side/free world bounds (2D `side`) and 3D world extent."
        },
        worldHeight: {
          types: [
            "number"
          ],
          description: "worldHeight prop"
        },
        pixelsPerUnit: {
          types: [
            "number"
          ],
          description: "3D world-unit \u2192 pixel scale (perspective/side depth). 3D only."
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D-only presentation."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "shadows prop"
        },
        showCoordinates: {
          types: [
            "boolean"
          ],
          description: "showCoordinates prop"
        },
        showTileInfo: {
          types: [
            "boolean"
          ],
          description: "showTileInfo prop"
        },
        fogOfWar: {
          types: [
            "array"
          ],
          description: "fogOfWar prop",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D scene light rig as data \u2014 ambient/directional/hemisphere/point lights + optional 'room' environment for PBR reflections. Omitted \u2192 the standard fixed rig.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D post-processing stack \u2014 bloom (emissive glow / neon) and vignette. Omitted \u2192 no post pass.",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        tileClickEvent: {
          types: [
            "string"
          ],
          description: "--- Shared interaction (LOLO-owned). The painter emitted at runtime depends on",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: false
            },
            {
              name: "y",
              type: "number",
              required: false
            },
            {
              name: "tileId",
              type: "string",
              required: false
            },
            {
              name: "z",
              type: "number",
              required: false
            }
          ]
        },
        unitClickEvent: {
          types: [
            "string"
          ],
          description: "unitClickEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "unitId",
              type: "string",
              required: false
            },
            {
              name: "x",
              type: "number",
              required: false
            },
            {
              name: "z",
              type: "number",
              required: false
            }
          ]
        },
        tileHoverEvent: {
          types: [
            "string"
          ],
          description: "tileHoverEvent prop",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: false
            },
            {
              name: "y",
              type: "number",
              required: false
            },
            {
              name: "tileId",
              type: "string",
              required: false
            },
            {
              name: "z",
              type: "number",
              required: false
            }
          ]
        },
        tileLeaveEvent: {
          types: [
            "string"
          ],
          description: "tileLeaveEvent prop",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: []
        },
        featureClickEvent: {
          types: [
            "string"
          ],
          description: "Feature-click event (3D only).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "featureId",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "z",
              type: "number",
              required: true
            },
            {
              name: "type",
              type: "string",
              required: false
            },
            {
              name: "elevation",
              type: "number",
              required: false
            }
          ]
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event (device-agnostic input), emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event, emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "--- Scene-edit mode (a game studio selecting/dragging drawables). Purely"
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "The currently-selected drawable id (controlled); `null`/undefined = none."
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Fired on a click that changes selection \u2014 before `selectEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onMove: {
          types: [
            "function"
          ],
          description: "Fired once per drag gesture, on drop \u2014 before `moveEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "x",
              type: "number"
            },
            {
              name: "y",
              type: "number"
            }
          ]
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Declarative selection event, `UI:{selectEvent} { id }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        moveEvent: {
          types: [
            "string"
          ],
          description: "Declarative move event, `UI:{moveEvent} { id, x, y }` \u2014 fired once on drop.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Declarative JSX drawable children (`<DrawShape .../>` composed in paint order). Forwarded to the 2D host, which collects them via drawable registry context."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawHost: true
    },
    presence: {
      type: "presence",
      category: "component",
      sourceFile: "components/core/atoms/Presence.tsx",
      tier: "atoms",
      family: "core",
      description: "Presence component",
      suggestedFor: [
        "presence"
      ],
      typicalSize: "small",
      propsSchema: {
        animation: {
          types: [
            "string"
          ],
          description: "Which motion preset to use (maps to `animate-<name>-in/out`).",
          required: true,
          enumValues: [
            "modal",
            "overlay",
            "slide-up",
            "drawer",
            "popover",
            "toast",
            "fade",
            "page"
          ]
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Per-instance opt-out. Global opt-out is `--motion-enable` / reduced-motion. @default true",
          default: true
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fired after the exit animation completes (before unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        show: {
          types: [
            "boolean"
          ],
          description: "When false, the exit animation runs, then children unmount.",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "page-transition": {
      type: "page-transition",
      category: "component",
      sourceFile: "components/core/molecules/PageTransition.tsx",
      tier: "molecules",
      family: "core",
      description: "PageTransition component",
      suggestedFor: [
        "page",
        "transition",
        "page transition"
      ],
      typicalSize: "medium",
      propsSchema: {
        locationKey: {
          types: [
            "string"
          ],
          description: "Value that changes on navigation (a page path).",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "import-preview-tree": {
      type: "import-preview-tree",
      category: "component",
      sourceFile: "components/core/molecules/import/ImportPreviewTree.tsx",
      tier: "molecules",
      family: "core",
      description: "ImportPreviewTree component",
      suggestedFor: [
        "import",
        "preview",
        "tree",
        "import preview tree"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind each group tile unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        units: {
          types: [
            "array"
          ],
          description: "Staged units to preview",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              ref: {
                types: [
                  "string"
                ]
              },
              targetEntity: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              fields: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  controlValue: true
                }
              },
              parentRef: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "ref",
              "targetEntity",
              "fields"
            ]
          }
        },
        skipped: {
          types: [
            "array"
          ],
          description: "Elements skipped during extraction, with reasons",
          items: {
            types: [
              "object"
            ],
            properties: {
              ref: {
                types: [
                  "string"
                ]
              },
              reason: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "ref",
              "reason"
            ]
          },
          default: []
        },
        entityDisplay: {
          types: [
            "object"
          ],
          description: "Entity name \u2192 display labels; falls back to the entity name",
          required: true,
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              singular: {
                types: [
                  "string"
                ]
              },
              plural: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "singular",
              "plural"
            ]
          }
        },
        onConfirm: {
          types: [
            "function"
          ],
          description: "Called when the user confirms the staged import",
          kind: "callback",
          callbackArgs: []
        },
        onCancel: {
          types: [
            "function"
          ],
          description: "Called when the user cancels the staged import",
          kind: "callback",
          callbackArgs: []
        },
        confirmLabel: {
          types: [
            "string"
          ],
          description: "Confirm button label"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Cancel button label"
        },
        indent: {
          types: [
            "number"
          ],
          description: "Indent per tree depth in px (default: 16)",
          default: 16
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "import-progress": {
      type: "import-progress",
      category: "component",
      sourceFile: "components/core/molecules/import/ImportProgress.tsx",
      tier: "molecules",
      family: "core",
      description: "ImportProgress component",
      suggestedFor: [
        "import",
        "progress",
        "import progress"
      ],
      typicalSize: "medium",
      propsSchema: {
        step: {
          types: [
            "string"
          ],
          description: "Current lifecycle step",
          required: true,
          enumValues: [
            "fetching",
            "mapping",
            "reviewing",
            "committing",
            "done",
            "failed"
          ]
        },
        counts: {
          types: [
            "object"
          ],
          description: "Unit counts",
          properties: {
            staged: {
              types: [
                "number"
              ]
            },
            committed: {
              types: [
                "number"
              ]
            },
            failed: {
              types: [
                "number"
              ]
            }
          }
        },
        labels: {
          types: [
            "object"
          ],
          description: "Step label overrides",
          mapKeyEnum: [
            "fetching",
            "mapping",
            "reviewing",
            "committing",
            "done",
            "failed"
          ],
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "import-source-picker": {
      type: "import-source-picker",
      category: "component",
      sourceFile: "components/core/molecules/import/ImportSourcePicker.tsx",
      tier: "molecules",
      family: "core",
      description: "ImportSourcePicker component",
      suggestedFor: [
        "import",
        "source",
        "picker",
        "import source picker"
      ],
      typicalSize: "medium",
      propsSchema: {
        sources: {
          types: [
            "array"
          ],
          description: "Source options to render",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "action",
                  "file"
                ]
              },
              accept: {
                types: [
                  "string"
                ]
              },
              multiple: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Called with the source id when an 'action' option is picked",
          kind: "callback",
          callbackArgs: [
            {
              name: "sourceId",
              type: "string"
            }
          ]
        },
        onFilesSelected: {
          types: [
            "function"
          ],
          description: "Called with the chosen files, read as text, when a 'file' option resolves",
          kind: "callback",
          callbackArgs: [
            {
              name: "files",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    size: {
                      types: [
                        "number"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    text: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "name",
                    "size",
                    "type",
                    "text"
                  ]
                }
              }
            }
          ]
        },
        title: {
          types: [
            "string"
          ],
          description: "Optional heading above the options"
        },
        moreSources: {
          types: [
            "node"
          ],
          description: "Generic slot for additional sources rendered below the options"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "draw-group": {
      type: "draw-group",
      category: "game",
      sourceFile: "components/game/atoms/DrawGroup.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawGroup component",
      suggestedFor: [
        "draw",
        "group",
        "draw group"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-group"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        scale: {
          types: [
            "number"
          ],
          description: "Uniform scale applied to the whole group."
        },
        rotate: {
          types: [
            "number"
          ],
          description: "Rotation in radians (painter units)."
        },
        rotation: {
          types: [
            "array"
          ],
          description: "Euler rotation `[x, y, z]` in radians (world axes) \u2014 per-axis joint rotation for the 3D backend; a group placed at a joint with children authored joint-local IS a bone. The 2D painter applies only the in-plane `z` component, after `rotate`.",
          tupleItems: [
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            }
          ],
          items: {
            types: [
              "number"
            ]
          }
        },
        bone: {
          types: [
            "string"
          ],
          description: "Skeleton bone name. A named group is addressable by a `draw-mesh` `skin` block (smooth skinning): the skinned mesh deforms with this group's animated transform. Names resolve within the nearest enclosing `skeleton: true` group (falling back to the canvas), so multiple instances of one rig never collide. Distinct from `id` (the hit-test handle)."
        },
        skeleton: {
          types: [
            "boolean"
          ],
          description: "Marks this group as a skeleton SCOPE: `bone` names and `skin` bindings in its subtree resolve against each other only \u2014 one scope per rig instance."
        },
        animation: {
          types: [
            "object"
          ],
          description: "Keyframe animation over the group TRANSFORM tracks (`offsetX/Y/Z` in cells, `rotateX/Y/Z` in radians, `scale`; the 2D painter also honors `opacity`) \u2014 children inherit it, so nested animated groups form a joint chain. Material tracks belong on leaf meshes.",
          properties: {
            durationMs: {
              types: [
                "number"
              ]
            },
            loop: {
              types: [
                "boolean"
              ]
            },
            keyframes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  at: {
                    types: [
                      "number"
                    ]
                  },
                  offsetX: {
                    types: [
                      "number"
                    ]
                  },
                  offsetY: {
                    types: [
                      "number"
                    ]
                  },
                  offsetZ: {
                    types: [
                      "number"
                    ]
                  },
                  rotateX: {
                    types: [
                      "number"
                    ]
                  },
                  rotateY: {
                    types: [
                      "number"
                    ]
                  },
                  rotateZ: {
                    types: [
                      "number"
                    ]
                  },
                  scale: {
                    types: [
                      "number"
                    ]
                  },
                  opacity: {
                    types: [
                      "number"
                    ]
                  },
                  emissiveIntensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  emissive: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "at"
                ]
              }
            }
          },
          propertyRequired: [
            "durationMs",
            "keyframes"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        },
        clip: {
          types: [
            "string"
          ],
          description: "Clip the group's items to an SVG path (`d` syntax) in world units relative to the group origin \u2014 masked reveals, meters, portraits."
        },
        items: {
          types: [
            "array"
          ],
          description: "The grouped drawables, painted through the normal dispatch.",
          required: true,
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        }
      },
      drawable: true,
      drawHost: true
    },
    "emoji-picker": {
      type: "emoji-picker",
      category: "component",
      sourceFile: "components/core/molecules/EmojiPicker.tsx",
      tier: "molecules",
      family: "core",
      description: "EmojiPicker component",
      suggestedFor: [
        "emoji",
        "picker",
        "emoji picker"
      ],
      typicalSize: "medium",
      propsSchema: {
        pickEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 picking an emoji emits UI:{pickEvent} with { emoji } via eventBus",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "emoji",
              type: "string",
              required: true
            }
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Which side of the trigger the panel opens on",
          enumValues: [
            "top",
            "bottom"
          ],
          default: "top"
        },
        triggerIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon shown on the trigger button",
          default: "smile"
        },
        triggerLabel: {
          types: [
            "string"
          ],
          description: 'Accessible label for the trigger button (the translated "Add emoji" when omitted)'
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the trigger button"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "draw-mesh": {
      type: "draw-mesh",
      category: "game",
      sourceFile: "components/game/atoms/DrawMesh.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawMesh component",
      suggestedFor: [
        "draw",
        "mesh",
        "draw mesh"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-mesh"
          ]
        },
        shape: {
          types: [
            "string"
          ],
          description: "shape prop",
          required: true,
          enumValues: [
            "box",
            "sphere",
            "capsule",
            "cylinder",
            "cone",
            "torus",
            "plane",
            "circle",
            "polyhedron"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; `z` is height in cells (projected to world Y).",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        width: {
          types: [
            "number"
          ],
          description: "Box/plane X extent in cells (default 1)."
        },
        height: {
          types: [
            "number"
          ],
          description: "Box Y (height) extent / capsule-cylinder-cone length in cells (default 1)."
        },
        depth: {
          types: [
            "number"
          ],
          description: "Box/plane Z extent in cells (default `width`)."
        },
        radius: {
          types: [
            "number"
          ],
          description: "Sphere/capsule/cone/torus/circle radius in cells (default 0.4)."
        },
        radiusTop: {
          types: [
            "number"
          ],
          description: "Cylinder top radius (default `radius`)."
        },
        radiusBottom: {
          types: [
            "number"
          ],
          description: "Cylinder bottom radius (default `radius`)."
        },
        tube: {
          types: [
            "number"
          ],
          description: "Torus tube radius in cells (default `radius` / 3)."
        },
        segments: {
          types: [
            "number"
          ],
          description: "Curved-surface tessellation, clamped 3..64 (default 24). 4\u20138 + `flatShading` = low-poly."
        },
        vertices: {
          types: [
            "array"
          ],
          description: "`polyhedron` only: raw vertices as scene-frame `[x, y, z]` triples in cells, `z` = up.",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            }
          }
        },
        faces: {
          types: [
            "array"
          ],
          description: "`polyhedron` only: triangle index triples into `vertices`, wound counter-clockwise seen from OUTSIDE in the scene frame.",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            }
          }
        },
        uvs: {
          types: [
            "array"
          ],
          description: "`polyhedron` only: per-vertex `[u, v]` texture coordinates parallel to `vertices`, glTF convention (`v` grows DOWN from the image's top-left). Pairs with `material.map`.",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            }
          }
        },
        skin: {
          types: [
            "object"
          ],
          description: "`polyhedron` only: smooth-skinning weights binding the vertices to named bone groups. Vertices must then be authored in BIND POSE model space; the bones' animated transforms deform them.",
          properties: {
            bones: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            inverseBindMatrices: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            indices: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            weights: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          },
          propertyRequired: [
            "bones",
            "inverseBindMatrices",
            "indices",
            "weights"
          ]
        },
        rotation: {
          types: [
            "array"
          ],
          description: "Euler rotation `[x, y, z]` in radians.",
          tupleItems: [
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            }
          ],
          items: {
            types: [
              "number"
            ]
          }
        },
        pivot: {
          types: [
            "string"
          ],
          description: "`bottom` (default) rests the mesh on its position's ground plane; `center` centers it there. For `polyhedron`, `bottom` lifts by \u2212min(z) and `center` applies no offset \u2014 the vertices are authoritative.",
          enumValues: [
            "bottom",
            "center"
          ]
        },
        material: {
          types: [
            "object"
          ],
          description: "material prop",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "standard",
                "physical",
                "toon",
                "basic"
              ]
            },
            color: {
              types: [
                "string"
              ]
            },
            metalness: {
              types: [
                "number"
              ]
            },
            roughness: {
              types: [
                "number"
              ]
            },
            emissive: {
              types: [
                "string"
              ]
            },
            emissiveIntensity: {
              types: [
                "number"
              ]
            },
            opacity: {
              types: [
                "number"
              ]
            },
            transmission: {
              types: [
                "number"
              ]
            },
            ior: {
              types: [
                "number"
              ]
            },
            flatShading: {
              types: [
                "boolean"
              ]
            },
            side: {
              types: [
                "string"
              ],
              enumValues: [
                "front",
                "back",
                "double"
              ]
            },
            map: {
              types: [
                "string"
              ]
            }
          }
        },
        outline: {
          types: [
            "object"
          ],
          description: "outline prop",
          properties: {
            color: {
              types: [
                "string"
              ]
            },
            width: {
              types: [
                "number"
              ]
            }
          }
        },
        castShadow: {
          types: [
            "boolean"
          ],
          description: "castShadow prop"
        },
        receiveShadow: {
          types: [
            "boolean"
          ],
          description: "receiveShadow prop"
        },
        animation: {
          types: [
            "object"
          ],
          description: "Keyframe animation over transform/material tracks; the 3D host runs it per frame.",
          properties: {
            durationMs: {
              types: [
                "number"
              ]
            },
            loop: {
              types: [
                "boolean"
              ]
            },
            keyframes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  at: {
                    types: [
                      "number"
                    ]
                  },
                  offsetX: {
                    types: [
                      "number"
                    ]
                  },
                  offsetY: {
                    types: [
                      "number"
                    ]
                  },
                  offsetZ: {
                    types: [
                      "number"
                    ]
                  },
                  rotateX: {
                    types: [
                      "number"
                    ]
                  },
                  rotateY: {
                    types: [
                      "number"
                    ]
                  },
                  rotateZ: {
                    types: [
                      "number"
                    ]
                  },
                  scale: {
                    types: [
                      "number"
                    ]
                  },
                  opacity: {
                    types: [
                      "number"
                    ]
                  },
                  emissiveIntensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  emissive: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "at"
                ]
              }
            }
          },
          propertyRequired: [
            "durationMs",
            "keyframes"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity; multiplies the material and enclosing group opacity."
        }
      },
      drawable: true
    },
    "algo-graph-canvas": {
      type: "algo-graph-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/AlgoGraphCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "AlgoGraphCanvas component",
      suggestedFor: [
        "algo",
        "graph",
        "canvas",
        "algo graph canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        nodes: {
          types: [
            "array"
          ],
          description: "nodes prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "unvisited",
                  "frontier",
                  "current",
                  "visited",
                  "goal",
                  "path"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              badge: {
                types: [
                  "object"
                ],
                properties: {
                  text: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "text"
                ]
              }
            },
            required: [
              "id"
            ]
          },
          default: []
        },
        edges: {
          types: [
            "array"
          ],
          description: "edges prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              directed: {
                types: [
                  "boolean"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "tree",
                  "relaxed",
                  "candidate",
                  "path"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        layout: {
          types: [
            "string"
          ],
          description: "Deterministic layout to compute node positions; `manual` passes through `x`/`y`.",
          enumValues: [
            "manual",
            "tree",
            "layered",
            "circle"
          ],
          default: "manual"
        },
        root: {
          types: [
            "string"
          ],
          description: "`tree` layout only: preferred root id. Falls back to the first indegree-0 node."
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "interactive prop",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "animate prop",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "Fires when a node circle is clicked, alongside `onShapeClick`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id",
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "fx-overlay": {
      type: "fx-overlay",
      category: "container",
      sourceFile: "components/core/molecules/FxOverlay.tsx",
      tier: "molecules",
      family: "core",
      description: "FxOverlay component",
      suggestedFor: [
        "overlay",
        "fx overlay"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Live screen-space fx entries (the mechanic's fx list filtered to `space == \"screen\"`); mount starts each item's animation, ttl decay unmounts it.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              effect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "confetti",
                  "flash",
                  "shake",
                  "sparkle",
                  "float-text",
                  "streak-glow"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              ttl: {
                types: [
                  "number"
                ]
              },
              maxTtl: {
                types: [
                  "number"
                ]
              },
              bornAt: {
                types: [
                  "number"
                ]
              },
              space: {
                types: [
                  "string"
                ],
                enumValues: [
                  "world",
                  "screen"
                ]
              },
              particleCount: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "id",
              "type",
              "ttl"
            ]
          }
        },
        tickMs: {
          types: [
            "number"
          ],
          description: "The fx mechanic's decay tick period in ms; per-item animation duration = maxTtl \xD7 tickMs. Default 500."
        },
        children: {
          types: [
            "node"
          ],
          description: "Wrapped content \u2014 the `shake` target and the overlay's bounds."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names on the outermost element."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "draw-fx-layer": {
      type: "draw-fx-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawFxLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawFxLayer component",
      suggestedFor: [
        "draw",
        "layer",
        "draw fx layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-fx-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: 'The live fx entries \u2014 the mechanic\'s `@entity.fx` list passed whole; `space: "screen"` entries are skipped (the overlay owns those).',
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              ttl: {
                types: [
                  "number"
                ]
              },
              maxTtl: {
                types: [
                  "number"
                ]
              },
              bornAt: {
                types: [
                  "number"
                ]
              },
              space: {
                types: [
                  "string"
                ],
                enumValues: [
                  "world",
                  "screen"
                ]
              },
              effect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "confetti",
                  "flash",
                  "shake",
                  "sparkle",
                  "float-text",
                  "streak-glow"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              },
              particleCount: {
                types: [
                  "number"
                ]
              },
              animation: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "type",
              "x",
              "ttl"
            ]
          }
        },
        presets: {
          types: [
            "array"
          ],
          description: "Consumer-declared effect vocabulary \u2014 one row per fx `type` (look + recipe). Unknown types fall back to a neutral spark burst.",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              color2: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              shape: {
                types: [
                  "string"
                ],
                enumValues: [
                  "spark",
                  "ring",
                  "puff",
                  "streak"
                ]
              },
              count: {
                types: [
                  "number"
                ]
              },
              glow: {
                types: [
                  "number"
                ]
              },
              gravity: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              },
              space: {
                types: [
                  "string"
                ],
                enumValues: [
                  "world",
                  "screen"
                ]
              },
              effect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "confetti",
                  "flash",
                  "shake",
                  "sparkle",
                  "float-text",
                  "streak-glow"
                ]
              },
              particleCount: {
                types: [
                  "number"
                ]
              },
              animation: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "type"
            ]
          }
        },
        art: {
          types: [
            "object"
          ],
          description: "Vector art per fx type (an asset-art map: type \u2192 anim-state \u2192 drawables); a type's `idle` state wins over sprites/procedural.",
          mapValue: {
            types: [
              "object"
            ],
            mapValue: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                drawableSlot: true
              }
            }
          }
        },
        sprites: {
          types: [
            "object"
          ],
          description: "Sprite asset per fx type (an asset-manifest effects map) \u2014 used when no vector art matches.",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              url: {
                types: [
                  "asset"
                ]
              },
              role: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              animations: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              style: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pixel",
                  "vector",
                  "hd",
                  "1-bit",
                  "isometric"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              dimension: {
                types: [
                  "string"
                ],
                enumValues: [
                  "2d",
                  "3d"
                ]
              },
              aspect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "1:1",
                  "16:9",
                  "5:7",
                  "8:1",
                  "4:1",
                  "5:3",
                  "1:2"
                ]
              },
              atlas: {
                types: [
                  "asset"
                ]
              },
              sprite: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              thumbnailUrl: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "url",
              "role",
              "category"
            ]
          }
        },
        tickMs: {
          types: [
            "number"
          ],
          description: "The fx mechanic's decay tick period in ms; lifetime = maxTtl \xD7 tickMs. Default 500."
        },
        textColor: {
          types: [
            "string"
          ],
          description: "Floating-message text color when an item declares none. Default `#ffe066`."
        }
      },
      drawable: true,
      drawHost: true
    },
    "rich-text-editor": {
      type: "rich-text-editor",
      category: "component",
      sourceFile: "components/core/molecules/RichTextEditor.tsx",
      tier: "molecules",
      family: "core",
      description: "RichTextEditor component",
      suggestedFor: [
        "rich",
        "text",
        "editor",
        "rich text editor"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "string"
          ],
          description: "The rich text content as HTML. Sanitized before rendering and on every change."
        },
        onChange: {
          types: [
            "function"
          ],
          description: "onChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "changeEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "readOnly prop",
          default: false
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "placeholder prop"
        },
        showToolbar: {
          types: [
            "boolean"
          ],
          description: "showToolbar prop",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "document-panel": {
      type: "document-panel",
      category: "component",
      sourceFile: "components/core/molecules/DocumentPanel.tsx",
      tier: "molecules",
      family: "core",
      description: "DocumentPanel \u2014 a record rendered as one continuous document: editable title over the rich-text body, editing controls tucked into the header corner.",
      capabilities: "document page, note editor, wiki page, article editor, writing surface, rich text document, content editing canvas",
      suggestedFor: [
        "document",
        "panel",
        "document panel"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "The record's id \u2014 carried on every commit and action event."
        },
        title: {
          types: [
            "string"
          ],
          description: 'Document title (falls back to "Untitled" for a blank draft)'
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Muted byline under the title; hidden when empty"
        },
        coverImage: {
          types: [
            "string"
          ],
          description: "Cover/hero image URL painted as a full-bleed banner above the header row (Notion/article-hero shape); hidden when empty"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Document icon rendered beside the title (a file glyph, a domain icon); hidden when empty"
        },
        value: {
          types: [
            "string"
          ],
          description: "The document body as rich-text HTML"
        },
        actions: {
          types: [
            "array"
          ],
          description: 'Header actions (settings, history, \u2026). At most one explicit primary stays inline; the rest live under a single "\u22EF" menu \u2014 the title owns its row.',
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: "0 collapses even the primary action into the menu"
        },
        editing: {
          types: [
            "boolean"
          ],
          description: "false = read surface with click-to-edit body; true = live editor",
          default: false
        },
        titleCommitEvent: {
          types: [
            "string"
          ],
          description: "Emitted when the title commits: { title, id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "title",
              type: "string",
              required: true
            },
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        contentChangeEvent: {
          types: [
            "string"
          ],
          description: "Forwarded to the embedded RichTextEditor's autosave stream: { value }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        editEvent: {
          types: [
            "string"
          ],
          description: "Emitted by the header Edit affordance and a click on the read body: { id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        doneEvent: {
          types: [
            "string"
          ],
          description: "Emitted by the Done button that ends the editing session: { id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        editLabel: {
          types: [
            "string"
          ],
          description: "editLabel prop"
        },
        doneLabel: {
          types: [
            "string"
          ],
          description: "doneLabel prop"
        },
        autosaveHint: {
          types: [
            "string"
          ],
          description: "Muted caption beside Done telling the author work persists on its own"
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder for an empty body"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "document-details": {
      type: "document-details",
      category: "display",
      sourceFile: "components/core/molecules/DocumentDetails.tsx",
      tier: "molecules",
      family: "core",
      description: "DocumentDetails \u2014 the document's metadata as a compact settings card for the rail beside a DocumentPanel, each property committing in place.",
      capabilities: "document settings, page details, post settings sidebar, metadata panel, document properties, publish settings",
      fieldsContract: "display",
      suggestedFor: [
        "document",
        "details",
        "document details"
      ],
      typicalSize: "medium",
      propsSchema: {
        entity: {
          types: [
            "object"
          ],
          description: "The loaded record \u2014 supplies the id every commit carries and the values the rows read.",
          kind: "entity",
          cardinality: "record"
        },
        fields: {
          types: [
            "array"
          ],
          description: "The properties shown; empty renders nothing.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              key: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "boolean",
                  "select",
                  "readonly",
                  "image"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        metaCommitEvent: {
          types: [
            "string"
          ],
          description: "Emitted when a property commits: { id, patch } \u2014 patch carries the id plus the one changed field, ready for a partial persist update.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "patch",
              type: "object",
              required: true,
              schema: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  controlValue: true
                }
              }
            }
          ]
        },
        relationEvent: {
          types: [
            "string"
          ],
          description: "Makes relation chips clickable: emitted with { field, id, name } when a hydrated relation value (or one row of a many-relation) is clicked \u2014 the host routes it to the linked record's own surface.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "field",
              type: "string",
              required: true
            },
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "name",
              type: "string",
              required: true
            }
          ]
        },
        title: {
          types: [
            "string"
          ],
          description: 'Card heading (defaults to the localized "Details")'
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "floating-toolbar": {
      type: "floating-toolbar",
      category: "component",
      sourceFile: "components/core/molecules/FloatingToolbar.tsx",
      tier: "molecules",
      family: "core",
      description: "FloatingToolbar component",
      suggestedFor: [
        "floating",
        "toolbar",
        "floating toolbar"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Tool items rendered as icon buttons",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              action: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              actionPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "action"
              },
              active: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              testId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "icon",
              "label"
            ]
          }
        },
        position: {
          types: [
            "string"
          ],
          description: "Strip position within the app frame.",
          enumValues: [
            "bottom-center",
            "bottom-left",
            "bottom-right",
            "top-center",
            "top-left",
            "top-right"
          ],
          default: "bottom-center"
        },
        children: {
          types: [
            "node"
          ],
          description: "Custom cells appended after the items, separated by a divider"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes on the positioned root"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dock-layout": {
      type: "dock-layout",
      category: "layout",
      sourceFile: "components/core/organisms/layout/DockLayout.tsx",
      tier: "organisms",
      family: "core",
      description: "DockLayout component",
      suggestedFor: [
        "dock",
        "layout",
        "dock layout"
      ],
      typicalSize: "large",
      propsSchema: {
        rail: {
          types: [
            "node"
          ],
          description: "Fixed-width far-left vertical strip (e.g. an icon nav rail)."
        },
        sidebar: {
          types: [
            "node"
          ],
          description: "Collapsible, resizable left sidebar."
        },
        main: {
          types: [
            "node"
          ],
          description: "Required center region.",
          required: true
        },
        bottomPanel: {
          types: [
            "node"
          ],
          description: "Collapsible, resizable-height bottom panel."
        },
        statusBar: {
          types: [
            "node"
          ],
          description: "Slim strip pinned to the bottom of the frame, below the bottom panel."
        },
        secondarySidebar: {
          types: [
            "node"
          ],
          description: "Collapsible right sidebar."
        },
        railWidth: {
          types: [
            "number"
          ],
          description: "Width of `rail` in pixels; the rail sizes the column itself when omitted."
        },
        secondarySidebarWidth: {
          types: [
            "number"
          ],
          description: "Width of `secondarySidebar` in pixels (fixed \u2014 not resizable). @default 280",
          default: 280
        },
        sidebarCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether `sidebar` is collapsed (controlled; the layout keeps its own state when omitted)."
        },
        onSidebarCollapsedChange: {
          types: [
            "function"
          ],
          description: "onSidebarCollapsedChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "collapsed",
              type: "boolean"
            }
          ]
        },
        sidebarWidth: {
          types: [
            "number"
          ],
          description: "Sidebar size as a SplitPane ratio (0-100, percentage of the sidebar/main split). @default 20",
          default: 20
        },
        onSidebarWidthChange: {
          types: [
            "function"
          ],
          description: "onSidebarWidthChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "width",
              type: "number"
            }
          ]
        },
        sidebarMinSize: {
          types: [
            "number"
          ],
          description: "Minimum sidebar size in pixels, forwarded to SplitPane's `minSize`. @default 160",
          default: 160
        },
        bottomPanelCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether `bottomPanel` is collapsed (controlled; the layout keeps its own state when omitted)."
        },
        onBottomPanelCollapsedChange: {
          types: [
            "function"
          ],
          description: "onBottomPanelCollapsedChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "collapsed",
              type: "boolean"
            }
          ]
        },
        bottomPanelHeight: {
          types: [
            "number"
          ],
          description: "Bottom panel size as a SplitPane ratio (0-100, percentage given to the panel). @default 30",
          default: 30
        },
        onBottomPanelHeightChange: {
          types: [
            "function"
          ],
          description: "onBottomPanelHeightChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "height",
              type: "number"
            }
          ]
        },
        bottomPanelMinSize: {
          types: [
            "number"
          ],
          description: "Minimum bottom panel size in pixels, forwarded to SplitPane's `minSize`. @default 120",
          default: 120
        },
        secondarySidebarCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether `secondarySidebar` is collapsed (controlled; the layout keeps its own state when omitted)."
        },
        onSecondarySidebarCollapsedChange: {
          types: [
            "function"
          ],
          description: "onSecondarySidebarCollapsedChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "collapsed",
              type: "boolean"
            }
          ]
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "The mobile variant; decided by the viewport (narrower than 1024px) when omitted."
        },
        secondarySidebarLabel: {
          types: [
            "string"
          ],
          description: 'Compact: the top-bar button that opens `secondarySidebar` (e.g. "Inspector", "Chat").'
        },
        secondarySidebarIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "secondarySidebarIcon prop"
        },
        bottomPanelLabel: {
          types: [
            "string"
          ],
          description: "Compact: the top-bar button that opens `bottomPanel`."
        },
        bottomPanelIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "bottomPanelIcon prop"
        },
        topBarActions: {
          types: [
            "node"
          ],
          description: "Compact: the host's own actions at the end of the top bar (e.g. Run)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes on the root frame."
        },
        railClassName: {
          types: [
            "string"
          ],
          description: "railClassName prop"
        },
        sidebarClassName: {
          types: [
            "string"
          ],
          description: "sidebarClassName prop"
        },
        mainClassName: {
          types: [
            "string"
          ],
          description: "mainClassName prop"
        },
        bottomPanelClassName: {
          types: [
            "string"
          ],
          description: "bottomPanelClassName prop"
        },
        statusBarClassName: {
          types: [
            "string"
          ],
          description: "statusBarClassName prop"
        },
        secondarySidebarClassName: {
          types: [
            "string"
          ],
          description: "secondarySidebarClassName prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "command-palette": {
      type: "command-palette",
      category: "component",
      sourceFile: "components/core/molecules/CommandPalette.tsx",
      tier: "molecules",
      family: "core",
      description: "CommandPalette component",
      suggestedFor: [
        "command",
        "palette",
        "command palette"
      ],
      typicalSize: "medium",
      propsSchema: {
        open: {
          types: [
            "boolean"
          ],
          description: "Whether the palette overlay is open",
          required: true
        },
        onOpenChange: {
          types: [
            "function"
          ],
          description: "Called to open/close the overlay (Escape, overlay click, or a selection)",
          required: true,
          kind: "callback",
          callbackArgs: [
            {
              name: "open",
              type: "boolean"
            }
          ]
        },
        commands: {
          types: [
            "array"
          ],
          description: "Commands listed in the palette, filtered as the user types",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              shortcut: {
                types: [
                  "string"
                ]
              },
              keywords: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              group: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              action: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              actionPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "action"
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Called with the selected command (click, or Enter on the highlighted row)",
          kind: "callback",
          callbackArgs: [
            {
              name: "command",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  icon: {
                    types: [
                      "icon",
                      "string"
                    ]
                  },
                  shortcut: {
                    types: [
                      "string"
                    ]
                  },
                  keywords: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  disabled: {
                    types: [
                      "boolean"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ],
                    kind: "event"
                  },
                  action: {
                    types: [
                      "string"
                    ],
                    kind: "event"
                  },
                  actionPayload: {
                    types: [
                      "object"
                    ],
                    eventPayloadBrand: true,
                    payloadFor: "action"
                  }
                },
                required: [
                  "id",
                  "label"
                ]
              }
            }
          ]
        },
        placeholder: {
          types: [
            "string"
          ],
          description: 'Search field placeholder. Defaults to the translated "Type a command...".'
        },
        emptyLabel: {
          types: [
            "string"
          ],
          description: "Label shown when no command matches the query. Defaults to the translated message."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes on the overlay's dialog"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-audio-cue": {
      type: "game-audio-cue",
      category: "game",
      sourceFile: "components/game/atoms/GameAudioCue.tsx",
      tier: "atoms",
      family: "game",
      description: "GameAudioCue component",
      suggestedFor: [
        "game",
        "audio",
        "cue",
        "game audio cue"
      ],
      typicalSize: "small",
      propsSchema: {
        cue: {
          types: [
            "string"
          ],
          description: "Manifest key played as a one-shot SFX each time `cueSeq` advances."
        },
        cueSeq: {
          types: [
            "number"
          ],
          description: "Monotonically increasing counter \u2014 bumping it re-triggers `cue`."
        },
        music: {
          types: [
            "string"
          ],
          description: "Manifest key to crossfade to as looping background music; unset/empty stops music."
        },
        muted: {
          types: [
            "boolean"
          ],
          description: "Muted state, owned externally and mirrored into the hook."
        },
        volume: {
          types: [
            "number"
          ],
          description: "Master volume 0-1, owned externally and mirrored into the hook."
        },
        manifest: {
          types: [
            "object"
          ],
          description: "Sound manifest \u2014 keys mapped to sound definitions (path, volume, loop, ...).",
          required: true,
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              path: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              volume: {
                types: [
                  "number"
                ]
              },
              loop: {
                types: [
                  "boolean"
                ]
              },
              poolSize: {
                types: [
                  "number"
                ]
              },
              autostart: {
                types: [
                  "boolean"
                ]
              },
              crossfade: {
                types: [
                  "boolean"
                ]
              },
              crossfadeDurationMs: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "path"
            ]
          }
        },
        baseUrl: {
          types: [
            "string"
          ],
          description: "Base URL prepended to every manifest path."
        }
      }
    },
    "ui-slot-component": {
      type: "ui-slot-component",
      category: "layout",
      sourceFile: "components/core/organisms/UISlotRenderer.tsx",
      tier: "organisms",
      family: "core",
      description: "UISlotComponent component",
      suggestedFor: [
        "slot",
        "component",
        "u i slot component"
      ],
      typicalSize: "large",
      propsSchema: {
        slot: {
          types: [
            "object"
          ],
          description: "slot prop",
          required: true
        },
        portal: {
          types: [
            "boolean"
          ],
          description: "portal prop",
          default: false
        },
        position: {
          types: [
            "string"
          ],
          description: "position prop",
          enumValues: [
            "left",
            "right",
            "top-right",
            "top-left",
            "bottom-right",
            "bottom-left"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        draggable: {
          types: [
            "boolean"
          ],
          description: "draggable prop"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "entity prop"
        },
        children: {
          types: [
            "node",
            "function"
          ],
          description: "Compiled mode: render children directly instead of resolving from context. A function receives the slot's presence \u2014 compiled self-overlaying content (Modal / ConfirmDialog) takes it as `isOpen`/`onExited` to play its exit."
        },
        pattern: {
          types: [
            "string"
          ],
          description: "Pattern type for data-pattern attribute (compiled mode)"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for data-source-trait attribute (compiled mode)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        awaitingSkeleton: {
          types: [
            "object"
          ],
          description: "Compiled mode: the predicted skeleton while this trait's server round trip is in flight (the View selects it by the pending transition). Shown only while the slot is empty. Runtime mode reads it from `AwaitingSkeletonContext`."
        },
        fallback: {
          types: [
            "node"
          ],
          description: "Host-supplied stock content for a slot-host region (Studio V4 \xA714 Part I2). Ignored whenever `children` is passed \u2014 `children` (compiled mode) always wins, so a compiled organism's slot can never be silently shadowed by a host `fallback`. In `portal` mode `fallback` never portals: it paints inline at this component's own position in the tree, while slot content (if present) still portals as usual \u2014 a plugin filling `modal` doesn't relocate the host's inline empty-state."
        },
        mode: {
          types: [
            "string"
          ],
          description: "`'replace'` (default): slot content when present, else `fallback`. `'append'`: `fallback` first, then slot content when present \u2014 both render together, so `fallback` is a fixture, not just a placeholder.",
          enumValues: [
            "replace",
            "append"
          ],
          default: "replace"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "draw-skinned-mesh": {
      type: "draw-skinned-mesh",
      category: "game",
      sourceFile: "components/game/atoms/DrawSkinnedMesh.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawSkinnedMesh component",
      suggestedFor: [
        "draw",
        "skinned",
        "mesh",
        "draw skinned mesh"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-skinned-mesh"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; mesh local coords are world units relative to its projected top-left.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        asset: {
          types: [
            "object"
          ],
          description: "The texture the mesh UVs cut into.",
          required: true,
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        mesh: {
          types: [
            "object"
          ],
          description: "Inline bind mesh; wins over `meshUrl`.",
          properties: {
            vertices: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  u: {
                    types: [
                      "number"
                    ]
                  },
                  v: {
                    types: [
                      "number"
                    ]
                  },
                  weights: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        bone: {
                          types: [
                            "number"
                          ]
                        },
                        w: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "bone",
                        "w"
                      ]
                    }
                  }
                },
                required: [
                  "x",
                  "y",
                  "u",
                  "v",
                  "weights"
                ]
              }
            },
            triangles: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                },
                tupleItems: [
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  }
                ]
              }
            }
          },
          propertyRequired: [
            "vertices",
            "triangles"
          ]
        },
        meshUrl: {
          types: [
            "string"
          ],
          description: "URL of a JSON `SkinMesh` (fetched + cached); used when `mesh` is absent."
        },
        bones: {
          types: [
            "array"
          ],
          description: "The skeleton (bind pose; FK chain via `parent` names). Falls back to the rigbundle's `bones` when empty.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              parent: {
                types: [
                  "string"
                ],
                nullable: true
              },
              pivot: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                },
                tupleItems: [
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  }
                ]
              },
              tip: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                },
                tupleItems: [
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  }
                ]
              }
            },
            required: [
              "name",
              "pivot",
              "tip"
            ]
          }
        },
        pose: {
          types: [
            "object"
          ],
          description: "Static pose (bone name \u2192 degrees relative to bind). Overridden by `clip` + `frame`.",
          mapValue: {
            types: [
              "number"
            ]
          }
        },
        clip: {
          types: [
            "object"
          ],
          description: "Pose animation clip; with `frame`, pose = `frames[frame % frames.length]`.",
          properties: {
            frames: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          },
          propertyRequired: [
            "frames"
          ]
        },
        clipName: {
          types: [
            "string"
          ],
          description: "Named clip inside the rigbundle at `meshUrl` (`clips[clipName].frames`); used when `clip` is absent. Empty/unknown = no clip."
        },
        frame: {
          types: [
            "number"
          ],
          description: "Clip frame counter (host-driven paint clock frame)."
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        },
        weightsOverlay: {
          types: [
            "boolean"
          ],
          description: "Paint per-triangle dominant-bone colors instead of the texture (rig inspection)."
        },
        ghost: {
          types: [
            "object"
          ],
          description: "Onion skin: render the mesh at this clip frame underneath, at this opacity.",
          properties: {
            frame: {
              types: [
                "number"
              ]
            },
            opacity: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "frame",
            "opacity"
          ]
        }
      },
      drawable: true
    },
    gantt: {
      type: "gantt",
      category: "component",
      sourceFile: "components/core/molecules/Gantt.tsx",
      tier: "molecules",
      family: "core",
      description: "Gantt \u2014 view-only task schedule rendering rows as bars on a day axis.",
      capabilities: "gantt chart, project timeline, schedule view, task bars, dependency arrows, roadmap, milestone plan",
      fieldsContract: "display",
      suggestedFor: [
        "gantt"
      ],
      typicalSize: "medium",
      propsSchema: {
        tasks: {
          types: [
            "object",
            "array"
          ],
          description: 'Schema entity data \u2014 the task rows to place on the axis. pattern-sync tags it `kind:"entity", cardinality:"collection"` so consumers bind the domain entity without name-matching the prop.',
          kind: "entity",
          cardinality: "collection",
          default: []
        },
        links: {
          types: [
            "array"
          ],
          description: "Dependency arrows between task ids",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        titleField: {
          types: [
            "string"
          ],
          description: "Row field holding the bar label. Defaults to `title`.",
          default: "title"
        },
        startField: {
          types: [
            "string"
          ],
          description: "Row field holding the start timestamp (ISO or epoch). Defaults to `start`.",
          default: "start"
        },
        endField: {
          types: [
            "string"
          ],
          description: "Row field holding the end timestamp (ISO or epoch). Defaults to `end`. When absent, `durationField` (days) is used instead.",
          default: "end"
        },
        durationField: {
          types: [
            "string"
          ],
          description: "Row field holding the task length in days, used when the row has no end."
        },
        statusField: {
          types: [
            "string"
          ],
          description: "Row field holding the bar status. Defaults to `status`.",
          default: "status"
        },
        statusColorMap: {
          types: [
            "object"
          ],
          description: "Bar colour per exact status value; unmapped statuses are primary bars.",
          mapValue: {
            types: [
              "string"
            ],
            enumValues: [
              "default",
              "primary",
              "secondary",
              "success",
              "warning",
              "danger",
              "error",
              "info",
              "neutral",
              "destructive"
            ]
          }
        },
        groupField: {
          types: [
            "string"
          ],
          description: "Row field rows are grouped under header rows by. Empty (default) = flat list.",
          default: ""
        },
        rangeStart: {
          types: [
            "string",
            "date"
          ],
          description: "First visible day (ISO or Date). Defaults to 2 days before the earliest task."
        },
        rangeEnd: {
          types: [
            "string",
            "date"
          ],
          description: "Last visible day (ISO or Date). Defaults to 2 days after the latest task end."
        },
        showToday: {
          types: [
            "boolean"
          ],
          description: "Paint the today marker line when today falls inside the range (default true).",
          default: true
        },
        dayWidth: {
          types: [
            "number"
          ],
          description: "Pixels per day on the axis (default 28)",
          default: 28
        },
        barClickEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a bar is clicked: UI:{barClickEvent} with { id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    image: {
      type: "image",
      category: "component",
      sourceFile: "components/core/atoms/Image.tsx",
      tier: "atoms",
      family: "core",
      description: "Image component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "image"
      ],
      typicalSize: "small",
      propsSchema: {
        src: {
          types: [
            "asset"
          ],
          description: "Image source URL",
          required: true
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alternative text; empty string marks the image decorative",
          required: true
        },
        fit: {
          types: [
            "string"
          ],
          description: "How the image fills its box",
          enumValues: [
            "cover",
            "contain",
            "fill",
            "none"
          ],
          default: "cover"
        },
        aspect: {
          types: [
            "string"
          ],
          description: `Fixed aspect ratio of the box; "auto" keeps the image's own`,
          enumValues: [
            "auto",
            "1/1",
            "4/3",
            "3/2",
            "16/9",
            "21/9"
          ],
          default: "auto"
        },
        rounded: {
          types: [
            "string"
          ],
          description: "Corner rounding",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ],
          default: "md"
        },
        bordered: {
          types: [
            "boolean"
          ],
          description: "Draw a subtle border around the image",
          default: false
        },
        loading: {
          types: [
            "string"
          ],
          description: '"lazy" defers off-screen images; "eager" for above-the-fold media',
          enumValues: [
            "lazy",
            "eager"
          ],
          default: "lazy"
        },
        position: {
          types: [
            "string"
          ],
          description: "Which part of the image stays visible when it is cropped",
          enumValues: [
            "center",
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "center"
        },
        srcSet: {
          types: [
            "string"
          ],
          description: "Candidate sources at different widths (the HTML `srcset`)"
        },
        sizes: {
          types: [
            "string"
          ],
          description: "Rendered widths at each breakpoint (the HTML `sizes`)"
        },
        priority: {
          types: [
            "boolean"
          ],
          description: "The page's main image: load it first",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "geometric-pattern": {
      type: "geometric-pattern",
      category: "component",
      sourceFile: "components/core/molecules/GeometricPattern.tsx",
      tier: "molecules",
      family: "core",
      description: "GeometricPattern component",
      suggestedFor: [
        "geometric",
        "pattern",
        "geometric pattern"
      ],
      typicalSize: "medium",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Which geometric tile design",
          enumValues: [
            "star8",
            "star6",
            "khatam",
            "star10",
            "star12",
            "rosette-double",
            "rosette-filled",
            "seigaiha",
            "greek-key",
            "celtic-knot",
            "kolam",
            "arch",
            "arabesque-vine",
            "arabesque-net"
          ],
          default: "star8"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Layout mode",
          enumValues: [
            "background",
            "left",
            "right",
            "dual",
            "around",
            "frame"
          ],
          default: "background"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "Overall opacity (default: 0.06)",
          default: 0.06
        },
        color: {
          types: [
            "string"
          ],
          description: "Stroke color passed to PatternTile",
          default: "var(--color-primary)"
        },
        scale: {
          types: [
            "number"
          ],
          description: "Tile scale multiplier (default: 1)",
          default: 1
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width passed to PatternTile",
          default: 0.5
        },
        children: {
          types: [
            "node"
          ],
          description: "Content to wrap (used in frame mode)"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "icon-button": {
      type: "icon-button",
      category: "component",
      sourceFile: "components/core/molecules/IconButton.tsx",
      tier: "molecules",
      family: "core",
      description: "IconButton component",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "icon",
        "button",
        "icon button"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "primary",
            "secondary",
            "ghost",
            "danger",
            "success",
            "warning",
            "default",
            "link"
          ],
          default: "ghost"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Asset image rendered as the leading icon; takes precedence over icon/leftIcon when provided.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable the button (greys out, blocks click events)"
        },
        href: {
          types: [
            "string"
          ],
          description: "Makes the button a link: `#anchor` scrolls, an absolute URL loads, a path navigates in-app"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Runs on activation, on the button or, with `href`, on the link; calling `preventDefault()` there keeps a link from following its `href`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "event",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        "data-testid": {
          types: [
            "string"
          ],
          description: "Test identifier for automated tests"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "icon prop",
          required: true
        },
        label: {
          types: [
            "string"
          ],
          description: "What the button does \u2014 its accessible name and its tooltip.",
          required: true
        },
        tooltipPosition: {
          types: [
            "string"
          ],
          description: "Where the tooltip opens.",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "right"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "top-nav-item": {
      type: "top-nav-item",
      category: "navigation",
      sourceFile: "components/core/molecules/TopNavItem.tsx",
      tier: "molecules",
      family: "core",
      description: "TopNavItem component",
      suggestedFor: [
        "top",
        "nav",
        "item",
        "top nav item"
      ],
      typicalSize: "medium",
      propsSchema: {
        item: {
          types: [
            "object"
          ],
          description: "item prop",
          required: true,
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            href: {
              types: [
                "string"
              ]
            },
            icon: {
              types: [
                "icon",
                "string"
              ]
            },
            badge: {
              types: [
                "string",
                "number"
              ]
            },
            active: {
              types: [
                "boolean"
              ]
            },
            onClick: {
              types: [
                "function"
              ]
            },
            children: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                cyclic: true,
                cyclicTypeName: "TopNavItemData"
              }
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        activeHref: {
          types: [
            "string"
          ],
          description: "The href of the active page, used to mark the item (or its parent) active."
        },
        tone: {
          types: [
            "string"
          ],
          description: "Active styling: `solid` fills with the primary colour (dashboard top bar); `soft` tints it (site header).",
          enumValues: [
            "solid",
            "soft"
          ],
          default: "solid"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "change-list": {
      type: "change-list",
      category: "display",
      sourceFile: "components/core/molecules/ChangeList.tsx",
      tier: "molecules",
      family: "core",
      description: "The plain-language changes in one version (added / modified / removed).",
      capabilities: "change summary, what changed, version changes, diff summary, release notes list, modification list",
      suggestedFor: [
        "change",
        "list",
        "change list"
      ],
      typicalSize: "medium",
      propsSchema: {
        changes: {
          types: [
            "array",
            "object"
          ],
          description: "The changes. Accepts a typed list or a render-ui payload binding.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "added",
                  "modified",
                  "removed"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              before: {
                types: [
                  "string"
                ]
              },
              after: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "type",
              "title"
            ]
          }
        },
        inspectEvent: {
          types: [
            "string"
          ],
          description: "Emitted by a change's Inspect button. Payload: { title, type }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "title",
              type: "string",
              required: true
            },
            {
              name: "type",
              type: "string",
              required: true,
              enumValues: [
                "added",
                "modified",
                "removed"
              ]
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "commit-row": {
      type: "commit-row",
      category: "component",
      sourceFile: "components/core/molecules/CommitRow.tsx",
      tier: "molecules",
      family: "core",
      description: "One version in a history list \u2014 a commit row with select and restore.",
      capabilities: "version history row, commit entry, revision list item, change log entry, restore point, undo history item",
      suggestedFor: [
        "commit",
        "row",
        "commit row"
      ],
      typicalSize: "medium",
      propsSchema: {
        sha: {
          types: [
            "string"
          ],
          description: "Commit id; the first 7 characters are shown, the whole id on hover.",
          required: true
        },
        message: {
          types: [
            "string"
          ],
          description: "Commit message.",
          required: true
        },
        author: {
          types: [
            "string"
          ],
          description: "author prop"
        },
        timestamp: {
          types: [
            "string"
          ],
          description: 'Display time (already formatted, e.g. "2 min ago").'
        },
        kind: {
          types: [
            "string"
          ],
          description: "What made this version (a user save, an agent turn, a restore, an undo/redo, a snapshot, a merge of a teammate's work).",
          enumValues: [
            "user",
            "agent",
            "restore",
            "undo",
            "redo",
            "snapshot",
            "merge"
          ]
        },
        additions: {
          types: [
            "number"
          ],
          description: "additions prop"
        },
        deletions: {
          types: [
            "number"
          ],
          description: "deletions prop"
        },
        selected: {
          types: [
            "boolean"
          ],
          description: "The row the user picked (shows its detail).",
          default: false
        },
        current: {
          types: [
            "boolean"
          ],
          description: "The version the app is at now \u2014 it cannot be restored to.",
          default: false
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Emitted when the row is picked. Payload: { sha }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "sha",
              type: "string",
              required: true
            }
          ]
        },
        restoreEvent: {
          types: [
            "string"
          ],
          description: "Emitted by the Restore button. Payload: { sha }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "sha",
              type: "string",
              required: true
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "avl-state-machine": {
      type: "avl-state-machine",
      category: "display",
      sourceFile: "components/avl/molecules/AvlStateMachine.tsx",
      tier: "molecules",
      family: "avl",
      description: "AvlStateMachine component",
      suggestedFor: [
        "avl",
        "state",
        "machine",
        "avl state machine"
      ],
      typicalSize: "medium",
      propsSchema: {
        trait: {
          types: [
            "object"
          ],
          description: "The trait to draw (see `parseTraitLevel`).",
          required: true,
          properties: {
            name: {
              types: [
                "string"
              ]
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            states: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  isInitial: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  },
                  isTerminal: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  }
                },
                required: [
                  "name"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  guard: {
                    types: [
                      "sexpr"
                    ],
                    nullable: true
                  },
                  effects: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        args: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "sexpr"
                            ]
                          }
                        }
                      },
                      required: [
                        "type",
                        "args"
                      ]
                    }
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "from",
                  "to",
                  "event",
                  "effects",
                  "index"
                ]
              }
            },
            emittedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listenedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            }
          },
          propertyRequired: [
            "name",
            "linkedEntity",
            "states",
            "transitions",
            "emittedEvents",
            "listenedEvents"
          ],
          example: {
            name: "OrderFlow",
            linkedEntity: "Order",
            states: [
              {
                name: "browsing",
                isInitial: true,
                isTerminal: false
              },
              {
                name: "editing",
                isInitial: false,
                isTerminal: false
              },
              {
                name: "saving",
                isInitial: false,
                isTerminal: false
              },
              {
                name: "confirmed",
                isInitial: false,
                isTerminal: true
              },
              {
                name: "failed",
                isInitial: false,
                isTerminal: false
              }
            ],
            transitions: [
              {
                from: "browsing",
                to: "editing",
                event: "EDIT",
                effects: [
                  {
                    type: "render-ui",
                    args: []
                  }
                ],
                index: 0
              },
              {
                from: "editing",
                to: "saving",
                event: "SAVE",
                effects: [
                  {
                    type: "persist",
                    args: []
                  },
                  {
                    type: "notify",
                    args: []
                  }
                ],
                index: 1
              },
              {
                from: "saving",
                to: "confirmed",
                event: "SAVED",
                effects: [
                  {
                    type: "emit",
                    args: []
                  },
                  {
                    type: "render-ui",
                    args: []
                  }
                ],
                index: 2
              },
              {
                from: "saving",
                to: "failed",
                event: "SAVE_FAILED",
                effects: [
                  {
                    type: "notify",
                    args: []
                  }
                ],
                index: 3
              },
              {
                from: "failed",
                to: "editing",
                event: "RETRY",
                effects: [],
                index: 4
              },
              {
                from: "editing",
                to: "browsing",
                event: "CANCEL",
                effects: [
                  {
                    type: "render-ui",
                    args: []
                  }
                ],
                index: 5
              },
              {
                from: "confirmed",
                to: "browsing",
                event: "DONE",
                effects: [
                  {
                    type: "navigate",
                    args: []
                  }
                ],
                index: 6
              }
            ],
            emittedEvents: [
              "ORDER_SAVED"
            ],
            listenedEvents: [
              "PAYMENT_OK"
            ]
          }
        },
        activeState: {
          types: [
            "string"
          ],
          description: "State execution is currently in."
        },
        activeTransition: {
          types: [
            "number"
          ],
          description: "`index` of the transition that just fired."
        },
        visitedStates: {
          types: [
            "array"
          ],
          description: "States already passed through, in order.",
          items: {
            types: [
              "string"
            ]
          }
        },
        stateClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{stateClickEvent} with { stateId } when a state is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stateId",
              type: "string",
              required: true
            }
          ]
        },
        transitionClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{transitionClickEvent} with { index, event, from, to } when a transition label is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "event",
              type: "string",
              required: true
            },
            {
              name: "from",
              type: "string",
              required: true
            },
            {
              name: "to",
              type: "string",
              required: true
            }
          ]
        },
        onTransitionClick: {
          types: [
            "function"
          ],
          description: "Direct transition-click callback, for React hosts (e.g. a canvas node).",
          kind: "callback",
          callbackArgs: [
            {
              name: "transition",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  index: {
                    types: [
                      "number"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "index",
                  "event",
                  "from",
                  "to"
                ]
              }
            }
          ]
        },
        selectedState: {
          types: [
            "string"
          ],
          description: "State picked in an editor (first click)."
        },
        pendingSourceState: {
          types: [
            "string"
          ],
          description: "Source of a transition being drawn \u2014 the next state click picks its target."
        },
        nodeShape: {
          types: [
            "string"
          ],
          description: "Node silhouette. @default 'pill'",
          enumValues: [
            "pill",
            "gear"
          ],
          default: "pill"
        },
        entityFields: {
          types: [
            "array"
          ],
          description: "Entity field names listed in the header.",
          items: {
            types: [
              "string"
            ]
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Flow direction. @default 'ltr'",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        showHeader: {
          types: [
            "boolean"
          ],
          description: "Show the trait header (name, entity, listens/emits). @default true",
          default: true
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations shown in a popover on hover: a note per state name, per transition event and per effect type.",
          properties: {
            states: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            transitions: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            effects: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            }
          },
          example: {
            states: {
              pending: {
                title: "Unpaid",
                body: "Every order starts here."
              }
            },
            transitions: {
              PAY: {
                body: "Only fires when the amount is positive."
              }
            },
            effects: {
              persist: {
                body: "Saves the order."
              }
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "avl-glyph": {
      type: "avl-glyph",
      category: "component",
      sourceFile: "components/avl/molecules/AvlGlyph.tsx",
      tier: "molecules",
      family: "avl",
      description: "AvlGlyph component",
      suggestedFor: [
        "avl",
        "glyph",
        "avl glyph"
      ],
      typicalSize: "medium",
      propsSchema: {
        kind: {
          types: [
            "string"
          ],
          description: "Which notation symbol to draw.",
          required: true,
          enumValues: [
            "application",
            "orbital",
            "entity",
            "trait",
            "page",
            "state",
            "transition",
            "event",
            "guard",
            "effect",
            "field",
            "fieldType",
            "binding",
            "bindingRef",
            "persistence",
            "operator",
            "sexpr",
            "literal"
          ],
          example: "state"
        },
        label: {
          types: [
            "string"
          ],
          description: "Caption, and the drawn text for operator / literal / bindingRef symbols."
        },
        effectType: {
          types: [
            "string"
          ],
          description: "Effect symbol variant. @default 'set'",
          default: "set"
        },
        fieldType: {
          types: [
            "object"
          ],
          description: "Field-type symbol variant. @default 'string'",
          default: "string"
        },
        persistence: {
          types: [
            "object"
          ],
          description: "Persistence symbol variant (also the entity's ring). @default 'persistent'",
          default: "persistent"
        },
        namespace: {
          types: [
            "object"
          ],
          description: "Operator category tint. @default 'arithmetic'",
          default: "arithmetic"
        },
        size: {
          types: [
            "string"
          ],
          description: "Symbol height. @default 'md'",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        showCaption: {
          types: [
            "boolean"
          ],
          description: "Show the caption under the symbol. @default true",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "agent-activity-feed": {
      type: "agent-activity-feed",
      category: "component",
      sourceFile: "components/core/molecules/AgentActivityFeed.tsx",
      tier: "molecules",
      family: "core",
      description: "AgentActivityFeed component",
      suggestedFor: [
        "agent",
        "activity",
        "feed",
        "agent activity feed"
      ],
      typicalSize: "medium",
      propsSchema: {
        activities: {
          types: [
            "array"
          ],
          description: "activities prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "message"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "user",
                      "assistant",
                      "system"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isStreaming: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "role",
                  "content",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_call"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  argsText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isExecuting: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "argsText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_result"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  detail: {
                    types: [
                      "string"
                    ]
                  },
                  rows: {
                    types: [
                      "number"
                    ]
                  },
                  resultText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "success",
                  "resultText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "file_operation"
                    ]
                  },
                  operation: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ls",
                      "read_file",
                      "write_file",
                      "edit_file"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "operation",
                  "path",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_diff"
                    ]
                  },
                  filePath: {
                    types: [
                      "string"
                    ]
                  },
                  hunks: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        oldStart: {
                          types: [
                            "number"
                          ]
                        },
                        oldLines: {
                          types: [
                            "number"
                          ]
                        },
                        newStart: {
                          types: [
                            "number"
                          ]
                        },
                        newLines: {
                          types: [
                            "number"
                          ]
                        },
                        lines: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ],
                                enumValues: [
                                  "add",
                                  "remove",
                                  "context"
                                ]
                              },
                              content: {
                                types: [
                                  "string"
                                ]
                              }
                            },
                            required: [
                              "type",
                              "content"
                            ]
                          }
                        }
                      },
                      required: [
                        "oldStart",
                        "oldLines",
                        "newStart",
                        "newLines",
                        "lines"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "filePath",
                  "hunks",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "error"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "coordinator_decision"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  reason: {
                    types: [
                      "string"
                    ]
                  },
                  priorOrganism: {
                    types: [
                      "string"
                    ],
                    nullable: true
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "reason",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "plan_committed"
                    ]
                  },
                  orbitals: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitals",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pending_question"
                    ]
                  },
                  questionId: {
                    types: [
                      "string"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "questionId",
                  "question",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "clarification_question"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "organism",
                      "molecule",
                      "atom_trait"
                    ]
                  },
                  scope: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      }
                    }
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  candidates: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        id: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        description: {
                          types: [
                            "string"
                          ]
                        },
                        whyThisFits: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "id",
                        "label",
                        "description",
                        "whyThisFits"
                      ]
                    }
                  },
                  skipDefault: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "scope",
                  "question",
                  "candidates",
                  "skipDefault",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "analysis"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  organismReason: {
                    types: [
                      "string"
                    ]
                  },
                  complexity: {
                    types: [
                      "object"
                    ],
                    properties: {
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      reasoning: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "category",
                      "reasoning"
                    ]
                  },
                  renames: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to"
                      ]
                    }
                  },
                  deletes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "renames",
                  "deletes",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_started"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_done"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_change"
                    ]
                  },
                  changeKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital-added",
                      "orbital-removed",
                      "entity-fields-changed",
                      "trait-added",
                      "trait-removed",
                      "trait-config-changed",
                      "state-machine-changed",
                      "guard-changed",
                      "effect-changed",
                      "render-ui-changed",
                      "event-wiring-changed",
                      "page-changed",
                      "theme-changed",
                      "behavior-composed"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "changeKind",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "done"
                    ]
                  },
                  orbitalCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalCount",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "cancelled"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "llm_response"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  llm: {
                    types: [
                      "object"
                    ],
                    properties: {
                      service: {
                        types: [
                          "string"
                        ]
                      },
                      provider: {
                        types: [
                          "string"
                        ]
                      },
                      model: {
                        types: [
                          "string"
                        ]
                      },
                      systemPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      userPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      toolCallsJson: {
                        types: [
                          "string"
                        ]
                      },
                      attempt: {
                        types: [
                          "number"
                        ]
                      },
                      durationMs: {
                        types: [
                          "number"
                        ]
                      },
                      promptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      completionTokens: {
                        types: [
                          "number"
                        ]
                      },
                      cachedPromptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      costUSD: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "service",
                      "provider",
                      "model",
                      "systemPrompt",
                      "userPrompt",
                      "durationMs"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "content",
                  "timestamp"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        working: {
          types: [
            "boolean"
          ],
          description: "The agent is running: show the thinking row after the last activity.",
          default: false
        },
        onJump: {
          types: [
            "function"
          ],
          description: "onJump prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "target",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "orbital"
                ]
              }
            }
          ]
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: "emptyTitle prop"
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "emptyDescription prop"
        },
        followLatest: {
          types: [
            "boolean"
          ],
          description: "Scroll the latest row into view as rows arrive. Off for a list of feeds (a run history). @default true",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "agent-activity-row": {
      type: "agent-activity-row",
      category: "component",
      sourceFile: "components/core/molecules/AgentActivityFeed.tsx",
      tier: "molecules",
      family: "core",
      description: "AgentActivityRow component",
      suggestedFor: [
        "agent",
        "activity",
        "row",
        "agent activity row"
      ],
      typicalSize: "medium",
      propsSchema: {
        activity: {
          types: [
            "object"
          ],
          description: "activity prop",
          required: true,
          variants: [
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "message"
                  ]
                },
                role: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "user",
                    "assistant",
                    "system"
                  ]
                },
                content: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                },
                isStreaming: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "type",
                "role",
                "content",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "tool_call"
                  ]
                },
                tool: {
                  types: [
                    "string"
                  ]
                },
                kind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "input",
                    "read",
                    "invalid"
                  ]
                },
                argsText: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                },
                isExecuting: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "type",
                "tool",
                "argsText",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "tool_result"
                  ]
                },
                tool: {
                  types: [
                    "string"
                  ]
                },
                kind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "input",
                    "read",
                    "invalid"
                  ]
                },
                success: {
                  types: [
                    "boolean"
                  ]
                },
                detail: {
                  types: [
                    "string"
                  ]
                },
                rows: {
                  types: [
                    "number"
                  ]
                },
                resultText: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "tool",
                "success",
                "resultText",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "file_operation"
                  ]
                },
                operation: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "ls",
                    "read_file",
                    "write_file",
                    "edit_file"
                  ]
                },
                path: {
                  types: [
                    "string"
                  ]
                },
                success: {
                  types: [
                    "boolean"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "operation",
                "path",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "schema_diff"
                  ]
                },
                filePath: {
                  types: [
                    "string"
                  ]
                },
                hunks: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      oldStart: {
                        types: [
                          "number"
                        ]
                      },
                      oldLines: {
                        types: [
                          "number"
                        ]
                      },
                      newStart: {
                        types: [
                          "number"
                        ]
                      },
                      newLines: {
                        types: [
                          "number"
                        ]
                      },
                      lines: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            type: {
                              types: [
                                "string"
                              ],
                              enumValues: [
                                "add",
                                "remove",
                                "context"
                              ]
                            },
                            content: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "type",
                            "content"
                          ]
                        }
                      }
                    },
                    required: [
                      "oldStart",
                      "oldLines",
                      "newStart",
                      "newLines",
                      "lines"
                    ]
                  }
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "filePath",
                "hunks",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "error"
                  ]
                },
                message: {
                  types: [
                    "string"
                  ]
                },
                code: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "message",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "coordinator_decision"
                  ]
                },
                organism: {
                  types: [
                    "string"
                  ]
                },
                reason: {
                  types: [
                    "string"
                  ]
                },
                priorOrganism: {
                  types: [
                    "string"
                  ],
                  nullable: true
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "organism",
                "reason",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "plan_committed"
                  ]
                },
                orbitals: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitals",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "pending_question"
                  ]
                },
                questionId: {
                  types: [
                    "string"
                  ]
                },
                question: {
                  types: [
                    "string"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "questionId",
                "question",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "clarification_question"
                  ]
                },
                level: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "organism",
                    "molecule",
                    "atom_trait"
                  ]
                },
                scope: {
                  types: [
                    "object"
                  ],
                  properties: {
                    orbitalName: {
                      types: [
                        "string"
                      ]
                    },
                    traitName: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                question: {
                  types: [
                    "string"
                  ]
                },
                candidates: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      id: {
                        types: [
                          "string"
                        ]
                      },
                      label: {
                        types: [
                          "string"
                        ]
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      },
                      whyThisFits: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "id",
                      "label",
                      "description",
                      "whyThisFits"
                    ]
                  }
                },
                skipDefault: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "level",
                "scope",
                "question",
                "candidates",
                "skipDefault",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "analysis"
                  ]
                },
                organism: {
                  types: [
                    "string"
                  ]
                },
                organismReason: {
                  types: [
                    "string"
                  ]
                },
                complexity: {
                  types: [
                    "object"
                  ],
                  properties: {
                    category: {
                      types: [
                        "string"
                      ]
                    },
                    reasoning: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "category",
                    "reasoning"
                  ]
                },
                renames: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      from: {
                        types: [
                          "string"
                        ]
                      },
                      to: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "from",
                      "to"
                    ]
                  }
                },
                deletes: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "organism",
                "renames",
                "deletes",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "orbital_started"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitalName",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "orbital_done"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                traitCount: {
                  types: [
                    "number"
                  ]
                },
                transitionCount: {
                  types: [
                    "number"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitalName",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "schema_change"
                  ]
                },
                changeKind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "orbital-added",
                    "orbital-removed",
                    "entity-fields-changed",
                    "trait-added",
                    "trait-removed",
                    "trait-config-changed",
                    "state-machine-changed",
                    "guard-changed",
                    "effect-changed",
                    "render-ui-changed",
                    "event-wiring-changed",
                    "page-changed",
                    "theme-changed",
                    "behavior-composed"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                traitName: {
                  types: [
                    "string"
                  ]
                },
                transitionEvent: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "changeKind",
                "orbitalName",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "done"
                  ]
                },
                orbitalCount: {
                  types: [
                    "number"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitalCount",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "cancelled"
                  ]
                },
                message: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "message",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "llm_response"
                  ]
                },
                content: {
                  types: [
                    "string"
                  ]
                },
                llm: {
                  types: [
                    "object"
                  ],
                  properties: {
                    service: {
                      types: [
                        "string"
                      ]
                    },
                    provider: {
                      types: [
                        "string"
                      ]
                    },
                    model: {
                      types: [
                        "string"
                      ]
                    },
                    systemPrompt: {
                      types: [
                        "string"
                      ]
                    },
                    userPrompt: {
                      types: [
                        "string"
                      ]
                    },
                    toolCallsJson: {
                      types: [
                        "string"
                      ]
                    },
                    attempt: {
                      types: [
                        "number"
                      ]
                    },
                    durationMs: {
                      types: [
                        "number"
                      ]
                    },
                    promptTokens: {
                      types: [
                        "number"
                      ]
                    },
                    completionTokens: {
                      types: [
                        "number"
                      ]
                    },
                    cachedPromptTokens: {
                      types: [
                        "number"
                      ]
                    },
                    costUSD: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "service",
                    "provider",
                    "model",
                    "systemPrompt",
                    "userPrompt",
                    "durationMs"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "content",
                "timestamp"
              ]
            }
          ],
          discriminator: "type"
        },
        onJump: {
          types: [
            "function"
          ],
          description: '"Jump to" affordance \u2014 rendered only when supplied and the activity names an orbital.',
          kind: "callback",
          callbackArgs: [
            {
              name: "target",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "orbital"
                ]
              }
            }
          ]
        },
        facts: {
          types: [
            "object"
          ],
          description: "What the row knows from its list (`activityRowFacts`).",
          required: true,
          properties: {
            settled: {
              types: [
                "boolean"
              ]
            },
            readyCount: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "settled",
            "readyCount"
          ]
        }
      }
    },
    "agent-chat-feed": {
      type: "agent-chat-feed",
      category: "component",
      sourceFile: "components/core/molecules/AgentChatFeed.tsx",
      tier: "molecules",
      family: "core",
      description: "AgentChatFeed \u2014 a chat region's view of an agent turn: the activities the host's audience selected, rendered by `AgentActivityFeed`, with raw LLM responses behind a \"Raw LLM\" switch (off by default, so the conversation stays readable until they're wanted). The switch is remembered per host.",
      suggestedFor: [
        "agent",
        "chat",
        "feed",
        "agent chat feed"
      ],
      typicalSize: "medium",
      propsSchema: {
        activities: {
          types: [
            "array"
          ],
          description: "Activities already selected by the host's manifest audience.",
          required: true,
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "message"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "user",
                      "assistant",
                      "system"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isStreaming: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "role",
                  "content",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_call"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  argsText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isExecuting: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "argsText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_result"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  detail: {
                    types: [
                      "string"
                    ]
                  },
                  rows: {
                    types: [
                      "number"
                    ]
                  },
                  resultText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "success",
                  "resultText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "file_operation"
                    ]
                  },
                  operation: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ls",
                      "read_file",
                      "write_file",
                      "edit_file"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "operation",
                  "path",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_diff"
                    ]
                  },
                  filePath: {
                    types: [
                      "string"
                    ]
                  },
                  hunks: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        oldStart: {
                          types: [
                            "number"
                          ]
                        },
                        oldLines: {
                          types: [
                            "number"
                          ]
                        },
                        newStart: {
                          types: [
                            "number"
                          ]
                        },
                        newLines: {
                          types: [
                            "number"
                          ]
                        },
                        lines: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ],
                                enumValues: [
                                  "add",
                                  "remove",
                                  "context"
                                ]
                              },
                              content: {
                                types: [
                                  "string"
                                ]
                              }
                            },
                            required: [
                              "type",
                              "content"
                            ]
                          }
                        }
                      },
                      required: [
                        "oldStart",
                        "oldLines",
                        "newStart",
                        "newLines",
                        "lines"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "filePath",
                  "hunks",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "error"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "coordinator_decision"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  reason: {
                    types: [
                      "string"
                    ]
                  },
                  priorOrganism: {
                    types: [
                      "string"
                    ],
                    nullable: true
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "reason",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "plan_committed"
                    ]
                  },
                  orbitals: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitals",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pending_question"
                    ]
                  },
                  questionId: {
                    types: [
                      "string"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "questionId",
                  "question",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "clarification_question"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "organism",
                      "molecule",
                      "atom_trait"
                    ]
                  },
                  scope: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      }
                    }
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  candidates: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        id: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        description: {
                          types: [
                            "string"
                          ]
                        },
                        whyThisFits: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "id",
                        "label",
                        "description",
                        "whyThisFits"
                      ]
                    }
                  },
                  skipDefault: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "scope",
                  "question",
                  "candidates",
                  "skipDefault",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "analysis"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  organismReason: {
                    types: [
                      "string"
                    ]
                  },
                  complexity: {
                    types: [
                      "object"
                    ],
                    properties: {
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      reasoning: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "category",
                      "reasoning"
                    ]
                  },
                  renames: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to"
                      ]
                    }
                  },
                  deletes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "renames",
                  "deletes",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_started"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_done"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_change"
                    ]
                  },
                  changeKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital-added",
                      "orbital-removed",
                      "entity-fields-changed",
                      "trait-added",
                      "trait-removed",
                      "trait-config-changed",
                      "state-machine-changed",
                      "guard-changed",
                      "effect-changed",
                      "render-ui-changed",
                      "event-wiring-changed",
                      "page-changed",
                      "theme-changed",
                      "behavior-composed"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "changeKind",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "done"
                    ]
                  },
                  orbitalCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalCount",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "cancelled"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "llm_response"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  llm: {
                    types: [
                      "object"
                    ],
                    properties: {
                      service: {
                        types: [
                          "string"
                        ]
                      },
                      provider: {
                        types: [
                          "string"
                        ]
                      },
                      model: {
                        types: [
                          "string"
                        ]
                      },
                      systemPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      userPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      toolCallsJson: {
                        types: [
                          "string"
                        ]
                      },
                      attempt: {
                        types: [
                          "number"
                        ]
                      },
                      durationMs: {
                        types: [
                          "number"
                        ]
                      },
                      promptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      completionTokens: {
                        types: [
                          "number"
                        ]
                      },
                      cachedPromptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      costUSD: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "service",
                      "provider",
                      "model",
                      "systemPrompt",
                      "userPrompt",
                      "durationMs"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "content",
                  "timestamp"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: "emptyTitle prop",
          required: true
        },
        rawLlmPreferenceKey: {
          types: [
            "string"
          ],
          description: "localStorage key remembering the Raw LLM switch for this host.",
          required: true
        },
        idPrefix: {
          types: [
            "string"
          ],
          description: "Prefix for the scroller test id and the switch's element id.",
          required: true
        },
        working: {
          types: [
            "boolean"
          ],
          description: "The agent is running \u2014 the feed shows it is thinking.",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "avl-transition-explainer": {
      type: "avl-transition-explainer",
      category: "component",
      sourceFile: "components/avl/molecules/AvlTransitionExplainer.tsx",
      tier: "molecules",
      family: "avl",
      description: "One transition explained step by step: from-state, event and to-state, the guard that must hold, and each effect it runs in order.",
      capabilities: "transition explainer, event guard effects walkthrough, what happens when an event fires",
      suggestedFor: [
        "avl",
        "transition",
        "explainer",
        "avl transition explainer"
      ],
      typicalSize: "medium",
      propsSchema: {
        orbitalName: {
          types: [
            "string"
          ],
          description: "Orbital the transition belongs to.",
          required: true
        },
        traitName: {
          types: [
            "string"
          ],
          description: "Trait the transition belongs to.",
          required: true
        },
        transition: {
          types: [
            "object"
          ],
          description: "The transition to explain.",
          required: true,
          properties: {
            from: {
              types: [
                "string"
              ]
            },
            to: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ]
            },
            guard: {
              types: [
                "sexpr"
              ]
            },
            effects: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  args: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "sexpr"
                      ]
                    }
                  }
                },
                required: [
                  "type",
                  "args"
                ]
              }
            },
            index: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "from",
            "to",
            "event",
            "effects",
            "index"
          ],
          example: {
            from: "pending",
            to: "paid",
            event: "PAY",
            effects: [
              {
                type: "set",
                args: []
              },
              {
                type: "persist",
                args: []
              }
            ],
            index: 0
          }
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations shown in a popover on hover: the event's note and per-effect-type notes.",
          properties: {
            states: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            transitions: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            effects: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            }
          },
          example: {
            transitions: {
              PAY: {
                title: "Paying",
                body: "The customer pays the order total."
              }
            },
            effects: {
              persist: {
                body: "Saves the order."
              }
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "flow-canvas": {
      type: "flow-canvas",
      category: "display",
      sourceFile: "components/avl/organisms/FlowCanvas.tsx",
      tier: "organisms",
      family: "avl",
      description: "FlowCanvas component",
      suggestedFor: [
        "flow",
        "canvas",
        "flow canvas"
      ],
      typicalSize: "large",
      propsSchema: {
        schema: {
          types: [
            "sexpr",
            "string"
          ],
          description: "schema prop",
          required: true,
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core",
            acceptsString: true
          }
        },
        mockData: {
          types: [
            "object"
          ],
          description: "mockData prop",
          nonAuthorable: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number",
            "string"
          ],
          description: "width prop",
          default: "100%"
        },
        height: {
          types: [
            "number",
            "string"
          ],
          description: "height prop",
          default: 500
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "onNodeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "context",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "overview",
                      "trait-expanded",
                      "system",
                      "code",
                      "transition"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  transitionIndex: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital"
                ]
              }
            }
          ]
        },
        systemMap: {
          types: [
            "object"
          ],
          description: 'With `initialLevel="system"`: what an orbital chip does (drill in, hover preview, open code, preview).',
          properties: {
            openTraits: {
              types: [
                "function"
              ]
            },
            renderPreview: {
              types: [
                "function"
              ]
            },
            openCodeEvent: {
              types: [
                "string"
              ]
            },
            previewEvent: {
              types: [
                "string"
              ]
            },
            highlighted: {
              types: [
                "string"
              ]
            },
            selectDependency: {
              types: [
                "function"
              ]
            }
          },
          propertyRequired: [
            "openTraits"
          ]
        },
        systemDependencies: {
          types: [
            "object"
          ],
          description: 'With `initialLevel="system"`: the app\'s imports, for the Dependencies lens (absent: Events only).',
          properties: {
            orbitals: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            behaviors: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  layer: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "io",
                      "std",
                      "primitive",
                      "unknown"
                    ]
                  },
                  imports: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "layer",
                  "imports"
                ]
              }
            }
          },
          propertyRequired: [
            "orbitals",
            "behaviors"
          ]
        },
        traitView: {
          types: [
            "object"
          ],
          description: "Trait level: which lens (and trait filter) shows \u2014 pass with `onTraitViewChange` to keep it across remounts.",
          properties: {
            lens: {
              types: [
                "string"
              ],
              enumValues: [
                "flow",
                "traits"
              ]
            },
            filter: {
              types: [
                "object"
              ],
              properties: {
                unit: {
                  types: [
                    "string"
                  ]
                },
                traits: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                }
              },
              required: [
                "unit",
                "traits"
              ],
              nullable: true
            }
          },
          propertyRequired: [
            "lens"
          ]
        },
        onTraitViewChange: {
          types: [
            "function"
          ],
          description: "onTraitViewChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "view",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  lens: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "flow",
                      "traits"
                    ]
                  },
                  filter: {
                    types: [
                      "object"
                    ],
                    properties: {
                      unit: {
                        types: [
                          "string"
                        ]
                      },
                      traits: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      }
                    },
                    required: [
                      "unit",
                      "traits"
                    ],
                    nullable: true
                  }
                },
                required: [
                  "lens"
                ]
              }
            }
          ]
        },
        openBehaviorEvent: {
          types: [
            "string"
          ],
          description: "Dependencies lens: emits UI:{openBehaviorEvent} with { name } to open a selected behavior's source.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "name",
              type: "string",
              required: true
            }
          ]
        },
        scene: {
          types: [
            "object"
          ],
          description: 'With `initialLevel="trait-expanded"`: played steps lighting each trait card\'s state machine.',
          properties: {
            steps: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  payload: {
                    types: [
                      "object"
                    ],
                    eventPayloadBrand: true
                  },
                  result: {
                    types: [
                      "object"
                    ]
                  }
                },
                required: [
                  "orbital",
                  "trait",
                  "from",
                  "payload",
                  "result"
                ]
              }
            },
            cursor: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "steps",
            "cursor"
          ]
        },
        onFocusChange: {
          types: [
            "function"
          ],
          description: "Fired when the focused orbital, the scope, or the focused card's state changes.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "focus",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  scope: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "local",
                      "world"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "orbital",
                  "scope",
                  "state"
                ]
              }
            }
          ]
        },
        initialOrbital: {
          types: [
            "string"
          ],
          description: 'The orbital cards open on (local view) \u2014 or, with `initialLevel="trait-expanded"`, the orbital whose traits are shown.'
        },
        initialLevel: {
          types: [
            "string"
          ],
          description: "`trait-expanded`: one card per trait of `initialOrbital` (cosmic). Default: orbital cards.",
          enumValues: [
            "overview",
            "trait-expanded",
            "system"
          ]
        },
        focusedOrbital: {
          types: [
            "string"
          ],
          description: "Focus this orbital (e.g. picked in a Layers panel); changing it moves the focus without remounting."
        },
        initialSelectedNode: {
          types: [
            "object"
          ],
          description: "Pre-select a node on mount (opens OrbInspector).",
          decoder: {
            name: "decodePreviewNodeData",
            from: "@almadar/ui/avl"
          },
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            traitName: {
              types: [
                "string"
              ]
            },
            stateName: {
              types: [
                "string"
              ]
            },
            transitionEvent: {
              types: [
                "string"
              ]
            },
            fromState: {
              types: [
                "string"
              ]
            },
            toState: {
              types: [
                "string"
              ]
            },
            cardWidth: {
              types: [
                "number"
              ]
            },
            cardLabel: {
              types: [
                "string"
              ],
              enumValues: [
                "screen"
              ]
            },
            enteredBy: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            patterns: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  pattern: {
                    types: [
                      "sexpr"
                    ]
                  }
                },
                required: [
                  "slot",
                  "pattern"
                ]
              }
            },
            eventSources: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  positionHint: {
                    types: [
                      "number"
                    ]
                  },
                  payloadFields: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        required: {
                          types: [
                            "boolean"
                          ]
                        }
                      },
                      required: [
                        "name",
                        "type"
                      ]
                    }
                  }
                },
                required: [
                  "event",
                  "patternType",
                  "path",
                  "positionHint"
                ]
              }
            },
            layer: {
              types: [
                "string"
              ]
            },
            stateRole: {
              types: [
                "string"
              ],
              enumValues: [
                "initial",
                "terminal",
                "hub",
                "default"
              ]
            },
            effectTypes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            guard: {
              types: [
                "sexpr"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "string"
              ]
            },
            fieldCount: {
              types: [
                "number"
              ]
            },
            traitCount: {
              types: [
                "number"
              ]
            },
            pageRoutes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            status: {
              types: [
                "string"
              ],
              enumValues: [
                "idle",
                "running",
                "success",
                "error"
              ]
            },
            sourceSchemaName: {
              types: [
                "string"
              ]
            },
            focused: {
              types: [
                "boolean"
              ]
            },
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "trait-card",
                "system-orbital",
                "system-band",
                "dependency",
                "dependency-column"
              ]
            },
            dependencyColumn: {
              types: [
                "string"
              ],
              enumValues: [
                "app",
                "own",
                "io",
                "std",
                "primitive",
                "unknown",
                "client",
                "host",
                "service",
                "store",
                "external"
              ]
            },
            dependencyRole: {
              types: [
                "string"
              ],
              enumValues: [
                "selected",
                "upstream",
                "downstream",
                "both",
                "dim"
              ]
            },
            flowTraits: {
              types: [
                "number"
              ]
            },
            flowRenderPieces: {
              types: [
                "number"
              ]
            },
            unitStatus: {
              types: [
                "string"
              ],
              enumValues: [
                "ok",
                "degraded",
                "down",
                "idle"
              ]
            },
            unitMetric: {
              types: [
                "string"
              ]
            },
            bandKind: {
              types: [
                "string"
              ],
              enumValues: [
                "connected",
                "standalone"
              ]
            },
            bandCount: {
              types: [
                "number"
              ]
            },
            wireEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  fromState: {
                    types: [
                      "string"
                    ]
                  },
                  toState: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "event",
                  "fromState",
                  "toState"
                ]
              }
            },
            emits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listens: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            _fullSchema: {
              types: [
                "sexpr"
              ]
            },
            _mockData: {
              types: [
                "object"
              ],
              nonAuthorable: true
            }
          },
          propertyRequired: [
            "orbitalName",
            "patterns",
            "eventSources"
          ]
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "Enable editing in the inspector. When true, fields become inputs."
        },
        onSchemaChange: {
          types: [
            "function"
          ],
          description: "Called when the user edits the schema via the inspector.",
          kind: "callback",
          callbackArgs: [
            {
              name: "schema",
              type: "sexpr"
            }
          ]
        },
        onPatternDelete: {
          types: [
            "function"
          ],
          description: "Called when the user presses Delete/Backspace with patterns selected (all of a multi-select).",
          kind: "callback",
          callbackArgs: [
            {
              name: "context",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  patternIds: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  nodeData: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      },
                      stateName: {
                        types: [
                          "string"
                        ]
                      },
                      transitionEvent: {
                        types: [
                          "string"
                        ]
                      },
                      fromState: {
                        types: [
                          "string"
                        ]
                      },
                      toState: {
                        types: [
                          "string"
                        ]
                      },
                      cardWidth: {
                        types: [
                          "number"
                        ]
                      },
                      cardLabel: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "screen"
                        ]
                      },
                      enteredBy: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      patterns: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            slot: {
                              types: [
                                "string"
                              ]
                            },
                            pattern: {
                              types: [
                                "sexpr"
                              ]
                            }
                          },
                          required: [
                            "slot",
                            "pattern"
                          ]
                        }
                      },
                      eventSources: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            patternType: {
                              types: [
                                "string"
                              ]
                            },
                            label: {
                              types: [
                                "string"
                              ]
                            },
                            path: {
                              types: [
                                "string"
                              ]
                            },
                            positionHint: {
                              types: [
                                "number"
                              ]
                            },
                            payloadFields: {
                              types: [
                                "array"
                              ],
                              items: {
                                types: [
                                  "object"
                                ],
                                properties: {
                                  name: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  type: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  required: {
                                    types: [
                                      "boolean"
                                    ]
                                  }
                                },
                                required: [
                                  "name",
                                  "type"
                                ]
                              }
                            }
                          },
                          required: [
                            "event",
                            "patternType",
                            "path",
                            "positionHint"
                          ]
                        }
                      },
                      layer: {
                        types: [
                          "string"
                        ]
                      },
                      stateRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "initial",
                          "terminal",
                          "hub",
                          "default"
                        ]
                      },
                      effectTypes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      guard: {
                        types: [
                          "sexpr"
                        ]
                      },
                      entityName: {
                        types: [
                          "string"
                        ]
                      },
                      persistence: {
                        types: [
                          "string"
                        ]
                      },
                      fieldCount: {
                        types: [
                          "number"
                        ]
                      },
                      traitCount: {
                        types: [
                          "number"
                        ]
                      },
                      pageRoutes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      status: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "idle",
                          "running",
                          "success",
                          "error"
                        ]
                      },
                      sourceSchemaName: {
                        types: [
                          "string"
                        ]
                      },
                      focused: {
                        types: [
                          "boolean"
                        ]
                      },
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "trait-card",
                          "system-orbital",
                          "system-band",
                          "dependency",
                          "dependency-column"
                        ]
                      },
                      dependencyColumn: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "app",
                          "own",
                          "io",
                          "std",
                          "primitive",
                          "unknown",
                          "client",
                          "host",
                          "service",
                          "store",
                          "external"
                        ]
                      },
                      dependencyRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "selected",
                          "upstream",
                          "downstream",
                          "both",
                          "dim"
                        ]
                      },
                      flowTraits: {
                        types: [
                          "number"
                        ]
                      },
                      flowRenderPieces: {
                        types: [
                          "number"
                        ]
                      },
                      unitStatus: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "ok",
                          "degraded",
                          "down",
                          "idle"
                        ]
                      },
                      unitMetric: {
                        types: [
                          "string"
                        ]
                      },
                      bandKind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "connected",
                          "standalone"
                        ]
                      },
                      bandCount: {
                        types: [
                          "number"
                        ]
                      },
                      wireEvents: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            fromState: {
                              types: [
                                "string"
                              ]
                            },
                            toState: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "event",
                            "fromState",
                            "toState"
                          ]
                        }
                      },
                      emits: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      listens: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      linkedEntity: {
                        types: [
                          "string"
                        ]
                      },
                      _fullSchema: {
                        types: [
                          "sexpr"
                        ]
                      },
                      _mockData: {
                        types: [
                          "object"
                        ],
                        nonAuthorable: true
                      }
                    },
                    required: [
                      "orbitalName",
                      "patterns",
                      "eventSources"
                    ]
                  },
                  elements: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        level: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "node",
                            "slot",
                            "field",
                            "effect",
                            "trait",
                            "page",
                            "orbital"
                          ]
                        },
                        orbital: {
                          types: [
                            "string"
                          ]
                        },
                        trait: {
                          types: [
                            "string"
                          ]
                        },
                        transition: {
                          types: [
                            "string"
                          ]
                        },
                        state: {
                          types: [
                            "string"
                          ]
                        },
                        slot: {
                          types: [
                            "string"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        patternType: {
                          types: [
                            "string"
                          ]
                        },
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        source: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "level",
                        "orbital",
                        "label"
                      ]
                    }
                  }
                },
                required: [
                  "patternIds",
                  "nodeData"
                ]
              }
            }
          ]
        },
        tools: {
          types: [
            "array"
          ],
          description: "Editing tools the canvas turns on (a persona's shell manifest declares them); all by default.",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "multiSelect",
              "clipboard",
              "inlineText",
              "zoomToSelection",
              "designHandles",
              "autoLayout",
              "sizeLimits",
              "absolute",
              "guides",
              "measure"
            ]
          }
        },
        elementAccess: {
          types: [
            "function"
          ],
          description: "What each element lets the user change (knob, data-bound, fixed by a behavior); every prop is editable without it.",
          callbackArgs: [
            {
              name: "focus",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "node",
                      "slot",
                      "field",
                      "effect",
                      "trait",
                      "page",
                      "orbital"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  },
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  source: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital",
                  "label"
                ]
              }
            }
          ],
          renderCallback: true
        },
        onEventWire: {
          types: [
            "function"
          ],
          description: "Called when the user drags from a source handle to a target handle (event wiring).",
          kind: "callback",
          callbackArgs: [
            {
              name: "wire",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  eventName: {
                    types: [
                      "string"
                    ]
                  },
                  sourceOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  targetOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  sourceTraitName: {
                    types: [
                      "string"
                    ]
                  },
                  targetTraitName: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "eventName",
                  "sourceOrbital",
                  "targetOrbital"
                ]
              }
            }
          ]
        },
        behaviorMeta: {
          types: [
            "object"
          ],
          description: "Behavior layer metadata for node styling (layer color bands).",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              layer: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "layer"
            ]
          }
        },
        orbitalStatus: {
          types: [
            "object"
          ],
          description: "Per-orbital generation status. Keyed by orbital name. Drives the spinner + accent border treatment on the overview node and (future) the hover trace surface. Defaults to `'idle'` for missing entries. Consumers derive this from agent subagent_start/complete SSE events.",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        onOrbitalHover: {
          types: [
            "function"
          ],
          description: "Hover handler for overview-level orbital nodes. Fires with the orbital name on enter and `null` on leave. Reserved for the upcoming trace tooltip: consumers will use it to anchor a popover that shows the subagent's live trace + reasoning for the hovered orbital.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "orbitalName",
              type: "string"
            }
          ]
        },
        layoutHint: {
          types: [
            "string"
          ],
          description: "Layout hint: 'pipeline' renders nodes left-to-right, 'grid' (default) uses sqrt-based grid.",
          enumValues: [
            "pipeline",
            "grid"
          ]
        },
        onNodeSelect: {
          types: [
            "function"
          ],
          description: "Called when the user clicks a node in overview level (for composition hints).",
          kind: "callback",
          callbackArgs: [
            {
              name: "orbitalName",
              type: "string"
            }
          ]
        },
        onZoomChange: {
          types: [
            "function"
          ],
          description: "onZoomChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "level",
              type: "string"
            },
            {
              name: "context",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        focusTarget: {
          types: [
            "object"
          ],
          description: "focusTarget prop",
          properties: {
            type: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "type",
            "name"
          ]
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop"
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "animated prop"
        },
        initialTrait: {
          types: [
            "string"
          ],
          description: "initialTrait prop"
        },
        stateCoverage: {
          types: [
            "object"
          ],
          description: "stateCoverage prop",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        userType: {
          types: [
            "string"
          ],
          description: "Studio persona viewing the canvas. Drives `OrbInspector` tab/section visibility \u2014 designers and builders hide the raw `code` tab and the architect-only Entity / raw-guard / raw-effects sections; architects see everything. Default `'builder'` preserves pre-Phase-2 behavior.",
          enumValues: [
            "builder",
            "designer",
            "architect"
          ],
          default: "builder"
        },
        themeManifest: {
          types: [
            "object"
          ],
          description: "Project theme tokens (Design System tab only). Forwarded to `OrbInspector` so the Styles tab can render an editable token list when the selection originates from the synthesized `__design_system__` schema."
        },
        nodePositions: {
          types: [
            "object"
          ],
          description: "Persisted node positions keyed by node id. When the node set changes (level/schema switch), any id present here overrides the computed layout position, restoring a user's manual drag arrangement. Node ids are unique per view level, so a single flat map covers overview + expanded. A saved `width` restores a card frame the designer resized.",
          decoder: {
            name: "decodeNodePlacements",
            from: "@almadar/ui/avl"
          },
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          }
        },
        onPositionsChange: {
          types: [
            "function"
          ],
          description: "Fired on node drag-stop and card resize with the full {id \u2192 placement} map of the current node set, so the consumer can persist the arrangement.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "positions",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    },
                    width: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              }
            }
          ]
        },
        onSelectedNodeChange: {
          types: [
            "function"
          ],
          description: "Fired whenever the internal `selectedNode` changes \u2014 select, clear-on- escape, clear-on-level-change, and pattern-selection sync all route through this. Lets a consumer mirror selection into a persistent properties panel rendered outside the canvas (see `externalInspector`).",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  stateName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  fromState: {
                    types: [
                      "string"
                    ]
                  },
                  toState: {
                    types: [
                      "string"
                    ]
                  },
                  cardWidth: {
                    types: [
                      "number"
                    ]
                  },
                  cardLabel: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "screen"
                    ]
                  },
                  enteredBy: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  patterns: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        slot: {
                          types: [
                            "string"
                          ]
                        },
                        pattern: {
                          types: [
                            "sexpr"
                          ]
                        }
                      },
                      required: [
                        "slot",
                        "pattern"
                      ]
                    }
                  },
                  eventSources: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        patternType: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        positionHint: {
                          types: [
                            "number"
                          ]
                        },
                        payloadFields: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              name: {
                                types: [
                                  "string"
                                ]
                              },
                              type: {
                                types: [
                                  "string"
                                ]
                              },
                              required: {
                                types: [
                                  "boolean"
                                ]
                              }
                            },
                            required: [
                              "name",
                              "type"
                            ]
                          }
                        }
                      },
                      required: [
                        "event",
                        "patternType",
                        "path",
                        "positionHint"
                      ]
                    }
                  },
                  layer: {
                    types: [
                      "string"
                    ]
                  },
                  stateRole: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "initial",
                      "terminal",
                      "hub",
                      "default"
                    ]
                  },
                  effectTypes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  guard: {
                    types: [
                      "sexpr"
                    ]
                  },
                  entityName: {
                    types: [
                      "string"
                    ]
                  },
                  persistence: {
                    types: [
                      "string"
                    ]
                  },
                  fieldCount: {
                    types: [
                      "number"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  pageRoutes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  status: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "idle",
                      "running",
                      "success",
                      "error"
                    ]
                  },
                  sourceSchemaName: {
                    types: [
                      "string"
                    ]
                  },
                  focused: {
                    types: [
                      "boolean"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "trait-card",
                      "system-orbital",
                      "system-band",
                      "dependency",
                      "dependency-column"
                    ]
                  },
                  dependencyColumn: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "app",
                      "own",
                      "io",
                      "std",
                      "primitive",
                      "unknown",
                      "client",
                      "host",
                      "service",
                      "store",
                      "external"
                    ]
                  },
                  dependencyRole: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "selected",
                      "upstream",
                      "downstream",
                      "both",
                      "dim"
                    ]
                  },
                  flowTraits: {
                    types: [
                      "number"
                    ]
                  },
                  flowRenderPieces: {
                    types: [
                      "number"
                    ]
                  },
                  unitStatus: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ok",
                      "degraded",
                      "down",
                      "idle"
                    ]
                  },
                  unitMetric: {
                    types: [
                      "string"
                    ]
                  },
                  bandKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "connected",
                      "standalone"
                    ]
                  },
                  bandCount: {
                    types: [
                      "number"
                    ]
                  },
                  wireEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        fromState: {
                          types: [
                            "string"
                          ]
                        },
                        toState: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "event",
                        "fromState",
                        "toState"
                      ]
                    }
                  },
                  emits: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listens: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  linkedEntity: {
                    types: [
                      "string"
                    ]
                  },
                  _fullSchema: {
                    types: [
                      "sexpr"
                    ]
                  },
                  _mockData: {
                    types: [
                      "object"
                    ],
                    nonAuthorable: true
                  }
                },
                required: [
                  "orbitalName",
                  "patterns",
                  "eventSources"
                ]
              }
            }
          ]
        },
        onSelectedPatternChange: {
          types: [
            "function"
          ],
          description: "Fired whenever the in-node pattern selection changes (a pattern click inside `OrbPreviewNode`, cleared on background click / Escape / pattern delete). `PatternSelectionContext` is internal to this component's own render tree, so an externally-rendered `OrbInspector` (see `externalInspector`) has no other way to learn which pattern the user picked \u2014 pass this straight through to `OrbInspector`'s `selectedPattern` prop to keep Design-tab prop editing working outside the canvas.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "pattern",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  patternId: {
                    types: [
                      "string"
                    ]
                  },
                  nodeData: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      },
                      stateName: {
                        types: [
                          "string"
                        ]
                      },
                      transitionEvent: {
                        types: [
                          "string"
                        ]
                      },
                      fromState: {
                        types: [
                          "string"
                        ]
                      },
                      toState: {
                        types: [
                          "string"
                        ]
                      },
                      cardWidth: {
                        types: [
                          "number"
                        ]
                      },
                      cardLabel: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "screen"
                        ]
                      },
                      enteredBy: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      patterns: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            slot: {
                              types: [
                                "string"
                              ]
                            },
                            pattern: {
                              types: [
                                "sexpr"
                              ]
                            }
                          },
                          required: [
                            "slot",
                            "pattern"
                          ]
                        }
                      },
                      eventSources: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            patternType: {
                              types: [
                                "string"
                              ]
                            },
                            label: {
                              types: [
                                "string"
                              ]
                            },
                            path: {
                              types: [
                                "string"
                              ]
                            },
                            positionHint: {
                              types: [
                                "number"
                              ]
                            },
                            payloadFields: {
                              types: [
                                "array"
                              ],
                              items: {
                                types: [
                                  "object"
                                ],
                                properties: {
                                  name: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  type: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  required: {
                                    types: [
                                      "boolean"
                                    ]
                                  }
                                },
                                required: [
                                  "name",
                                  "type"
                                ]
                              }
                            }
                          },
                          required: [
                            "event",
                            "patternType",
                            "path",
                            "positionHint"
                          ]
                        }
                      },
                      layer: {
                        types: [
                          "string"
                        ]
                      },
                      stateRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "initial",
                          "terminal",
                          "hub",
                          "default"
                        ]
                      },
                      effectTypes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      guard: {
                        types: [
                          "sexpr"
                        ]
                      },
                      entityName: {
                        types: [
                          "string"
                        ]
                      },
                      persistence: {
                        types: [
                          "string"
                        ]
                      },
                      fieldCount: {
                        types: [
                          "number"
                        ]
                      },
                      traitCount: {
                        types: [
                          "number"
                        ]
                      },
                      pageRoutes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      status: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "idle",
                          "running",
                          "success",
                          "error"
                        ]
                      },
                      sourceSchemaName: {
                        types: [
                          "string"
                        ]
                      },
                      focused: {
                        types: [
                          "boolean"
                        ]
                      },
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "trait-card",
                          "system-orbital",
                          "system-band",
                          "dependency",
                          "dependency-column"
                        ]
                      },
                      dependencyColumn: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "app",
                          "own",
                          "io",
                          "std",
                          "primitive",
                          "unknown",
                          "client",
                          "host",
                          "service",
                          "store",
                          "external"
                        ]
                      },
                      dependencyRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "selected",
                          "upstream",
                          "downstream",
                          "both",
                          "dim"
                        ]
                      },
                      flowTraits: {
                        types: [
                          "number"
                        ]
                      },
                      flowRenderPieces: {
                        types: [
                          "number"
                        ]
                      },
                      unitStatus: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "ok",
                          "degraded",
                          "down",
                          "idle"
                        ]
                      },
                      unitMetric: {
                        types: [
                          "string"
                        ]
                      },
                      bandKind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "connected",
                          "standalone"
                        ]
                      },
                      bandCount: {
                        types: [
                          "number"
                        ]
                      },
                      wireEvents: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            fromState: {
                              types: [
                                "string"
                              ]
                            },
                            toState: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "event",
                            "fromState",
                            "toState"
                          ]
                        }
                      },
                      emits: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      listens: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      linkedEntity: {
                        types: [
                          "string"
                        ]
                      },
                      _fullSchema: {
                        types: [
                          "sexpr"
                        ]
                      },
                      _mockData: {
                        types: [
                          "object"
                        ],
                        nonAuthorable: true
                      }
                    },
                    required: [
                      "orbitalName",
                      "patterns",
                      "eventSources"
                    ]
                  },
                  sourceTrait: {
                    types: [
                      "string"
                    ]
                  },
                  rect: {
                    types: [
                      "object"
                    ],
                    properties: {
                      top: {
                        types: [
                          "number"
                        ]
                      },
                      left: {
                        types: [
                          "number"
                        ]
                      },
                      width: {
                        types: [
                          "number"
                        ]
                      },
                      height: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "top",
                      "left",
                      "width",
                      "height"
                    ]
                  },
                  selection: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  offsetInParent: {
                    types: [
                      "object"
                    ],
                    properties: {
                      left: {
                        types: [
                          "number"
                        ]
                      },
                      top: {
                        types: [
                          "number"
                        ]
                      },
                      right: {
                        types: [
                          "number"
                        ]
                      },
                      bottom: {
                        types: [
                          "number"
                        ]
                      },
                      parentWidth: {
                        types: [
                          "number"
                        ]
                      },
                      parentHeight: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "left",
                      "top",
                      "right",
                      "bottom",
                      "parentWidth",
                      "parentHeight"
                    ]
                  },
                  focus: {
                    types: [
                      "object"
                    ],
                    properties: {
                      level: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "node",
                          "slot",
                          "field",
                          "effect",
                          "trait",
                          "page",
                          "orbital"
                        ]
                      },
                      orbital: {
                        types: [
                          "string"
                        ]
                      },
                      trait: {
                        types: [
                          "string"
                        ]
                      },
                      transition: {
                        types: [
                          "string"
                        ]
                      },
                      state: {
                        types: [
                          "string"
                        ]
                      },
                      slot: {
                        types: [
                          "string"
                        ]
                      },
                      path: {
                        types: [
                          "string"
                        ]
                      },
                      patternType: {
                        types: [
                          "string"
                        ]
                      },
                      entity: {
                        types: [
                          "string"
                        ]
                      },
                      source: {
                        types: [
                          "string"
                        ]
                      },
                      label: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "level",
                      "orbital",
                      "label"
                    ]
                  },
                  elements: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        level: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "node",
                            "slot",
                            "field",
                            "effect",
                            "trait",
                            "page",
                            "orbital"
                          ]
                        },
                        orbital: {
                          types: [
                            "string"
                          ]
                        },
                        trait: {
                          types: [
                            "string"
                          ]
                        },
                        transition: {
                          types: [
                            "string"
                          ]
                        },
                        state: {
                          types: [
                            "string"
                          ]
                        },
                        slot: {
                          types: [
                            "string"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        patternType: {
                          types: [
                            "string"
                          ]
                        },
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        source: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "level",
                        "orbital",
                        "label"
                      ]
                    }
                  }
                },
                required: [
                  "patternType",
                  "nodeData"
                ]
              }
            }
          ]
        },
        externalInspector: {
          types: [
            "boolean"
          ],
          description: "When true, FlowCanvas does not render its inline `OrbInspector` \u2014 the consumer is expected to render one externally, fed by `onSelectedNodeChange` + `onSelectedPatternChange`. Selection highlighting, keyboard delete, and every other behavior is unchanged. Default `false` preserves the built-in inline panel.",
          default: false
        },
        verify: {
          types: [
            "object"
          ],
          description: "A host that verifies from the canvas: cards show verify controls and the trait's verdict, and follow `UI:CANVAS_SHOW_STATE` to the states a run reaches.",
          properties: {
            verdictOf: {
              types: [
                "function"
              ]
            },
            playing: {
              types: [
                "function"
              ]
            }
          },
          propertyRequired: [
            "verdictOf",
            "playing"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "layers-panel": {
      type: "layers-panel",
      category: "display",
      sourceFile: "components/avl/organisms/LayersPanel.tsx",
      tier: "organisms",
      family: "avl",
      description: "LayersPanel component",
      suggestedFor: [
        "layers",
        "panel",
        "layers panel"
      ],
      typicalSize: "large",
      propsSchema: {
        schema: {
          types: [
            "sexpr"
          ],
          description: "schema prop",
          required: true,
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "Currently selected node id (any level of the ID scheme above)."
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "onSelect prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onReorder: {
          types: [
            "function"
          ],
          description: "Reorder a node within the tree. Threaded to FileTree's `onNodeReorder` seam \u2014 presence-gated there, so omitting it just means no dragging.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "newParentId",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ]
        }
      }
    },
    "orb-inspector": {
      type: "orb-inspector",
      category: "debug",
      sourceFile: "components/avl/organisms/OrbInspector.tsx",
      tier: "organisms",
      family: "avl",
      description: "OrbInspector component",
      suggestedFor: [
        "orb",
        "inspector",
        "orb inspector"
      ],
      typicalSize: "large",
      propsSchema: {
        node: {
          types: [
            "object"
          ],
          description: "node prop",
          required: true,
          decoder: {
            name: "decodePreviewNodeData",
            from: "@almadar/ui/avl"
          },
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            traitName: {
              types: [
                "string"
              ]
            },
            stateName: {
              types: [
                "string"
              ]
            },
            transitionEvent: {
              types: [
                "string"
              ]
            },
            fromState: {
              types: [
                "string"
              ]
            },
            toState: {
              types: [
                "string"
              ]
            },
            cardWidth: {
              types: [
                "number"
              ]
            },
            cardLabel: {
              types: [
                "string"
              ],
              enumValues: [
                "screen"
              ]
            },
            enteredBy: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            patterns: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  pattern: {
                    types: [
                      "sexpr"
                    ]
                  }
                },
                required: [
                  "slot",
                  "pattern"
                ]
              }
            },
            eventSources: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  positionHint: {
                    types: [
                      "number"
                    ]
                  },
                  payloadFields: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        required: {
                          types: [
                            "boolean"
                          ]
                        }
                      },
                      required: [
                        "name",
                        "type"
                      ]
                    }
                  }
                },
                required: [
                  "event",
                  "patternType",
                  "path",
                  "positionHint"
                ]
              }
            },
            layer: {
              types: [
                "string"
              ]
            },
            stateRole: {
              types: [
                "string"
              ],
              enumValues: [
                "initial",
                "terminal",
                "hub",
                "default"
              ]
            },
            effectTypes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            guard: {
              types: [
                "sexpr"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "string"
              ]
            },
            fieldCount: {
              types: [
                "number"
              ]
            },
            traitCount: {
              types: [
                "number"
              ]
            },
            pageRoutes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            status: {
              types: [
                "string"
              ],
              enumValues: [
                "idle",
                "running",
                "success",
                "error"
              ]
            },
            sourceSchemaName: {
              types: [
                "string"
              ]
            },
            focused: {
              types: [
                "boolean"
              ]
            },
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "trait-card",
                "system-orbital",
                "system-band",
                "dependency",
                "dependency-column"
              ]
            },
            dependencyColumn: {
              types: [
                "string"
              ],
              enumValues: [
                "app",
                "own",
                "io",
                "std",
                "primitive",
                "unknown",
                "client",
                "host",
                "service",
                "store",
                "external"
              ]
            },
            dependencyRole: {
              types: [
                "string"
              ],
              enumValues: [
                "selected",
                "upstream",
                "downstream",
                "both",
                "dim"
              ]
            },
            flowTraits: {
              types: [
                "number"
              ]
            },
            flowRenderPieces: {
              types: [
                "number"
              ]
            },
            unitStatus: {
              types: [
                "string"
              ],
              enumValues: [
                "ok",
                "degraded",
                "down",
                "idle"
              ]
            },
            unitMetric: {
              types: [
                "string"
              ]
            },
            bandKind: {
              types: [
                "string"
              ],
              enumValues: [
                "connected",
                "standalone"
              ]
            },
            bandCount: {
              types: [
                "number"
              ]
            },
            wireEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  fromState: {
                    types: [
                      "string"
                    ]
                  },
                  toState: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "event",
                  "fromState",
                  "toState"
                ]
              }
            },
            emits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listens: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            _fullSchema: {
              types: [
                "sexpr"
              ]
            },
            _mockData: {
              types: [
                "object"
              ],
              nonAuthorable: true
            }
          },
          propertyRequired: [
            "orbitalName",
            "patterns",
            "eventSources"
          ]
        },
        schema: {
          types: [
            "sexpr"
          ],
          description: "schema prop",
          required: true,
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "editable prop",
          default: false
        },
        userType: {
          types: [
            "string"
          ],
          description: "Studio persona viewing the inspector. Controls tab visibility (Code is architect-only, Styles is universal) and section visibility (Entity, raw guard, raw effects are architect-only). Default `'builder'` matches pre-Phase-2 behavior except for the new Styles tab, which every persona gets.",
          enumValues: [
            "builder",
            "designer",
            "architect"
          ],
          default: "builder"
        },
        themeManifest: {
          types: [
            "object"
          ],
          description: "Project theme tokens. The Styles tab reads them for Global edits (and, on the synthesized `__design_system__` schema, lists them all); those edits emit `UI:PROP_CHANGE` with `scope: 'global'` + `tokenGroup`/`tokenKey`."
        },
        defaultTab: {
          types: [
            "string"
          ],
          description: "Initial tab on mount. Local state still owns the active tab after that \u2014 this does not make the component controlled. Enables deep-linking into a specific tab (e.g. from a LayersPanel selection).",
          enumValues: [
            "inspector",
            "design",
            "prototype",
            "code"
          ]
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "Fires whenever the tab-bar selection changes.",
          kind: "callback",
          callbackArgs: [
            {
              name: "tab",
              type: "string"
            }
          ]
        },
        onSchemaChange: {
          types: [
            "function"
          ],
          description: "onSchemaChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "schema",
              type: "sexpr"
            }
          ]
        },
        onClose: {
          types: [
            "function"
          ],
          description: "onClose prop",
          required: true,
          kind: "callback",
          callbackArgs: []
        },
        selectedPattern: {
          types: [
            "object"
          ],
          description: "In-node pattern selection, when this inspector is rendered OUTSIDE `FlowCanvas` (see `FlowCanvas`'s `externalInspector` + `onSelectedPatternChange`). `PatternSelectionContext` only reaches descendants of `FlowCanvas`'s own render tree, so a standalone consumer has no other way to feed pattern-level selection in \u2014 pass `FlowCanvas`'s `onSelectedPatternChange` payload straight through here. When omitted (the inline, built-in-inspector usage), falls back to `PatternSelectionContext`, unchanged.",
          decoder: {
            name: "decodeSelectedPattern",
            from: "@almadar/ui/avl"
          },
          properties: {
            patternType: {
              types: [
                "string"
              ]
            },
            patternId: {
              types: [
                "string"
              ]
            },
            nodeData: {
              types: [
                "object"
              ],
              properties: {
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                traitName: {
                  types: [
                    "string"
                  ]
                },
                stateName: {
                  types: [
                    "string"
                  ]
                },
                transitionEvent: {
                  types: [
                    "string"
                  ]
                },
                fromState: {
                  types: [
                    "string"
                  ]
                },
                toState: {
                  types: [
                    "string"
                  ]
                },
                cardWidth: {
                  types: [
                    "number"
                  ]
                },
                cardLabel: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "screen"
                  ]
                },
                enteredBy: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                patterns: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      slot: {
                        types: [
                          "string"
                        ]
                      },
                      pattern: {
                        types: [
                          "sexpr"
                        ]
                      }
                    },
                    required: [
                      "slot",
                      "pattern"
                    ]
                  }
                },
                eventSources: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      patternType: {
                        types: [
                          "string"
                        ]
                      },
                      label: {
                        types: [
                          "string"
                        ]
                      },
                      path: {
                        types: [
                          "string"
                        ]
                      },
                      positionHint: {
                        types: [
                          "number"
                        ]
                      },
                      payloadFields: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            name: {
                              types: [
                                "string"
                              ]
                            },
                            type: {
                              types: [
                                "string"
                              ]
                            },
                            required: {
                              types: [
                                "boolean"
                              ]
                            }
                          },
                          required: [
                            "name",
                            "type"
                          ]
                        }
                      }
                    },
                    required: [
                      "event",
                      "patternType",
                      "path",
                      "positionHint"
                    ]
                  }
                },
                layer: {
                  types: [
                    "string"
                  ]
                },
                stateRole: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "initial",
                    "terminal",
                    "hub",
                    "default"
                  ]
                },
                effectTypes: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                guard: {
                  types: [
                    "sexpr"
                  ]
                },
                entityName: {
                  types: [
                    "string"
                  ]
                },
                persistence: {
                  types: [
                    "string"
                  ]
                },
                fieldCount: {
                  types: [
                    "number"
                  ]
                },
                traitCount: {
                  types: [
                    "number"
                  ]
                },
                pageRoutes: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                status: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "idle",
                    "running",
                    "success",
                    "error"
                  ]
                },
                sourceSchemaName: {
                  types: [
                    "string"
                  ]
                },
                focused: {
                  types: [
                    "boolean"
                  ]
                },
                kind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "trait-card",
                    "system-orbital",
                    "system-band",
                    "dependency",
                    "dependency-column"
                  ]
                },
                dependencyColumn: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "app",
                    "own",
                    "io",
                    "std",
                    "primitive",
                    "unknown",
                    "client",
                    "host",
                    "service",
                    "store",
                    "external"
                  ]
                },
                dependencyRole: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "selected",
                    "upstream",
                    "downstream",
                    "both",
                    "dim"
                  ]
                },
                flowTraits: {
                  types: [
                    "number"
                  ]
                },
                flowRenderPieces: {
                  types: [
                    "number"
                  ]
                },
                unitStatus: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "ok",
                    "degraded",
                    "down",
                    "idle"
                  ]
                },
                unitMetric: {
                  types: [
                    "string"
                  ]
                },
                bandKind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "connected",
                    "standalone"
                  ]
                },
                bandCount: {
                  types: [
                    "number"
                  ]
                },
                wireEvents: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                transitions: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      fromState: {
                        types: [
                          "string"
                        ]
                      },
                      toState: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "event",
                      "fromState",
                      "toState"
                    ]
                  }
                },
                emits: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                listens: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                linkedEntity: {
                  types: [
                    "string"
                  ]
                },
                _fullSchema: {
                  types: [
                    "sexpr"
                  ]
                },
                _mockData: {
                  types: [
                    "object"
                  ],
                  nonAuthorable: true
                }
              },
              required: [
                "orbitalName",
                "patterns",
                "eventSources"
              ]
            },
            sourceTrait: {
              types: [
                "string"
              ]
            },
            rect: {
              types: [
                "object"
              ],
              properties: {
                top: {
                  types: [
                    "number"
                  ]
                },
                left: {
                  types: [
                    "number"
                  ]
                },
                width: {
                  types: [
                    "number"
                  ]
                },
                height: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "top",
                "left",
                "width",
                "height"
              ]
            },
            selection: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            offsetInParent: {
              types: [
                "object"
              ],
              properties: {
                left: {
                  types: [
                    "number"
                  ]
                },
                top: {
                  types: [
                    "number"
                  ]
                },
                right: {
                  types: [
                    "number"
                  ]
                },
                bottom: {
                  types: [
                    "number"
                  ]
                },
                parentWidth: {
                  types: [
                    "number"
                  ]
                },
                parentHeight: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "left",
                "top",
                "right",
                "bottom",
                "parentWidth",
                "parentHeight"
              ]
            },
            focus: {
              types: [
                "object"
              ],
              properties: {
                level: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "node",
                    "slot",
                    "field",
                    "effect",
                    "trait",
                    "page",
                    "orbital"
                  ]
                },
                orbital: {
                  types: [
                    "string"
                  ]
                },
                trait: {
                  types: [
                    "string"
                  ]
                },
                transition: {
                  types: [
                    "string"
                  ]
                },
                state: {
                  types: [
                    "string"
                  ]
                },
                slot: {
                  types: [
                    "string"
                  ]
                },
                path: {
                  types: [
                    "string"
                  ]
                },
                patternType: {
                  types: [
                    "string"
                  ]
                },
                entity: {
                  types: [
                    "string"
                  ]
                },
                source: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "level",
                "orbital",
                "label"
              ]
            },
            elements: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "node",
                      "slot",
                      "field",
                      "effect",
                      "trait",
                      "page",
                      "orbital"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  },
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  source: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital",
                  "label"
                ]
              }
            }
          },
          propertyRequired: [
            "patternType",
            "nodeData"
          ]
        },
        elementAccess: {
          types: [
            "function"
          ],
          description: "What the selected element lets the user change; `ElementEditAccessContext` (inside a canvas) when omitted.",
          callbackArgs: [
            {
              name: "focus",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "node",
                      "slot",
                      "field",
                      "effect",
                      "trait",
                      "page",
                      "orbital"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  },
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  source: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital",
                  "label"
                ]
              }
            }
          ],
          renderCallback: true
        }
      }
    }
  },
  categories: [
    "display",
    "header",
    "form",
    "filter",
    "navigation",
    "state",
    "dashboard",
    "container",
    "layout",
    "game",
    "debug",
    "meta",
    "component",
    "template",
    "visualization",
    "media"
  ]
};

// src/patterns/integrators-registry.json
var integrators_registry_default = {
  version: "1.0.0",
  exportedAt: "2026-10-09T19:11:33.426Z",
  integrators: {
    github: {
      name: "github",
      description: "GitHub integration - Git operations and GitHub REST API",
      category: "devtools",
      actions: [
        {
          name: "cloneRepo",
          description: "Clone a repository via the git CLI into a local target directory, using the configured token for auth.",
          params: [
            {
              name: "repoUrl",
              type: "string",
              required: true,
              description: "HTTPS clone URL, e.g. https://github.com/owner/repo. Owner/repo are parsed from it when not preconfigured."
            },
            {
              name: "targetDir",
              type: "string",
              required: true,
              description: "Local directory to clone into."
            },
            {
              name: "branch",
              type: "string",
              required: false,
              description: "Branch to check out after cloning; defaults to the repo's default branch."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "createBranch",
          description: "Create a new local branch via the git CLI in the configured working directory.",
          params: [
            {
              name: "branchName",
              type: "string",
              required: true,
              description: "Name of the branch to create."
            },
            {
              name: "baseBranch",
              type: "string",
              required: false,
              description: "Branch to branch from; defaults to the current branch."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "commit",
          description: "Commit staged (or given) changes via the git CLI in the configured working directory.",
          params: [
            {
              name: "message",
              type: "string",
              required: true,
              description: "Commit message."
            },
            {
              name: "files",
              type: "string[]",
              required: false,
              description: "Specific files to commit; when omitted, all changes are committed."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "push",
          description: "Push a local branch to the remote via the git CLI, using the configured token for auth.",
          params: [
            {
              name: "branchName",
              type: "string",
              required: true,
              description: "Branch name to push."
            },
            {
              name: "force",
              type: "boolean",
              required: false,
              description: "Force-push (overwrites remote history); never set true in an agent context."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "createPR",
          description: "Open a pull request via the GitHub REST API.",
          params: [
            {
              name: "title",
              type: "string",
              required: true,
              description: "Pull request title."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Pull request description body."
            },
            {
              name: "baseBranch",
              type: "string",
              required: true,
              description: "Branch the PR merges into, e.g. main."
            },
            {
              name: "headBranch",
              type: "string",
              required: true,
              description: "Branch the PR merges from (the feature branch)."
            },
            {
              name: "draft",
              type: "boolean",
              required: false,
              description: "Open as a draft PR instead of ready-for-review."
            }
          ],
          responseShape: {
            number: "number",
            url: "string",
            title: "string"
          }
        },
        {
          name: "getPRComments",
          description: "List review comments on a pull request via the GitHub REST API.",
          params: [
            {
              name: "prNumber",
              type: "number",
              required: true,
              description: "Pull request number."
            }
          ],
          responseShape: {
            comments: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "number",
                    required: true
                  },
                  body: {
                    type: "string",
                    required: true
                  },
                  user: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "listIssues",
          description: "List issues on the configured repository via the GitHub REST API.",
          params: [
            {
              name: "state",
              type: "string",
              required: false,
              description: "Issue state filter: 'open', 'closed', or 'all'; defaults to open."
            },
            {
              name: "labels",
              type: "string[]",
              required: false,
              description: "Only issues carrying all of these labels."
            },
            {
              name: "per_page",
              type: "number",
              required: false,
              description: "Page size for the GitHub API request."
            }
          ],
          responseShape: {
            issues: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  number: {
                    type: "number",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  state: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "getIssue",
          description: "Fetch a single issue's details via the GitHub REST API.",
          params: [
            {
              name: "issueNumber",
              type: "number",
              required: true,
              description: "Issue number."
            }
          ],
          responseShape: {
            number: "number",
            title: "string",
            body: "string",
            state: "string"
          }
        },
        {
          name: "getFile",
          description: "Read a file's content from a repository at a given ref (declared intent \u2014 dispatch is being wired).",
          params: [
            {
              name: "owner",
              type: "string",
              required: true,
              description: "Repository owner (user or org)."
            },
            {
              name: "repo",
              type: "string",
              required: true,
              description: "Repository name."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "File path within the repository."
            },
            {
              name: "ref",
              type: "string",
              required: false,
              description: "Branch, tag, or commit SHA to read from; defaults to the default branch."
            }
          ],
          responseShape: {
            path: "string",
            content: "string",
            sha: "string",
            size: "number"
          }
        },
        {
          name: "listCommits",
          description: "List commits on a repository, optionally filtered by file path (declared intent \u2014 dispatch is being wired).",
          params: [
            {
              name: "owner",
              type: "string",
              required: true,
              description: "Repository owner (user or org)."
            },
            {
              name: "repo",
              type: "string",
              required: true,
              description: "Repository name."
            },
            {
              name: "path",
              type: "string",
              required: false,
              description: "Only commits touching this file path."
            },
            {
              name: "ref",
              type: "string",
              required: false,
              description: "Branch, tag, or commit SHA to start from; defaults to the default branch."
            },
            {
              name: "per_page",
              type: "number",
              required: false,
              description: "Page size for the GitHub API request."
            }
          ],
          responseShape: {
            commits: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  sha: {
                    type: "string",
                    required: true
                  },
                  message: {
                    type: "string",
                    required: true
                  },
                  author: {
                    type: "string",
                    required: true
                  },
                  date: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "createIssue",
          description: "Open a new issue on a repository (declared intent \u2014 dispatch is being wired).",
          params: [
            {
              name: "owner",
              type: "string",
              required: true,
              description: "Repository owner (user or org)."
            },
            {
              name: "repo",
              type: "string",
              required: true,
              description: "Repository name."
            },
            {
              name: "title",
              type: "string",
              required: true,
              description: "Issue title."
            },
            {
              name: "body",
              type: "string",
              required: false,
              description: "Issue body/description."
            },
            {
              name: "labels",
              type: "string[]",
              required: false,
              description: "Labels to apply to the new issue."
            }
          ],
          responseShape: {
            number: "number",
            title: "string",
            url: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    stripe: {
      name: "stripe",
      description: "Stripe payment processing - create intents, confirm payments, refunds",
      category: "payment",
      actions: [
        {
          name: "createPaymentIntent",
          description: "Create a Stripe PaymentIntent for a one-off charge.",
          params: [
            {
              name: "amount",
              type: "number",
              required: true,
              description: "Charge amount in the currency's smallest unit (e.g. cents for USD)."
            },
            {
              name: "currency",
              type: "string",
              required: true,
              description: "ISO 4217 currency code, e.g. usd."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Arbitrary key-value tags attached to the PaymentIntent (string values only).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            id: "string",
            clientSecret: "string",
            status: "string",
            amount: "number",
            currency: "string"
          },
          synonyms: "charge, pay"
        },
        {
          name: "confirmPayment",
          description: "Confirm a previously created PaymentIntent, attempting to complete the charge.",
          params: [
            {
              name: "paymentIntentId",
              type: "string",
              required: true,
              description: "The PaymentIntent id returned by createPaymentIntent."
            }
          ],
          responseShape: {
            id: "string",
            status: "string"
          }
        },
        {
          name: "refund",
          description: "Refund a charged PaymentIntent, in full or in part.",
          params: [
            {
              name: "paymentIntentId",
              type: "string",
              required: true,
              description: "The PaymentIntent id to refund."
            },
            {
              name: "amount",
              type: "number",
              required: false,
              description: "Amount to refund in the currency's smallest unit; omit for a full refund."
            }
          ],
          responseShape: {
            id: "string",
            status: "string",
            amount: "number"
          }
        },
        {
          name: "createCustomer",
          description: "Create a Stripe Customer for an Almadar user; the user id is stored as Stripe metadata so webhooks resolve back to it.",
          params: [
            {
              name: "email",
              type: "string",
              required: true,
              description: "Billing email."
            },
            {
              name: "almadarUid",
              type: "string",
              required: true,
              description: "The Almadar user the customer belongs to."
            },
            {
              name: "displayName",
              type: "string",
              required: false,
              description: "Customer display name; omitted when unknown."
            }
          ],
          responseShape: {
            customerId: "string",
            email: "string",
            almadarUid: "string"
          }
        },
        {
          name: "createCheckoutSession",
          description: "Create a hosted Checkout Session subscribing a customer to a tier; the caller redirects to `url`.",
          params: [
            {
              name: "customerId",
              type: "string",
              required: false,
              description: "Existing customer id; Stripe creates one at checkout when omitted."
            },
            {
              name: "tier",
              type: "'solo' | 'teams'",
              required: true,
              description: "Tier to subscribe to; its price comes from STRIPE_PRICE_SOLO / STRIPE_PRICE_TEAMS."
            },
            {
              name: "quantity",
              type: "number",
              required: true,
              description: "Seats (solo is always 1)."
            },
            {
              name: "successUrl",
              type: "string",
              required: true,
              description: "Where Stripe returns after a successful checkout."
            },
            {
              name: "cancelUrl",
              type: "string",
              required: true,
              description: "Where Stripe returns when checkout is cancelled."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Tags written onto the created subscription (string values only).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            url: "string",
            sessionId: "string",
            customerId: "string"
          }
        },
        {
          name: "createPortalSession",
          description: "Create a hosted Billing Portal session for a customer's self-service plan management; the caller redirects to `url`.",
          params: [
            {
              name: "customerId",
              type: "string",
              required: true,
              description: "Customer id."
            },
            {
              name: "returnUrl",
              type: "string",
              required: true,
              description: "Where the portal returns to."
            }
          ],
          responseShape: {
            url: "string",
            customerId: "string"
          }
        },
        {
          name: "getSubscription",
          description: "Fetch one subscription in canonical form.",
          params: [
            {
              name: "subscriptionId",
              type: "string",
              required: true,
              description: "Subscription id."
            }
          ],
          responseShape: {}
        },
        {
          name: "createSubscription",
          description: "Subscribe a customer to a tier directly (no Checkout).",
          params: [
            {
              name: "customerId",
              type: "string",
              required: true,
              description: "Customer id."
            },
            {
              name: "tier",
              type: "'solo' | 'teams'",
              required: true,
              description: "Tier to subscribe to."
            },
            {
              name: "quantity",
              type: "number",
              required: true,
              description: "Seats."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Tags written onto the subscription (string values only).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {}
        },
        {
          name: "updateSubscription",
          description: "Change a subscription's seat count or its cancel-at-period-end flag.",
          params: [
            {
              name: "subscriptionId",
              type: "string",
              required: true,
              description: "Subscription id."
            },
            {
              name: "quantity",
              type: "number",
              required: false,
              description: "New seat count; unchanged when omitted."
            },
            {
              name: "cancelAtPeriodEnd",
              type: "boolean",
              required: false,
              description: "Set or clear cancel-at-period-end; unchanged when omitted."
            }
          ],
          responseShape: {}
        },
        {
          name: "cancelSubscription",
          description: "Cancel a subscription, at the period end (default) or immediately.",
          params: [
            {
              name: "subscriptionId",
              type: "string",
              required: true,
              description: "Subscription id."
            },
            {
              name: "atPeriodEnd",
              type: "boolean",
              required: false,
              description: "Cancel at the period end so access lasts until then; defaults to true."
            }
          ],
          responseShape: {}
        }
      ],
      runtimes: [
        "node"
      ]
    },
    llm: {
      name: "llm",
      description: "LLM service for content generation, classification, and structured extraction",
      category: "ai",
      actions: [
        {
          name: "generate",
          description: "Generate text from a prompt.",
          params: [
            {
              name: "systemPrompt",
              type: "string",
              required: false,
              description: "System prompt steering the model's behavior."
            },
            {
              name: "userPrompt",
              type: "string",
              required: true,
              description: "The user/task prompt sent to the model."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            },
            {
              name: "temperature",
              type: "number",
              required: false,
              description: "Sampling temperature; higher is more random."
            },
            {
              name: "maxTokens",
              type: "number",
              required: false,
              description: "Maximum tokens to generate."
            }
          ],
          responseShape: {
            content: "string",
            usage: {
              type: "object",
              properties: {
                promptTokens: {
                  type: "number",
                  required: true
                },
                completionTokens: {
                  type: "number",
                  required: true
                },
                totalTokens: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "classify",
          description: "Classify text into one of the given categories.",
          params: [
            {
              name: "text",
              type: "string",
              required: true,
              description: "Text to classify."
            },
            {
              name: "categories",
              type: "string[]",
              required: true,
              description: "Closed set of category labels the model must choose from."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            category: "string",
            confidence: "number",
            reasoning: "string"
          }
        },
        {
          name: "classifyMany",
          description: "Classify many texts in one model call. Every item's id comes back exactly once with a category from\nthe declared set; there is no reasoning, so the reply stays short.",
          params: [
            {
              name: "items",
              type: "array",
              required: true,
              description: "The texts, each with the id its verdict comes back under.",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  text: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "categories",
              type: "string[]",
              required: true,
              description: "Closed set of category labels the model must choose from."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            verdicts: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  category: {
                    type: "string",
                    required: true
                  },
                  confidence: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "extract",
          description: "Extract structured data from text according to a JSON schema.",
          params: [
            {
              name: "text",
              type: "string",
              required: true,
              description: "Source text to extract data from."
            },
            {
              name: "schema",
              type: "JsonSchemaDefinition",
              required: true,
              description: "JSON Schema describing the fields to extract.",
              properties: {
                type: {
                  type: "string",
                  required: false
                },
                properties: {
                  type: "object",
                  required: false,
                  description: "Keyed by field name \u2014 each named field carries its OWN nested schema\n(real JSON-Schema semantics; a single un-keyed nested schema here would\nsay every field of an object shares one shape).",
                  mapValue: {
                    type: "JsonSchemaDefinition",
                    cyclic: true,
                    cyclicTypeName: "JsonSchemaDefinition"
                  }
                },
                required: {
                  type: "string[]",
                  required: false
                },
                items: {
                  type: "JsonSchemaDefinition",
                  required: false,
                  cyclic: true,
                  cyclicTypeName: "JsonSchemaDefinition"
                },
                additionalProperties: {
                  type: "JsonSchemaDefinition",
                  required: false,
                  cyclic: true,
                  cyclicTypeName: "JsonSchemaDefinition"
                },
                description: {
                  type: "string",
                  required: false
                },
                enum: {
                  type: "string[]",
                  required: false,
                  description: 'Enum values are serialized to strings on the wire \u2014 pass `["a","b"]` even for non-string enums.'
                }
              }
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            data: {
              type: "ExtractedData",
              shapedBy: "schema"
            },
            confidence: "number"
          },
          synonyms: "parse, structure"
        },
        {
          name: "summarize",
          description: "Summarize text with optional length and style constraints.",
          params: [
            {
              name: "text",
              type: "string",
              required: true,
              description: "Text to summarize."
            },
            {
              name: "maxLength",
              type: "number",
              required: false,
              description: "Target maximum summary length in words."
            },
            {
              name: "style",
              type: "string",
              required: false,
              description: "Summary style, e.g. 'bullet' or 'detailed'."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            summary: "string",
            keyPoints: "string[]"
          }
        },
        {
          name: "call-tools",
          description: "Answer a request by operating the running app as the caller: the model may\nfire the declared inputs and read the entities named in `tools`, through the\nhost's ordinary dispatch (guards, policies and listens all apply), until it\nreplies. Hitting `maxSteps` is a failure.",
          params: [
            {
              name: "messages",
              type: "AgentChatMessage[]",
              required: true,
              description: "The conversation so far, oldest first; the last turn is the request.",
              items: {
                type: "AgentChatMessage",
                properties: {
                  role: {
                    type: '"user" | "assistant"',
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "tools",
              type: "AgentTool[]",
              required: true,
              description: "The inputs and entities the model may use.",
              items: {
                type: "AgentTool",
                properties: {
                  event: {
                    type: "EventAddress",
                    required: false
                  },
                  payload: {
                    type: "AgentToolField[]",
                    required: false,
                    items: {
                      type: "AgentToolField",
                      properties: {
                        name: {
                          type: "string",
                          required: true
                        },
                        type: {
                          type: "string",
                          required: true
                        },
                        required: {
                          type: "boolean",
                          required: false
                        },
                        values: {
                          type: "string[]",
                          required: false,
                          description: "Closed value set \u2014 element-wise for an array type."
                        },
                        properties: {
                          type: "AgentToolField[]",
                          required: false,
                          description: "Fields of an object (or `[object]`) type.",
                          items: {
                            type: "AgentToolField",
                            cyclic: true,
                            cyclicTypeName: "AgentToolField"
                          }
                        },
                        description: {
                          type: "string",
                          required: false
                        }
                      }
                    }
                  },
                  read: {
                    type: "string",
                    required: false
                  },
                  scope: {
                    type: '"declared"',
                    required: false
                  }
                }
              }
            },
            {
              name: "instructions",
              type: "string",
              required: false,
              description: "Standing instructions for the model."
            },
            {
              name: "maxSteps",
              type: "number",
              required: false,
              description: "Model round-trips allowed before the call fails (default 8)."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            reply: "string",
            steps: "AgentActivity[]",
            usage: {
              type: "object",
              properties: {
                promptTokens: {
                  type: "number",
                  required: true
                },
                completionTokens: {
                  type: "number",
                  required: true
                },
                totalTokens: {
                  type: "number",
                  required: true
                },
                modelCalls: {
                  type: "number",
                  required: true
                }
              }
            }
          },
          synonyms: "agent, assistant, tool use, act"
        },
        {
          name: "embed",
          description: "Generate embeddings for an array of texts.",
          params: [
            {
              name: "texts",
              type: "string[]",
              required: true,
              description: "Texts to embed, one vector per entry, in order."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Embedding model id override."
            }
          ],
          responseShape: {
            embeddings: "number[][]"
          }
        }
      ],
      runtimes: [
        "node",
        "browser"
      ]
    },
    ml: {
      name: "ml",
      description: "ML service - model inference on a trained or off-the-shelf checkpoint, reached via a deployed serving orbital",
      category: "ai",
      actions: [
        {
          name: "infer",
          description: "Run inference against a deployed model checkpoint.",
          params: [
            {
              name: "model",
              type: "string",
              required: true,
              description: "`hf:<id>` for off-the-shelf weights, or a masar checkpoint id."
            },
            {
              name: "input",
              type: "ServiceParamsValue",
              required: true,
              description: "JSON-safe model input (tensor-shaped payload from `contract/entity-to-tensor`)."
            }
          ],
          responseShape: {
            output: "ServiceParamsValue",
            confidence: "number",
            violations: "ServiceParamsValue[]"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    youtube: {
      name: "youtube",
      description: "YouTube Data API - search videos, get video/channel details",
      category: "media",
      actions: [
        {
          name: "search",
          description: "Search YouTube via the Data API and return matching video snippets.",
          params: [
            {
              name: "query",
              type: "string",
              required: true,
              description: "Search query text."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum number of results; defaults to 10."
            },
            {
              name: "type",
              type: "string",
              required: false,
              description: "Resource type filter passed to the Data API (e.g. 'video', 'channel', 'playlist')."
            }
          ],
          responseShape: {}
        },
        {
          name: "getVideo",
          description: "Fetch a video's snippet and statistics via the YouTube Data API.",
          params: [
            {
              name: "videoId",
              type: "string",
              required: true,
              description: "YouTube video id."
            }
          ],
          responseShape: {
            title: "string",
            description: "string",
            viewCount: "string",
            likeCount: "string"
          }
        },
        {
          name: "getChannel",
          description: "Fetch a channel's snippet and statistics via the YouTube Data API.",
          params: [
            {
              name: "channelId",
              type: "string",
              required: true,
              description: "YouTube channel id."
            }
          ],
          responseShape: {
            name: "string",
            description: "string",
            subscriberCount: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    twilio: {
      name: "twilio",
      description: "Twilio messaging - send SMS, WhatsApp messages",
      category: "messaging",
      actions: [
        {
          name: "sendSMS",
          description: "Send an SMS message via Twilio.",
          params: [
            {
              name: "to",
              type: "string",
              required: true,
              description: "Destination phone number, E.164 format."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Message text."
            },
            {
              name: "from",
              type: "string",
              required: false,
              description: "Sender phone number; defaults to TWILIO_PHONE_NUMBER."
            }
          ],
          responseShape: {
            sid: "string",
            status: "string"
          },
          synonyms: "text message, text"
        },
        {
          name: "sendWhatsApp",
          description: "Send a WhatsApp message via Twilio, using the configured phone number as sender on both ends.",
          params: [
            {
              name: "to",
              type: "string",
              required: true,
              description: "Destination phone number, E.164 format (sent as whatsapp:number)."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Message text."
            }
          ],
          responseShape: {
            sid: "string",
            status: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    email: {
      name: "email",
      description: "Transactional email via SendGrid/Resend",
      category: "messaging",
      actions: [
        {
          name: "send",
          description: "Send a transactional email via the configured provider (SendGrid or Resend).",
          params: [
            {
              name: "to",
              type: "string",
              required: true,
              description: "Recipient email address."
            },
            {
              name: "subject",
              type: "string",
              required: true,
              description: "Email subject line."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Plain-text (or HTML, if `htmlBody` is absent) body content."
            },
            {
              name: "from",
              type: "string",
              required: false,
              description: "Sender address; defaults to FROM_EMAIL."
            },
            {
              name: "htmlBody",
              type: "string",
              required: false,
              description: "HTML content; when present `body` becomes the plain-text alternative."
            },
            {
              name: "replyTo",
              type: "string",
              required: false,
              description: "Reply-To address."
            },
            {
              name: "templateId",
              type: "string",
              required: false,
              description: "Provider template reference (SendGrid dynamic templates; Resend rejects it loudly)."
            }
          ],
          responseShape: {
            id: "string",
            status: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    webhook: {
      name: "webhook",
      description: "Outbound webhook - signed HTTP POST notifications to external endpoints",
      category: "messaging",
      actions: [
        {
          name: "send",
          description: "POST a JSON event notification to an external URL, HMAC-SHA256-signed when a secret is configured; 5xx responses retry, 4xx do not.",
          params: [
            {
              name: "url",
              type: "string",
              required: true,
              description: "Destination URL to POST to."
            },
            {
              name: "event",
              type: "string",
              required: true,
              description: "Event name, carried in the body and the X-Almadar-Event header."
            },
            {
              name: "payload",
              type: "object",
              required: false,
              description: "Event payload; defaults to an empty object.",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "secret",
              type: "string",
              required: false,
              description: "Per-call HMAC signing secret, overriding WEBHOOK_SIGNING_SECRET."
            }
          ],
          responseShape: {
            status: "number",
            ok: "boolean",
            durationMs: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    push: {
      name: "push",
      description: "Web Push - VAPID-signed browser push notifications",
      category: "messaging",
      actions: [
        {
          name: "send",
          description: "Send a VAPID-signed Web Push notification to a browser PushSubscription endpoint.",
          params: [
            {
              name: "subscription",
              type: "object",
              required: true,
              description: "Browser PushSubscription: endpoint URL plus the p256dh/auth encryption keys.",
              properties: {
                endpoint: {
                  type: "string",
                  required: true
                },
                keys: {
                  type: "object",
                  required: true,
                  properties: {
                    p256dh: {
                      type: "string",
                      required: true
                    },
                    auth: {
                      type: "string",
                      required: true
                    }
                  }
                }
              }
            },
            {
              name: "title",
              type: "string",
              required: true,
              description: "Notification title."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Notification body text."
            },
            {
              name: "url",
              type: "string",
              required: false,
              description: "URL to open when the notification is clicked."
            },
            {
              name: "icon",
              type: "string",
              required: false,
              description: "Notification icon URL."
            }
          ],
          responseShape: {
            statusCode: "number",
            ok: "boolean",
            expired: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    calendar: {
      name: "calendar",
      description: "Google Calendar - events, free-busy, watch channels (service account / domain-wide delegation)",
      category: "infrastructure",
      actions: [
        {
          name: "listEvents",
          description: "List events on a Google Calendar; pass `syncToken` for incremental sync instead of a time window (the API rejects combining them).",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID (or 'primary')."
            },
            {
              name: "timeMin",
              type: "string",
              required: false,
              description: "ISO start of the time window (ignored when `syncToken` is set)."
            },
            {
              name: "timeMax",
              type: "string",
              required: false,
              description: "ISO end of the time window (ignored when `syncToken` is set)."
            },
            {
              name: "syncToken",
              type: "string",
              required: false,
              description: "Incremental-sync token from a previous call's `nextSyncToken`; supersedes timeMin/timeMax."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum events to return; defaults to 250."
            }
          ],
          responseShape: {
            events: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  summary: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  location: {
                    type: "string",
                    required: true
                  },
                  start: {
                    type: "string",
                    required: true
                  },
                  end: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  },
                  updated: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            nextSyncToken: "string | null"
          }
        },
        {
          name: "createEvent",
          description: "Create a calendar event; an all-day event uses a 10-char (YYYY-MM-DD) start with no `end`/`durationMinutes`, defaulting to the next day.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "summary",
              type: "string",
              required: true,
              description: "Event title."
            },
            {
              name: "description",
              type: "string",
              required: false,
              description: "Event description."
            },
            {
              name: "location",
              type: "string",
              required: false,
              description: "Event location text."
            },
            {
              name: "start",
              type: "string",
              required: true,
              description: "ISO start (10 chars = all-day date, longer = dateTime)."
            },
            {
              name: "end",
              type: "string",
              required: false,
              description: "ISO end; if omitted, derived from `durationMinutes` or the all-day next-day convention."
            },
            {
              name: "durationMinutes",
              type: "number",
              required: false,
              description: "Duration in minutes used to derive `end` when `end` is omitted (non-all-day events only)."
            }
          ],
          responseShape: {
            id: "string",
            status: "string",
            htmlLink: "string"
          }
        },
        {
          name: "updateEvent",
          description: "Patch an existing event's fields; only fields present in params are changed.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "eventId",
              type: "string",
              required: true,
              description: "Id of the event to update."
            },
            {
              name: "summary",
              type: "string",
              required: false,
              description: "New event title."
            },
            {
              name: "description",
              type: "string",
              required: false,
              description: "New event description."
            },
            {
              name: "location",
              type: "string",
              required: false,
              description: "New event location text."
            },
            {
              name: "start",
              type: "string",
              required: false,
              description: "New ISO start."
            },
            {
              name: "end",
              type: "string",
              required: false,
              description: "New ISO end."
            }
          ],
          responseShape: {
            id: "string",
            status: "string"
          }
        },
        {
          name: "deleteEvent",
          description: "Delete a calendar event.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "eventId",
              type: "string",
              required: true,
              description: "Id of the event to delete."
            }
          ],
          responseShape: {
            id: "string",
            deleted: "boolean"
          }
        },
        {
          name: "freeBusy",
          description: "Query busy time blocks on a calendar within a window.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "timeMin",
              type: "string",
              required: true,
              description: "ISO start of the query window."
            },
            {
              name: "timeMax",
              type: "string",
              required: true,
              description: "ISO end of the query window."
            }
          ],
          responseShape: {
            busy: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  start: {
                    type: "string",
                    required: true
                  },
                  end: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "watch",
          description: "Register a push-notification channel for calendar change events (Google Calendar API watch).",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "channelId",
              type: "string",
              required: true,
              description: "Caller-chosen id identifying this notification channel."
            },
            {
              name: "address",
              type: "string",
              required: true,
              description: "HTTPS callback URL Google POSTs change notifications to."
            },
            {
              name: "ttlSeconds",
              type: "number",
              required: false,
              description: "Requested channel time-to-live in seconds."
            }
          ],
          responseShape: {
            channelId: "string",
            resourceId: "string",
            expiration: "string"
          }
        },
        {
          name: "stopWatch",
          description: "Stop an active watch channel, ending calendar change notifications.",
          params: [
            {
              name: "channelId",
              type: "string",
              required: true,
              description: "The channel id from the corresponding `watch` call."
            },
            {
              name: "resourceId",
              type: "string",
              required: true,
              description: "The resourceId returned by the corresponding `watch` call."
            }
          ],
          responseShape: {
            stopped: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    drive: {
      name: "drive",
      description: "Google Drive - files, folders, sharing (service account / domain-wide delegation)",
      category: "infrastructure",
      actions: [
        {
          name: "listFiles",
          description: "List non-trashed files in a Google Drive folder, optionally filtered by a Drive API query clause.",
          params: [
            {
              name: "folderId",
              type: "string",
              required: false,
              description: "Restrict to files whose parents include this folder id."
            },
            {
              name: "query",
              type: "string",
              required: false,
              description: "Extra Drive API query clause, ANDed with the folder/trashed filters."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum files to return; defaults to 100."
            }
          ],
          responseShape: {
            files: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  mimeType: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  },
                  modifiedTime: {
                    type: "string",
                    required: true
                  },
                  webViewLink: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "getFile",
          description: "Download a file's content, base64-encoded.",
          params: [
            {
              name: "fileId",
              type: "string",
              required: true,
              description: "Drive file id."
            }
          ],
          responseShape: {
            id: "string",
            name: "string",
            mimeType: "string",
            content: "string",
            size: "number"
          }
        },
        {
          name: "uploadFile",
          description: "Upload a file to Drive; writes go through the user OAuth client when configured (service-account uploads have no storage quota on personal accounts).",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "File name to create."
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: "File content as a base64 data URL (data:mime;base64,...) or raw base64/plain text."
            },
            {
              name: "mimeType",
              type: "string",
              required: false,
              description: "MIME type; inferred from a data URL when omitted."
            },
            {
              name: "folderId",
              type: "string",
              required: false,
              description: "Parent folder id; defaults to GOOGLE_DRIVE_FOLDER_ID."
            }
          ],
          responseShape: {
            id: "string",
            name: "string",
            webViewLink: "string"
          },
          synonyms: "upload file"
        },
        {
          name: "createFolder",
          description: "Create a new Drive folder.",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Folder name to create."
            },
            {
              name: "parentId",
              type: "string",
              required: false,
              description: "Parent folder id; defaults to GOOGLE_DRIVE_FOLDER_ID."
            }
          ],
          responseShape: {
            id: "string",
            name: "string"
          }
        },
        {
          name: "shareFile",
          description: "Grant a user permission on a Drive file.",
          params: [
            {
              name: "fileId",
              type: "string",
              required: true,
              description: "Drive file id."
            },
            {
              name: "email",
              type: "string",
              required: true,
              description: "Email address of the grantee."
            },
            {
              name: "role",
              type: "'reader' | 'writer' | 'commenter'",
              required: false,
              description: "Permission level; defaults to 'reader'."
            }
          ],
          responseShape: {
            shared: "boolean",
            permissionId: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    metaAds: {
      name: "metaAds",
      description: "Meta Ads - read-only Marketing Insights (spend, campaigns)",
      category: "marketing",
      actions: [
        {
          name: "getSpend",
          description: "Fetch total ad spend, impressions, and clicks for an account over a date range via the Meta Graph API Marketing Insights endpoint (read-only).",
          params: [
            {
              name: "accountId",
              type: "string",
              required: false,
              description: "Ad account id (with or without the `act_` prefix); defaults to META_AD_ACCOUNT_ID."
            },
            {
              name: "since",
              type: "string",
              required: true,
              description: "Range start date (YYYY-MM-DD)."
            },
            {
              name: "until",
              type: "string",
              required: true,
              description: "Range end date (YYYY-MM-DD)."
            }
          ],
          responseShape: {
            spend: "number",
            currency: "string",
            impressions: "number",
            clicks: "number"
          }
        },
        {
          name: "listCampaigns",
          description: "List campaigns on an ad account via the Meta Graph API (read-only); dailyBudget is converted from Meta's minor-unit reporting.",
          params: [
            {
              name: "accountId",
              type: "string",
              required: false,
              description: "Ad account id (with or without the `act_` prefix); defaults to META_AD_ACCOUNT_ID."
            },
            {
              name: "status",
              type: "string",
              required: false,
              description: "Filter by campaign effective_status (e.g. 'ACTIVE', 'PAUSED')."
            }
          ],
          responseShape: {
            campaigns: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  },
                  dailyBudget: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    accounting: {
      name: "accounting",
      description: "Accounting export - invoice/journal rows to import-ready CSV",
      category: "finance",
      actions: [
        {
          name: "exportInvoices",
          description: "Shape invoice rows into an import-ready CSV export (no accounting-vendor connection \u2014 generic column set).",
          params: [
            {
              name: "invoices",
              type: "array",
              required: true,
              description: "Invoice rows to export, in column order id/number/customer/issuedAt/dueAt/currency/net/tax/gross/status.",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  number: {
                    type: "string",
                    required: true
                  },
                  customer: {
                    type: "string",
                    required: true
                  },
                  issuedAt: {
                    type: "string",
                    required: true
                  },
                  dueAt: {
                    type: "string",
                    required: true
                  },
                  currency: {
                    type: "string",
                    required: true
                  },
                  net: {
                    type: "number",
                    required: true
                  },
                  tax: {
                    type: "number",
                    required: true
                  },
                  gross: {
                    type: "number",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "format",
              type: "'csv'",
              required: false,
              description: "Export format; only 'csv' is currently supported."
            }
          ],
          responseShape: {
            content: "string",
            filename: "string",
            count: "number"
          }
        },
        {
          name: "exportJournal",
          description: "Shape journal entry rows into an import-ready CSV export.",
          params: [
            {
              name: "entries",
              type: "array",
              required: true,
              description: "Journal entry rows to export, in column order date/account/description/debit/credit/reference.",
              items: {
                type: "object",
                properties: {
                  date: {
                    type: "string",
                    required: true
                  },
                  account: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  debit: {
                    type: "number",
                    required: true
                  },
                  credit: {
                    type: "number",
                    required: true
                  },
                  reference: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "format",
              type: "'csv'",
              required: false,
              description: "Export format; only 'csv' is currently supported."
            }
          ],
          responseShape: {
            content: "string",
            filename: "string",
            count: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    banking: {
      name: "banking",
      description: "Banking - GoCardless Bank Account Data (requisitions, accounts, transactions)",
      category: "finance",
      actions: [
        {
          name: "createRequisition",
          description: "Start a GoCardless Bank Account Data requisition (bank-connection consent flow); the returned `link` is where the end user authorizes access.",
          params: [
            {
              name: "institutionId",
              type: "string",
              required: true,
              description: "GoCardless institution id (the bank to connect)."
            },
            {
              name: "redirectUrl",
              type: "string",
              required: true,
              description: "URL GoCardless redirects the user to after consent."
            },
            {
              name: "reference",
              type: "string",
              required: false,
              description: "Caller-chosen reference to correlate the requisition."
            }
          ],
          responseShape: {
            requisitionId: "string",
            link: "string"
          }
        },
        {
          name: "listAccounts",
          description: "List account ids linked under a completed requisition.",
          params: [
            {
              name: "requisitionId",
              type: "string",
              required: true,
              description: "Requisition id from createRequisition."
            }
          ],
          responseShape: {
            accounts: "string[]"
          }
        },
        {
          name: "listTransactions",
          description: "List booked transactions on a linked bank account, optionally filtered by date range.",
          params: [
            {
              name: "accountId",
              type: "string",
              required: true,
              description: "GoCardless account id."
            },
            {
              name: "dateFrom",
              type: "string",
              required: false,
              description: "Only transactions booked on/after this date (YYYY-MM-DD)."
            },
            {
              name: "dateTo",
              type: "string",
              required: false,
              description: "Only transactions booked on/before this date (YYYY-MM-DD)."
            }
          ],
          responseShape: {
            transactions: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  amount: {
                    type: "number",
                    required: true
                  },
                  currency: {
                    type: "string",
                    required: true
                  },
                  date: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  counterparty: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    esign: {
      name: "esign",
      description: "E-signature - send/track/download envelopes (DocuSign)",
      category: "infrastructure",
      actions: [
        {
          name: "sendEnvelope",
          description: "Send a document for e-signature via DocuSign; accepts the document content as a data URL or raw base64.",
          params: [
            {
              name: "recipientEmail",
              type: "string",
              required: true,
              description: "Signer's email address."
            },
            {
              name: "recipientName",
              type: "string",
              required: true,
              description: "Signer's display name."
            },
            {
              name: "documentName",
              type: "string",
              required: true,
              description: "Document file name (its extension also picks the DocuSign fileExtension)."
            },
            {
              name: "documentContent",
              type: "string",
              required: true,
              description: "Document content as a base64 data URL or raw base64."
            },
            {
              name: "emailSubject",
              type: "string",
              required: false,
              description: 'Envelope email subject; defaults to "Please sign: " + documentName.'
            }
          ],
          responseShape: {
            envelopeId: "string",
            status: "string"
          }
        },
        {
          name: "getEnvelopeStatus",
          description: "Poll a DocuSign envelope's signing status.",
          params: [
            {
              name: "envelopeId",
              type: "string",
              required: true,
              description: "DocuSign envelope id from sendEnvelope."
            }
          ],
          responseShape: {
            status: "string",
            completedAt: "string"
          }
        },
        {
          name: "downloadDocument",
          description: "Download the combined signed document(s) for a completed envelope, base64-encoded PDF.",
          params: [
            {
              name: "envelopeId",
              type: "string",
              required: true,
              description: "DocuSign envelope id."
            }
          ],
          responseShape: {
            content: "string",
            documentName: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    docker: {
      name: "docker",
      description: "Docker \u2014 container and image management",
      category: "devtools",
      actions: [
        {
          name: "build",
          description: "Simulate building a container image (in-memory backend \u2014 no real Docker daemon/SDK; a real build is not yet implemented).",
          params: [
            {
              name: "tag",
              type: "string",
              required: true,
              description: "Image tag to build, e.g. myapp:latest."
            },
            {
              name: "dockerfile",
              type: "string",
              required: false,
              description: "Dockerfile path; defaults to 'Dockerfile'."
            },
            {
              name: "context",
              type: "string",
              required: false,
              description: "Build context directory; defaults to '.'."
            },
            {
              name: "buildArgs",
              type: "object",
              required: false,
              description: "Build-time --build-arg values.",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            imageId: "string",
            tag: "string",
            size: "number",
            buildTime: "number"
          }
        },
        {
          name: "run",
          description: "Simulate starting a container from an image (in-memory backend).",
          params: [
            {
              name: "image",
              type: "string",
              required: true,
              description: "Image to run (as built/tagged by the `build` action, or an arbitrary name)."
            },
            {
              name: "name",
              type: "string",
              required: false,
              description: "Container name; auto-generated when omitted."
            },
            {
              name: "ports",
              type: "array",
              required: false,
              description: "Host/container port mappings.",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "number",
                    required: true
                  },
                  container: {
                    type: "number",
                    required: true
                  },
                  protocol: {
                    type: "string",
                    required: false
                  }
                }
              }
            },
            {
              name: "env",
              type: "object",
              required: false,
              description: "Environment variables set inside the container.",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "volumes",
              type: "array",
              required: false,
              description: "Host/container path mounts.",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "string",
                    required: true
                  },
                  container: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "command",
              type: "string",
              required: false,
              description: "Override command run inside the container."
            }
          ],
          responseShape: {
            containerId: "string",
            name: "string",
            status: "string",
            ports: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "number",
                    required: true
                  },
                  container: {
                    type: "number",
                    required: true
                  },
                  protocol: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "stop",
          description: "Stop a running (or paused) simulated container.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            }
          ],
          responseShape: {
            containerId: "string",
            status: "string",
            stoppedAt: "number"
          }
        },
        {
          name: "remove",
          description: "Remove a simulated container; fails if it is running unless `force` is set.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            },
            {
              name: "force",
              type: "boolean",
              required: false,
              description: "Remove even if the container is currently running."
            }
          ],
          responseShape: {
            containerId: "string",
            removed: "boolean"
          }
        },
        {
          name: "logs",
          description: "Fetch generated log lines for a simulated container.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            },
            {
              name: "tail",
              type: "number",
              required: false,
              description: "Number of most-recent lines to return; defaults to 100."
            },
            {
              name: "since",
              type: "string",
              required: false,
              description: "Only lines timestamped at/after this ISO time."
            }
          ],
          responseShape: {
            containerId: "string",
            logs: "string[]",
            lineCount: "number"
          }
        },
        {
          name: "status",
          description: "Fetch a simulated container's current status and metadata.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            }
          ],
          responseShape: {
            containerId: "string",
            name: "string",
            image: "string",
            status: "string",
            ports: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "number",
                    required: true
                  },
                  container: {
                    type: "number",
                    required: true
                  },
                  protocol: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            createdAt: "number",
            startedAt: "number | null",
            stoppedAt: "number | null"
          }
        },
        {
          name: "list",
          description: "List simulated containers; by default only running ones (like `docker ps`).",
          params: [
            {
              name: "all",
              type: "boolean",
              required: false,
              description: "Include stopped/exited containers, not just running ones."
            },
            {
              name: "filterByLabel",
              type: "string",
              required: false,
              description: "Filter by a `key` or `key=value` label match."
            }
          ],
          responseShape: {
            containers: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  image: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  },
                  ports: {
                    type: "array",
                    required: true,
                    items: {
                      type: "object",
                      properties: {
                        host: {
                          type: "number",
                          required: true
                        },
                        container: {
                          type: "number",
                          required: true
                        },
                        protocol: {
                          type: "string",
                          required: true
                        }
                      }
                    }
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            total: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    storage: {
      name: "storage",
      description: "Object storage \u2014 upload, download, delete, list files",
      category: "infrastructure",
      actions: [
        {
          name: "upload",
          description: "Upload an object to an S3-compatible bucket (real S3/R2/MinIO backend when storage credentials are configured, in-memory dev fallback otherwise \u2014 refused in production). Admits either the canonical `key`+`content` pair or the file-form `file` payload (UploadDropZone's `{ name, size, type, content }` shape); when `file` is present its `key`/`content` are derived from it.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Target bucket; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: false,
              description: "Object key to store under (canonical shape); ignored when `file` is present."
            },
            {
              name: "content",
              type: "string",
              required: false,
              description: "Object content as a base64 data URL, raw base64, or plain text (canonical shape); ignored when `file` is present."
            },
            {
              name: "contentType",
              type: "string",
              required: false,
              description: "MIME type override for the canonical shape; inferred from a data URL when omitted."
            },
            {
              name: "file",
              type: "object",
              required: false,
              description: "File-form upload payload (as emitted by UploadDropZone); `content` is a base64 data URL.",
              properties: {
                name: {
                  type: "string",
                  required: true
                },
                size: {
                  type: "number",
                  required: true
                },
                type: {
                  type: "string",
                  required: true
                },
                content: {
                  type: "string",
                  required: false
                }
              }
            },
            {
              name: "acl",
              type: "'public' | 'private'",
              required: false,
              description: "Object ACL \u2014 'public' produces a public URL, otherwise the URL is signed/private."
            },
            {
              name: "maxSize",
              type: "number",
              required: false,
              description: "Reject the upload if the file-form payload's declared size exceeds this many bytes."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Object metadata stored alongside the content (S3-compatible object stores carry metadata as string headers \u2014 never structured values).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            key: "string",
            bucket: "string",
            size: "number",
            etag: "string",
            id: "string",
            url: "string"
          },
          synonyms: "upload file, upload object"
        },
        {
          name: "uploadMany",
          description: "Upload a whole UploadDropZone batch in one call, one item per file \u2014 the FSM-friendly shape for bulk-persisting uploaded-file rows.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Target bucket; defaults to STORAGE_BUCKET."
            },
            {
              name: "files",
              type: "array",
              required: true,
              description: "Files to upload, in UploadDropZone's `{ name, size, type, content }` shape.",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  },
                  type: {
                    type: "string",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: false
                  }
                }
              }
            },
            {
              name: "acl",
              type: "'public' | 'private'",
              required: false,
              description: "Object ACL applied to every file in the batch."
            },
            {
              name: "maxSize",
              type: "number",
              required: false,
              description: "Reject any file whose declared size exceeds this many bytes."
            }
          ],
          responseShape: {
            items: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  key: {
                    type: "string",
                    required: true
                  },
                  url: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  sizeBytes: {
                    type: "number",
                    required: true
                  },
                  mimeType: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            count: "number"
          }
        },
        {
          name: "download",
          description: "Download an object's content, base64-encoded.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket the object lives in; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: true,
              description: "Object key."
            }
          ],
          responseShape: {
            content: "string",
            contentType: "string",
            size: "number",
            metadata: {
              type: "object",
              mapValue: {
                type: "string"
              }
            }
          }
        },
        {
          name: "list",
          description: "List object keys in a bucket, optionally prefix-filtered, one page at a time.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket to list; defaults to STORAGE_BUCKET."
            },
            {
              name: "prefix",
              type: "string",
              required: false,
              description: "Only keys starting with this prefix."
            },
            {
              name: "maxKeys",
              type: "number",
              required: false,
              description: "Maximum keys per page; defaults to 1000."
            },
            {
              name: "continuationToken",
              type: "string",
              required: false,
              description: "Pagination token from a previous call's `nextToken`."
            }
          ],
          responseShape: {
            keys: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  key: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  },
                  lastModified: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            truncated: "boolean",
            nextToken: "string"
          }
        },
        {
          name: "delete",
          description: "Delete an object from a bucket.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket the object lives in; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: true,
              description: "Object key to delete."
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "getSignedUrl",
          description: "Generate a time-limited pre-signed URL for direct GET or PUT access to an object.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket the object lives in; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: true,
              description: "Object key."
            },
            {
              name: "expiresIn",
              type: "number",
              required: false,
              description: "URL validity window in seconds; defaults to 3600."
            },
            {
              name: "operation",
              type: "'get' | 'put'",
              required: false,
              description: "Whether the URL authorizes a read or a write; defaults to 'get'."
            }
          ],
          responseShape: {
            url: "string",
            expiresAt: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    queue: {
      name: "queue",
      description: "Message queue \u2014 publish, subscribe, acknowledge messages",
      category: "infrastructure",
      actions: [
        {
          name: "enqueue",
          description: "Add a job to an in-memory job queue, ordered by priority (higher first) among ready jobs.",
          params: [
            {
              name: "queue",
              type: "string",
              required: true,
              description: "Queue name."
            },
            {
              name: "payload",
              type: "ServiceParams",
              required: true,
              description: "Job payload \u2014 the caller declares its own shape.",
              shapedBy: "payload"
            },
            {
              name: "delay",
              type: "number",
              required: false,
              description: "Delay before the job becomes eligible for dequeue, in milliseconds."
            },
            {
              name: "priority",
              type: "number",
              required: false,
              description: "Priority \u2014 higher values are dequeued before lower ones."
            }
          ],
          responseShape: {
            jobId: "string",
            position: "number"
          }
        },
        {
          name: "dequeue",
          description: "Pop the highest-priority pending job whose delay has elapsed, marking it 'processing'; returns null if none is ready.",
          params: [
            {
              name: "queue",
              type: "string",
              required: true,
              description: "Queue name."
            }
          ],
          responseShape: {
            job: {
              type: "QueueJob | null",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                payload: {
                  type: "ServiceParams",
                  required: true,
                  description: "Whatever `enqueue` was given \u2014 the caller declares its own shape.",
                  shapedBy: "payload"
                },
                enqueuedAt: {
                  type: "number",
                  required: true
                },
                priority: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "status",
          description: "Look up a job's current status by id.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id from enqueue."
            }
          ],
          responseShape: {
            status: "'pending' | 'processing' | 'completed' | 'failed'",
            job: {
              type: "QueueJob | null",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                payload: {
                  type: "ServiceParams",
                  required: true,
                  description: "Whatever `enqueue` was given \u2014 the caller declares its own shape.",
                  shapedBy: "payload"
                },
                enqueuedAt: {
                  type: "number",
                  required: true
                },
                priority: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "complete",
          description: "Mark a 'processing' job completed and store its result; no-op (returns false) if the job isn't in 'processing' state.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id."
            },
            {
              name: "result",
              type: "ServiceParams",
              required: false,
              description: "Result payload to attach to the job \u2014 the caller declares its own shape (not necessarily the same shape as the job's `payload`).",
              shapedBy: "result"
            }
          ],
          responseShape: {
            completed: "boolean"
          }
        },
        {
          name: "fail",
          description: "Mark a 'processing' job failed; no-op (returns false) if the job isn't in 'processing' state.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id."
            },
            {
              name: "error",
              type: "string",
              required: false,
              description: "Failure reason to attach to the job."
            }
          ],
          responseShape: {
            failed: "boolean"
          }
        },
        {
          name: "cancel",
          description: "Remove a 'pending' job from its queue; no-op (returns false) once it has been dequeued.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id."
            }
          ],
          responseShape: {
            cancelled: "boolean"
          }
        },
        {
          name: "size",
          description: "Count pending and processing jobs in a queue.",
          params: [
            {
              name: "queue",
              type: "string",
              required: true,
              description: "Queue name."
            }
          ],
          responseShape: {
            size: "number",
            pending: "number",
            processing: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    redis: {
      name: "redis",
      description: "Redis \u2014 cache get/set/delete, pub/sub",
      category: "infrastructure",
      actions: [
        {
          name: "get",
          description: "Read a key's value from the in-memory cache; returns null (as `value`) if absent or expired.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            }
          ],
          responseShape: {
            value: {
              type: "ServiceParams",
              shapedBy: "value"
            }
          }
        },
        {
          name: "set",
          description: "Write a key's value, optionally with an expiry.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            },
            {
              name: "value",
              type: "ServiceParams",
              required: true,
              description: "Value to store \u2014 the caller declares its own shape.",
              shapedBy: "value"
            },
            {
              name: "ttl",
              type: "number",
              required: false,
              description: "Time-to-live in seconds; omit for no expiry."
            }
          ],
          responseShape: {
            ok: "boolean"
          }
        },
        {
          name: "delete",
          description: "Delete a key.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "lock",
          description: "Acquire a mutex on a key; fails (returns acquired:false) if another lock on the key is still alive.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Lock key."
            },
            {
              name: "ttl",
              type: "number",
              required: false,
              description: "Lock time-to-live in milliseconds; defaults to 30000 (30s)."
            }
          ],
          responseShape: {
            acquired: "boolean",
            lockId: "string"
          }
        },
        {
          name: "unlock",
          description: "Release a lock; only succeeds when `lockId` matches the current holder.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Lock key."
            },
            {
              name: "lockId",
              type: "string",
              required: true,
              description: "Lock id returned by the corresponding `lock` call."
            }
          ],
          responseShape: {
            released: "boolean"
          }
        },
        {
          name: "increment",
          description: "Atomically add to a numeric key's value (creating it at 0 if absent), preserving any existing TTL.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            },
            {
              name: "by",
              type: "number",
              required: false,
              description: "Amount to add; defaults to 1 (can be negative to decrement)."
            }
          ],
          responseShape: {
            value: "number"
          }
        },
        {
          name: "expire",
          description: "Set (or reset) a key's expiry; no-op (returns set:false) if the key doesn't exist or is already expired.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            },
            {
              name: "ttl",
              type: "number",
              required: true,
              description: "New time-to-live in seconds."
            }
          ],
          responseShape: {
            set: "boolean"
          }
        },
        {
          name: "publish",
          description: "Publish a message to a channel; delivered synchronously to in-process subscriber callbacks only.",
          params: [
            {
              name: "channel",
              type: "string",
              required: true,
              description: "Channel name."
            },
            {
              name: "message",
              type: "ServiceParams",
              required: true,
              description: "Message payload \u2014 the caller declares its own shape.",
              shapedBy: "message"
            }
          ],
          responseShape: {
            receivers: "number"
          }
        },
        {
          name: "subscribe",
          description: "Register interest in a channel; does not itself wire a callback (that happens at a higher-level API).",
          params: [
            {
              name: "channel",
              type: "string",
              required: true,
              description: "Channel name."
            }
          ],
          responseShape: {
            subscribed: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    oauth: {
      name: "oauth",
      description: "OAuth/OIDC \u2014 authorization-code + PKCE flow, token management, userinfo",
      category: "auth",
      actions: [
        {
          name: "authorize",
          description: "Build the provider authorization URL (PKCE + state) for an OAuth/OIDC login redirect; OIDC discovery (Google) or static OAuth 2 metadata (GitHub) when OAUTH_CLIENT_ID/SECRET are configured, an in-memory mock otherwise.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Identity provider; OIDC issuer defaults to Google unless OIDC_ISSUER_URL overrides it; GitHub uses its OAuth 2 endpoints."
            },
            {
              name: "scopes",
              type: "string[]",
              required: true,
              description: "OAuth scopes to request."
            },
            {
              name: "redirectUri",
              type: "string",
              required: true,
              description: "Callback URL the provider redirects back to after consent."
            },
            {
              name: "subject",
              type: "string",
              required: false,
              description: "Opaque id of whoever started the flow (the verified user); `token` returns it so the host can refuse anyone else completing."
            }
          ],
          responseShape: {
            authUrl: "string",
            state: "string"
          }
        },
        {
          name: "token",
          description: "Exchange an authorization code for tokens, consuming the one-time-use `state` from the matching `authorize` call.",
          params: [
            {
              name: "code",
              type: "string",
              required: true,
              description: "Authorization code from the provider's redirect callback."
            },
            {
              name: "state",
              type: "string",
              required: true,
              description: "The `state` value returned by `authorize`."
            },
            {
              name: "subject",
              type: "string",
              required: false,
              description: "Who is completing; must equal the `subject` bound at `authorize`, checked before the code is exchanged."
            }
          ],
          responseShape: {
            accessToken: "string",
            refreshToken: "string",
            expiresIn: "number",
            tokenType: "'bearer'",
            scope: "string[]",
            subject: "string"
          }
        },
        {
          name: "refresh",
          description: "Exchange a refresh token for a new access token.",
          params: [
            {
              name: "refreshToken",
              type: "string",
              required: true,
              description: "Refresh token from a prior `token` call."
            }
          ],
          responseShape: {
            accessToken: "string",
            expiresIn: "number"
          }
        },
        {
          name: "revoke",
          description: "Revoke an access or refresh token.",
          params: [
            {
              name: "token",
              type: "string",
              required: true,
              description: "The access or refresh token to revoke."
            }
          ],
          responseShape: {
            revoked: "boolean"
          }
        },
        {
          name: "userinfo",
          description: "Fetch the authenticated user's profile claims for an access token.",
          params: [
            {
              name: "accessToken",
              type: "string",
              required: true,
              description: "Access token from a prior `token`/`refresh` call."
            }
          ],
          responseShape: {
            sub: "string",
            email: "string",
            name: "string",
            picture: "string"
          }
        },
        {
          name: "connect",
          description: "Complete an authorization for the signed-in caller and keep the grant server-side in the host's connection store; the tokens never return to the program.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Provider the authorization was started with."
            },
            {
              name: "code",
              type: "string",
              required: true,
              description: "Authorization code from the provider's redirect."
            },
            {
              name: "state",
              type: "string",
              required: true,
              description: "The `state` returned by `authorize`; it must have been bound to the same caller."
            }
          ],
          responseShape: {}
        },
        {
          name: "connection",
          description: "The signed-in caller's stored connection to a provider.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Provider to report on."
            }
          ],
          responseShape: {}
        },
        {
          name: "disconnect",
          description: "Forget the signed-in caller's stored connection to a provider.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Provider to disconnect."
            }
          ],
          responseShape: {}
        }
      ],
      runtimes: [
        "node"
      ]
    },
    credentials: {
      name: "credentials",
      description: "Tenant credential store \u2014 masked list, set/remove, test connection (settings-page backend)",
      category: "auth",
      actions: [
        {
          name: "list",
          description: "List declared credentials and their masked (last-4) configuration status, plus the integrations mode and each service's status; open to any caller.",
          params: [
            {
              name: "service",
              type: "string",
              required: false,
              description: "Restrict the listing to one service's declared credentials; omit for all services."
            }
          ],
          responseShape: {
            enabled: "boolean",
            mode: "'mock' | 'real'",
            services: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the service name."
                  },
                  service: {
                    type: "string",
                    required: true
                  },
                  configured: {
                    type: "boolean",
                    required: true,
                    description: "The service has a working configuration on this host."
                  },
                  mocked: {
                    type: "boolean",
                    required: true,
                    description: "The service answers with mock data (mock mode)."
                  },
                  status: {
                    type: "'real' | 'emulated' | 'mocked' | 'not configured'",
                    required: true,
                    description: "One word for what a call to this service does now; `emulated` runs on the local Firebase emulator."
                  },
                  backend: {
                    type: "'production' | 'simulated' | 'stub'",
                    required: true
                  },
                  needs: {
                    type: "string",
                    required: true,
                    description: "What a not-configured service is missing (env var names); empty otherwise."
                  }
                }
              }
            },
            entries: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: `<service>/<envVar>`."
                  },
                  service: {
                    type: "string",
                    required: true
                  },
                  envVar: {
                    type: "string",
                    required: true
                  },
                  required: {
                    type: "boolean",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  configured: {
                    type: "boolean",
                    required: true
                  },
                  source: {
                    type: "'store' | 'env' | 'none'",
                    required: true
                  },
                  last4: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "set",
          description: "Save a credential value into the hosted store; the env var must be one declared for the service (or a well-formed connection-ref name for `database`). Admin/owner role required.",
          params: [
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration name the credential belongs to."
            },
            {
              name: "envVar",
              type: "string",
              required: true,
              description: "Declared env-var name being set."
            },
            {
              name: "value",
              type: "string",
              required: true,
              description: "Secret value to store; never logged."
            }
          ],
          responseShape: {
            saved: "boolean",
            service: "string",
            envVar: "string",
            last4: "string"
          }
        },
        {
          name: "remove",
          description: "Delete a stored credential from the hosted store. Admin/owner role required.",
          params: [
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration name the credential belongs to."
            },
            {
              name: "envVar",
              type: "string",
              required: true,
              description: "Env-var name to remove."
            }
          ],
          responseShape: {
            removed: "boolean"
          }
        },
        {
          name: "test",
          description: "Check whether a service's required credentials are present and, if a live probe is declared for it, exercise the service to confirm they actually work. Admin/owner role required.",
          params: [
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration name to test."
            }
          ],
          responseShape: {
            service: "string",
            configured: "boolean",
            missing: "string[]",
            probed: "boolean",
            ok: "boolean",
            message: "string"
          }
        },
        {
          name: "mode",
          description: "Switch every integration between mock data and its real backend, live. Mock is refused under NODE_ENV=production. Admin/owner role required.",
          params: [
            {
              name: "mode",
              type: "'mock' | 'real'",
              required: true,
              description: "Target integrations mode."
            }
          ],
          responseShape: {
            mode: "'mock' | 'real'"
          }
        },
        {
          name: "rotate",
          description: "Re-encrypt all stored credentials under the current master key (ALMADAR_CREDENTIAL_MASTER_KEY), retiring ALMADAR_CREDENTIAL_MASTER_KEY_PREVIOUS. Admin/owner role required.",
          params: [],
          responseShape: {
            rotated: "number",
            alreadyCurrent: "number",
            unreadable: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    otel: {
      name: "otel",
      description: "OpenTelemetry \u2014 spans, metrics, logs",
      category: "infrastructure",
      actions: [
        {
          name: "startSpan",
          description: "Start a trace span (in-memory backend \u2014 no OpenTelemetry SDK/exporter).",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Span name."
            },
            {
              name: "attributes",
              type: "object",
              required: false,
              description: "Key-value attributes attached to the span (OTel attribute values are scalar).",
              mapValue: {
                type: "string | number | boolean"
              }
            },
            {
              name: "traceId",
              type: "string",
              required: false,
              description: "Existing trace id to attach this span to; a new one is generated when omitted."
            }
          ],
          responseShape: {
            spanId: "string",
            traceId: "string"
          }
        },
        {
          name: "endSpan",
          description: "Mark a span ended, optionally recording its final status; no-op (returns ended:false) for an unknown span id.",
          params: [
            {
              name: "spanId",
              type: "string",
              required: true,
              description: "Span id from startSpan."
            },
            {
              name: "status",
              type: "'ok' | 'error'",
              required: false,
              description: "Final span status."
            }
          ],
          responseShape: {
            ended: "boolean"
          }
        },
        {
          name: "addEvent",
          description: "Attach a timestamped event to an open span; no-op (returns added:false) for an unknown span id.",
          params: [
            {
              name: "spanId",
              type: "string",
              required: true,
              description: "Span id from startSpan."
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: "Event name."
            },
            {
              name: "attributes",
              type: "object",
              required: false,
              description: "Key-value attributes attached to the event (OTel attribute values are scalar).",
              mapValue: {
                type: "string | number | boolean"
              }
            }
          ],
          responseShape: {
            added: "boolean"
          }
        },
        {
          name: "recordMetric",
          description: "Record a metric sample: counters accumulate, gauges overwrite, histograms append to the sample list.",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Metric name."
            },
            {
              name: "value",
              type: "number",
              required: true,
              description: "Sample value."
            },
            {
              name: "type",
              type: "'counter' | 'gauge' | 'histogram'",
              required: false,
              description: "Aggregation kind; defaults to 'counter'."
            },
            {
              name: "labels",
              type: "object",
              required: false,
              description: "Key-value labels attached to the metric (replaces prior labels on the same name; OTel label values are scalar).",
              mapValue: {
                type: "string | number | boolean"
              }
            }
          ],
          responseShape: {
            recorded: "boolean"
          }
        },
        {
          name: "getSpan",
          description: "Fetch a stored span's full record by id; null if unknown.",
          params: [
            {
              name: "spanId",
              type: "string",
              required: true,
              description: "Span id from startSpan."
            }
          ],
          responseShape: {}
        },
        {
          name: "getMetrics",
          description: "Fetch all recorded metrics, keyed by metric name.",
          params: [],
          responseShape: {}
        }
      ],
      runtimes: [
        "node"
      ]
    },
    cli: {
      name: "cli",
      description: "Almadar CLI integration - schema validation and compilation",
      category: "devtools",
      actions: [
        {
          name: "validate",
          description: "Validate an orbital schema by writing it to a temp file and shelling out to `npx",
          params: [
            {
              name: "schema",
              type: "string",
              required: true,
              description: "Raw .orb schema content to validate."
            }
          ],
          responseShape: {
            valid: "boolean",
            errors: "string[]",
            warnings: "string[]",
            summary: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    workspace: {
      name: "workspace",
      description: "Workspace - a project's files and their git history through the host's workspace resolver",
      category: "devtools",
      actions: [
        {
          name: "tree",
          description: "List the workspace's files and directories under a path.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id (the app the files belong to)."
            },
            {
              name: "path",
              type: "string",
              required: false,
              description: "Workspace-relative directory to list; the root when omitted."
            }
          ],
          responseShape: {
            nodes: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the workspace-relative path."
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  type: {
                    type: "'file' | 'directory'",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "readFile",
          description: "Read one workspace file.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "Workspace-relative file path."
            }
          ],
          responseShape: {
            path: "string",
            exists: "boolean",
            content: "string"
          }
        },
        {
          name: "writeFile",
          description: "Write one workspace file (read-only mounts refuse).",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "Workspace-relative file path."
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: "New file text."
            }
          ],
          responseShape: {
            path: "string",
            written: "boolean"
          }
        },
        {
          name: "removeFile",
          description: "Delete one workspace file; deleting an absent file is not an error.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "Workspace-relative file path."
            }
          ],
          responseShape: {
            path: "string",
            removed: "boolean"
          }
        },
        {
          name: "snapshot",
          description: "Commit the current app files as a snapshot.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "reason",
              type: "string",
              required: true,
              description: "Why the snapshot is taken (the commit subject)."
            }
          ],
          responseShape: {
            committed: "boolean",
            oid: "string"
          }
        },
        {
          name: "history",
          description: "The app's snapshots, newest first, and whether undo/redo can step.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Most snapshots to list; all when omitted."
            }
          ],
          responseShape: {
            snapshots: {
              type: "WorkspaceSnapshotRow[]",
              items: {
                type: "WorkspaceSnapshotRow",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Commit oid."
                  },
                  timestamp: {
                    type: "number",
                    required: true,
                    description: "Commit time, epoch ms."
                  },
                  reason: {
                    type: "string",
                    required: true,
                    description: "The commit subject."
                  },
                  kind: {
                    type: "'user' | 'agent' | 'restore' | 'undo' | 'redo' | 'snapshot' | 'merge' | ''",
                    required: true,
                    description: "What made the commit; empty on commits written before kinds were stamped."
                  },
                  target: {
                    type: "string",
                    required: true,
                    description: "A restore, undo or redo commit: the commit it acted on; empty otherwise."
                  }
                }
              }
            },
            canUndo: "boolean",
            canRedo: "boolean"
          }
        },
        {
          name: "changes",
          description: "The app files one snapshot changed, with their text before and after.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "oid",
              type: "string",
              required: true,
              description: "Commit oid."
            }
          ],
          responseShape: {
            changes: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the file path."
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  before: {
                    type: "string",
                    required: true,
                    description: "Text before the commit; empty when `added`."
                  },
                  after: {
                    type: "string",
                    required: true,
                    description: "Text after the commit; empty when `removed`."
                  },
                  change: {
                    type: "'added' | 'removed' | 'modified'",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "restore",
          description: "Make the app what it was at a snapshot, as a new commit.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "oid",
              type: "string",
              required: true,
              description: "Commit oid to restore."
            }
          ],
          responseShape: {
            changed: "boolean",
            oid: "string"
          }
        },
        {
          name: "undo",
          description: "Step back the newest change not already undone, as a new commit.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            }
          ],
          responseShape: {
            changed: "boolean",
            oid: "string"
          }
        },
        {
          name: "redo",
          description: "Re-apply the newest undo not already redone, as a new commit.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            }
          ],
          responseShape: {
            changed: "boolean",
            oid: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    apikeys: {
      name: "apikeys",
      description: "API keys - mint bearer keys an app issues, hashed with the salt the server verifies them by",
      category: "auth",
      actions: [
        {
          name: "mint",
          description: "Mint a new API key. The plaintext is returned once for its owner to copy; store only the hash.",
          params: [],
          responseShape: {
            plaintext: "string",
            keyHash: "string",
            lastFour: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    hosting: {
      name: "hosting",
      description: "Hosting - publish an app as <handle>/<name>, serve it, roll back, read its topology and logs (implemented by @almadar-io/hosting)",
      category: "infrastructure",
      actions: [
        {
          name: "deploy",
          description: "Publish an app: run the pre-deploy gate, make it its handle's next package version and serve it at `<handle>/<name>`. Streams one progress message per step.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: "The app (workspace) to publish."
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: "The published name, lowercase kebab-case."
            },
            {
              name: "description",
              type: "string",
              required: true,
              description: "What the app is."
            },
            {
              name: "tags",
              type: "string[]",
              required: true,
              description: "Catalog tags."
            },
            {
              name: "teamId",
              type: "string",
              required: false,
              description: "Publish as this team (owner or admin only); as the caller when omitted."
            },
            {
              name: "private",
              type: "boolean",
              required: false,
              description: "A paid team's app served to its members only."
            }
          ],
          responseShape: {
            ok: "boolean",
            failedStep: "string",
            findings: "string[]",
            version: "number",
            uses: "string",
            packageVersion: "string",
            liveUrl: "string"
          }
        },
        {
          name: "status",
          description: "What an app is published as, the version served, its history and its masked credentials.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: "The app."
            }
          ],
          responseShape: {
            published: "boolean",
            handle: "string",
            name: "string",
            teamOnly: "boolean",
            liveUrl: "string",
            hasActive: "boolean",
            active: {
              type: "HostingDeploymentRow",
              properties: {
                id: {
                  type: "string",
                  required: true,
                  description: "Row id: the version as text."
                },
                version: {
                  type: "number",
                  required: true
                },
                commit: {
                  type: "string",
                  required: true,
                  description: "The workspace commit deployed."
                },
                target: {
                  type: "'shared-host' | 'own-cloud'",
                  required: true
                },
                packageVersion: {
                  type: "string",
                  required: true,
                  description: "The handle package version a shared-host deployment serves; empty for own-cloud."
                },
                publishedBy: {
                  type: "string",
                  required: true,
                  description: "Who published it; empty when unrecorded."
                },
                recordedAt: {
                  type: "number",
                  required: true,
                  description: "When it was recorded, epoch ms; 0 when unrecorded."
                }
              }
            },
            history: {
              type: "HostingDeploymentRow[]",
              items: {
                type: "HostingDeploymentRow",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the version as text."
                  },
                  version: {
                    type: "number",
                    required: true
                  },
                  commit: {
                    type: "string",
                    required: true,
                    description: "The workspace commit deployed."
                  },
                  target: {
                    type: "'shared-host' | 'own-cloud'",
                    required: true
                  },
                  packageVersion: {
                    type: "string",
                    required: true,
                    description: "The handle package version a shared-host deployment serves; empty for own-cloud."
                  },
                  publishedBy: {
                    type: "string",
                    required: true,
                    description: "Who published it; empty when unrecorded."
                  },
                  recordedAt: {
                    type: "number",
                    required: true,
                    description: "When it was recorded, epoch ms; 0 when unrecorded."
                  }
                }
              }
            },
            credentials: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: `<service>/<envVar>`."
                  },
                  service: {
                    type: "string",
                    required: true
                  },
                  envVar: {
                    type: "string",
                    required: true
                  },
                  last4: {
                    type: "string",
                    required: true
                  },
                  updatedAt: {
                    type: "number",
                    required: true,
                    description: "Epoch ms."
                  }
                }
              }
            },
            credentialsEnabled: "boolean"
          }
        },
        {
          name: "setCredential",
          description: "Store one of a published app's own credentials.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration the credential belongs to."
            },
            {
              name: "envVar",
              type: "string",
              required: true,
              description: "Declared env var."
            },
            {
              name: "value",
              type: "string",
              required: true,
              description: "Secret value; never logged."
            }
          ],
          responseShape: {
            saved: "boolean",
            last4: "string"
          }
        },
        {
          name: "rollback",
          description: "Serve an earlier deployment version again.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "version",
              type: "number",
              required: true,
              description: "The deployment version to serve."
            }
          ],
          responseShape: {
            hasActive: "boolean",
            active: {
              type: "HostingDeploymentRow",
              properties: {
                id: {
                  type: "string",
                  required: true,
                  description: "Row id: the version as text."
                },
                version: {
                  type: "number",
                  required: true
                },
                commit: {
                  type: "string",
                  required: true,
                  description: "The workspace commit deployed."
                },
                target: {
                  type: "'shared-host' | 'own-cloud'",
                  required: true
                },
                packageVersion: {
                  type: "string",
                  required: true,
                  description: "The handle package version a shared-host deployment serves; empty for own-cloud."
                },
                publishedBy: {
                  type: "string",
                  required: true,
                  description: "Who published it; empty when unrecorded."
                },
                recordedAt: {
                  type: "number",
                  required: true,
                  description: "When it was recorded, epoch ms; 0 when unrecorded."
                }
              }
            }
          }
        },
        {
          name: "topology",
          description: "The published app's clients, host, services and stores, with the calls observed between them.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "sinceMs",
              type: "number",
              required: false,
              description: "Window start, epoch ms; the last 24 hours when omitted."
            }
          ],
          responseShape: {
            sinceMs: "number",
            units: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  column: {
                    type: "'client' | 'host' | 'service' | 'store' | 'external'",
                    required: true
                  },
                  label: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "'ok' | 'degraded' | 'down' | 'idle'",
                    required: true
                  },
                  metric: {
                    type: "string",
                    required: true,
                    description: "Empty when the unit reports none."
                  }
                }
              }
            },
            edges: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  source: {
                    type: "string",
                    required: true
                  },
                  target: {
                    type: "string",
                    required: true
                  },
                  label: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "'ok' | 'degraded' | 'down' | 'idle'",
                    required: true
                  },
                  calls: {
                    type: "number",
                    required: true
                  },
                  errors: {
                    type: "number",
                    required: true
                  },
                  p50Ms: {
                    type: "number",
                    required: true,
                    description: "Median latency in ms; 0 when unmeasured."
                  }
                }
              }
            },
            dashboardUrl: "string"
          }
        },
        {
          name: "logs",
          description: "One topology unit's log tail.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "unit",
              type: "string",
              required: true,
              description: "Topology unit id."
            },
            {
              name: "sinceMs",
              type: "number",
              required: false,
              description: "Window start, epoch ms; the last 24 hours when omitted."
            }
          ],
          responseShape: {
            lines: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the line's position in the tail."
                  },
                  time: {
                    type: "string",
                    required: true
                  },
                  severity: {
                    type: "string",
                    required: true
                  },
                  message: {
                    type: "string",
                    required: true
                  },
                  op: {
                    type: "string",
                    required: true,
                    description: "Empty when the line is not a call."
                  },
                  ok: {
                    type: "boolean",
                    required: true,
                    description: "Whether the call succeeded; true for a line that is not a call."
                  },
                  durationMs: {
                    type: "number",
                    required: true,
                    description: "0 when unmeasured."
                  },
                  error: {
                    type: "string",
                    required: true,
                    description: "Empty when none."
                  }
                }
              }
            }
          }
        },
        {
          name: "export",
          description: "Build the app for its owner's own cloud (compiled code + Terraform) behind the same gate; the archive is stored and offered by URL.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            ok: "boolean",
            failedStep: "string",
            findings: "string[]",
            url: "string",
            filename: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    registry: {
      name: "registry",
      description: "Registry - handles and the behavior packages published under them (implemented by @almadar-io/hosting)",
      category: "devtools",
      actions: [
        {
          name: "handles",
          description: "The caller's handle and each of their teams' handles.",
          params: [],
          responseShape: {
            handle: "string",
            teams: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the team id."
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  handle: {
                    type: "string",
                    required: true,
                    description: "Empty when the team has not claimed one."
                  }
                }
              }
            }
          }
        },
        {
          name: "claimHandle",
          description: "Claim a handle for the caller or one of their teams (owner or admin).",
          params: [
            {
              name: "handle",
              type: "string",
              required: true,
              description: "The handle to claim."
            },
            {
              name: "teamId",
              type: "string",
              required: false,
              description: "Claim for this team; for the caller when omitted."
            }
          ],
          responseShape: {
            claimed: "boolean",
            refusal: "'' | 'invalid' | 'reserved' | 'taken' | 'kept' | 'forbidden'",
            message: "string"
          }
        },
        {
          name: "packages",
          description: "The latest version of the caller's handle package and the behaviors in it.",
          params: [],
          responseShape: {
            handle: "string",
            version: "string",
            behaviors: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the behavior name."
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  uses: {
                    type: "string",
                    required: true,
                    description: "The import string, `<handle>/<name>`."
                  }
                }
              }
            }
          }
        },
        {
          name: "publishBehavior",
          description: "Offer a behavior to the community library (reviewed as a pull request).",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Behavior name, lowercase kebab-case."
            },
            {
              name: "description",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "visibility",
              type: "'public' | 'private'",
              required: true,
              description: ""
            },
            {
              name: "program",
              type: "string",
              required: true,
              description: "The behavior's program as `.orb` JSON text."
            }
          ],
          responseShape: {
            name: "string",
            prUrl: "string"
          }
        },
        {
          name: "teamBehaviors",
          description: "A team's behaviors (team owner or admin).",
          params: [
            {
              name: "teamId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            behaviors: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the behavior name."
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  handle: {
                    type: "string",
                    required: true
                  },
                  version: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  tags: {
                    type: "string[]",
                    required: true
                  },
                  enabled: {
                    type: "boolean",
                    required: true
                  },
                  createdAt: {
                    type: "number",
                    required: true,
                    description: "Epoch ms."
                  },
                  createdBy: {
                    type: "string",
                    required: true
                  },
                  orbitalCount: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "setTeamBehaviorEnabled",
          description: "Enable or disable one of a team's behaviors for its members (team owner or admin).",
          params: [
            {
              name: "teamId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "enabled",
              type: "boolean",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            found: "boolean",
            enabled: "boolean"
          }
        },
        {
          name: "removeTeamBehavior",
          description: "Remove one of a team's behaviors (team owner or admin).",
          params: [
            {
              name: "teamId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            removed: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    deepagent: {
      name: "deepagent",
      description: "AI code generation agent with tool use - generates schemas, code, and manages projects",
      category: "ai",
      actions: [
        {
          name: "sendMessage",
          description: "Send a chat message to the DeepAgent server (POST /api/agent/message), continuing an existing thread or starting one.",
          params: [
            {
              name: "message",
              type: "string",
              required: true,
              description: "Message text."
            },
            {
              name: "threadId",
              type: "string",
              required: false,
              description: "Existing thread id to continue; a new thread starts when omitted."
            },
            {
              name: "skill",
              type: "string",
              required: false,
              description: "Named skill to invoke for this message."
            },
            {
              name: "context",
              type: "IntegrationParams",
              required: false,
              description: "Extra context passed through to the agent \u2014 the caller declares its own shape.",
              shapedBy: "context"
            }
          ],
          responseShape: {
            response: "string",
            threadId: "string"
          }
        },
        {
          name: "cancelGeneration",
          description: "Cancel an in-flight generation on a DeepAgent thread (POST /api/agent/cancel).",
          params: [
            {
              name: "threadId",
              type: "string",
              required: true,
              description: "Thread whose generation to cancel."
            }
          ],
          responseShape: {
            cancelled: "boolean"
          }
        },
        {
          name: "validateSchema",
          description: "Validate a schema via the DeepAgent server (POST /api/schema/validate).",
          params: [
            {
              name: "schema",
              type: "string",
              required: true,
              description: "Schema content to validate."
            }
          ],
          responseShape: {
            valid: "boolean",
            errors: "string[]"
          }
        },
        {
          name: "compileSchema",
          description: "Compile a schema to a target shell via the DeepAgent server (POST /api/schema/compile).",
          params: [
            {
              name: "schema",
              type: "string",
              required: true,
              description: "Schema content to compile."
            },
            {
              name: "shell",
              type: "string",
              required: false,
              description: "Target codegen shell (e.g. 'typescript')."
            }
          ],
          responseShape: {
            output: "string",
            files: "string[]"
          }
        },
        {
          name: "getThreadHistory",
          description: "Fetch a thread's message history via the DeepAgent server (POST /api/agent/history).",
          params: [
            {
              name: "threadId",
              type: "string",
              required: true,
              description: "Thread id."
            }
          ],
          responseShape: {
            messages: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  role: {
                    type: "string",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  },
                  timestamp: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    database: {
      name: "database",
      description: "Generic SQL database integration - read-only queries over external connections",
      category: "infrastructure",
      actions: [
        {
          name: "query",
          description: "Run a single read-only SELECT against a Postgres connection (write statements and multi-statement SQL are rejected before execution).",
          params: [
            {
              name: "connectionRef",
              type: "string",
              required: true,
              description: "Env-var NAME whose value is the Postgres connection string \u2014 never the secret itself; resolved from the credential store/environment at run time."
            },
            {
              name: "sql",
              type: "string",
              required: true,
              description: "SQL text; must be exactly one SELECT statement."
            },
            {
              name: "params",
              type: "DatabaseQueryParamValue[]",
              required: false,
              description: "Positional bind parameters for the query."
            }
          ],
          responseShape: {
            rows: {
              type: "DatabaseRow[]",
              shapedBy: "rows"
            },
            rowCount: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    wikimedia: {
      name: "wikimedia",
      description: "Wikipedia API \u2014 redirect-resolved page lookup (description, portrait, lead extract)",
      category: "reference",
      actions: [
        {
          name: "getPage",
          description: "Look up a Wikipedia page (redirect-resolved): a one-line description, a Commons thumbnail, and the lead-section extract; empty fields on a miss.",
          params: [
            {
              name: "title",
              type: "string",
              required: true,
              description: "Page title to look up."
            }
          ],
          responseShape: {
            title: "string",
            description: "string",
            portraitUrl: "string",
            extract: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    iconify: {
      name: "iconify",
      description: "Iconify API \u2014 brand-logo / icon resolution, search, and body fetch",
      category: "reference",
      actions: [
        {
          name: "svgExists",
          description: "Check whether an icon exists at the given Iconify API path (a GET, not a HEAD, despite the name).",
          params: [
            {
              name: "path",
              type: "string",
              required: true,
              description: "Iconify API path segment to probe, e.g. 'prefix/name.svg'."
            }
          ],
          responseShape: {
            exists: "boolean"
          }
        },
        {
          name: "search",
          description: "Search Iconify's icon sets by relevance.",
          params: [
            {
              name: "query",
              type: "string",
              required: true,
              description: "Search query text."
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Maximum icon ids to return; defaults to 1."
            }
          ],
          responseShape: {
            icons: "string[]"
          }
        },
        {
          name: "getIconBody",
          description: "Fetch an icon's raw SVG body (for color-tone classification); null if the icon or its collection isn't found.",
          params: [
            {
              name: "iconId",
              type: "string",
              required: true,
              description: 'Icon id in "prefix:name" form.'
            }
          ],
          responseShape: {
            body: "string | null"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    arxiv: {
      name: "arxiv",
      description: "arXiv API \u2014 frontier academic preprint search (emerging-axis research backend)",
      category: "reference",
      actions: [
        {
          name: "search",
          description: "Search arXiv's public Atom API by relevance.",
          params: [
            {
              name: "query",
              type: "string",
              required: true,
              description: "Search query text (matched across all fields)."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum results to return; defaults to 8."
            }
          ],
          responseShape: {
            results: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  summary: {
                    type: "string",
                    required: true
                  },
                  authors: {
                    type: "string[]",
                    required: true
                  },
                  published: {
                    type: "string",
                    required: true
                  },
                  url: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    rigger: {
      name: "rigger",
      description: "Local rigger service (FastAPI) \u2014 mesh rigging build + render",
      category: "media",
      actions: [
        {
          name: "health",
          description: "Liveness probe of the local rigger service.",
          params: [],
          responseShape: {
            ok: "boolean"
          }
        },
        {
          name: "buildMesh",
          description: "Build a rigged mesh bundle from a source image via the local rigger service.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            }
          ],
          responseShape: {
            bundlePath: "string",
            boneCount: "number",
            vertexCount: "number",
            triangleCount: "number",
            clips: "string[]"
          }
        },
        {
          name: "render",
          description: "Render a rigged mesh bundle to output files via the local rigger service.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "preset",
              type: "string",
              required: true,
              description: "Render preset name."
            },
            {
              name: "outDir",
              type: "string",
              required: true,
              description: "Output directory for rendered files."
            },
            {
              name: "mode",
              type: "string",
              required: false,
              description: "Render path: 'mesh' (skinned, deforming) or 'cutout' (rigid per-part layers). Defaults to 'mesh'."
            },
            {
              name: "supersample",
              type: "number",
              required: false,
              description: "Supersampling factor for the cutout path. Defaults to 1."
            }
          ],
          responseShape: {
            outdir: "string",
            frames: "number",
            framePaths: "string[]",
            sheet: "string",
            gif: "string",
            atlas: "string"
          }
        },
        {
          name: "listPresets",
          description: "List the available animation presets (name, fps, frame count, bone roles) from the local rigger service.",
          params: [],
          responseShape: {
            presets: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  fps: {
                    type: "number",
                    required: true
                  },
                  frames: {
                    type: "number",
                    required: true
                  },
                  bones: {
                    type: "object[]",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "loadRig",
          description: "Load a rig file, resolving `cloneOf` parts to concrete box/pivot geometry.",
          params: [
            {
              name: "rigPath",
              type: "string",
              required: true,
              description: "Path to the rig JSON file."
            }
          ],
          responseShape: {
            rig: "object",
            resolved: "object"
          }
        },
        {
          name: "saveRig",
          description: "Save a rig file; the server validates it and rejects a malformed part.",
          params: [
            {
              name: "rigPath",
              type: "string",
              required: true,
              description: "Path to write the rig JSON file."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            }
          ],
          responseShape: {
            rigPath: "string",
            parts: "number"
          }
        },
        {
          name: "ownership",
          description: "Render the per-part pixel ownership map for a rig over a source image.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "outPath",
              type: "string",
              required: true,
              description: "Path to write the ownership-map PNG."
            }
          ],
          responseShape: {
            outPath: "string",
            legend: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  color: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "buildLayers",
          description: "Build per-part cutout layer images (name, box, z-order, pivot) from a source image and rig.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "outDir",
              type: "string",
              required: true,
              description: "Output directory for the layer images."
            }
          ],
          responseShape: {
            layers: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  x: {
                    type: "number",
                    required: true
                  },
                  y: {
                    type: "number",
                    required: true
                  },
                  w: {
                    type: "number",
                    required: true
                  },
                  h: {
                    type: "number",
                    required: true
                  },
                  z: {
                    type: "number",
                    required: true
                  },
                  pivot: {
                    type: "[number, number]",
                    required: true
                  },
                  isRest: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "pose",
          description: "Sample a pose/animation preset into per-frame, per-part transforms for a rig.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "roles",
              type: "object",
              required: true,
              description: "Map of part name to preset bone role.",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "preset",
              type: "string",
              required: false,
              description: "Animation preset name."
            },
            {
              name: "overrides",
              type: "object",
              required: false,
              description: "Per-part motion overrides applied over the preset."
            }
          ],
          responseShape: {
            fps: "number",
            frames: {
              type: "array",
              items: {
                type: "object",
                mapValue: {
                  type: "object",
                  properties: {
                    angle: {
                      type: "number",
                      required: true
                    },
                    bx: {
                      type: "number",
                      required: true
                    },
                    by: {
                      type: "number",
                      required: true
                    }
                  }
                }
              }
            }
          }
        },
        {
          name: "generate",
          description: "Generate one image on the local image server (text-to-image, or a Kontext edit conditioned on `sourcePath`).",
          params: [
            {
              name: "model",
              type: "'z-image-turbo' | 'kontext'",
              required: true,
              description: "Generation model: 'z-image-turbo' (text-to-image) or 'kontext' (edit; requires sourcePath)."
            },
            {
              name: "prompt",
              type: "string",
              required: true,
              description: "The generation/edit prompt."
            },
            {
              name: "seed",
              type: "number",
              required: false,
              description: "Random seed; omit for a fresh one each call."
            },
            {
              name: "size",
              type: "string",
              required: false,
              description: "Output image size, e.g. '1024x1024'. Defaults to '1024x1024'."
            },
            {
              name: "steps",
              type: "number",
              required: false,
              description: "Diffusion step count."
            },
            {
              name: "sourcePath",
              type: "string",
              required: false,
              description: "Path to the source image to edit; REQUIRED for 'kontext'."
            },
            {
              name: "outDir",
              type: "string",
              required: true,
              description: "Output directory the generated PNG is written under."
            }
          ],
          responseShape: {
            outPath: "string",
            model: "string",
            prompt: "string",
            seed: "number | null",
            size: "string",
            sourcePath: "string | null",
            elapsedMs: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    page: {
      name: "page",
      description: "Browser tabs \u2014 open, navigate, query, watch, treat and interact with page elements (generic; selectors are parameters)",
      category: "infrastructure",
      actions: [
        {
          name: "listTabs",
          description: "List open tabs, optionally only those whose URL matches a match pattern.",
          params: [
            {
              name: "match",
              type: "string",
              required: false,
              description: 'URL match pattern, e.g. "https://example.com/*".'
            }
          ],
          responseShape: {
            tabs: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true
                  },
                  url: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  active: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "openTab",
          description: "Open a URL in a new tab.",
          params: [
            {
              name: "url",
              type: "string",
              required: true,
              description: "URL to open."
            },
            {
              name: "params",
              type: "object",
              required: false,
              description: "Query parameters added to the URL, encoded (e.g. a search term).",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "match",
              type: "string",
              required: false,
              description: "URL match pattern of the pages this tab will be read from (query/apply/act). The URL must match\nit, and a browser extension asks for access to these pages."
            },
            {
              name: "active",
              type: "boolean",
              required: true,
              description: "Whether the new tab takes focus."
            }
          ],
          responseShape: {
            tab: "number"
          }
        },
        {
          name: "closeTab",
          description: "Close a tab the program opened.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id from openTab or listTabs."
            }
          ],
          responseShape: {
            closed: "boolean"
          }
        },
        {
          name: "navigate",
          description: "Load a URL in an existing tab.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "url",
              type: "string",
              required: true,
              description: "URL to load."
            }
          ],
          responseShape: {
            url: "string"
          }
        },
        {
          name: "query",
          description: "Read the elements of a tab's page that match a selector, with the requested fields.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "selector",
              type: "string",
              required: true,
              description: "CSS selector of the elements to read."
            },
            {
              name: "fields",
              type: "object",
              required: true,
              description: "Field name to how to read it from each element.",
              mapValue: {
                type: "PageFieldSpec",
                properties: {
                  selector: {
                    type: "string",
                    required: false,
                    description: "Descendant selector within the matched element; absent reads the element itself."
                  },
                  attr: {
                    type: "string",
                    required: false,
                    description: "Attribute to read; absent reads the text content."
                  },
                  resolve: {
                    type: "boolean",
                    required: false,
                    description: "The value is a URL: return it resolved against the page (a relative link becomes absolute)."
                  },
                  template: {
                    type: "string",
                    required: false,
                    description: "Compose the value from other fields of the same element instead of reading it: `{name}` stands\nfor that field's value (a read field, not another template). Empty when any named field is empty.\nExcludes `selector` and `attr`."
                  },
                  required: {
                    type: "boolean",
                    required: false,
                    description: "Leave the element out until this field reads non-empty; a watch reports it once it fills in."
                  }
                }
              }
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Maximum elements to return."
            },
            {
              name: "waitMs",
              type: "number",
              required: false,
              description: "Wait up to this many milliseconds for the page to load and a first match to appear."
            }
          ],
          responseShape: {
            tab: "number",
            elements: {
              type: "PageElement[]",
              items: {
                type: "PageElement",
                properties: {
                  elementId: {
                    type: "string",
                    required: true
                  },
                  fields: {
                    type: "object",
                    required: true,
                    mapValue: {
                      type: "string"
                    }
                  }
                }
              }
            }
          }
        },
        {
          name: "watch",
          description: "Report every element that matches a selector, now and as the page changes, in every tab whose URL\nmatches. Each element arrives once as the program's declared external input `event`, carrying\n`{ tab, elementId, url, fields }`.",
          params: [
            {
              name: "match",
              type: "string",
              required: true,
              description: "URL match pattern of the tabs to watch."
            },
            {
              name: "selector",
              type: "string",
              required: true,
              description: "CSS selector of the elements to report."
            },
            {
              name: "fields",
              type: "object",
              required: true,
              description: "Field name to how to read it from each element.",
              mapValue: {
                type: "PageFieldSpec",
                properties: {
                  selector: {
                    type: "string",
                    required: false,
                    description: "Descendant selector within the matched element; absent reads the element itself."
                  },
                  attr: {
                    type: "string",
                    required: false,
                    description: "Attribute to read; absent reads the text content."
                  },
                  resolve: {
                    type: "boolean",
                    required: false,
                    description: "The value is a URL: return it resolved against the page (a relative link becomes absolute)."
                  },
                  template: {
                    type: "string",
                    required: false,
                    description: "Compose the value from other fields of the same element instead of reading it: `{name}` stands\nfor that field's value (a read field, not another template). Empty when any named field is empty.\nExcludes `selector` and `attr`."
                  },
                  required: {
                    type: "boolean",
                    required: false,
                    description: "Leave the element out until this field reads non-empty; a watch reports it once it fills in."
                  }
                }
              }
            },
            {
              name: "event",
              type: "EventAddress",
              required: true,
              description: "The declared external input each element fires, addressed like a `listens` source from the calling trait."
            }
          ],
          responseShape: {
            watchId: "string"
          }
        },
        {
          name: "unwatch",
          description: "Stop a watch.",
          params: [
            {
              name: "watchId",
              type: "string",
              required: true,
              description: "Id returned by watch."
            }
          ],
          responseShape: {
            stopped: "boolean"
          }
        },
        {
          name: "apply",
          description: "Apply a declared visual treatment to an element, with an optional visible label.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "elementId",
              type: "string",
              required: true,
              description: "Element id from query or watch."
            },
            {
              name: "treatment",
              type: "'hide' | 'blur' | 'highlight' | 'label' | 'restore'",
              required: true,
              description: "The treatment; `restore` undoes any earlier one."
            },
            {
              name: "label",
              type: "string",
              required: false,
              description: "Text shown with the treatment (e.g. why it was applied)."
            }
          ],
          responseShape: {
            applied: "boolean",
            tab: "number",
            elementId: "string",
            treatment: "'hide' | 'blur' | 'highlight' | 'label' | 'restore'"
          }
        },
        {
          name: "applyMany",
          description: "Apply treatments to many elements at once, one command per tab. An element that has left its\npage, or whose tab has closed, is listed in `missing`; it does not fail the others.",
          params: [
            {
              name: "items",
              type: "array",
              required: true,
              description: "",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true,
                    description: "Tab id."
                  },
                  elementId: {
                    type: "string",
                    required: true,
                    description: "Element id from query or watch."
                  },
                  treatment: {
                    type: "'hide' | 'blur' | 'highlight' | 'label' | 'restore'",
                    required: true,
                    description: "The treatment; `restore` undoes any earlier one."
                  },
                  label: {
                    type: "string",
                    required: false,
                    description: "Text shown with the treatment (e.g. why it was applied)."
                  }
                }
              }
            }
          ],
          responseShape: {
            applied: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true
                  },
                  elementId: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            missing: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true
                  },
                  elementId: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "act",
          description: "Interact with an element: click it, focus it, or type text into it.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "elementId",
              type: "string",
              required: true,
              description: "Element id from query or watch."
            },
            {
              name: "action",
              type: "'click' | 'focus' | 'type'",
              required: true,
              description: "The interaction."
            },
            {
              name: "text",
              type: "string",
              required: false,
              description: 'Text to type (action "type" only).'
            }
          ],
          responseShape: {
            done: "boolean",
            tab: "number",
            elementId: "string"
          }
        }
      ],
      runtimes: [
        "browser"
      ]
    },
    knowledge: {
      name: "knowledge",
      description: "Learner knowledge source \u2014 connection status, sign-in, and the topics currently being learned",
      category: "ai",
      actions: [
        {
          name: "status",
          description: "Whether a learner account is connected, without starting a sign-in.",
          params: [],
          responseShape: {
            connected: "boolean",
            account: "string"
          }
        },
        {
          name: "connect",
          description: "Sign in to the knowledge source and keep the session.",
          params: [],
          responseShape: {
            connected: "boolean",
            account: "string"
          }
        },
        {
          name: "currentTopics",
          description: "The topics the learner is currently working on, most current first.",
          params: [
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Maximum topics to return."
            },
            {
              name: "sinceDays",
              type: "number",
              required: false,
              description: "Only topics touched within this many days."
            }
          ],
          responseShape: {
            topics: {
              type: "KnowledgeTopic[]",
              items: {
                type: "KnowledgeTopic",
                properties: {
                  label: {
                    type: "string",
                    required: true
                  },
                  keywords: {
                    type: "string[]",
                    required: true
                  },
                  weight: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "listPaths",
          description: "The learner's paths, filtered, sorted and paged; `search` ranks by meaning.",
          params: [
            {
              name: "search",
              type: "string",
              required: false,
              description: "Free-text search; ranks paths semantically."
            },
            {
              name: "sort",
              type: "'recent' | 'oldest' | 'az' | 'za'",
              required: false,
              description: ""
            },
            {
              name: "clusterId",
              type: "string",
              required: false,
              description: "Only paths in this cluster."
            },
            {
              name: "page",
              type: "number",
              required: false,
              description: ""
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Paths per page (1\u201348, default 9)."
            }
          ],
          responseShape: {
            items: {
              type: "KnowledgePath[]",
              items: {
                type: "KnowledgePath",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  conceptCount: {
                    type: "number",
                    required: true
                  },
                  learnedCount: {
                    type: "number",
                    required: true,
                    description: "Concepts the viewer has learned (mastery 3)."
                  },
                  levelCount: {
                    type: "number",
                    required: true
                  },
                  seedConcept: {
                    type: "string",
                    required: true,
                    description: "Name of the concept the path grew from; empty when it has none."
                  },
                  clusterId: {
                    type: "string",
                    required: true,
                    description: "The path's cluster id; empty when unclustered."
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  },
                  updatedAt: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            total: "number",
            page: "number",
            totalPages: "number",
            clusters: {
              type: "KnowledgePathCluster[]",
              items: {
                type: "KnowledgePathCluster",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "pathMap",
          description: "Every path with how they relate: similarity edges, shared-concept edges and clusters.",
          params: [],
          responseShape: {
            paths: {
              type: "KnowledgePath[]",
              items: {
                type: "KnowledgePath",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  conceptCount: {
                    type: "number",
                    required: true
                  },
                  learnedCount: {
                    type: "number",
                    required: true,
                    description: "Concepts the viewer has learned (mastery 3)."
                  },
                  levelCount: {
                    type: "number",
                    required: true
                  },
                  seedConcept: {
                    type: "string",
                    required: true,
                    description: "Name of the concept the path grew from; empty when it has none."
                  },
                  clusterId: {
                    type: "string",
                    required: true,
                    description: "The path's cluster id; empty when unclustered."
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  },
                  updatedAt: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            similarity: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  source: {
                    type: "string",
                    required: true
                  },
                  target: {
                    type: "string",
                    required: true
                  },
                  weight: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            sharedConcepts: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  source: {
                    type: "string",
                    required: true
                  },
                  target: {
                    type: "string",
                    required: true
                  },
                  weight: {
                    type: "number",
                    required: true
                  },
                  shared: {
                    type: "string[]",
                    required: true
                  },
                  linked: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            },
            clusters: {
              type: "KnowledgePathCluster[]",
              items: {
                type: "KnowledgePathCluster",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "deletePath",
          description: "Delete a path with everything derived from it.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "conceptsByLayer",
          description: "A path's goal and its levels of concepts.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            graphId: "string",
            title: "string",
            goal: "string",
            seedConcept: "string",
            layers: {
              type: "KnowledgeLayer[]",
              items: {
                type: "KnowledgeLayer",
                properties: {
                  layerNumber: {
                    type: "number",
                    required: true
                  },
                  goal: {
                    type: "string",
                    required: true
                  },
                  review: {
                    type: "string",
                    required: true
                  },
                  concepts: {
                    type: "KnowledgeConcept[]",
                    required: true,
                    items: {
                      type: "KnowledgeConcept",
                      properties: {
                        id: {
                          type: "string",
                          required: true
                        },
                        name: {
                          type: "string",
                          required: true
                        },
                        description: {
                          type: "string",
                          required: true
                        },
                        layer: {
                          type: "number",
                          required: true
                        },
                        isSeed: {
                          type: "boolean",
                          required: true
                        },
                        sequence: {
                          type: "number",
                          required: true
                        },
                        prerequisites: {
                          type: "string[]",
                          required: true
                        },
                        parents: {
                          type: "string[]",
                          required: true,
                          description: "Names of the concepts this one builds on (the parents a level expansion links it to)."
                        },
                        masteryLevel: {
                          type: "number",
                          required: true,
                          description: "0 not started \u2026 3 learned."
                        },
                        hasLesson: {
                          type: "boolean",
                          required: true,
                          description: "Whether a lesson has been generated for the concept."
                        },
                        canonicalKey: {
                          type: "string",
                          required: true,
                          description: "The graph engine's canonical key for the concept; peers address it as `concept:<canonicalKey>`."
                        },
                        routeKey: {
                          type: "string",
                          required: true,
                          description: "URL-safe key for the concept's page (concept ids are free text); `conceptDetail` takes it."
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        },
        {
          name: "expand",
          description: "Generate the path's next level of concepts. Streams progress: `delta`s, and one `step` per concept name as the model writes it.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "numConcepts",
              type: "number",
              required: false,
              description: "How many concepts to add (default 10)."
            },
            {
              name: "difficulty",
              type: "GraphDifficulty",
              required: false,
              description: "The level the learner chose; recorded on the path's goal and used from then on."
            }
          ],
          responseShape: {
            levelName: "string",
            added: {
              type: "KnowledgeConcept[]",
              items: {
                type: "KnowledgeConcept",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  layer: {
                    type: "number",
                    required: true
                  },
                  isSeed: {
                    type: "boolean",
                    required: true
                  },
                  sequence: {
                    type: "number",
                    required: true
                  },
                  prerequisites: {
                    type: "string[]",
                    required: true
                  },
                  parents: {
                    type: "string[]",
                    required: true,
                    description: "Names of the concepts this one builds on (the parents a level expansion links it to)."
                  },
                  masteryLevel: {
                    type: "number",
                    required: true,
                    description: "0 not started \u2026 3 learned."
                  },
                  hasLesson: {
                    type: "boolean",
                    required: true,
                    description: "Whether a lesson has been generated for the concept."
                  },
                  canonicalKey: {
                    type: "string",
                    required: true,
                    description: "The graph engine's canonical key for the concept; peers address it as `concept:<canonicalKey>`."
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page (concept ids are free text); `conceptDetail` takes it."
                  }
                }
              }
            }
          }
        },
        {
          name: "layerPractice",
          description: "Generate the practice and review for one level. Streams progress.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "layerNumber",
              type: "number",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            review: "string"
          }
        },
        {
          name: "conceptDetail",
          description: "One concept with its lesson, notes, questions and neighbours.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "routeKey",
              type: "string",
              required: true,
              description: "The concept's `routeKey` (from any concept or concept ref); pages carry it in their URL."
            }
          ],
          responseShape: {
            concept: {
              type: "KnowledgeConcept",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                name: {
                  type: "string",
                  required: true
                },
                description: {
                  type: "string",
                  required: true
                },
                layer: {
                  type: "number",
                  required: true
                },
                isSeed: {
                  type: "boolean",
                  required: true
                },
                sequence: {
                  type: "number",
                  required: true
                },
                prerequisites: {
                  type: "string[]",
                  required: true
                },
                parents: {
                  type: "string[]",
                  required: true,
                  description: "Names of the concepts this one builds on (the parents a level expansion links it to)."
                },
                masteryLevel: {
                  type: "number",
                  required: true,
                  description: "0 not started \u2026 3 learned."
                },
                hasLesson: {
                  type: "boolean",
                  required: true,
                  description: "Whether a lesson has been generated for the concept."
                },
                canonicalKey: {
                  type: "string",
                  required: true,
                  description: "The graph engine's canonical key for the concept; peers address it as `concept:<canonicalKey>`."
                },
                routeKey: {
                  type: "string",
                  required: true,
                  description: "URL-safe key for the concept's page (concept ids are free text); `conceptDetail` takes it."
                }
              }
            },
            pathTitle: "string",
            lessonId: "string",
            lesson: "string",
            prerequisites: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            parents: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            previous: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            next: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            notes: {
              type: "KnowledgeNote[]",
              items: {
                type: "KnowledgeNote",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  text: {
                    type: "string",
                    required: true
                  },
                  selectedText: {
                    type: "string",
                    required: true
                  },
                  timestamp: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            questions: {
              type: "KnowledgeQuestion[]",
              items: {
                type: "KnowledgeQuestion",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  question: {
                    type: "string",
                    required: true
                  },
                  answer: {
                    type: "string",
                    required: true
                  },
                  selectedText: {
                    type: "string",
                    required: true
                  },
                  timestamp: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "explain",
          description: "Generate (or regenerate) the lesson for a concept. Streams progress.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "simple",
              type: "boolean",
              required: false,
              description: "A shorter, simpler lesson."
            }
          ],
          responseShape: {
            lessonId: "string",
            lesson: "string"
          }
        },
        {
          name: "saveLesson",
          description: "Save an edited lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            saved: "boolean"
          }
        },
        {
          name: "answerQuestion",
          description: "Answer a question about a lesson and keep it with the lesson. Streams progress.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "question",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "selectedText",
              type: "string",
              required: false,
              description: "The lesson text the question is about."
            }
          ],
          responseShape: {
            question: {
              type: "KnowledgeQuestion",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                question: {
                  type: "string",
                  required: true
                },
                answer: {
                  type: "string",
                  required: true
                },
                selectedText: {
                  type: "string",
                  required: true
                },
                timestamp: {
                  type: "number",
                  required: true
                }
              }
            },
            relatedConcepts: {
              type: "KnowledgeRelatedConcept[]",
              items: {
                type: "KnowledgeRelatedConcept",
                properties: {
                  graphId: {
                    type: "string",
                    required: true
                  },
                  conceptId: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  text: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "addNote",
          description: "Attach a note to a lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "text",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "selectedText",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            note: {
              type: "KnowledgeNote",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                text: {
                  type: "string",
                  required: true
                },
                selectedText: {
                  type: "string",
                  required: true
                },
                timestamp: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "deleteNote",
          description: "Remove a note from a lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "noteId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "deleteQuestion",
          description: "Remove a question and its answer from a lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "questionId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "recordLessonProgress",
          description: "Record what the learner did on a concept (activation answer, reflection, a Bloom-level answer) and\nrecompute its mastery: 0 nothing recorded, 1 one signal, 2 two or three, 3 four (an activation answer, a reflection, any Bloom\nanswer, three Bloom answers); once at 2 or above it is kept. A Bloom answer counts once per level; `correct` defaults to false and only a\ncorrect answer adds the level to the completed levels. `markLearned` stays authoritative (3 / 0).",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "activationResponse",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "reflection",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "bloomLevel",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "correct",
              type: "boolean",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            masteryLevel: "number"
          }
        },
        {
          name: "markLearned",
          description: "Mark a concept learned (mastery 3) or not yet (0).",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "learned",
              type: "boolean",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            masteryLevel: "number"
          }
        },
        {
          name: "visualization",
          description: "Choose a std learning instrument for a concept and generate its roster. Streams progress: `start`, then one `step` naming the chosen instrument.\nNo result is cached: every call generates afresh.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "markerDescription",
              type: "string",
              required: false,
              description: "What the lesson asks to visualise."
            }
          ],
          responseShape: {
            mode: "string",
            rosterJson: "string"
          }
        },
        {
          name: "createGoalWithGraph",
          description: "Create a learning goal and its path from what the learner wants to learn. Streams progress: `delta`s, and `step`s as the goal is read: its `title` and `description` as they grow, then each `milestone` once.",
          params: [
            {
              name: "anchorAnswer",
              type: "string",
              required: true,
              description: `The learner's answer to "what do you want to learn?".`
            }
          ],
          responseShape: {
            graphId: "string",
            goalTitle: "string",
            description: "string",
            milestones: "string[]",
            difficulty: "GraphDifficulty"
          }
        },
        {
          name: "importPreview",
          description: "Stage an import without writing anything.",
          params: [
            {
              name: "adapter",
              type: "'markdown' | 'paste'",
              required: true,
              description: ""
            },
            {
              name: "documents",
              type: "KnowledgeImportDocument[]",
              required: true,
              description: "",
              items: {
                type: "KnowledgeImportDocument",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  raw: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          ],
          responseShape: {
            units: {
              type: "KnowledgeImportUnit[]",
              items: {
                type: "KnowledgeImportUnit",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  targetEntity: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  parentRef: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            errors: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  message: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "importCommit",
          description: "Import documents into a path (a new one when `graphId` is omitted).",
          params: [
            {
              name: "adapter",
              type: "'markdown' | 'paste'",
              required: true,
              description: ""
            },
            {
              name: "documents",
              type: "KnowledgeImportDocument[]",
              required: true,
              description: "",
              items: {
                type: "KnowledgeImportDocument",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  raw: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "graphId",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            graphId: "string",
            committed: "number",
            failed: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  reason: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "importItems",
          description: "Commit an approved, already-structured proposal (items the learner reviewed) into a path (a new one when `graphId` is omitted; `title` names it, else the first item's title).\nEach item is `{ ref, targetEntity, title, parentRef, content, front?, back? }`, all strings: `ref` is the item's id inside the proposal; `parentRef` is another item's `ref`, or `''` for top level.\n`Concept` (title = name, content = its lesson text) may sit under a `LearningGoal` or `Concept`; `Lesson` (content = lesson text) and `FlashCard` (`front`/`back`, defaulting to title/content when omitted or blank) need a parent `Concept`; `LearningGoal` (title = name, content = description) is top level.\nItems are validated one by one: a blank title, duplicate `ref`, dangling or cyclic `parentRef`, or a parent of the wrong kind fails that item (and its descendants) in `failed` while the rest commit. A new path with nothing committed is removed and the call fails.",
          params: [
            {
              name: "items",
              type: "KnowledgeImportItem[]",
              required: true,
              description: "",
              items: {
                type: "KnowledgeImportItem",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  targetEntity: {
                    type: "'Concept' | 'Lesson' | 'FlashCard' | 'LearningGoal'",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  parentRef: {
                    type: "string",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  },
                  front: {
                    type: "string",
                    required: false
                  },
                  back: {
                    type: "string",
                    required: false
                  }
                }
              }
            },
            {
              name: "graphId",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "title",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            graphId: "string",
            committed: "number",
            failed: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  reason: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "peers",
          description: "Other learners on the same concept or path. `nodeKey` is `concept:<canonicalKey>` (the concept's `canonicalKey`) or `path:<id>`.",
          params: [
            {
              name: "nodeKey",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            peers: {
              type: "KnowledgePeer[]",
              items: {
                type: "KnowledgePeer",
                properties: {
                  peerRef: {
                    type: "string",
                    required: true
                  },
                  handle: {
                    type: "string",
                    required: true
                  },
                  conceptsCompleted: {
                    type: "number",
                    required: true
                  },
                  convergencePct: {
                    type: "number",
                    required: true
                  },
                  activeNow: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "touchNode",
          description: "Record that the learner is on this concept or path, so peers can find them. `nodeKey` is `concept:<canonicalKey>` (the concept's `canonicalKey`) or `path:<id>`.",
          params: [
            {
              name: "nodeKey",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            touched: "boolean"
          }
        },
        {
          name: "openConnection",
          description: "Open an anonymous discussion with a peer.",
          params: [
            {
              name: "peerRef",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "originKind",
              type: "'concept' | 'path'",
              required: true,
              description: ""
            },
            {
              name: "canonicalId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            connectionId: "string"
          }
        },
        {
          name: "connection",
          description: "One discussion with its topics and messages.",
          params: [
            {
              name: "connectionId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            status: "'active' | 'archived'",
            peerHandle: "string",
            originKind: "'concept' | 'path'",
            originLabel: "string",
            badges: {
              type: "KnowledgeBadge[]",
              items: {
                type: "KnowledgeBadge",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  label: {
                    type: "string",
                    required: true
                  },
                  kind: {
                    type: "'subtopic' | 'friction'",
                    required: true
                  },
                  active: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            },
            messages: {
              type: "KnowledgeMessage[]",
              items: {
                type: "KnowledgeMessage",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  fromViewer: {
                    type: "boolean",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  },
                  flagged: {
                    type: "boolean",
                    required: true
                  },
                  moderationReason: {
                    type: "string",
                    required: true,
                    description: "Why moderation flagged it; empty when it has no reason."
                  }
                }
              }
            }
          }
        },
        {
          name: "setActiveBadge",
          description: "Choose the discussion topic.",
          params: [
            {
              name: "connectionId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "badgeId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            saved: "boolean"
          }
        },
        {
          name: "sendMessage",
          description: "Send a message in a discussion.",
          params: [
            {
              name: "connectionId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "activeBadgeId",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            messageId: "string"
          }
        },
        {
          name: "conceptPersona",
          description: "The real person behind a concept, grounded in their public biography, for an in-character chat.",
          params: [
            {
              name: "conceptLabel",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "context",
              type: "string",
              required: false,
              description: "The learner's subject, so an ambiguous concept resolves in the right field."
            }
          ],
          responseShape: {
            name: "string",
            description: "string",
            portraitUrl: "string",
            greeting: "string",
            instructions: "string"
          }
        },
        {
          name: "conceptIcon",
          description: "An icon for a concept label.",
          params: [
            {
              name: "label",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            icon: "string",
            tone: "'themed' | 'dark' | 'light' | 'none'"
          }
        },
        {
          name: "getPreferences",
          description: "The learner's model choices for generating levels and lessons.",
          params: [],
          responseShape: {
            expandModel: "string",
            explainModel: "string"
          }
        },
        {
          name: "setPreferences",
          description: "Change the learner's model choices.",
          params: [
            {
              name: "expandModel",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "explainModel",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            saved: "boolean"
          }
        }
      ],
      runtimes: [
        "node",
        "browser"
      ]
    },
    analytics: {
      name: "analytics",
      description: "Cookieless first-party analytics \u2014 pageviews and named events beaconed to a collector endpoint",
      category: "marketing",
      actions: [
        {
          name: "pageview",
          description: "Record a visit to a page.",
          params: [
            {
              name: "endpoint",
              type: "string",
              required: true,
              description: "Collector ingest URL; empty sends nothing."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: `The page's path, starting with "/".`
            },
            {
              name: "domain",
              type: "string",
              required: false,
              description: "Site id recorded with the visit; empty or absent records the page's host."
            }
          ],
          responseShape: {
            sent: "boolean"
          }
        },
        {
          name: "track",
          description: "Record a named event (a download, a sign-up) on a page.",
          params: [
            {
              name: "endpoint",
              type: "string",
              required: true,
              description: "Collector ingest URL; empty sends nothing."
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: 'Event name; "pageview" is reserved.'
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: `The page's path, starting with "/".`
            },
            {
              name: "props",
              type: "object",
              required: false,
              description: "Event properties.",
              mapValue: {
                type: "string | number | boolean"
              }
            },
            {
              name: "domain",
              type: "string",
              required: false,
              description: "Site id recorded with the event; empty or absent records the page's host."
            }
          ],
          responseShape: {
            sent: "boolean"
          }
        }
      ],
      runtimes: [
        "browser"
      ]
    }
  },
  categories: [
    "ai",
    "auth",
    "devtools",
    "finance",
    "infrastructure",
    "marketing",
    "media",
    "messaging",
    "payment",
    "reference"
  ]
};

// src/patterns/component-mapping.json
var component_mapping_default = {
  version: "1.0.0",
  exportedAt: "2026-10-10T01:07:16.949Z",
  mappings: {
    "page-header": {
      component: "PageHeader",
      importPath: "@/components/core/molecules/PageHeader",
      category: "header"
    },
    "detail-panel": {
      component: "DetailPanel",
      importPath: "@/components/core/organisms/DetailPanel",
      category: "display"
    },
    form: {
      component: "Form",
      importPath: "@/components/core/organisms/Form",
      category: "form"
    },
    "form-section": {
      component: "Form",
      importPath: "@/components/core/organisms/Form",
      category: "form"
    },
    "form-actions": {
      component: "ButtonGroup",
      importPath: "@/components/core/molecules/ButtonGroup",
      category: "form"
    },
    "filter-group": {
      component: "FilterGroup",
      importPath: "@/components/core/molecules/FilterGroup",
      category: "filter"
    },
    "search-input": {
      component: "SearchInput",
      importPath: "@/components/core/molecules/SearchInput",
      category: "filter"
    },
    "empty-state": {
      component: "EmptyState",
      importPath: "@/components/core/molecules/EmptyState",
      category: "state"
    },
    "loading-state": {
      component: "LoadingState",
      importPath: "@/components/core/molecules/LoadingState",
      category: "state"
    },
    "error-state": {
      component: "ErrorState",
      importPath: "@/components/core/molecules/ErrorState",
      category: "state"
    },
    notification: {
      component: "Toast",
      importPath: "@/components/core/molecules/Toast",
      category: "state"
    },
    tabs: {
      component: "Tabs",
      importPath: "@/components/core/molecules/Tabs",
      category: "navigation"
    },
    breadcrumb: {
      component: "Breadcrumb",
      importPath: "@/components/core/molecules/Breadcrumb",
      category: "navigation"
    },
    "wizard-container": {
      component: "WizardContainer",
      importPath: "@/components/core/molecules/WizardContainer",
      category: "navigation"
    },
    "wizard-progress": {
      component: "WizardProgress",
      importPath: "@/components/core/molecules/WizardProgress",
      category: "navigation"
    },
    "wizard-navigation": {
      component: "WizardNavigation",
      importPath: "@/components/core/molecules/WizardNavigation",
      category: "navigation"
    },
    pagination: {
      component: "Pagination",
      importPath: "@/components/core/molecules/Pagination",
      category: "navigation"
    },
    card: {
      component: "Card",
      importPath: "@/components/core/atoms/Card",
      category: "display"
    },
    modal: {
      component: "Modal",
      importPath: "@/components/core/molecules/Modal",
      category: "layout"
    },
    drawer: {
      component: "Drawer",
      importPath: "@/components/core/molecules/Drawer",
      category: "layout"
    },
    "confirm-dialog": {
      component: "ConfirmDialog",
      importPath: "@/components/core/molecules/ConfirmDialog",
      category: "layout"
    },
    "runtime-debugger": {
      component: "RuntimeDebugger",
      importPath: "@/components/core/organisms/RuntimeDebugger",
      category: "debug"
    },
    "repeatable-form-section": {
      component: "RepeatableFormSection",
      importPath: "@/components/core/molecules/RepeatableFormSection",
      category: "form"
    },
    box: {
      component: "Box",
      importPath: "@/components/core/atoms/Box",
      category: "layout"
    },
    grid: {
      component: "Grid",
      importPath: "@/components/core/molecules/Grid",
      category: "layout"
    },
    center: {
      component: "Center",
      importPath: "@/components/core/atoms/Center",
      category: "layout"
    },
    spacer: {
      component: "Spacer",
      importPath: "@/components/core/atoms/Spacer",
      category: "layout"
    },
    divider: {
      component: "Divider",
      importPath: "@/components/core/atoms/Divider",
      category: "layout"
    },
    button: {
      component: "Button",
      importPath: "@/components/core/atoms/Button",
      category: "atom"
    },
    badge: {
      component: "Badge",
      importPath: "@/components/core/atoms/Badge",
      category: "atom"
    },
    avatar: {
      component: "Avatar",
      importPath: "@/components/core/atoms/Avatar",
      category: "atom"
    },
    icon: {
      component: "Icon",
      importPath: "@/components/core/atoms/Icon",
      category: "atom"
    },
    spinner: {
      component: "Spinner",
      importPath: "@/components/core/atoms/Spinner",
      category: "atom"
    },
    input: {
      component: "Input",
      importPath: "@/components/core/atoms/Input",
      category: "form"
    },
    textarea: {
      component: "Textarea",
      importPath: "@/components/core/atoms/Textarea",
      category: "form"
    },
    select: {
      component: "Select",
      importPath: "@/components/core/atoms/Select",
      category: "form"
    },
    checkbox: {
      component: "Checkbox",
      importPath: "@/components/core/atoms/Checkbox",
      category: "form"
    },
    radio: {
      component: "Radio",
      importPath: "@/components/core/atoms/Radio",
      category: "form"
    },
    switch: {
      component: "Switch",
      importPath: "@/components/core/atoms/Switch",
      category: "form"
    },
    label: {
      component: "Label",
      importPath: "@/components/core/atoms/Label",
      category: "form"
    },
    alert: {
      component: "Alert",
      importPath: "@/components/core/molecules/Alert",
      category: "state"
    },
    accordion: {
      component: "Accordion",
      importPath: "@/components/core/molecules/Accordion",
      category: "display"
    },
    tooltip: {
      component: "Tooltip",
      importPath: "@/components/core/molecules/Tooltip",
      category: "atom"
    },
    popover: {
      component: "Popover",
      importPath: "@/components/core/molecules/Popover",
      category: "atom"
    },
    menu: {
      component: "Menu",
      importPath: "@/components/core/molecules/Menu",
      category: "navigation"
    },
    "progress-bar": {
      component: "ProgressBar",
      importPath: "@/components/core/atoms/ProgressBar",
      category: "display"
    },
    container: {
      component: "Container",
      importPath: "@/components/core/molecules/Container",
      category: "layout"
    },
    "simple-grid": {
      component: "SimpleGrid",
      importPath: "@/components/core/molecules/SimpleGrid",
      category: "layout"
    },
    "studio-home-web": {
      component: "StudioHomeWebTemplate",
      importPath: "@/clients/builder/templates/StudioHomeWebTemplate",
      category: "template",
      client: "builder"
    },
    "studio-home-electron": {
      component: "StudioHomeElectronTemplate",
      importPath: "@/clients/builder/templates/StudioHomeElectronTemplate",
      category: "template",
      client: "builder"
    },
    "studio-project": {
      component: "StudioProjectTemplate",
      importPath: "@/clients/builder/templates/StudioProjectTemplate",
      category: "template",
      client: "builder"
    },
    "hex-battlefield": {
      component: "BattlefieldTemplate",
      importPath: "@/clients/trait-wars/templates/BattlefieldTemplate",
      category: "template",
      client: "trait-wars"
    },
    "hero-selection": {
      component: "HexTraitWarsGame",
      importPath: "@/clients/trait-wars/templates/HexTraitWarsGame",
      category: "template",
      client: "trait-wars"
    },
    "hero-profile": {
      component: "HeroProfileTemplate",
      importPath: "@/clients/trait-wars/templates/HeroProfileTemplate",
      category: "template",
      client: "trait-wars"
    },
    "army-builder": {
      component: "ArmyBuilderTemplate",
      importPath: "@/clients/trait-wars/templates/ArmyBuilderTemplate",
      category: "template",
      client: "trait-wars"
    },
    "conditional-wrapper": {
      component: "ConditionalWrapper",
      importPath: "@/components/core/atoms/ConditionalWrapper",
      category: "component"
    },
    "law-reference-tooltip": {
      component: "LawReferenceTooltip",
      importPath: "@/components/core/atoms/LawReferenceTooltip",
      category: "component"
    },
    overlay: {
      component: "Overlay",
      importPath: "@/components/core/atoms/Overlay",
      category: "container"
    },
    stack: {
      component: "Stack",
      importPath: "@/components/core/atoms/Stack",
      category: "component"
    },
    vstack: {
      component: "VStack",
      importPath: "@/components/core/atoms/Stack",
      category: "component"
    },
    hstack: {
      component: "HStack",
      importPath: "@/components/core/atoms/Stack",
      category: "component"
    },
    "text-highlight": {
      component: "TextHighlight",
      importPath: "@/components/core/atoms/TextHighlight",
      category: "component"
    },
    "theme-toggle": {
      component: "ThemeToggle",
      importPath: "@/components/core/atoms/ThemeToggle",
      category: "component"
    },
    typography: {
      component: "Typography",
      importPath: "@/components/core/atoms/Typography",
      category: "component"
    },
    flex: {
      component: "Flex",
      importPath: "@/components/core/molecules/Flex",
      category: "component"
    },
    "floating-action-button": {
      component: "FloatingActionButton",
      importPath: "@/components/core/molecules/FloatingActionButton",
      category: "component"
    },
    "form-field": {
      component: "FormField",
      importPath: "@/components/core/molecules/FormField",
      category: "form"
    },
    "form-section-header": {
      component: "FormSectionHeader",
      importPath: "@/components/core/molecules/FormSectionHeader",
      category: "form"
    },
    "input-group": {
      component: "InputGroup",
      importPath: "@/components/core/molecules/InputGroup",
      category: "form"
    },
    "relation-select": {
      component: "RelationSelect",
      importPath: "@/components/core/molecules/RelationSelect",
      category: "form"
    },
    "side-panel": {
      component: "SidePanel",
      importPath: "@/components/core/molecules/SidePanel",
      category: "component"
    },
    "violation-alert": {
      component: "ViolationAlert",
      importPath: "@/components/core/molecules/ViolationAlert",
      category: "state"
    },
    "stat-display": {
      component: "StatDisplay",
      importPath: "@/components/core/molecules/StatDisplay",
      category: "display"
    },
    "drawer-slot": {
      component: "DrawerSlot",
      importPath: "@/components/core/organisms/DrawerSlot",
      category: "layout"
    },
    header: {
      component: "Header",
      importPath: "@/components/core/molecules/Header",
      category: "header"
    },
    "modal-slot": {
      component: "ModalSlot",
      importPath: "@/components/core/organisms/ModalSlot",
      category: "layout"
    },
    navigation: {
      component: "Navigation",
      importPath: "@/components/core/molecules/Navigation",
      category: "navigation"
    },
    "orbital-visualization": {
      component: "OrbitalVisualization",
      importPath: "@/components/core/molecules/OrbitalVisualization",
      category: "display"
    },
    section: {
      component: "Section",
      importPath: "@/components/core/molecules/Section",
      category: "display"
    },
    sidebar: {
      component: "Sidebar",
      importPath: "@/components/core/molecules/Sidebar",
      category: "navigation"
    },
    split: {
      component: "Split",
      importPath: "@/components/core/molecules/Split",
      category: "layout"
    },
    "toast-slot": {
      component: "ToastSlot",
      importPath: "@/components/core/organisms/ToastSlot",
      category: "layout"
    },
    "auth-layout": {
      component: "AuthLayout",
      importPath: "@/components/core/templates/AuthLayout",
      category: "template"
    },
    "counter-template": {
      component: "CounterTemplate",
      importPath: "@/components/core/templates/CounterTemplate",
      category: "template"
    },
    "dashboard-layout": {
      component: "DashboardLayout",
      importPath: "@/components/core/templates/DashboardLayout",
      category: "template"
    },
    "generic-app-template": {
      component: "GenericAppTemplate",
      importPath: "@/components/core/templates/GenericAppTemplate",
      category: "template"
    },
    "game-shell": {
      component: "GameShell",
      importPath: "@/components/game/templates/GameShell",
      category: "game"
    },
    chart: {
      component: "Chart",
      importPath: "@/components/core/molecules/Chart",
      category: "visualization"
    },
    meter: {
      component: "Meter",
      importPath: "@/components/core/molecules/Meter",
      category: "visualization"
    },
    timeline: {
      component: "Timeline",
      importPath: "@/components/core/organisms/Timeline",
      category: "display"
    },
    "media-gallery": {
      component: "MediaGallery",
      importPath: "@/components/core/organisms/MediaGallery",
      category: "media"
    },
    "signature-pad": {
      component: "SignaturePad",
      importPath: "@/components/core/molecules/SignaturePad",
      category: "form"
    },
    "document-viewer": {
      component: "DocumentViewer",
      importPath: "@/components/core/molecules/DocumentViewer",
      category: "display"
    },
    "graph-canvas": {
      component: "GraphCanvas",
      importPath: "@/components/core/molecules/GraphCanvas",
      category: "visualization"
    },
    "error-boundary": {
      component: "ErrorBoundary",
      importPath: "@/components/core/molecules/ErrorBoundary",
      category: "state"
    },
    skeleton: {
      component: "Skeleton",
      importPath: "@/components/core/molecules/Skeleton",
      category: "component"
    },
    "quiz-block": {
      component: "QuizBlock",
      importPath: "@/components/core/molecules/QuizBlock",
      category: "component"
    },
    "code-block": {
      component: "CodeBlock",
      importPath: "@/components/core/molecules/CodeBlock",
      category: "component"
    },
    "markdown-content": {
      component: "MarkdownContent",
      importPath: "@/components/core/molecules/MarkdownContent",
      category: "component"
    },
    "content-renderer": {
      component: "ContentRenderer",
      importPath: "@/components/core/molecules/ContentRenderer",
      category: "display"
    },
    "jazari-state-machine": {
      component: "JazariStateMachine",
      importPath: "@/components/core/molecules/JazariStateMachine",
      category: "display"
    },
    "state-machine-view": {
      component: "StateMachineView",
      importPath: "@/components/core/organisms/StateMachineView",
      category: "display"
    },
    "scaled-diagram": {
      component: "ScaledDiagram",
      importPath: "@/components/core/molecules/ScaledDiagram",
      category: "component"
    },
    "day-cell": {
      component: "DayCell",
      importPath: "@/components/core/atoms/DayCell",
      category: "component"
    },
    "flip-container": {
      component: "FlipContainer",
      importPath: "@/components/core/atoms/FlipContainer",
      category: "layout"
    },
    "time-slot-cell": {
      component: "TimeSlotCell",
      importPath: "@/components/core/atoms/TimeSlotCell",
      category: "layout"
    },
    "calendar-grid": {
      component: "CalendarGrid",
      importPath: "@/components/core/molecules/CalendarGrid",
      category: "display"
    },
    "chart-legend": {
      component: "ChartLegend",
      importPath: "@/components/core/molecules/ChartLegend",
      category: "display"
    },
    "date-range-selector": {
      component: "DateRangeSelector",
      importPath: "@/components/core/molecules/DateRangeSelector",
      category: "form"
    },
    "flip-card": {
      component: "FlipCard",
      importPath: "@/components/core/molecules/FlipCard",
      category: "display"
    },
    "graph-view": {
      component: "GraphView",
      importPath: "@/components/core/molecules/GraphView",
      category: "component"
    },
    "data-grid": {
      component: "DataGrid",
      importPath: "@/components/core/molecules/DataGrid",
      category: "display"
    },
    "data-list": {
      component: "DataList",
      importPath: "@/components/core/molecules/DataList",
      category: "display"
    },
    "line-chart": {
      component: "LineChart",
      importPath: "@/components/core/molecules/LineChart",
      category: "display"
    },
    "progress-dots": {
      component: "ProgressDots",
      importPath: "@/components/core/molecules/ProgressDots",
      category: "component"
    },
    "map-view": {
      component: "MapView",
      importPath: "@/components/core/molecules/MapView",
      category: "component"
    },
    "range-slider": {
      component: "RangeSlider",
      importPath: "@/components/core/atoms/RangeSlider",
      category: "component"
    },
    "status-dot": {
      component: "StatusDot",
      importPath: "@/components/core/atoms/StatusDot",
      category: "display"
    },
    "trend-indicator": {
      component: "TrendIndicator",
      importPath: "@/components/core/atoms/TrendIndicator",
      category: "component"
    },
    lightbox: {
      component: "Lightbox",
      importPath: "@/components/core/molecules/Lightbox",
      category: "component"
    },
    "number-stepper": {
      component: "NumberStepper",
      importPath: "@/components/core/molecules/NumberStepper",
      category: "component"
    },
    "star-rating": {
      component: "StarRating",
      importPath: "@/components/core/molecules/StarRating",
      category: "component"
    },
    "upload-drop-zone": {
      component: "UploadDropZone",
      importPath: "@/components/core/molecules/UploadDropZone",
      category: "component"
    },
    "animated-counter": {
      component: "AnimatedCounter",
      importPath: "@/components/core/molecules/AnimatedCounter",
      category: "component"
    },
    "confetti-effect": {
      component: "ConfettiEffect",
      importPath: "@/components/core/atoms/ConfettiEffect",
      category: "component"
    },
    "infinite-scroll-sentinel": {
      component: "InfiniteScrollSentinel",
      importPath: "@/components/core/atoms/InfiniteScrollSentinel",
      category: "component"
    },
    "typewriter-text": {
      component: "TypewriterText",
      importPath: "@/components/core/atoms/TypewriterText",
      category: "component"
    },
    carousel: {
      component: "Carousel",
      importPath: "@/components/core/molecules/Carousel",
      category: "component"
    },
    "pull-to-refresh": {
      component: "PullToRefresh",
      importPath: "@/components/core/molecules/PullToRefresh",
      category: "component"
    },
    "sortable-list": {
      component: "SortableList",
      importPath: "@/components/core/molecules/SortableList",
      category: "display"
    },
    "swipeable-row": {
      component: "SwipeableRow",
      importPath: "@/components/core/molecules/SwipeableRow",
      category: "component"
    },
    "section-header": {
      component: "SectionHeader",
      importPath: "@/components/core/atoms/SectionHeader",
      category: "header"
    },
    "feature-card": {
      component: "FeatureCard",
      importPath: "@/components/core/molecules/FeatureCard",
      category: "display"
    },
    "feature-grid": {
      component: "FeatureGrid",
      importPath: "@/components/core/molecules/FeatureGrid",
      category: "display"
    },
    "social-proof": {
      component: "SocialProof",
      importPath: "@/components/core/molecules/SocialProof",
      category: "component"
    },
    "animated-graphic": {
      component: "AnimatedGraphic",
      importPath: "@/components/core/atoms/AnimatedGraphic",
      category: "component"
    },
    "animated-reveal": {
      component: "AnimatedReveal",
      importPath: "@/components/core/atoms/AnimatedReveal",
      category: "component"
    },
    "pattern-tile": {
      component: "PatternTile",
      importPath: "@/components/core/atoms/PatternTile",
      category: "game"
    },
    "edge-decoration": {
      component: "EdgeDecoration",
      importPath: "@/components/core/molecules/EdgeDecoration",
      category: "component"
    },
    "ui-slot-renderer": {
      component: "UISlotRenderer",
      importPath: "@/components/core/organisms/UISlotRenderer",
      category: "layout"
    },
    "trait-frame": {
      component: "TraitFrame",
      importPath: "@/components/core/atoms/TraitFrame",
      category: "component"
    },
    "file-tree": {
      component: "FileTree",
      importPath: "@/components/core/molecules/FileTree",
      category: "component"
    },
    "filter-pill": {
      component: "FilterPill",
      importPath: "@/components/core/atoms/FilterPill",
      category: "filter"
    },
    "likert-scale": {
      component: "LikertScale",
      importPath: "@/components/core/molecules/LikertScale",
      category: "component"
    },
    "matrix-question": {
      component: "MatrixQuestion",
      importPath: "@/components/core/molecules/MatrixQuestion",
      category: "component"
    },
    "qr-scanner": {
      component: "QrScanner",
      importPath: "@/components/core/molecules/QrScanner",
      category: "component"
    },
    "vote-stack": {
      component: "VoteStack",
      importPath: "@/components/core/molecules/VoteStack",
      category: "component"
    },
    "branching-logic-builder": {
      component: "BranchingLogicBuilder",
      importPath: "@/components/core/molecules/BranchingLogicBuilder",
      category: "display"
    },
    "reply-tree": {
      component: "ReplyTree",
      importPath: "@/components/core/molecules/ReplyTree",
      category: "display"
    },
    "version-diff": {
      component: "VersionDiff",
      importPath: "@/components/core/molecules/VersionDiff",
      category: "display"
    },
    "option-constraint-group": {
      component: "OptionConstraintGroup",
      importPath: "@/components/core/molecules/OptionConstraintGroup",
      category: "component"
    },
    "positioned-canvas": {
      component: "PositionedCanvas",
      importPath: "@/components/core/molecules/PositionedCanvas",
      category: "game"
    },
    "choice-button": {
      component: "ChoiceButton",
      importPath: "@/components/game/atoms/ChoiceButton",
      category: "game"
    },
    "control-button": {
      component: "ControlButton",
      importPath: "@/components/game/atoms/ControlButton",
      category: "game"
    },
    "dialogue-bubble": {
      component: "DialogueBubble",
      importPath: "@/components/game/atoms/DialogueBubble",
      category: "game"
    },
    "health-bar": {
      component: "HealthBar",
      importPath: "@/components/game/atoms/HealthBar",
      category: "game"
    },
    "score-display": {
      component: "ScoreDisplay",
      importPath: "@/components/game/atoms/ScoreDisplay",
      category: "game"
    },
    "timer-display": {
      component: "TimerDisplay",
      importPath: "@/components/game/atoms/TimerDisplay",
      category: "game"
    },
    "svg-branch": {
      component: "SvgBranch",
      importPath: "@/components/core/atoms/SvgBranch",
      category: "component"
    },
    "svg-connection": {
      component: "SvgConnection",
      importPath: "@/components/core/atoms/SvgConnection",
      category: "component"
    },
    "svg-flow": {
      component: "SvgFlow",
      importPath: "@/components/core/atoms/SvgFlow",
      category: "component"
    },
    "svg-grid": {
      component: "SvgGrid",
      importPath: "@/components/core/atoms/SvgGrid",
      category: "display"
    },
    "svg-lobe": {
      component: "SvgLobe",
      importPath: "@/components/core/atoms/SvgLobe",
      category: "component"
    },
    "svg-mesh": {
      component: "SvgMesh",
      importPath: "@/components/core/atoms/SvgMesh",
      category: "component"
    },
    "svg-morph": {
      component: "SvgMorph",
      importPath: "@/components/core/atoms/SvgMorph",
      category: "component"
    },
    "svg-node": {
      component: "SvgNode",
      importPath: "@/components/core/atoms/SvgNode",
      category: "component"
    },
    "svg-pulse": {
      component: "SvgPulse",
      importPath: "@/components/core/atoms/SvgPulse",
      category: "component"
    },
    "svg-ring": {
      component: "SvgRing",
      importPath: "@/components/core/atoms/SvgRing",
      category: "component"
    },
    "svg-shield": {
      component: "SvgShield",
      importPath: "@/components/core/atoms/SvgShield",
      category: "component"
    },
    "svg-stack": {
      component: "SvgStack",
      importPath: "@/components/core/atoms/SvgStack",
      category: "component"
    },
    "doc-breadcrumb": {
      component: "DocBreadcrumb",
      importPath: "@/components/core/molecules/DocBreadcrumb",
      category: "navigation"
    },
    "doc-pagination": {
      component: "DocPagination",
      importPath: "@/components/core/molecules/DocPagination",
      category: "navigation"
    },
    "doc-search": {
      component: "DocSearch",
      importPath: "@/components/core/molecules/DocSearch",
      category: "filter"
    },
    "doc-sidebar": {
      component: "DocSidebar",
      importPath: "@/components/core/molecules/DocSidebar",
      category: "navigation"
    },
    "doc-toc": {
      component: "DocTOC",
      importPath: "@/components/core/molecules/DocTOC",
      category: "component"
    },
    "gradient-divider": {
      component: "GradientDivider",
      importPath: "@/components/core/molecules/GradientDivider",
      category: "component"
    },
    "behavior-view": {
      component: "BehaviorView",
      importPath: "@/components/avl/molecules/BehaviorView",
      category: "component"
    },
    "module-card": {
      component: "ModuleCard",
      importPath: "@/components/avl/molecules/ModuleCard",
      category: "display"
    },
    "game-hud": {
      component: "GameHud",
      importPath: "@/components/game/molecules/GameHud",
      category: "game"
    },
    "game-menu": {
      component: "GameMenu",
      importPath: "@/components/game/molecules/GameMenu",
      category: "game"
    },
    "stat-badge": {
      component: "StatBadge",
      importPath: "@/components/game/molecules/StatBadge",
      category: "game"
    },
    "game-audio-toggle": {
      component: "GameAudioToggle",
      importPath: "@/components/game/atoms/GameAudioToggle",
      category: "game"
    },
    "trait-slot": {
      component: "TraitSlot",
      importPath: "@/components/game/molecules/TraitSlot",
      category: "game"
    },
    "action-palette": {
      component: "ActionPalette",
      importPath: "@/components/game/molecules/ActionPalette",
      category: "game"
    },
    "action-tile": {
      component: "ActionTile",
      importPath: "@/components/game/molecules/ActionTile",
      category: "game"
    },
    "sequence-bar": {
      component: "SequenceBar",
      importPath: "@/components/game/molecules/SequenceBar",
      category: "game"
    },
    "dashboard-grid": {
      component: "DashboardGrid",
      importPath: "@/components/core/organisms/DashboardGrid",
      category: "game"
    },
    "split-pane": {
      component: "SplitPane",
      importPath: "@/components/core/organisms/SplitPane",
      category: "layout"
    },
    "tabbed-container": {
      component: "TabbedContainer",
      importPath: "@/components/core/organisms/TabbedContainer",
      category: "navigation"
    },
    "master-detail-layout": {
      component: "MasterDetailLayout",
      importPath: "@/components/core/organisms/MasterDetailLayout",
      category: "display"
    },
    sparkline: {
      component: "Sparkline",
      importPath: "@/components/core/atoms/Sparkline",
      category: "component"
    },
    "date-range-picker": {
      component: "DateRangePicker",
      importPath: "@/components/core/molecules/DateRangePicker",
      category: "component"
    },
    aside: {
      component: "Aside",
      importPath: "@/components/core/atoms/Aside",
      category: "component"
    },
    dialog: {
      component: "Dialog",
      importPath: "@/components/core/atoms/Dialog",
      category: "container"
    },
    "tag-input": {
      component: "TagInput",
      importPath: "@/components/core/molecules/TagInput",
      category: "form"
    },
    "table-view": {
      component: "TableView",
      importPath: "@/components/core/molecules/TableView",
      category: "display"
    },
    "stat-card": {
      component: "StatCard",
      importPath: "@/components/core/organisms/StatCard",
      category: "display"
    },
    "activation-block": {
      component: "ActivationBlock",
      importPath: "@/components/core/molecules/ActivationBlock",
      category: "component"
    },
    "bloom-quiz-block": {
      component: "BloomQuizBlock",
      importPath: "@/components/core/molecules/BloomQuizBlock",
      category: "component"
    },
    "connection-block": {
      component: "ConnectionBlock",
      importPath: "@/components/core/molecules/ConnectionBlock",
      category: "component"
    },
    "reflection-block": {
      component: "ReflectionBlock",
      importPath: "@/components/core/molecules/ReflectionBlock",
      category: "component"
    },
    "code-runner-panel": {
      component: "CodeRunnerPanel",
      importPath: "@/components/core/organisms/CodeRunnerPanel",
      category: "display"
    },
    "segment-renderer": {
      component: "SegmentRenderer",
      importPath: "@/components/core/organisms/SegmentRenderer",
      category: "display"
    },
    "chat-bar": {
      component: "ChatBar",
      importPath: "@/components/core/organisms/ChatBar",
      category: "display"
    },
    "subagent-trace-panel": {
      component: "SubagentTracePanel",
      importPath: "@/components/core/organisms/SubagentTracePanel",
      category: "display"
    },
    "canvas-2d": {
      component: "Canvas2D",
      importPath: "@/components/game/molecules/Canvas2D",
      category: "game"
    },
    "control-grid": {
      component: "ControlGrid",
      importPath: "@/components/game/molecules/ControlGrid",
      category: "game"
    },
    "game-icon": {
      component: "GameIcon",
      importPath: "@/components/core/atoms/GameIcon",
      category: "game"
    },
    "form-layout": {
      component: "FormLayout",
      importPath: "@/components/core/molecules/FormSection",
      category: "form"
    },
    "learning-canvas": {
      component: "LearningCanvas",
      importPath: "@/components/learning/atoms/LearningCanvas",
      category: "learning"
    },
    "biology-canvas": {
      component: "BiologyCanvas",
      importPath: "@/components/learning/molecules/BiologyCanvas",
      category: "learning"
    },
    "chemistry-canvas": {
      component: "ChemistryCanvas",
      importPath: "@/components/learning/molecules/ChemistryCanvas",
      category: "learning"
    },
    "math-canvas": {
      component: "MathCanvas",
      importPath: "@/components/learning/molecules/MathCanvas",
      category: "learning"
    },
    "physics-canvas": {
      component: "PhysicsCanvas",
      importPath: "@/components/learning/molecules/PhysicsCanvas",
      category: "learning"
    },
    "state-graph": {
      component: "StateGraph",
      importPath: "@/components/game/molecules/StateGraph",
      category: "game"
    },
    "state-json-view": {
      component: "StateJsonView",
      importPath: "@/components/game/molecules/StateJsonView",
      category: "game"
    },
    "algorithm-canvas": {
      component: "AlgorithmCanvas",
      importPath: "@/components/learning/molecules/AlgorithmCanvas",
      category: "learning"
    },
    "atlas-image": {
      component: "AtlasImage",
      importPath: "@/components/core/atoms/AtlasImage",
      category: "component"
    },
    "atlas-panel": {
      component: "AtlasPanel",
      importPath: "@/components/core/atoms/AtlasImage",
      category: "component"
    },
    "draw-shape": {
      component: "DrawShape",
      importPath: "@/components/game/atoms/DrawShape",
      category: "game"
    },
    "draw-sprite": {
      component: "DrawSprite",
      importPath: "@/components/game/atoms/DrawSprite",
      category: "game"
    },
    "draw-skinned-mesh": {
      component: "DrawSkinnedMesh",
      importPath: "@/components/game/atoms/DrawSkinnedMesh",
      category: "game"
    },
    "draw-text": {
      component: "DrawText",
      importPath: "@/components/game/atoms/DrawText",
      category: "game"
    },
    "draw-shape-layer": {
      component: "DrawShapeLayer",
      importPath: "@/components/game/molecules/DrawShapeLayer",
      category: "game"
    },
    "draw-sprite-layer": {
      component: "DrawSpriteLayer",
      importPath: "@/components/game/molecules/DrawSpriteLayer",
      category: "game"
    },
    "draw-text-layer": {
      component: "DrawTextLayer",
      importPath: "@/components/game/molecules/DrawTextLayer",
      category: "game"
    },
    canvas: {
      component: "Canvas",
      importPath: "@/components/game/molecules/Canvas",
      category: "game"
    },
    presence: {
      component: "Presence",
      importPath: "@/components/core/atoms/Presence",
      category: "component"
    },
    "page-transition": {
      component: "PageTransition",
      importPath: "@/components/core/molecules/PageTransition",
      category: "component"
    },
    "import-preview-tree": {
      component: "ImportPreviewTree",
      importPath: "@/components/core/molecules/ImportPreviewTree",
      category: "component"
    },
    "import-progress": {
      component: "ImportProgress",
      importPath: "@/components/core/molecules/ImportProgress",
      category: "component"
    },
    "import-source-picker": {
      component: "ImportSourcePicker",
      importPath: "@/components/core/molecules/ImportSourcePicker",
      category: "component"
    },
    "draw-group": {
      component: "DrawGroup",
      importPath: "@/components/game/atoms/DrawGroup",
      category: "game"
    },
    "emoji-picker": {
      component: "EmojiPicker",
      importPath: "@/components/core/molecules/EmojiPicker",
      category: "component"
    },
    "draw-mesh": {
      component: "DrawMesh",
      importPath: "@/components/game/atoms/DrawMesh",
      category: "game"
    },
    "algo-graph-canvas": {
      component: "AlgoGraphCanvas",
      importPath: "@/components/learning/molecules/AlgoGraphCanvas",
      category: "learning"
    },
    "fx-overlay": {
      component: "FxOverlay",
      importPath: "@/components/core/molecules/FxOverlay",
      category: "container"
    },
    "draw-fx-layer": {
      component: "DrawFxLayer",
      importPath: "@/components/game/molecules/DrawFxLayer",
      category: "game"
    },
    "rich-text-editor": {
      component: "RichTextEditor",
      importPath: "@/components/core/molecules/RichTextEditor",
      category: "component"
    },
    "document-panel": {
      component: "DocumentPanel",
      importPath: "@/components/core/molecules/DocumentPanel",
      category: "component"
    },
    "document-details": {
      component: "DocumentDetails",
      importPath: "@/components/core/molecules/DocumentDetails",
      category: "display"
    },
    "floating-toolbar": {
      component: "FloatingToolbar",
      importPath: "@/components/core/molecules/FloatingToolbar",
      category: "component"
    },
    "dock-layout": {
      component: "DockLayout",
      importPath: "@/components/core/organisms/DockLayout",
      category: "layout"
    },
    "command-palette": {
      component: "CommandPalette",
      importPath: "@/components/core/molecules/CommandPalette",
      category: "component"
    },
    "game-audio-cue": {
      component: "GameAudioCue",
      importPath: "@/components/game/atoms/GameAudioCue",
      category: "game"
    },
    "ui-slot-component": {
      component: "UISlotComponent",
      importPath: "@/components/core/organisms/UISlotRenderer",
      category: "layout"
    },
    gantt: {
      component: "Gantt",
      importPath: "@/components/core/molecules/Gantt",
      category: "component"
    },
    image: {
      component: "Image",
      importPath: "@/components/core/atoms/Image",
      category: "component"
    },
    "geometric-pattern": {
      component: "GeometricPattern",
      importPath: "@/components/core/molecules/GeometricPattern",
      category: "component"
    },
    "icon-button": {
      component: "IconButton",
      importPath: "@/components/core/molecules/IconButton",
      category: "component"
    },
    "top-nav-item": {
      component: "TopNavItem",
      importPath: "@/components/core/molecules/TopNavItem",
      category: "navigation"
    },
    "change-list": {
      component: "ChangeList",
      importPath: "@/components/core/molecules/ChangeList",
      category: "display"
    },
    "commit-row": {
      component: "CommitRow",
      importPath: "@/components/core/molecules/CommitRow",
      category: "component"
    },
    "avl-state-machine": {
      component: "AvlStateMachine",
      importPath: "@/components/avl/molecules/AvlStateMachine",
      category: "display"
    },
    "avl-glyph": {
      component: "AvlGlyph",
      importPath: "@/components/avl/molecules/AvlGlyph",
      category: "component"
    },
    "agent-activity-feed": {
      component: "AgentActivityFeed",
      importPath: "@/components/core/molecules/AgentActivityFeed",
      category: "component"
    },
    "agent-activity-row": {
      component: "AgentActivityRow",
      importPath: "@/components/core/molecules/AgentActivityFeed",
      category: "component"
    },
    "agent-chat-feed": {
      component: "AgentChatFeed",
      importPath: "@/components/core/molecules/AgentChatFeed",
      category: "component"
    },
    "avl-transition-explainer": {
      component: "AvlTransitionExplainer",
      importPath: "@/components/avl/molecules/AvlTransitionExplainer",
      category: "component"
    },
    "flow-canvas": {
      component: "FlowCanvas",
      importPath: "@/components/avl/organisms/FlowCanvas",
      category: "display"
    },
    "layers-panel": {
      component: "LayersPanel",
      importPath: "@/components/avl/organisms/LayersPanel",
      category: "display"
    },
    "orb-inspector": {
      component: "OrbInspector",
      importPath: "@/components/avl/organisms/OrbInspector",
      category: "debug"
    }
  }
};

// src/patterns/event-contracts.json
var event_contracts_default = {
  version: "1.0.0",
  exportedAt: "2026-10-10T01:07:16.949Z",
  contracts: {
    form: {
      emits: [
        {
          event: "GLOBAL_VARIABLE_SET",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VIOLATION_DETECTED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "FIELD_CHANGED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VALIDATION_FAILED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [
        "SAVE",
        "CANCEL"
      ],
      entityAware: true
    },
    "form-section": {
      emits: [
        {
          event: "GLOBAL_VARIABLE_SET",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VIOLATION_DETECTED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "FIELD_CHANGED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VALIDATION_FAILED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [
        "SAVE",
        "CANCEL"
      ],
      entityAware: true
    },
    "form-actions": {
      emits: [
        {
          event: "DISPATCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true,
      configDriven: true
    },
    "detail-panel": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "page-header": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    "filter-group": {
      emits: [
        {
          event: "FILTER",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLEAR_FILTERS",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true,
      configDriven: true
    },
    "search-input": {
      emits: [
        {
          event: "SEARCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLEAR_SEARCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "empty-state": {
      emits: [
        {
          event: "CREATE",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    "loading-state": {
      emits: [],
      requires: [],
      entityAware: false
    },
    "error-state": {
      emits: [
        {
          event: "RETRY",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    notification: {
      emits: [
        {
          event: "DISMISS",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        },
        {
          event: "ACTION",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    tabs: {
      emits: [
        {
          event: "TAB_CHANGE",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              tabId: "string"
            }
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    breadcrumb: {
      emits: [],
      requires: [],
      entityAware: false
    },
    "wizard-container": {
      emits: [
        {
          event: "WIZARD_NEXT",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_BACK",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_COMPLETE",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_STEP_CHANGE",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              step: "number"
            }
          }
        }
      ],
      requires: [
        "WIZARD_COMPLETE"
      ],
      entityAware: false
    },
    "wizard-progress": {
      emits: [
        {
          event: "WIZARD_STEP_CLICK",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              step: "number"
            }
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    "wizard-navigation": {
      emits: [
        {
          event: "WIZARD_NEXT",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_BACK",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_COMPLETE",
          trigger: "click",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    pagination: {
      emits: [
        {
          event: "PAGE_CHANGE",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              page: "number"
            }
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    modal: {
      emits: [
        {
          event: "CLOSE",
          trigger: "close",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [
        "CLOSE"
      ],
      entityAware: false
    },
    drawer: {
      emits: [
        {
          event: "CLOSE",
          trigger: "close",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [
        "CLOSE"
      ],
      entityAware: false
    },
    "confirm-dialog": {
      emits: [
        {
          event: "CONFIRM",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "CANCEL",
          trigger: "click",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [
        "CONFIRM",
        "CANCEL"
      ],
      entityAware: false
    },
    card: {
      emits: [
        {
          event: "CLICK",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    "runtime-debugger": {
      emits: [],
      requires: [],
      entityAware: false
    },
    "repeatable-form-section": {
      emits: [
        {
          event: "SECTION_ADDED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SECTION_REMOVED",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "studio-home-web": {
      emits: [
        {
          event: "CREATE_PROJECT",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "SELECT_PROJECT",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              project: "object"
            }
          }
        },
        {
          event: "DELETE_PROJECT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              project: "object"
            }
          }
        },
        {
          event: "OPEN_PROJECT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              project: "object"
            }
          }
        },
        {
          event: "SEARCH",
          trigger: "input",
          payload: {
            type: "object",
            shape: {
              query: "string"
            }
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "studio-home-electron": {
      emits: [
        {
          event: "OPEN_FILE",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "CREATE_NEW",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "OPEN_RECENT",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              file: "object"
            }
          }
        },
        {
          event: "REMOVE_RECENT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              file: "object"
            }
          }
        },
        {
          event: "CLEAR_RECENT",
          trigger: "action",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "studio-project": {
      emits: [
        {
          event: "MODE_CHANGE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              mode: "string"
            }
          }
        },
        {
          event: "TAB_CHANGE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              tab: "string"
            }
          }
        },
        {
          event: "SAVE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "VALIDATE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "COMPILE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "PREVIEW",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "BACK",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "TOGGLE_AGENT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              visible: "boolean"
            }
          }
        },
        {
          event: "TOGGLE_INSPECTOR",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              visible: "boolean"
            }
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "drawer-slot": {
      emits: [
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CANCEL",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "modal-slot": {
      emits: [
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CANCEL",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "toast-slot": {
      emits: [
        {
          event: "DISMISS",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "document-viewer": {
      emits: [
        {
          event: "DOCUMENT_DOWNLOAD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "DOCUMENT_PRINT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "DOCUMENT_PAGE_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "media-gallery": {
      emits: [
        {
          event: "MEDIA_SELECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    table: {
      emits: [
        {
          event: "SORT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SELECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "DESELECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "PAGINATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SEARCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "code-block": {
      emits: [
        {
          event: "COPY_CODE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "EDITOR_FOCUS",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "EDITOR_BLUR",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "ui-slot-renderer": {
      emits: [
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CANCEL",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "game-menu": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "trait-slot": {
      emits: [
        {
          event: "CLICK",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "REMOVE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "activation-block": {
      emits: [
        {
          event: "SAVE_ACTIVATION",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "bloom-quiz-block": {
      emits: [
        {
          event: "ANSWER_BLOOM",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "reflection-block": {
      emits: [
        {
          event: "SAVE_REFLECTION",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "code-runner-panel": {
      emits: [
        {
          event: "COPY_CODE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "chat-bar": {
      emits: [
        {
          event: "CHAT_SEND",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "form-layout": {
      emits: [
        {
          event: "TOGGLE_COLLAPSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "data-grid": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "dashboard-layout": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "data-list": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "flow-canvas": {
      emits: [
        {
          event: "WIRE_VALIDATION_WARNING",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "orb-inspector": {
      emits: [
        {
          event: "NOTIFY",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "PROP_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "ADD_FIELD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "UPDATE_FIELD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "REMOVE_FIELD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "GUARD_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "ADD_EFFECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "REMOVE_EFFECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CODE_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SERVICE_MODE_TOGGLE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    }
  }
};

// src/patterns/pattern-types.ts
var PATTERN_TYPES = [
  "accordion",
  "action-palette",
  "action-tile",
  "activation-block",
  "agent-activity-feed",
  "agent-activity-row",
  "agent-chat-feed",
  "alert",
  "algo-graph-canvas",
  "algorithm-canvas",
  "animated-counter",
  "animated-graphic",
  "animated-reveal",
  "aside",
  "atlas-image",
  "atlas-panel",
  "auth-layout",
  "avatar",
  "avl-glyph",
  "avl-state-machine",
  "avl-transition-explainer",
  "badge",
  "behavior-view",
  "biology-canvas",
  "bloom-quiz-block",
  "box",
  "branching-logic-builder",
  "breadcrumb",
  "button",
  "calendar-grid",
  "canvas",
  "canvas-2d",
  "card",
  "carousel",
  "center",
  "change-list",
  "chart",
  "chart-legend",
  "chat-bar",
  "checkbox",
  "chemistry-canvas",
  "choice-button",
  "code-block",
  "code-runner-panel",
  "command-palette",
  "commit-row",
  "conditional-wrapper",
  "confetti-effect",
  "confirm-dialog",
  "connection-block",
  "container",
  "content-renderer",
  "control-button",
  "control-grid",
  "counter-template",
  "dashboard-grid",
  "dashboard-layout",
  "data-grid",
  "data-list",
  "date-range-picker",
  "date-range-selector",
  "day-cell",
  "detail-panel",
  "dialog",
  "dialogue-bubble",
  "divider",
  "doc-breadcrumb",
  "doc-pagination",
  "doc-search",
  "doc-sidebar",
  "doc-toc",
  "dock-layout",
  "document-details",
  "document-panel",
  "document-viewer",
  "draw-fx-layer",
  "draw-group",
  "draw-mesh",
  "draw-shape",
  "draw-shape-layer",
  "draw-skinned-mesh",
  "draw-sprite",
  "draw-sprite-layer",
  "draw-text",
  "draw-text-layer",
  "drawer",
  "drawer-slot",
  "edge-decoration",
  "emoji-picker",
  "empty-state",
  "error-boundary",
  "error-state",
  "feature-card",
  "feature-grid",
  "file-tree",
  "filter-group",
  "filter-pill",
  "flex",
  "flip-card",
  "flip-container",
  "floating-action-button",
  "floating-toolbar",
  "flow-canvas",
  "form",
  "form-actions",
  "form-field",
  "form-layout",
  "form-section",
  "form-section-header",
  "fx-overlay",
  "game-audio-cue",
  "game-audio-toggle",
  "game-hud",
  "game-icon",
  "game-menu",
  "game-shell",
  "gantt",
  "generic-app-template",
  "geometric-pattern",
  "gradient-divider",
  "graph-canvas",
  "graph-view",
  "grid",
  "header",
  "health-bar",
  "hstack",
  "icon",
  "icon-button",
  "image",
  "import-preview-tree",
  "import-progress",
  "import-source-picker",
  "infinite-scroll-sentinel",
  "input",
  "input-group",
  "jazari-state-machine",
  "label",
  "law-reference-tooltip",
  "layers-panel",
  "learning-canvas",
  "lightbox",
  "likert-scale",
  "line-chart",
  "loading-state",
  "map-view",
  "markdown-content",
  "master-detail-layout",
  "math-canvas",
  "matrix-question",
  "media-gallery",
  "menu",
  "meter",
  "modal",
  "modal-slot",
  "module-card",
  "navigation",
  "notification",
  "number-stepper",
  "option-constraint-group",
  "orb-inspector",
  "orbital-visualization",
  "overlay",
  "page-header",
  "page-transition",
  "pagination",
  "pattern-tile",
  "physics-canvas",
  "popover",
  "positioned-canvas",
  "presence",
  "progress-bar",
  "progress-dots",
  "pull-to-refresh",
  "qr-scanner",
  "quiz-block",
  "radio",
  "range-slider",
  "reflection-block",
  "relation-select",
  "repeatable-form-section",
  "reply-tree",
  "rich-text-editor",
  "runtime-debugger",
  "scaled-diagram",
  "score-display",
  "search-input",
  "section",
  "section-header",
  "segment-renderer",
  "select",
  "sequence-bar",
  "side-panel",
  "sidebar",
  "signature-pad",
  "simple-grid",
  "skeleton",
  "social-proof",
  "sortable-list",
  "spacer",
  "sparkline",
  "spinner",
  "split",
  "split-pane",
  "stack",
  "star-rating",
  "stat-badge",
  "stat-card",
  "stat-display",
  "state-graph",
  "state-json-view",
  "state-machine-view",
  "status-dot",
  "subagent-trace-panel",
  "svg-branch",
  "svg-connection",
  "svg-flow",
  "svg-grid",
  "svg-lobe",
  "svg-mesh",
  "svg-morph",
  "svg-node",
  "svg-pulse",
  "svg-ring",
  "svg-shield",
  "svg-stack",
  "swipeable-row",
  "switch",
  "tabbed-container",
  "table-view",
  "tabs",
  "tag-input",
  "text-highlight",
  "textarea",
  "theme-toggle",
  "time-slot-cell",
  "timeline",
  "timer-display",
  "toast-slot",
  "tooltip",
  "top-nav-item",
  "trait-frame",
  "trait-slot",
  "trend-indicator",
  "typewriter-text",
  "typography",
  "ui-slot-component",
  "ui-slot-renderer",
  "upload-drop-zone",
  "version-diff",
  "violation-alert",
  "vote-stack",
  "vstack",
  "wizard-container",
  "wizard-navigation",
  "wizard-progress"
];
function isValidPatternType(type) {
  return PATTERN_TYPES.includes(type);
}

// src/patterns/content-grade.ts
function isContentBodyPatternType(patternType) {
  const entry = patterns_registry_default.patterns?.[patternType];
  if (!entry?.propsSchema) return false;
  return Object.values(entry.propsSchema).some((def) => def.kind === "entity");
}
function isContentBodyPattern(pattern) {
  if (pattern === null) return false;
  if (typeof pattern === "string") return true;
  if ("children" in pattern && Array.isArray(pattern.children) && pattern.children.length > 0) return true;
  return isContentBodyPatternType(pattern.type);
}
function isMainSlotRenderUi(effect) {
  return Array.isArray(effect) && effect[0] === "render-ui" && effect[1] === "main";
}

// src/patterns/self-overlay.ts
var SELF_OVERLAY_PATTERN_TYPES = /* @__PURE__ */ new Set(["modal", "confirm-dialog"]);

// src/patterns/types.ts
var PROP_KINDS = [
  "event",
  "event-ref",
  "event-listen",
  "event-list",
  "event-map",
  "callback",
  "entity",
  "entry",
  "skeleton"
];

// src/patterns/helpers/prompt-helpers.ts
function getPatternsGroupedByCategory() {
  const patterns = patterns_registry_default.patterns || {};
  const grouped = {};
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const category = patternDef.category || "uncategorized";
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(patternName);
  }
  return grouped;
}
function getPatternPropsCompact() {
  const patterns = patterns_registry_default.patterns || {};
  const lines = [
    "| Pattern | Key Props |",
    "|---------|-----------|"
  ];
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const propsSchema = patternDef.propsSchema || {};
    const requiredProps = Object.keys(propsSchema).filter(
      (key) => propsSchema[key]?.required === true
    );
    const keyProps = requiredProps.length > 0 ? requiredProps.join(", ") : Object.keys(propsSchema).slice(0, 3).join(", ");
    lines.push(`| ${patternName} | ${keyProps} |`);
  }
  return lines.join("\n");
}
function getPatternActionsRef() {
  const patterns = patterns_registry_default.patterns || {};
  const lines = [
    "## Pattern Action Props",
    "",
    "| Pattern | Action Props | Notes |",
    "|---------|-------------|-------|"
  ];
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const propsSchema = patternDef.propsSchema || {};
    const actionProps = [];
    for (const [propName, propDef] of Object.entries(propsSchema)) {
      if (propName.includes("action") || propName.includes("Action") || propName.includes("event") || propName.includes("Event") || propName === "onSubmit" || propName === "onClick") {
        actionProps.push(propName);
      }
    }
    if (actionProps.length > 0) {
      const notes = patternDef.description || "";
      lines.push(`| ${patternName} | ${actionProps.join(", ")} | ${notes.slice(0, 50)} |`);
    }
  }
  return lines.join("\n");
}
function generatePatternDescription(patternType) {
  const patterns = patterns_registry_default.patterns || {};
  const patternDef = patterns[patternType];
  if (!patternDef) {
    return patternType;
  }
  const desc = patternDef.description;
  if (desc) {
    return desc;
  }
  const propsSchema = patternDef.propsSchema || {};
  const propCount = Object.keys(propsSchema).length;
  const requiredProps = Object.keys(propsSchema).filter(
    (key) => propsSchema[key]?.required === true
  );
  if (requiredProps.length > 0) {
    return `${patternType} (requires: ${requiredProps.join(", ")})`;
  }
  return `${patternType} (${propCount} props)`;
}
function getAllPatternTypes() {
  const patterns = patterns_registry_default.patterns || {};
  return Object.keys(patterns);
}
function getPatternMetadata(patternType) {
  const patterns = patterns_registry_default.patterns || {};
  return patterns[patternType] || null;
}
var ORB_ALLOWED_ENTITY_PATTERNS = /* @__PURE__ */ new Set([
  "data-list",
  "data-grid",
  "search-input",
  "form-section",
  "meter",
  "table-view",
  "filter-group",
  "timeline",
  "media-gallery"
]);
var ORB_EXCLUDED_CATEGORIES = /* @__PURE__ */ new Set([
  "debug",
  "template"
]);
function getOrbAllowedPatterns() {
  const patterns = patterns_registry_default.patterns || {};
  const grouped = {};
  for (const [name, def] of Object.entries(patterns)) {
    const cat = def.category || "uncategorized";
    if (ORB_EXCLUDED_CATEGORIES.has(cat)) continue;
    if (name.includes("3-d") || name.includes("3d")) continue;
    const hasEntity = def.propsSchema && "entity" in def.propsSchema;
    if (hasEntity && !ORB_ALLOWED_ENTITY_PATTERNS.has(name)) continue;
    const propsSchema = def.propsSchema || {};
    const keyProps = Object.keys(propsSchema).slice(0, 5);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({
      name,
      description: def.description || "",
      keyProps
    });
  }
  return grouped;
}
function getOrbAllowedPatternsCompact() {
  const grouped = getOrbAllowedPatterns();
  const lines = [];
  for (const [cat, items] of Object.entries(grouped).sort()) {
    lines.push(`#### ${cat} (${items.length})`);
    lines.push("| Pattern | Description | Key Props |");
    lines.push("|---------|-------------|-----------|");
    for (const item of items) {
      const desc = item.description.split("\n")[0].slice(0, 60);
      lines.push(`| \`${item.name}\` | ${desc} | ${item.keyProps.join(", ")} |`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
function getOrbAllowedPatternsSlim() {
  const grouped = getOrbAllowedPatterns();
  const lines = [];
  for (const [cat, items] of Object.entries(grouped).sort()) {
    lines.push(`## ${cat}`);
    for (const item of items) {
      const desc = item.description.split("\n")[0].slice(0, 80);
      lines.push(`- ${item.name}: ${desc}`);
    }
  }
  return lines.join("\n");
}
function getOrbAllowedPatternsFiltered(patternNames) {
  const nameSet = new Set(patternNames);
  const patterns = patterns_registry_default.patterns || {};
  const grouped = {};
  for (const [name, def] of Object.entries(patterns)) {
    if (!nameSet.has(name)) continue;
    const cat = def.category || "uncategorized";
    const propsSchema = def.propsSchema || {};
    const allProps = Object.keys(propsSchema);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({
      name,
      description: def.description || "",
      allProps
    });
  }
  const lines = [];
  for (const [cat, items] of Object.entries(grouped).sort()) {
    lines.push(`#### ${cat} (${items.length})`);
    lines.push("| Pattern | Description | Props |");
    lines.push("|---------|-------------|-------|");
    for (const item of items) {
      const desc = item.description.split("\n")[0].slice(0, 60);
      lines.push(`| \`${item.name}\` | ${desc} | ${item.allProps.join(", ")} |`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
var JsonValueSchema = z.lazy(
  () => z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValueSchema), z.record(JsonValueSchema)])
);
function isJsonObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function isJsonArray(value) {
  return Array.isArray(value);
}

// src/patterns/helpers/render-ui-pattern-types.ts
function collectPatternTypeTokens(node, out) {
  if (!isJsonObject(node)) return;
  const type = node["type"];
  if (typeof type === "string" && !type.startsWith("@")) out.add(type);
  const children = node["children"];
  if (isJsonArray(children)) {
    for (const child of children) collectPatternTypeTokens(child, out);
  }
}
function collectRenderUiPatternTypes(ir, out) {
  if (isJsonArray(ir)) {
    if (ir.length >= 3 && ir[0] === "render-ui" && typeof ir[1] === "string") {
      collectPatternTypeTokens(ir[2], out);
    }
    for (const item of ir) collectRenderUiPatternTypes(item, out);
  } else if (isJsonObject(ir)) {
    for (const value of Object.values(ir)) collectRenderUiPatternTypes(value, out);
  }
}
function renderUiPatternTypesOf(ir) {
  const out = /* @__PURE__ */ new Set();
  collectRenderUiPatternTypes(ir, out);
  return out;
}
function isPatternNode(value) {
  return value !== null && value !== void 0 && typeof value === "object" && !Array.isArray(value);
}
function collectRenderUiEntries(effect, out) {
  if (!Array.isArray(effect)) return;
  const head = effect[0];
  if (head === "render-ui") {
    const slot = effect[1];
    const pattern = effect[2];
    if (typeof slot === "string" && isPatternNode(pattern)) out.push({ slot, pattern });
    return;
  }
  for (const child of controlBody(effect)) collectRenderUiEntries(child, out);
}
function controlBody(effect) {
  switch (effect[0]) {
    case "do":
    case "atomic":
    case "async/all":
    case "async/sequence":
    case "async/race":
      return effect.slice(1);
    case "if":
    case "when":
    case "async/delay":
    case "async/debounce":
    case "async/throttle":
    case "async/interval":
      return effect.slice(2);
    case "let":
      return effect.slice(2);
    default:
      return [];
  }
}
function renderUiEntriesOf(effects) {
  const out = [];
  for (const effect of effects) collectRenderUiEntries(effect, out);
  return out;
}

// src/patterns/helpers/pattern-recommender.ts
var DOMAIN_KEYWORDS = {
  business: ["admin panels", "data-dense views", "list pages", "data entry", "comparisons"],
  dashboard: ["dashboards", "analytics", "overview pages", "overview screens", "data visualization", "reporting", "KPI indicators"],
  "e-commerce": ["visual items", "images", "previews", "data entry", "list pages"],
  content: ["visual items", "images", "previews", "media", "pages with multiple views"],
  social: ["activity logs", "visual items", "previews"],
  workflow: ["process tracking", "workflow steps", "multi-step forms", "wizards"],
  form: ["data entry", "create forms", "edit forms", "multi-step forms"],
  game: ["game", "real-time", "interactive"]
};
var EVENT_KEYWORDS = {
  INIT: ["list pages", "overview pages", "dashboards", "admin panels"],
  CREATE: ["create forms", "data entry", "multi-step forms"],
  EDIT: ["edit forms", "data entry"],
  VIEW: ["detail pages", "entity management", "pages with multiple views"],
  DELETE: ["destructive actions", "confirmations"],
  FILTER: ["filterable data", "search functionality", "quick filters"]
};
var SLOT_CATEGORY_AFFINITY = {
  main: ["display", "dashboard", "filter", "header", "layout", "visualization", "navigation", "template"],
  modal: ["form", "container", "state"],
  drawer: ["display", "form", "navigation"],
  sidebar: ["navigation", "filter"],
  overlay: ["container", "state"]
};
var STATE_CATEGORY_AFFINITY = {
  Browsing: ["display", "filter", "header", "dashboard", "visualization"],
  Creating: ["form", "navigation"],
  Editing: ["form", "navigation"],
  Viewing: ["display", "navigation"],
  Deleting: ["container", "state"]
};
function recommendPatterns(context, maxResults = 10) {
  const patterns = patterns_registry_default.patterns || {};
  const scores = /* @__PURE__ */ new Map();
  const domainKeywords = context.domainCategory ? DOMAIN_KEYWORDS[context.domainCategory] || [] : [];
  const eventKeywords = context.event ? EVENT_KEYWORDS[context.event] || [] : [];
  const slotCategories = context.slot ? SLOT_CATEGORY_AFFINITY[context.slot] || [] : [];
  const stateCategories = context.state ? STATE_CATEGORY_AFFINITY[context.state] || [] : [];
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const suggestedFor = patternDef.suggestedFor || [];
    const category = patternDef.category || "";
    let score = 0;
    const reasons = [];
    if (category === "game" && context.domainCategory !== "game") continue;
    if (category === "debug") continue;
    if (category === "template") continue;
    for (const keyword of domainKeywords) {
      if (suggestedFor.some((sf) => sf.toLowerCase().includes(keyword.toLowerCase()))) {
        score += 30;
        reasons.push(`domain:${keyword}`);
        break;
      }
    }
    for (const keyword of eventKeywords) {
      if (suggestedFor.some((sf) => sf.toLowerCase().includes(keyword.toLowerCase()))) {
        score += 25;
        reasons.push(`event:${keyword}`);
        break;
      }
    }
    if (slotCategories.includes(category)) {
      score += 20;
      reasons.push(`slot:${context.slot}`);
    }
    if (stateCategories.includes(category)) {
      score += 15;
      reasons.push(`state:${context.state}`);
    }
    if (context.hasEnumFields && ["filter-group", "tabs", "badge"].includes(patternName)) {
      score += 10;
      reasons.push("enum-fields");
    }
    if (context.hasDateFields && ["timeline", "chart"].includes(patternName)) {
      score += 10;
      reasons.push("date-fields");
    }
    if (context.hasNumericFields && ["stats", "chart", "meter", "progress-bar"].includes(patternName)) {
      score += 10;
      reasons.push("numeric-fields");
    }
    if (context.hasRelationFields && ["tabs", "master-detail-layout"].includes(patternName)) {
      score += 10;
      reasons.push("relation-fields");
    }
    if (context.hasMediaFields && ["data-grid", "media-gallery"].includes(patternName)) {
      score += 10;
      reasons.push("media-fields");
    }
    if (category === "display" || category === "dashboard") {
      const propsSchema = patternDef.propsSchema || {};
      if ("entity" in propsSchema) {
        score += 5;
        reasons.push("entity-aware");
      }
    }
    if (score > 0) {
      scores.set(patternName, { score, reasons });
    }
  }
  const ranked = [...scores.entries()].sort((a, b) => b[1].score - a[1].score).slice(0, maxResults);
  return ranked.map(([patternName, { score, reasons }]) => {
    const patternDef = patterns[patternName];
    const propsSchema = patternDef.propsSchema || {};
    const keyProps = Object.keys(propsSchema).filter((k) => propsSchema[k]?.required === true).slice(0, 5);
    if (keyProps.length === 0) {
      const meaningful = Object.keys(propsSchema).filter(
        (k) => !["className", "isLoading", "error", "entity"].includes(k)
      );
      keyProps.push(...meaningful.slice(0, 4));
    }
    return {
      pattern: patternName,
      score,
      reason: reasons.join(", "),
      category: patternDef.category || "unknown",
      description: patternDef.description || patternName,
      keyProps
    };
  });
}
function buildRecommendationContext(options) {
  const fields = options.entityFields || [];
  const fieldTypes = fields.map((f) => f.type);
  return {
    state: options.state,
    event: options.event,
    slot: options.slot,
    domainCategory: options.domainCategory,
    entityFieldTypes: fieldTypes,
    // Excludes `type: 'union'`: its `values` carries tagged-union variant
    // NAMES or a by-name recursive back-reference, never a real enum
    // vocabulary — counting it here would recommend enum-oriented patterns
    // (filter-group/tabs/badge) for a struct union or recursive struct field.
    hasEnumFields: fields.some((f) => f.type === "enum" || f.type !== "union" && f.values && f.values.length > 0),
    hasDateFields: fields.some((f) => ["date", "datetime", "timestamp"].includes(f.type)),
    hasNumericFields: fields.some((f) => ["number", "integer", "float", "decimal", "currency"].includes(f.type)),
    hasRelationFields: fields.some((f) => f.type === "relation"),
    hasMediaFields: fields.some((f) => ["image", "url", "media", "file"].includes(f.type))
  };
}
function formatRecommendationsForPrompt(recommendations) {
  if (recommendations.length === 0) return "";
  const lines = [
    "## Recommended Patterns",
    "",
    "| Pattern | Category | Key Props | Why |",
    "|---------|----------|-----------|-----|"
  ];
  for (const rec of recommendations) {
    const props = rec.keyProps.length > 0 ? rec.keyProps.join(", ") : "\u2014";
    lines.push(`| ${rec.pattern} | ${rec.category} | ${props} | ${rec.reason} |`);
  }
  lines.push("");
  lines.push("Use these patterns where appropriate. Combine multiple for rich INIT transitions.");
  return lines.join("\n");
}

// src/patterns/helpers/pattern-swap.ts
var PATTERNS = patterns_registry_default;
var CONTRACTS = event_contracts_default;
function entityInlet(patternType) {
  const propsSchema = PATTERNS.patterns?.[patternType]?.propsSchema;
  if (!propsSchema) return null;
  for (const prop of Object.values(propsSchema)) {
    if (prop.kind === "entity") return prop;
  }
  return propsSchema["entity"] ?? null;
}
function getEntityCardinality(patternType) {
  return entityInlet(patternType)?.cardinality ?? null;
}
function getEmittedEvents(patternType) {
  const emits = CONTRACTS.contracts?.[patternType]?.emits ?? [];
  const out = [];
  for (const e of emits) {
    if (typeof e.event === "string") out.push(e.event);
  }
  return out;
}
function findCompatiblePatterns(currentType, gate = {}) {
  const cardinality = getEntityCardinality(currentType);
  const required = gate.requiredEmits ?? [];
  const result = [];
  for (const type of Object.keys(PATTERNS.patterns ?? {})) {
    if (type === currentType) continue;
    if (getEntityCardinality(type) !== cardinality) continue;
    if (required.length > 0) {
      const emits = new Set(getEmittedEvents(type));
      if (!required.every((ev) => emits.has(ev))) continue;
    }
    result.push(type);
  }
  return result;
}

// src/patterns/index.ts
var registry = patterns_registry_default;
var PATTERN_REGISTRY = patterns_registry_default;
var INTEGRATORS_REGISTRY = integrators_registry_default;
var RUNTIMES_BY_SERVICE = new Map(
  Object.entries(integrators_registry_default.integrators).map(([name, entry]) => [name, entry.runtimes])
);
function isBrowserOnlyService(service) {
  const runtimes = RUNTIMES_BY_SERVICE.get(service) ?? [];
  return runtimes.includes("browser") && !runtimes.includes("node");
}
var COMPONENT_MAPPING = component_mapping_default;
var EVENT_CONTRACTS = event_contracts_default;
function getPatternDefinition(patternType) {
  return patterns_registry_default.patterns?.[patternType] ?? null;
}
function getComponentForPattern(patternType) {
  const mapping = component_mapping_default;
  return mapping.mappings?.[patternType]?.component ?? null;
}
function isEntityAwarePattern(patternType) {
  const definition = getPatternDefinition(patternType);
  if (!definition) return false;
  if (definition.entityAware === true) return true;
  const propsSchema = definition.propsSchema;
  if (!propsSchema) return false;
  return "entity" in propsSchema;
}
var EVENT_OUTLET_KINDS = /* @__PURE__ */ new Set(["event", "event-ref", "callback"]);
var eventKeyPropsCache = /* @__PURE__ */ new Map();
var eventListPropsCache = /* @__PURE__ */ new Map();
function eventKeyPropsOf(patternType) {
  const cached = eventKeyPropsCache.get(patternType);
  if (cached) return cached;
  const propsSchema = getPatternDefinition(patternType)?.propsSchema;
  const out = /* @__PURE__ */ new Set();
  for (const [prop, def] of Object.entries(propsSchema ?? {})) {
    if (def.kind && EVENT_OUTLET_KINDS.has(def.kind)) out.add(prop);
  }
  eventKeyPropsCache.set(patternType, out);
  return out;
}
function isValueInputPattern(patternType) {
  const propsSchema = getPatternDefinition(patternType)?.propsSchema;
  if (!propsSchema || !("value" in propsSchema)) return false;
  return eventKeyPropsOf(patternType).size > 0;
}
function eventListPropsOf(patternType) {
  const cached = eventListPropsCache.get(patternType);
  if (cached) return cached;
  const propsSchema = getPatternDefinition(patternType)?.propsSchema;
  const out = /* @__PURE__ */ new Map();
  for (const [prop, def] of Object.entries(propsSchema ?? {})) {
    if (def.kind === "event-list") out.set(prop, def.eventField ?? "event");
  }
  eventListPropsCache.set(patternType, out);
  return out;
}
function getPatternFieldsContract(patternType) {
  return getPatternDefinition(patternType)?.fieldsContract;
}
function getPatternMinUsableWidths() {
  const out = /* @__PURE__ */ new Map();
  for (const [name, entry] of Object.entries(patterns_registry_default.patterns ?? {})) {
    if (typeof entry.minUsableWidth === "number") out.set(name, entry.minUsableWidth);
  }
  return out;
}
function isDrawablePattern(patternType) {
  const definition = getPatternDefinition(patternType);
  if (!definition) return false;
  if (definition.drawable === true) return true;
  const propsSchema = definition.propsSchema;
  if (!propsSchema) return false;
  return Object.values(propsSchema).some(propGroundsDrawable);
}
function propGroundsDrawable(schema) {
  if (schema.scenePos) return true;
  if (schema.items && propGroundsDrawable(schema.items)) return true;
  if (schema.mapValue && propGroundsDrawable(schema.mapValue)) return true;
  if (schema.properties) {
    for (const v of Object.values(schema.properties)) {
      if (v && propGroundsDrawable(v)) return true;
    }
  }
  return false;
}
function isDrawHostPattern(patternType) {
  const definition = getPatternDefinition(patternType);
  if (!definition) return false;
  if (definition.drawHost === true) return true;
  const propsSchema = definition.propsSchema;
  if (!propsSchema) return false;
  return Object.values(propsSchema).some(propHostsDrawables);
}
function propHostsDrawables(schema) {
  if (schema.drawableSlot) return true;
  if (schema.items && propHostsDrawables(schema.items)) return true;
  if (schema.mapValue && propHostsDrawables(schema.mapValue)) return true;
  if (schema.properties) {
    for (const v of Object.values(schema.properties)) {
      if (v && propHostsDrawables(v)) return true;
    }
  }
  return false;
}

export { COMPONENT_MAPPING, EVENT_CONTRACTS, INTEGRATORS_REGISTRY, PATTERN_REGISTRY, PATTERN_TYPES, PROP_KINDS, SELF_OVERLAY_PATTERN_TYPES, buildRecommendationContext, collectRenderUiPatternTypes, component_mapping_default as componentMapping, event_contracts_default as eventContracts, eventKeyPropsOf, eventListPropsOf, findCompatiblePatterns, formatRecommendationsForPrompt, generatePatternDescription, getAllPatternTypes, getComponentForPattern, getEmittedEvents, getEntityCardinality, getOrbAllowedPatterns, getOrbAllowedPatternsCompact, getOrbAllowedPatternsFiltered, getOrbAllowedPatternsSlim, getPatternActionsRef, getPatternDefinition, getPatternFieldsContract, getPatternMetadata, getPatternMinUsableWidths, getPatternPropsCompact, getPatternsGroupedByCategory, integrators_registry_default as integratorsRegistry, isBrowserOnlyService, isContentBodyPattern, isContentBodyPatternType, isDrawHostPattern, isDrawablePattern, isEntityAwarePattern, isMainSlotRenderUi, isValidPatternType, isValueInputPattern, patterns_registry_default as patternsRegistry, recommendPatterns, registry, renderUiEntriesOf, renderUiPatternTypesOf };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map